import React, { useEffect, useState, useCallback, useRef, useMemo, lazy, Suspense } from "react";
import { MapProvider } from './MapContext';
import { buildMapsUrl, buildApiUrl, API_CONFIG } from '../utils/apiConfig';
import Map from 'ol/Map';
import View from 'ol/View';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import VectorLayer from 'ol/layer/Vector';
import VectorSource from 'ol/source/Vector';
import VectorTileLayer from 'ol/layer/VectorTile';
import VectorTileSource from 'ol/source/VectorTile';
import MVT from 'ol/format/MVT';
import Cluster from 'ol/source/Cluster';
import { defaults as defaultControls } from 'ol/control';
import axios from 'axios';
import { domToCanvas } from 'modern-screenshot';
import Feature from 'ol/Feature';
import Point from 'ol/geom/Point';
import GeoJSON from 'ol/format/GeoJSON';
import Polygon from 'ol/geom/Polygon';
import Style from 'ol/style/Style';
import Modify from 'ol/interaction/Modify';
import Translate from 'ol/interaction/Translate';
import Collection from 'ol/Collection';
import { transformExtent, toLonLat, fromLonLat, getPointResolution } from 'ol/proj';
import {Circle as CircleStyle, Fill, Stroke, Text, Icon as IconStyle} from 'ol/style';
import { buffer as bufferExtent, getCenter as getExtentCenter } from 'ol/extent';
import { unByKey } from 'ol/Observable';
import Geolocation from 'ol/Geolocation';
import LineString from 'ol/geom/LineString';
import { getLength as getGeodesicLength } from 'ol/sphere';
import 'ol/ol.css';
import 'ol-ext/dist/ol-ext.css';
import Transform from 'ol-ext/interaction/Transform';
import { Modal, Header, List, Icon, Button } from 'semantic-ui-react';

import { useAuth } from './authContext';
import TourDetailBarTourUser from './TourDetailBarTourUser';
import MapOnlyView from './MapOnlyView';
import LoadMenu from './LoadMenu';
import MapListBar from './MapListBar';
import PortDockEditBar from './PortDockEditBar';
import TourListBar from './TourListBar';
import FileShareBar from './FileShareBar';
import WorldHeritageSidebarPanel from './WorldHeritageSidebarPanel';
import { buildTilesUrl } from '../utils/apiConfig';
import { fetchTrackingKeys } from '../utils/trackingKeysApi';
import useShipLayersController from '../hooks/useShipLayersController';
import {
  formatDockingPointLabel,
  getEffectivePortCoordinates,
  getEffectivePortLocation,
  getSelectedDockingPoint,
} from '../utils/tourPortLocations';

// Design System imports
import { GeolocationButton } from '../design-system/components';
import { theme } from '../design-system/theme';
import { createMarkerStyle } from '../utils/mapStyles/markerStyles';
import {
  buildCruiseOverviewUrl,
  getCruiseContextFromLocation,
  getGuestCruiseCodeFromLocation,
} from '../utils/cruiseMapContinuity';

const COUNTRY_STATS_DEFAULT_METRIC = 'gdp_pc';
const COUNTRY_STATS_DEFAULT_CURRENCY = 'USD';
const COUNTRY_STATS_ALLOWED_CURRENCIES = ['AUD', 'NZD', 'EUR', 'USD', 'CAD', 'TRY', 'GBP'];
const COUNTRY_STATS_CURRENCY_METRICS = new Set(['gdp', 'gdp_pc', 'salary', 'single', 'rent', 'to_buy']);
const COUNTRY_STATS_DEFAULT_PALETTE = [
  '#FFEDA0',
  '#FED976',
  '#FEB24C',
  '#FD8D3C',
  '#FC4E2A',
  '#E31A1C',
  '#BD0026',
  '#800026',
];
const COUNTRY_STATS_TILE_BASE_URL = 'https://dapi.hosnedl.cloud/api/tiles/country_stats/{z}/{x}/{y}.pbf';
const HILLSHADE_TILE_URL = (
  import.meta.env.VITE_HILLSHADE_TILE_BASE_URL
  || '/tiles/hillshade-v2/{z}/{x}/{y}.webp'
);
const BATHYMETRY_TILE_URL = (
  import.meta.env.VITE_BATHYMETRY_TILE_BASE_URL
  || '/tiles/bathymetry-v3/{z}/{x}/{y}.webp'
);
const TRANSPARENT_TILE_DATA_URL = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
const NAUTICAL_MILE_METERS = 1852;

const buildMapListMarkerIconSrc = ({ selected = false, highlighted = false } = {}) => {
    const stroke = selected || highlighted ? '#ff7a1a' : '#ffffff';
    const strokeWidth = selected || highlighted ? 3.4 : 2.8;
    const svg = `
      <svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42">
        <circle cx="21" cy="21" r="17" fill="#007bff" stroke="${stroke}" stroke-width="${strokeWidth}"/>
        <path d="M12.8 14.7 17.8 12.7v15.8l-5 2V14.7Z" fill="#f8fafc"/>
        <path d="M17.8 12.7 24.2 15.3v15.8l-6.4-2.6V12.7Z" fill="#d1d5db"/>
        <path d="M24.2 15.3 29.2 13.3v15.8l-5 2V15.3Z" fill="#eef2f7"/>
        <path d="M12.8 14.7 17.8 12.7 24.2 15.3 29.2 13.3v15.8l-5 2-6.4-2.6-5 2V14.7Z" fill="none" stroke="#94a3b8" stroke-width="1.4" stroke-linejoin="round"/>
        <path d="M17.8 12.7v15.8M24.2 15.3v15.8" fill="none" stroke="#94a3b8" stroke-width="1.2" stroke-linecap="round"/>
      </svg>
    `;
    return `data:image/svg+xml;charset=UTF-8,${encodeURIComponent(svg)}`;
};

const hexToRgba = (hex, alpha) => {
  if (!hex || typeof hex !== 'string') {
    return `rgba(107, 114, 128, ${alpha})`;
  }
  const normalized = hex.replace('#', '').trim();
  if (normalized.length !== 6) {
    return hex;
  }
  const r = Number.parseInt(normalized.slice(0, 2), 16);
  const g = Number.parseInt(normalized.slice(2, 4), 16);
  const b = Number.parseInt(normalized.slice(4, 6), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const ShipMenu = lazy(() => import('./ShipMenu'));
const PegelModal = lazy(() => import('./PegelModal'));
const ChatBar = lazy(() => import('./ChatBar'));
const MapEditBar = lazy(() => import('./MapEditBar'));
const FlightTrackingBar = lazy(() => import('./FlightTrackingBar'));
const FlightPriceBar = lazy(() => import('./FlightPriceBar'));
const FlightPriceItineraryBar = lazy(() => import('./FlightPriceItineraryBar'));
const FlightRouteCreateBar = lazy(() => import('./FlightRouteCreateBar'));
const TourDetailBar = lazy(() => import('./TourDetailBar'));
const AroundMeBar = lazy(() => import('./AroundMeBar'));
const TrackingDevicesBar = lazy(() => import('./TrackingDevicesBar'));

const getDeclutterMarkerRadiusPx = (feature) => {
  const explicit = Number(feature?.get?.('markerRadiusPx'));
  if (Number.isFinite(explicit)) return Math.max(4, Math.min(36, explicit));
  if (feature?.get?.('isMapMarker') || feature?.get?.('isMapListMarker')) return 18;
  return feature?.get?.('number') ? 12 : 8;
};

const isDeclutterableMapMarker = (feature, { includeMapListMarkers = false } = {}) => {
  if (!feature?.getGeometry?.()) return false;
  if (includeMapListMarkers && feature.get('isMapListMarker')) return true;
  if (!feature.get('number')) return false;
  if (feature.get('disableMarkerDeclutter')) return false;
  if (feature.get('markerLabelType')) return false;
  if (feature.get('isABMarker') || feature.get('isLockMarker') || feature.get('isDockingMarker') || feature.get('isMapMarker')) {
    return false;
  }
  return true;
};

const clearMarkerDeclutterProperties = (feature) => {
  feature.unset?.('declutterDisplacementPx', true);
  feature.unset?.('declutterLeaderCoordinates', true);
};

const declutterMapMarkerFeatures = (features = [], map, options = {}) => {
  if (!map || !Array.isArray(features) || !features.length) return;
  const size = map.getSize?.();
  if (!size || size[0] <= 0 || size[1] <= 0) return;

  const candidates = features
    .filter((feature) => isDeclutterableMapMarker(feature, options))
    .map((feature, sourceIndex) => {
      const geometry = feature.getGeometry();
      const coordinate = geometry?.getCoordinates?.();
      if (!coordinate) return null;
      const pixel = map.getPixelFromCoordinate(coordinate);
      if (!pixel || !Number.isFinite(pixel[0]) || !Number.isFinite(pixel[1])) return null;
      const radius = getDeclutterMarkerRadiusPx(feature);
      const number = Number(feature.get('number'));
      return {
        feature,
        coordinate,
        sourceIndex,
        number: Number.isFinite(number) ? number : Number.MAX_SAFE_INTEGER,
        x: pixel[0],
        y: pixel[1],
        radius,
        minSpacing: Math.max(24, radius * 2.1 + 2),
      };
    })
    .filter(Boolean)
    .sort((a, b) => (a.number - b.number) || (a.sourceIndex - b.sourceIndex));

  features.forEach(clearMarkerDeclutterProperties);
  if (!candidates.length) return;

  const adjacency = new globalThis.Map(candidates.map((item) => [item, []]));
  for (let i = 0; i < candidates.length; i += 1) {
    for (let j = i + 1; j < candidates.length; j += 1) {
      const a = candidates[i];
      const b = candidates[j];
      const spacing = Math.max(a.minSpacing, b.minSpacing);
      if (Math.hypot(a.x - b.x, a.y - b.y) < spacing) {
        adjacency.get(a).push(b);
        adjacency.get(b).push(a);
      }
    }
  }

  const viewportPenalty = (x, y, item) => {
    const margin = item.radius + 6;
    let penalty = 0;
    if (x < margin) penalty += margin - x;
    if (y < margin) penalty += margin - y;
    if (x > size[0] - margin) penalty += x - (size[0] - margin);
    if (y > size[1] - margin) penalty += y - (size[1] - margin);
    return penalty;
  };

  const groups = [];
  const visited = new Set();
  candidates.forEach((item) => {
    if (visited.has(item)) return;
    const group = [];
    const stack = [item];
    visited.add(item);
    while (stack.length) {
      const current = stack.pop();
      group.push(current);
      (adjacency.get(current) || []).forEach((next) => {
        if (visited.has(next)) return;
        visited.add(next);
        stack.push(next);
      });
    }
    groups.push(group.sort((a, b) => (a.number - b.number) || (a.sourceIndex - b.sourceIndex)));
  });

  const applyPlacement = (item, x, y) => {
    const dx = x - item.x;
    const dy = y - item.y;

    if (Math.hypot(dx, dy) > Math.max(8, item.radius * 0.75)) {
      item.feature.set('declutterDisplacementPx', [dx, dy], true);
    }
    item.feature.changed?.();
  };

  groups.forEach((group) => {
    if (group.length <= 1) {
      applyPlacement(group[0], group[0].x, group[0].y);
      return;
    }

    const centerX = group.reduce((sum, item) => sum + item.x, 0) / group.length;
    const centerY = group.reduce((sum, item) => sum + item.y, 0) / group.length;
    const spacing = Math.max(...group.map((item) => item.minSpacing));
    const radius = Math.max(spacing * 0.82, (group.length * spacing) / (2 * Math.PI));
    const ordered = group
      .map((item, index) => ({
        item,
        index,
        angle: Math.atan2(item.y - centerY, item.x - centerX),
      }))
      .sort((a, b) => (a.angle - b.angle) || (a.item.number - b.item.number));
    const angles = ordered.map((_, index) => (-Math.PI / 2) + ((2 * Math.PI * index) / ordered.length));

    ordered.forEach(({ item }, index) => {
      let best = null;
      angles.forEach((baseAngle, angleIndex) => {
        [-Math.PI / 8, 0, Math.PI / 8].forEach((angleOffset, offsetIndex) => {
          const angle = baseAngle + angleOffset;
          const x = centerX + Math.cos(angle) * radius;
          const y = centerY + Math.sin(angle) * radius;
          const score = viewportPenalty(x, y, item) * 100
            + Math.hypot(x - item.x, y - item.y)
            + Math.abs(angleIndex - index) * 0.1
            + offsetIndex * 0.01;
          if (!best || score < best.score) {
            best = { x, y, score };
          }
        });
      });
      applyPlacement(item, best?.x ?? item.x, best?.y ?? item.y);
    });
  });
};

export default function MainMap({ enableUrlStateSync = false } = {}){
const {
    activeUser,
    userMaps,
    userTours,
    setUserMaps,
    corporateMaps,
    setCorporateMaps,
    activeMapToEdit,
    activeTourToShow,
    activeMapToShow,
    setActiveMapToEdit,
    setActiveMapToShow,
    setActiveTourToShow,
    setUserTours,
    fetchFullMapData,
    fetchFullTourData,
    isAuthenticating,
    isFullTourPending,
    getApiHeaders,
    getTileAuthQuery,
    authenticatedUser,
    adminSwitchableUsers,
    switchToUser,
    isUserSwitching,
    isImpersonating,
} = useAuth();
const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth <= 768 : false
  );
const [viewportSize, setViewportSize] = useState(() => ({
  width: typeof window !== 'undefined' ? window.innerWidth : 1366,
  height: typeof window !== 'undefined' ? (window.visualViewport?.height || window.innerHeight) : 768,
}));
const showBootLoading = isAuthenticating && !activeUser;
const lazyFallback = (
  <div
    style={{
      padding: '16px',
      display: 'grid',
      gap: '10px',
      fontFamily: theme.typography.fontFamily.primary,
      color: '#666',
    }}
  >
    <div
      style={{
        height: '14px',
        width: '60%',
        background: 'linear-gradient(90deg, #eee, #f5f5f5, #eee)',
        borderRadius: '8px',
      }}
    />
    <div
      style={{
        height: '10px',
        width: '90%',
        background: 'linear-gradient(90deg, #eee, #f5f5f5, #eee)',
        borderRadius: '8px',
      }}
    />
    <div
      style={{
        height: '10px',
        width: '80%',
        background: 'linear-gradient(90deg, #eee, #f5f5f5, #eee)',
        borderRadius: '8px',
      }}
    />
    <div
      style={{
        height: '120px',
        width: '100%',
        background: 'linear-gradient(90deg, #eee, #f5f5f5, #eee)',
        borderRadius: '12px',
      }}
    />
  </div>
);
const TARGET_RATIO =  1.15 / 1;

const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const getTouchDistance = (touches) => {
  if (!touches || touches.length < 2) return 0;
  const dx = Number(touches[0].clientX) - Number(touches[1].clientX);
  const dy = Number(touches[0].clientY) - Number(touches[1].clientY);
  return Math.hypot(dx, dy);
};

const getTouchMidpoint = (touches) => {
  if (!touches || touches.length < 2) return null;
  return {
    x: (Number(touches[0].clientX) + Number(touches[1].clientX)) / 2,
    y: (Number(touches[0].clientY) + Number(touches[1].clientY)) / 2,
  };
};

const getScaleStepMeters = (maxMeters) => {
  const steps = [
    50, 100, 200, 250, 500,
    1000, 2000, 2500, 5000,
    10000, 20000, 25000, 50000,
    100000, 150000, 200000, 300000, 400000, 500000,
    600000, 800000, 1000000,
  ];
  return steps.reduce((best, step) => (step <= maxMeters ? step : best), steps[0]);
};

const formatScaleMeters = (meters) => {
  if (!Number.isFinite(meters)) return "";
  if (meters >= 1852) {
    const nauticalMiles = meters / 1852;
    return `${nauticalMiles.toFixed(nauticalMiles >= 10 ? 0 : 1)} N.M.`;
  }
  if (meters >= 1000) {
    const km = meters / 1000;
    return `${km.toFixed(km % 1 === 0 ? 0 : 1)} km`;
  }
  return `${Math.round(meters)} m`;
};

const PREVIEW_PAPER_SIZES = {
  a4: { widthMm: 297, heightMm: 210, bottomPanelHeightMm: 46.5 },
  letter: { widthMm: 279.4, heightMm: 215.9, bottomPanelHeightMm: 50 },
};
const PREVIEW_DEFAULT_PAPER_SIZE = 'a4';
const PREVIEW_SIDE_MAP_ASPECT_RATIO = 1.15;

const getPreviewPaperSize = (paperSize) => (
  PREVIEW_PAPER_SIZES[paperSize] || PREVIEW_PAPER_SIZES[PREVIEW_DEFAULT_PAPER_SIZE]
);

const parseCssPixelValue = (value, fallback = 0) => {
  if (typeof value === 'number') {
    return Number.isFinite(value) ? Math.max(0, value) : fallback;
  }
  if (typeof value !== 'string') return fallback;
  const trimmed = value.trim();
  if (!trimmed) return fallback;
  const pxMatch = trimmed.match(/^(-?\d+(?:\.\d+)?)px$/);
  if (pxMatch) return Math.max(0, Number.parseFloat(pxMatch[1]));
  const numeric = Number.parseFloat(trimmed);
  return Number.isFinite(numeric) && /px$/.test(trimmed) ? Math.max(0, numeric) : fallback;
};

const normalizeCssLength = (value, fallback = '0px') => (
  typeof value === 'string' && value.trim() ? value.trim() : fallback
);

const isScrollableElement = (element) => {
  if (!(element instanceof Element)) return false;

  const style = window.getComputedStyle(element);
  const overflowY = style.overflowY || '';
  const overflowX = style.overflowX || '';
  const canScrollY = /(auto|scroll|overlay)/.test(overflowY) && element.scrollHeight > element.clientHeight + 1;
  const canScrollX = /(auto|scroll|overlay)/.test(overflowX) && element.scrollWidth > element.clientWidth + 1;

  return canScrollY || canScrollX;
};

const isInsideScrollableOverlay = (target, root) => {
  if (!(target instanceof Element)) return false;

  let current = target;
  while (current && current !== root) {
    if (
      current instanceof Element &&
      (current.getAttribute('data-scroll-overlay') === 'true' || isScrollableElement(current))
    ) {
      return true;
    }
    current = current.parentElement;
  }

  return false;
};

const mapRef = useRef(null);
const interactionRootRef = useRef(null);
const mapInstanceRef = useRef(null);
const layoutPreviewSnapshotUrlRef = useRef(null);
const clickKeyRef = useRef({ markers: null, buildings: null, rivers: null, countryStatsHover: null });
const markerDeclutterMoveKeyRef = useRef(null);
const layoutPreviewFitKeyRef = useRef(null);
const layoutPreviewProgrammaticFitUntilRef = useRef(0);
const layoutPreviewPreviousRotationRef = useRef(null);
const resizeObsRef = useRef(null);
const overlayZoomGestureStateRef = useRef({
  touchDistance: null,
  gestureScale: null,
});
// Editing / overlay / temp point / buildings
const editRectLayerRef = useRef(null);
const editRectSourceRef = useRef(null);
const editRectFeatureRef = useRef(null);

const shadeLayerRef = useRef(null);
const shadeSourceRef = useRef(null);
const shadeFeatureRef = useRef(null);

const tempPointLayerRef = useRef(null);
const tempPointSourceRef = useRef(null);
const tempPointFeatureRef = useRef(null);

// A/B Markers for draggable route points
const abMarkersLayerRef = useRef(null);
const abMarkersSourceRef = useRef(null);
const abMarkerAFeatureRef = useRef(null);
const abMarkerBFeatureRef = useRef(null);
const translateARef = useRef(null);
const translateBRef = useRef(null);

const buildingsLayerRef = useRef(null);
const buildingsSourceRef = useRef(null);

// Interactions
const modifyRef = useRef(null);
const translateRef = useRef(null);
const translatePointRef = useRef(null);
const resizeRef = useRef(null);
const selectionAspectRatioRef = useRef(TARGET_RATIO);
const geomChangeKeyRef   = useRef(null); // NEW: store OL event key

// Stable ref for mapMethods - maintains identity across re-renders to prevent context re-renders
const mapMethodsRef = useRef(null);




// ✅ Stable sources across remounts
const sourcesRef = useRef(null);
const clusterSourceRef = useRef(null);
if (!sourcesRef.current) {
    sourcesRef.current = {
    markers: new VectorSource(),
    mapListMarkers: new VectorSource(),
    geoJson: new VectorSource(),
    tours: new VectorSource(),
    poiNameLabels: new VectorSource(),
    layoutRoutePreview: new VectorSource(),
    };
    // Clustering is only for map-list map markers. Normal map markers are decluttered visually.
    if (API_CONFIG.ENABLE_MARKER_CLUSTERING) {
        clusterSourceRef.current = new Cluster({
            distance: API_CONFIG.CLUSTER_DISTANCE,
            minDistance: API_CONFIG.CLUSTER_MIN_DISTANCE,
            source: sourcesRef.current.mapListMarkers
        });
    }
}

useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    handleResize(); // ensure first render is correct
    return () => window.removeEventListener('resize', handleResize);
  }, []);

useEffect(() => {
  const handleResize = () => {
    setViewportSize({
      width: window.innerWidth,
      height: window.visualViewport?.height || window.innerHeight,
    });
  };

  handleResize();
  window.addEventListener('resize', handleResize);
  window.addEventListener('orientationchange', handleResize);
  window.visualViewport?.addEventListener('resize', handleResize);

  return () => {
    window.removeEventListener('resize', handleResize);
    window.removeEventListener('orientationchange', handleResize);
    window.visualViewport?.removeEventListener('resize', handleResize);
  };
}, []);

useEffect(() => {
  const setVhVar = () => {
    const height = window.visualViewport?.height || window.innerHeight;
    const vh = height * 0.01;
    document.documentElement.style.setProperty('--vh', `${vh}px`);
  };

  setVhVar();
  window.addEventListener('resize', setVhVar);
  window.addEventListener('orientationchange', setVhVar);
  window.visualViewport?.addEventListener('resize', setVhVar);

  return () => {
    window.removeEventListener('resize', setVhVar);
    window.removeEventListener('orientationchange', setVhVar);
    window.visualViewport?.removeEventListener('resize', setVhVar);
  };
}, []);

// Keep layer refs (optional but convenient)
const markersLayerRef = useRef(null);
const mapListMarkersLayerRef = useRef(null);
const poiNameLabelsLayerRef = useRef(null);
const layoutRoutePreviewLayerRef = useRef(null);
const geoJsonLayerRef  = useRef(null);
const routeDistanceLabelsLayerRef = useRef(null);
const routeDistanceLabelsSourceRef = useRef(null);
const routeDistanceLabelGroupsRef = useRef([]);
const toursLayerRef    = useRef(null);
const userDeviceLocationsLayerRef = useRef(null);
const userDeviceLocationsSourceRef = useRef(null);
const riverKmLayerRef = useRef(null);
const hillshadeLayerRef = useRef(null);
const bathymetryLayerRef = useRef(null);
const hydroBasinsLayerRef = useRef(null);
const holidayCountriesLayerRef = useRef(null);
const countryStatsLayerRef = useRef(null);
const countryStatsPaletteRef = useRef(COUNTRY_STATS_DEFAULT_PALETTE);
const countryStatsNullColorRef = useRef('#E5E7EB');
const countryStatsStyleCacheRef = useRef(new globalThis.Map());
const routeDistanceLabelStyleCacheRef = useRef(new globalThis.Map());
const countryCompareLayerRef = useRef(null);
const countryCompareSourceRef = useRef(null);
const countryCompareFeatureRef = useRef(null);
const riversLayerRef = useRef(null);
const riversSourceRef = useRef(null);
const riversLoadedRef = useRef(false);
const countryCompareTranslateRef = useRef(null);
const countryCompareTranslateStartKeyRef = useRef(null);
const countryCompareTranslatingKeyRef = useRef(null);
const countryCompareTranslateEndKeyRef = useRef(null);
const countryCompareBaseGeometry4326Ref = useRef(null);
const countryCompareBaseCenterLonLatRef = useRef(null);
const countryCompareBaseGeometryProjectedRef = useRef(null);
const countryCompareBaseCenterProjectedRef = useRef(null);
const countryCompareRotationRadiansRef = useRef(0);
const countryComparePointerCenterOffsetRef = useRef([0, 0]);
const countryCompareDragRafRef = useRef(null);
const poiNameLabelTranslateRef = useRef(null);
const poiNameLabelTranslateEndKeyRef = useRef(null);

const [mapReady, setMapReady] = useState(false);
const [showRiverKm, setShowRiverKm] = useState(false);
const [showHillshade, setShowHillshade] = useState(false);
const [showBathymetry, setShowBathymetry] = useState(false);
const [showHydroBasins, setShowHydroBasins] = useState(false);
const [showHolidayCountries, setShowHolidayCountries] = useState(false);
const [showCountryStats, setShowCountryStats] = useState(false);
const [countryStatsMetricOptions, setCountryStatsMetricOptions] = useState([]);
const [countryStatsMetricLoading, setCountryStatsMetricLoading] = useState(false);
const [countryStatsMetricError, setCountryStatsMetricError] = useState('');
const [countryStatsSelectedMetric, setCountryStatsSelectedMetric] = useState(COUNTRY_STATS_DEFAULT_METRIC);
const [countryStatsCurrencyOptions, setCountryStatsCurrencyOptions] = useState([COUNTRY_STATS_DEFAULT_CURRENCY]);
const [countryStatsCurrencyLoading, setCountryStatsCurrencyLoading] = useState(false);
const [countryStatsCurrencyError, setCountryStatsCurrencyError] = useState('');
const [countryStatsSelectedCurrency, setCountryStatsSelectedCurrency] = useState(COUNTRY_STATS_DEFAULT_CURRENCY);
const [countryStatsPalette, setCountryStatsPalette] = useState(COUNTRY_STATS_DEFAULT_PALETTE);
const [countryStatsNullColor, setCountryStatsNullColor] = useState('#E5E7EB');
const [countryStatsLegendMeta, setCountryStatsLegendMeta] = useState(null);
const [countryStatsLegendLoading, setCountryStatsLegendLoading] = useState(false);
const [countryStatsLegendError, setCountryStatsLegendError] = useState('');
const [countryStatsPanelCollapsed, setCountryStatsPanelCollapsed] = useState(false);
const [showCountryCompare, setShowCountryCompare] = useState(false);
const [countryComparePanelCollapsed, setCountryComparePanelCollapsed] = useState(false);
const [countryCompareOptions, setCountryCompareOptions] = useState([]);
const [countryCompareSelectedIso3, setCountryCompareSelectedIso3] = useState('');
const [countryCompareLoadingOptions, setCountryCompareLoadingOptions] = useState(false);
const [countryCompareLoadingShape, setCountryCompareLoadingShape] = useState(false);
const [countryCompareError, setCountryCompareError] = useState('');
const [countryCompareFeatureVersion, setCountryCompareFeatureVersion] = useState(0);
const [countryCompareResizeWithLatitude, setCountryCompareResizeWithLatitude] = useState(false);
const [countryCompareFlipDistortion, setCountryCompareFlipDistortion] = useState(true);
const [hydroBasinsLevel, setHydroBasinsLevel] = useState(6);
const [showRiversSystem, setShowRiversSystem] = useState(false);
const [isLocating, setIsLocating] = useState(false);
const [showMaptilerSatellite, setShowMaptilerSatellite] = useState(false);
const maptilerLayerRef = useRef(null);
const capabilityCodes = useMemo(
    () => (Array.isArray(activeUser?.capabilities)
        ? activeUser.capabilities
        : (Array.isArray(activeUser?.subscription?.capabilities) ? activeUser.subscription.capabilities : [])),
    [activeUser?.capabilities, activeUser?.subscription?.capabilities]
);
const capabilitySet = useMemo(() => new Set(capabilityCodes), [capabilityCodes]);
const hasCapabilityList = capabilityCodes.length > 0;
const hasCapability = useCallback((codes, legacyValue = false) => {
    const normalizedCodes = Array.isArray(codes) ? codes : [codes];
    if (capabilitySet.has('*')) {
        return true;
    }
    if (normalizedCodes.some((code) => capabilitySet.has(code))) {
        return true;
    }
    return !hasCapabilityList && Boolean(legacyValue);
}, [capabilitySet, hasCapabilityList]);
const canUseSatelliteTiles = useMemo(() => {
    if (capabilitySet.has('*')) {
        return true;
    }
    return capabilitySet.has('tiles.satellite.use');
}, [capabilitySet]);
const canShowUserDeviceLocations = useMemo(
    () => hasCapability('devices.view', activeUser?.subscription?.can_edit_tours),
    [activeUser?.subscription?.can_edit_tours, hasCapability]
);
const canViewRivers = useMemo(
    () => hasCapability(
        'rivers.view',
        activeUser?.subscription?.can_view_rivers ?? activeUser?.subscription?.can_navigate
    ),
    [activeUser?.subscription?.can_navigate, activeUser?.subscription?.can_view_rivers, hasCapability]
);
const canViewLocks = useMemo(
    () => hasCapability('locks.view', activeUser?.subscription?.can_view_locks),
    [activeUser?.subscription?.can_view_locks, hasCapability]
);
const canViewDocks = useMemo(
    () => hasCapability(
        'docks.view',
        activeUser?.subscription?.can_view_ports_docks ?? activeUser?.subscription?.can_view_locks
    ),
    [activeUser?.subscription?.can_view_locks, activeUser?.subscription?.can_view_ports_docks, hasCapability]
);
const canViewPegels = canViewRivers;
const isRestrictedGuestLayerContext = activeUser?.object_type === 'tour_user' || activeUser?.object_type === 'map';
const canShowRiverSystemLayer = !isRestrictedGuestLayerContext || canViewRivers;
const canShowLocksLayer = !isRestrictedGuestLayerContext || canViewLocks;
const canShowDocksLayer = !isRestrictedGuestLayerContext || canViewDocks;
const canShowPegelsLayer = !isRestrictedGuestLayerContext || canViewPegels;
const canSwitchUsers = Boolean(authenticatedUser?.role === 'admin');


// Sidebar state management
const [sidebarConfig, setSidebarConfig] = useState({
    type: null, // 'tour_user', 'tour_admin', etc.
    width: '0px',
    height: '0px',
    position: 'right' // 'left' or 'right'
});

// Dynamic sidebar height (updated when user drags to resize)
const [dynamicSidebarHeight, setDynamicSidebarHeight] = useState(null);
const previousSidebarTypeRef = useRef(null);
const [selectedWorldHeritageSite, setSelectedWorldHeritageSite] = useState(null);
const [showWorldHeritagePanel, setShowWorldHeritagePanel] = useState(false);
const [isLoadingWorldHeritageDetails, setIsLoadingWorldHeritageDetails] = useState(false);
const [worldHeritageDetailsError, setWorldHeritageDetailsError] = useState("");
const [worldHeritageParentSiteDetails, setWorldHeritageParentSiteDetails] = useState(null);
const [isLoadingWorldHeritageParentSiteDetails, setIsLoadingWorldHeritageParentSiteDetails] = useState(false);
const [worldHeritageParentSiteDetailsError, setWorldHeritageParentSiteDetailsError] = useState("");
const [holidayModalState, setHolidayModalState] = useState({
  open: false,
  loading: false,
  countryName: '',
  countryIso3: '',
  daysUntil: null,
  urgencyBucket: '',
  holidays: [],
  error: '',
});
const [countryStatsModalState, setCountryStatsModalState] = useState({
  open: false,
  loading: false,
  countryName: '',
  countryIso3: '',
  countryCapital: '',
  metrics: [],
  displayMedia: {},
  error: '',
});
const [countryStatsHoverHint, setCountryStatsHoverHint] = useState({
  visible: false,
  x: 0,
  y: 0,
  countryName: '',
  metricLabel: '',
  metricValue: '',
});

// Sidebar visibility states
const [showMapListBar, setShowMapListBar] = useState(false);
const [showSideEditBar, setShowSideEditBar] = useState(false);
const [showTourListBar, setShowTourListBar] = useState(false);
const [tourListWideSidebar, setTourListWideSidebar] = useState(false);
const [showTourDetailBar, setShowTourDetailBar] = useState(false);
const [tourDetailWideSidebar, setTourDetailWideSidebar] = useState(false);
const [showChatBar, setShowChatBar] = useState(false);
const [showShipMenu, setShowShipMenu] = useState(false);
const [showAroundMeBar, setShowAroundMeBar] = useState(false);
const [shipMenuRouteMode, setShipMenuRouteMode] = useState(null);
const [shipMenuActivateRiverMarker, setShipMenuActivateRiverMarker] = useState(false);
const [shipMenuRoutePrefill, setShipMenuRoutePrefill] = useState(null);
const [shipMenuPlannerPoints, setShipMenuPlannerPoints] = useState(null);
const [shipMenuReturnToAroundMe, setShipMenuReturnToAroundMe] = useState(null);
const [shipMenuReturnToTourDetail, setShipMenuReturnToTourDetail] = useState(false);
const [shipLayerClickEvent, setShipLayerClickEvent] = useState(null);
const shipLayerClickEventIdRef = useRef(0);
const [pegelLayerClickEvent, setPegelLayerClickEvent] = useState(null);
const pegelLayerClickEventIdRef = useRef(0);
const shipMenuInteractiveLayersRef = useRef({ routePathLayer: null, shipsLayer: null });
const shipMenuDockEditHandlerRef = useRef(null);
const [selectedShipLayerLock, setSelectedShipLayerLock] = useState(null);
const [selectedShipLayerDock, setSelectedShipLayerDock] = useState(null);
const [selectedShipLayerShip, setSelectedShipLayerShip] = useState(null);
const [selectedShipLayerPath, setSelectedShipLayerPath] = useState(null);
const [selectedShipHistoryMarker, setSelectedShipHistoryMarker] = useState(null);
const [showShipLayerLockModal, setShowShipLayerLockModal] = useState(false);
const [showShipLayerDockModal, setShowShipLayerDockModal] = useState(false);
const [showShipLayerShipModal, setShowShipLayerShipModal] = useState(false);
const [showShipLayerPathModal, setShowShipLayerPathModal] = useState(false);
const [showShipHistoryMarkerModal, setShowShipHistoryMarkerModal] = useState(false);
const [aroundMeRequestedSelectedLonLat, setAroundMeRequestedSelectedLonLat] = useState(null);
const [showPortDockEditBar, setShowPortDockEditBar] = useState(false);
const [pendingDockEditRequest, setPendingDockEditRequest] = useState(null);
const [showFlightTrackingBar, setShowFlightTrackingBar] = useState(false);
const [tourDetailMapOverlaysSuppressed, setTourDetailMapOverlaysSuppressed] = useState(false);
const [showFlightPriceBar, setShowFlightPriceBar] = useState(false);
const [flightPriceBarTourId, setFlightPriceBarTourId] = useState(null);
const [flightPriceBarMode, setFlightPriceBarMode] = useState("tour");
const [flightPriceHistoryState, setFlightPriceHistoryState] = useState({
  open: false,
  loading: false,
  data: null,
  selectedRoute: null,
});
  const [flightPriceHistoryFilters, setFlightPriceHistoryFilters] = useState({
    outbound_start_from: "",
    outbound_end_until: "",
    return_start_from: "",
    return_start_until: "",
    return_end_until: "",
    max_leg_duration: "",
    min_leg_duration: "",
    max_total_layover: "",
    min_total_layover: "",
    max_layover_minutes: "",
    min_layover_minutes: "",
});
const [returnToTourDetailOnClose, setReturnToTourDetailOnClose] = useState(false);
const [showFlightRouteCreateBar, setShowFlightRouteCreateBar] = useState(false);
const [flightRouteCreateBarTourId, setFlightRouteCreateBarTourId] = useState(null);
const [flightRouteCreateBarMode, setFlightRouteCreateBarMode] = useState("tour");
const [returnToTourDetailOnCloseCreate, setReturnToTourDetailOnCloseCreate] = useState(false);
const [showFileShareBar, setShowFileShareBar] = useState(false);
const [showMapOnlyView, setShowMapOnlyView] = useState(true);
const [showTourUserDetailView, setShowTourUserDetailView] = useState(true);
const [mapOnlyOriginContext, setMapOnlyOriginContext] = useState(null);
const [tourUserMapContext, setTourUserMapContext] = useState(null);
const [priceHistorySidebarState, setPriceHistorySidebarState] = useState({
  open: false,
  width: null,
  height: null,
});
const shipLayersController = useShipLayersController({
  getMap: () => mapInstanceRef.current,
  getApiHeaders,
  getTileAuthQuery,
});

useEffect(() => {
  countryStatsPaletteRef.current = Array.isArray(countryStatsPalette) && countryStatsPalette.length > 0
    ? countryStatsPalette
    : COUNTRY_STATS_DEFAULT_PALETTE;
  countryStatsNullColorRef.current = countryStatsNullColor || '#E5E7EB';
  if (typeof countryStatsStyleCacheRef.current?.clear === 'function') {
    countryStatsStyleCacheRef.current.clear();
  } else {
    countryStatsStyleCacheRef.current = new globalThis.Map();
  }
  countryStatsLayerRef.current?.changed?.();
}, [countryStatsNullColor, countryStatsPalette]);

const isWorldHeritageComponentPanel =
  String(selectedWorldHeritageSite?.feature_kind || "").toLowerCase() === "component";

const loadWorldHeritageSiteDetails = useCallback(async (siteId) => {
  if (!siteId) return;
  setIsLoadingWorldHeritageDetails(true);
  setWorldHeritageDetailsError("");
  try {
    const res = await axios.get(buildApiUrl(`list_world_heritage_sites/${siteId}/`), {
      headers: await getApiHeaders(),
    });
    const detail = res.data || {};
    setSelectedWorldHeritageSite((prev) => ({ ...(prev || {}), ...detail, feature_kind: "site" }));
  } catch (error) {
    console.error("Error fetching World Heritage site details:", error);
    setWorldHeritageDetailsError("Failed to load full site details.");
  } finally {
    setIsLoadingWorldHeritageDetails(false);
  }
}, [getApiHeaders]);

const loadWorldHeritageParentSiteDetails = useCallback(async (siteId) => {
  if (!siteId) return;
  setIsLoadingWorldHeritageParentSiteDetails(true);
  setWorldHeritageParentSiteDetailsError("");
  try {
    const res = await axios.get(buildApiUrl(`list_world_heritage_sites/${siteId}/`), {
      headers: await getApiHeaders(),
    });
    setWorldHeritageParentSiteDetails(res.data || null);
  } catch (error) {
    console.error("Error fetching World Heritage parent site details:", error);
    setWorldHeritageParentSiteDetails(null);
    setWorldHeritageParentSiteDetailsError("Failed to load parent site details.");
  } finally {
    setIsLoadingWorldHeritageParentSiteDetails(false);
  }
}, [getApiHeaders]);

const loadWorldHeritageComponentDetails = useCallback(async (componentId) => {
  if (!componentId) return;
  setIsLoadingWorldHeritageDetails(true);
  setWorldHeritageDetailsError("");
  try {
    const res = await axios.get(buildApiUrl(`world_heritage_site_components/${componentId}/`), {
      headers: await getApiHeaders(),
    });
    const detail = res.data || {};
    setSelectedWorldHeritageSite((prev) => ({ ...(prev || {}), ...detail, feature_kind: "component" }));
  } catch (error) {
    console.error("Error fetching World Heritage component details:", error);
    setWorldHeritageDetailsError("Failed to load full component details.");
  } finally {
    setIsLoadingWorldHeritageDetails(false);
  }
}, [getApiHeaders]);

const closeWorldHeritagePanel = useCallback(() => {
  setShowWorldHeritagePanel(false);
  setSelectedWorldHeritageSite(null);
  setWorldHeritageDetailsError("");
  setWorldHeritageParentSiteDetails(null);
  setWorldHeritageParentSiteDetailsError("");
  setIsLoadingWorldHeritageParentSiteDetails(false);
  shipLayersController.clearWorldHeritageActiveSelection?.();
}, [shipLayersController]);

const handleWorldHeritageFeatureClick = useCallback((whsData = {}) => {
  const featureKind =
    String(whsData?.feature_kind || "").toLowerCase() === "component" ? "component" : "site";
  const parentSiteId = Number(whsData?.parent_site_id || (featureKind === "site" ? whsData?.id : null));
  const featureId = Number(whsData?.id);

  if (Number.isFinite(parentSiteId)) {
    shipLayersController.setWorldHeritageActiveSelection?.({
      parentSiteId,
      siteId: featureKind === "site" && Number.isFinite(featureId) ? featureId : parentSiteId,
      componentId: featureKind === "component" && Number.isFinite(featureId) ? featureId : null,
    });
  }

  // Open WHS details as the active right sidebar (similar to AroundMeBar).
  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setTourDetailWideSidebar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);

  setSelectedWorldHeritageSite({ ...whsData, feature_kind: featureKind });
  setWorldHeritageDetailsError("");
  setShowWorldHeritagePanel(true);

  if (featureKind === "component" && whsData?.id) {
    setWorldHeritageParentSiteDetails(null);
    setWorldHeritageParentSiteDetailsError("");
    loadWorldHeritageComponentDetails(whsData.id);
    if (Number.isFinite(parentSiteId)) {
      loadWorldHeritageParentSiteDetails(parentSiteId);
    }
  } else if (whsData?.id) {
    setWorldHeritageParentSiteDetails(null);
    setWorldHeritageParentSiteDetailsError("");
    loadWorldHeritageSiteDetails(whsData.id);
  }
}, [loadWorldHeritageComponentDetails, loadWorldHeritageParentSiteDetails, loadWorldHeritageSiteDetails, shipLayersController]);

const openWorldHeritageParentSite = useCallback(async () => {
  const parentSiteId = selectedWorldHeritageSite?.parent_site_id || selectedWorldHeritageSite?.site_id;
  if (!parentSiteId) return;

  shipLayersController.setWorldHeritageActiveSelection?.({
    parentSiteId,
    siteId: parentSiteId,
    componentId: null,
  });

  setSelectedWorldHeritageSite((prev) => ({
    ...(prev || {}),
    id: Number(parentSiteId),
    feature_kind: "site",
    name: prev?.parent_site_name || prev?.name || "World Heritage Site",
    unesco_id: prev?.parent_site_unesco_id || prev?.unesco_id,
    category: prev?.parent_site_category || prev?.category,
  }));
  setWorldHeritageParentSiteDetails(null);
  setWorldHeritageParentSiteDetailsError("");
  setShowWorldHeritagePanel(true);
  await loadWorldHeritageSiteDetails(parentSiteId);
}, [loadWorldHeritageSiteDetails, selectedWorldHeritageSite, shipLayersController]);

const closeHolidayModal = useCallback(() => {
  setHolidayModalState((prev) => ({
    ...prev,
    open: false,
    loading: false,
    error: '',
  }));
}, []);

const closeCountryStatsModal = useCallback(() => {
  setCountryStatsModalState((prev) => ({
    ...prev,
    open: false,
    loading: false,
    error: '',
  }));
}, []);

const loadHolidayCountryModalData = useCallback(async (countryFeature = {}) => {
  const countryIso3 = String(countryFeature?.iso3 || '').trim().toUpperCase();
  const countryName = countryFeature?.country_name || countryFeature?.name || countryIso3 || 'Country';
  const featureDaysUntil = Number.isFinite(Number(countryFeature?.days_until))
    ? Number(countryFeature.days_until)
    : null;
  const featureUrgencyBucket = String(countryFeature?.urgency_bucket || '').trim();

  if (!countryIso3) {
    setHolidayModalState({
      open: true,
      loading: false,
      countryName,
      countryIso3: '',
      daysUntil: featureDaysUntil,
      urgencyBucket: featureUrgencyBucket,
      holidays: [],
      error: 'Country code is missing for this map feature.',
    });
    return;
  }

  setHolidayModalState({
    open: true,
    loading: true,
    countryName,
    countryIso3,
    daysUntil: featureDaysUntil,
    urgencyBucket: featureUrgencyBucket,
    holidays: [],
    error: '',
  });

  try {
    const response = await axios.get(
      buildApiUrl(`holidays/?country_code=${encodeURIComponent(countryIso3)}&page_size=500`),
      { headers: await getApiHeaders() }
    );

    const payload = response?.data;
    const holidays = Array.isArray(payload)
      ? payload
      : (Array.isArray(payload?.results) ? payload.results : []);

    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const endDate = new Date(today);
    endDate.setDate(endDate.getDate() + 21);

    const upcomingHolidays = holidays
      .map((holiday) => {
        const parsedDate = new Date(holiday?.date);
        return { ...holiday, __parsedDate: parsedDate };
      })
      .filter((holiday) => {
        const date = holiday.__parsedDate;
        return (
          date instanceof Date &&
          !Number.isNaN(date.getTime()) &&
          date >= today &&
          date <= endDate &&
          holiday?.global_holiday === true
        );
      })
      .sort((a, b) => a.__parsedDate.getTime() - b.__parsedDate.getTime())
      .map((holiday) => ({
        ...holiday,
        days_until: Math.max(
          0,
          Math.floor((holiday.__parsedDate.getTime() - today.getTime()) / (24 * 60 * 60 * 1000))
        ),
      }));

    setHolidayModalState((prev) => ({
      ...prev,
      open: true,
      loading: false,
      holidays: upcomingHolidays,
      error: '',
    }));
  } catch (error) {
    console.error('Error fetching holiday details for country:', countryIso3, error);
    setHolidayModalState((prev) => ({
      ...prev,
      open: true,
      loading: false,
      holidays: [],
      error: 'Failed to load upcoming holidays.',
    }));
  }
}, [getApiHeaders]);

const loadCountryStatsModalData = useCallback(async (countryFeature = {}) => {
  const countryIso3 = String(countryFeature?.iso3 || '').trim().toUpperCase();
  const countryName = countryFeature?.country_name || countryFeature?.name || countryIso3 || 'Country';

  if (!countryIso3) {
    setCountryStatsModalState({
      open: true,
      loading: false,
      countryName,
      countryIso3: '',
      countryCapital: '',
      metrics: [],
      displayMedia: {},
      error: 'Country code is missing for this map feature.',
    });
    return;
  }

  setCountryStatsModalState({
    open: true,
    loading: true,
    countryName,
    countryIso3,
    countryCapital: '',
    metrics: [],
    displayMedia: {},
    error: '',
  });

  try {
    const response = await axios.get(
      buildApiUrl(
        `country-stats/${encodeURIComponent(countryIso3)}/?base_currency=${encodeURIComponent(countryStatsSelectedCurrency)}`,
      ),
      { headers: await getApiHeaders() },
    );

    const payload = response?.data || {};
    const countryPayload = payload?.country || {};
    const metrics = Array.isArray(payload?.metrics) ? payload.metrics : [];
    const displayMedia = payload?.display_media && typeof payload.display_media === 'object'
      ? payload.display_media
      : {};

    setCountryStatsModalState({
      open: true,
      loading: false,
      countryName: countryPayload?.name || countryName,
      countryIso3: countryPayload?.iso3_code || countryIso3,
      countryCapital: countryPayload?.capital || '',
      metrics,
      displayMedia,
      error: '',
    });
  } catch (error) {
    console.error('Error fetching country stats details for country:', countryIso3, error);
    setCountryStatsModalState({
      open: true,
      loading: false,
      countryName,
      countryIso3,
      countryCapital: '',
      metrics: [],
      displayMedia: {},
      error: 'Failed to load country statistics.',
    });
  }
}, [countryStatsSelectedCurrency, getApiHeaders]);

const formatCountryStatsMetricValue = useCallback((metric) => {
  if (!metric) return 'No data';

  if (metric.value_text) {
    return String(metric.value_text);
  }

  if (Array.isArray(metric.components) && metric.components.length > 0) {
    return metric.components
      .slice()
      .sort((a, b) => Number(a.rank || 999) - Number(b.rank || 999))
      .slice(0, 6)
      .map((component) => {
        const componentValue = component?.value_text
          || (component?.value_numeric !== undefined && component?.value_numeric !== null
            ? `${component.value_numeric}${component.unit ? ` ${component.unit}` : ''}`
            : '');
        return componentValue ? `${component.label}: ${componentValue}` : component.label;
      })
      .join(', ');
  }

  if (metric.value_numeric !== undefined && metric.value_numeric !== null) {
    const numericValue = Number(metric.value_numeric);
    if (!Number.isFinite(numericValue)) {
      return String(metric.value_numeric);
    }
    const valueJson = metric.value_json && typeof metric.value_json === 'object' ? metric.value_json : {};
    const currencyCode = valueJson.currency_code ? `${valueJson.currency_code} ` : '';
    const unit = valueJson.unit === 'sqm'
      ? '/sq m'
      : valueJson.unit
      ? `/${valueJson.unit}`
      : '';
    const formatted = Number.isInteger(numericValue)
      ? numericValue.toLocaleString()
      : numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
    return `${currencyCode}${formatted}${unit || ''}`.trim();
  }

  return 'No data';
}, []);

const formatCountryStatsHoverValue = useCallback((rawValue) => {
  const numericValue = Number(rawValue);
  if (!Number.isFinite(numericValue)) {
    return rawValue === undefined || rawValue === null || rawValue === '' ? 'No data' : String(rawValue);
  }
  return Number.isInteger(numericValue)
    ? numericValue.toLocaleString()
    : numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
}, []);

const prettifyCountryStatsSourceName = useCallback((sourceName) => {
  const normalized = String(sourceName || '').trim().toLowerCase();
  const aliases = {
    world_bank: 'World Bank',
    un_pop: 'UN Population Division',
    fao: 'FAOSTAT',
    ceoworld_salary: 'CEOWORLD Magazine',
    cost_of_living_manual: 'Manual cost of living research',
    wiki_religion: 'Wikipedia',
    wiki_ethnicity: 'Wikipedia',
    wiki_elevation: 'Wikipedia',
    wiki_transport: 'Wikipedia',
  };
  if (aliases[normalized]) return aliases[normalized];
  return String(sourceName || '').replace(/_/g, ' ').replace(/\b\w/g, (char) => char.toUpperCase());
}, []);

const getCountryStatsSourceLink = useCallback((metric) => {
  if (!metric || typeof metric !== 'object') return '';

  const sourceName = String(metric.source_name || '').trim().toLowerCase();
  const seriesCode = String(metric.source_series_code || '').trim();
  if (sourceName === 'world_bank' && seriesCode) {
    return `https://data.worldbank.org/indicator/${encodeURIComponent(seriesCode)}`;
  }
  return metric.source_url ? String(metric.source_url) : '';
}, []);

const getCountryStatsMetricSourceRows = useCallback((metric) => {
  if (!metric || typeof metric !== 'object') return [];

  const rows = [];
  const sourceYear = metric.source_year ?? metric.year ?? null;
  if (sourceYear !== null && sourceYear !== undefined && sourceYear !== '') {
    rows.push({ label: 'Source year', value: String(sourceYear) });
  } else if (metric.year !== null && metric.year !== undefined && metric.year !== '') {
    rows.push({ label: 'Snapshot year', value: String(metric.year) });
  }

  if (metric.source_name) {
    rows.push({ label: 'Source', value: prettifyCountryStatsSourceName(metric.source_name) });
  }
  if (metric.source_dataset) {
    rows.push({ label: 'Dataset', value: String(metric.source_dataset) });
  }
  if (metric.source_series_code) {
    rows.push({ label: 'Indicator', value: String(metric.source_series_code) });
  }
  const sourceLink = getCountryStatsSourceLink(metric);
  if (sourceLink) {
    rows.push({ label: 'Link', value: String(sourceLink), href: String(sourceLink) });
  }
  return rows;
}, [getCountryStatsSourceLink, prettifyCountryStatsSourceName]);

const formatCountryStatsUnitLabel = useCallback((metric) => {
  const rawUnit = String(metric?.unit_label || '').replace(/<[^>]+>/g, ' ').replace(/`/g, ' ').trim();
  const normalizedUnit = rawUnit.replace(/\s+/g, ' ').trim();
  if (!normalizedUnit) return '';
  return normalizedUnit;
}, []);

const formatCountryStatsOptionUnitLabel = useCallback((metricOption) => {
  const rawUnit = String(metricOption?.html_short || '')
    .replace(/<[^>]+>/g, ' ')
    .replace(/[`$]/g, ' ')
    .trim();
  const normalizedUnit = rawUnit.replace(/\s+/g, ' ').trim();
  if (!normalizedUnit) return '';
  return normalizedUnit;
}, []);

const getCountryStatsSectionTitle = useCallback((category) => {
  const normalized = String(category || '').trim().toLowerCase();
  if (normalized === 'geo') return 'Geography';
  if (normalized === 'peopl' || normalized === 'covid') return 'People and society';
  if (normalized === 'econ') return 'Economy and prices';
  return 'Other';
}, []);

const countryStatsMetricSupportsCurrency = COUNTRY_STATS_CURRENCY_METRICS.has(String(countryStatsSelectedMetric || '').trim());
const currentCountryStatsMetricOption = useMemo(
  () => countryStatsMetricOptions.find((item) => item.data_name === countryStatsSelectedMetric) || null,
  [countryStatsMetricOptions, countryStatsSelectedMetric],
);

const formatCountryStatsLegendValue = useCallback((value) => {
  const numericValue = Number(value);
  if (!Number.isFinite(numericValue)) return 'No data';

  if (countryStatsMetricSupportsCurrency) {
    const roundedValue = Math.abs(numericValue) >= 100 ? Math.round(numericValue) : numericValue.toFixed(2);
    return `${countryStatsSelectedCurrency} ${Number(roundedValue).toLocaleString()}`;
  }

  if (Math.abs(numericValue) >= 1000) {
    return numericValue.toLocaleString(undefined, { maximumFractionDigits: 0 });
  }
  if (Math.abs(numericValue) >= 100) {
    return numericValue.toLocaleString(undefined, { maximumFractionDigits: 1 });
  }
  if (Math.abs(numericValue) >= 10) {
    return numericValue.toLocaleString(undefined, { maximumFractionDigits: 2 });
  }
  return numericValue.toLocaleString(undefined, { maximumFractionDigits: 3 });
}, [countryStatsMetricSupportsCurrency, countryStatsSelectedCurrency]);

const countryStatsLegendItems = useMemo(() => {
  if (!countryStatsLegendMeta) return [];
  const thresholds = Array.isArray(countryStatsLegendMeta.thresholds)
    ? countryStatsLegendMeta.thresholds.map((value) => Number(value)).filter((value) => Number.isFinite(value))
    : [];
  const palette = Array.isArray(countryStatsLegendMeta.palette) && countryStatsLegendMeta.palette.length > 0
    ? countryStatsLegendMeta.palette
    : COUNTRY_STATS_DEFAULT_PALETTE;
  const minValue = Number(countryStatsLegendMeta.min_value);
  const maxValue = Number(countryStatsLegendMeta.max_value);
  if (!Number.isFinite(minValue) || !Number.isFinite(maxValue) || palette.length === 0) {
    return [];
  }

  const items = [];
  for (let index = 0; index < palette.length; index += 1) {
    const lowerBound = index === 0 ? minValue : thresholds[index - 1];
    const upperBound = index < thresholds.length ? thresholds[index] : maxValue;

    let label = formatCountryStatsLegendValue(upperBound);
    if (index === 0) {
      label = `≤ ${formatCountryStatsLegendValue(upperBound)}`;
    } else if (index >= thresholds.length) {
      label = `> ${formatCountryStatsLegendValue(lowerBound)}`;
    } else {
      label = `${formatCountryStatsLegendValue(lowerBound)} – ${formatCountryStatsLegendValue(upperBound)}`;
    }

    items.push({
      color: palette[index],
      label,
    });
  }

  return items;
}, [countryStatsLegendMeta, formatCountryStatsLegendValue]);

useEffect(() => {
  if (shipLayersController.isWorldHeritageSitesVisible) return;
  if (!showWorldHeritagePanel && !selectedWorldHeritageSite) return;
  setShowWorldHeritagePanel(false);
  setSelectedWorldHeritageSite(null);
  setWorldHeritageDetailsError("");
  setWorldHeritageParentSiteDetails(null);
  setWorldHeritageParentSiteDetailsError("");
  setIsLoadingWorldHeritageParentSiteDetails(false);
}, [selectedWorldHeritageSite, shipLayersController.isWorldHeritageSitesVisible, showWorldHeritagePanel]);

useEffect(() => {
  if (!showWorldHeritagePanel) return;

  const anotherSidebarOpen =
    showMapListBar ||
    showTourListBar ||
    showTourDetailBar ||
    showChatBar ||
    showShipMenu ||
    showPortDockEditBar ||
    showFlightTrackingBar ||
    showFlightPriceBar ||
    showFlightRouteCreateBar ||
    showFileShareBar ||
    showAroundMeBar;

  if (!anotherSidebarOpen) return;
  closeWorldHeritagePanel();
}, [
  closeWorldHeritagePanel,
  showAroundMeBar,
  showChatBar,
  showFileShareBar,
  showFlightPriceBar,
  showFlightRouteCreateBar,
  showFlightTrackingBar,
  showMapListBar,
  showPortDockEditBar,
  showShipMenu,
  showTourDetailBar,
  showTourListBar,
  showWorldHeritagePanel,
]);

useEffect(() => {
  const map = mapInstanceRef.current;
  if (!map) return undefined;

  const clickKey = map.on('click', (evt) => {
    const shipLayerRefs = shipLayersController?.getLayerRefs?.() || {};
    const worldHeritageLayer = shipLayerRefs.worldHeritageLayer;
    if (!worldHeritageLayer || !worldHeritageLayer.getVisible?.()) return;

    const whsFeature = map.forEachFeatureAtPixel(
      evt.pixel,
      (feature) => feature,
      {
        layerFilter: (layer) => layer === worldHeritageLayer,
        hitTolerance: 6,
      }
    );

    if (!whsFeature) return;
    const whsData = whsFeature.getProperties ? whsFeature.getProperties() : {};
    console.log("World Heritage site clicked:", whsData);
    handleWorldHeritageFeatureClick(whsData);
  });

  return () => {
    if (clickKey) unByKey(clickKey);
  };
}, [handleWorldHeritageFeatureClick, shipLayersController]);

const handleShipMenuInteractiveLayersChange = useCallback((layers = {}) => {
  shipMenuInteractiveLayersRef.current = {
    routePathLayer: layers?.routePathLayer || null,
    shipsLayer: layers?.shipsLayer || null,
  };
}, []);

const handleShipMenuDockEditHandlerChange = useCallback((handler) => {
  shipMenuDockEditHandlerRef.current = typeof handler === 'function' ? handler : null;
}, []);

useEffect(() => {
  const map = mapInstanceRef.current;
  if (!map) return undefined;

  const clickKey = map.on('click', (evt) => {
    const shipLayerRefs = shipLayersController?.getLayerRefs?.() || {};
    const routePathLayer = shipMenuInteractiveLayersRef.current.routePathLayer;
    const shipsLayer = shipMenuInteractiveLayersRef.current.shipsLayer;

    const emitShipLayerClick = (type, payload = {}) => {
      shipLayerClickEventIdRef.current += 1;
      setShipLayerClickEvent({
        id: shipLayerClickEventIdRef.current,
        type,
        ...payload,
      });
    };

    if (routePathLayer) {
      const pathFeature = map.forEachFeatureAtPixel(
        evt.pixel,
        (feature) => feature,
        { layerFilter: (layer) => layer === routePathLayer, hitTolerance: 10 }
      );
      if (pathFeature) {
        const featureProperties = pathFeature.getProperties ? pathFeature.getProperties() : null;
        const featureKind = pathFeature.get?.("kind") || featureProperties?.kind || null;
        const stopData = featureProperties?.stopData || null;

        if (featureKind === "ship_history_move") {
          const moveData = featureProperties?.shipHistoryMoveData || {};
          const latitude = Number(moveData.latitude ?? featureProperties?.latitude);
          const longitude = Number(moveData.longitude ?? featureProperties?.longitude);
          const speedKmh = Number.isFinite(Number(featureProperties?.speedKmh))
            ? Number(featureProperties.speedKmh)
            : Number.isFinite(Number(featureProperties?.speed_kmh))
              ? Number(featureProperties.speed_kmh)
              : Number.isFinite(Number(moveData.speed))
                ? Number(moveData.speed) * 1.852
                : null;
          if (!showShipMenu) {
            setSelectedShipHistoryMarker({
              name: moveData.name || featureProperties?.name || "Tracking position",
              lastPosUpdate: moveData.last_pos_update || featureProperties?.recorded_at || featureProperties?.last || null,
              speedKmh,
              latitude: Number.isFinite(latitude) ? latitude : null,
              longitude: Number.isFinite(longitude) ? longitude : null,
            });
            setShowShipHistoryMarkerModal(true);
          }
          emitShipLayerClick("ship_history_marker", {
            properties: featureProperties,
          });
          return;
        }

        if (stopData && (featureKind === "lock" || featureKind === "dock")) {
          emitShipLayerClick("ship_history_stop", {
            properties: featureProperties,
          });
          return;
        }

        const routeInfo = pathFeature.get?.('routeInfo')
          || featureProperties?.routeInfo;
        if (routeInfo) {
          setSelectedShipLayerPath({
            routeInfo,
            properties: featureProperties,
          });
          setShowShipLayerPathModal(true);
          return;
        }
      }
    }

    const lockFeature = map.forEachFeatureAtPixel(
      evt.pixel,
      (feature) => feature,
      { layerFilter: (layer) => layer === shipLayerRefs.locksLayer, hitTolerance: 5 }
    );
    if (lockFeature) {
      setSelectedShipLayerLock(lockFeature.getProperties ? lockFeature.getProperties() : null);
      setShowShipLayerLockModal(true);
      return;
    }

    const dockFeature = map.forEachFeatureAtPixel(
      evt.pixel,
      (feature) => feature,
      { layerFilter: (layer) => layer === shipLayerRefs.docksLayer, hitTolerance: 5 }
    );
    if (dockFeature) {
      setSelectedShipLayerDock(dockFeature.getProperties ? dockFeature.getProperties() : null);
      setShowShipLayerDockModal(true);
      return;
    }

    if (shipsLayer) {
      const shipFeature = map.forEachFeatureAtPixel(
        evt.pixel,
        (feature) => feature,
        { layerFilter: (layer) => layer === shipsLayer, hitTolerance: 5 }
      );
      if (shipFeature) {
        const shipProperties = shipFeature.getProperties ? shipFeature.getProperties() : null;
        setSelectedShipLayerShip(shipProperties);
        setShowShipLayerShipModal(true);
        emitShipLayerClick('ship', {
          properties: shipProperties,
        });
        return;
      }
    }

    let pegelFeature = map.forEachFeatureAtPixel(
      evt.pixel,
      (feature) => {
        const isPegelFeature = feature?.get?.("kind") === "pegel";
        if (!isPegelFeature) return null;
        return feature;
      },
      { hitTolerance: 12 }
    );
    if (!pegelFeature) {
      const pegelSource = shipLayerRefs.pegelsLayer?.getSource?.();
      const closestPegelFeature = pegelSource?.getClosestFeatureToCoordinate?.(evt.coordinate) || null;
      const closestGeometry = closestPegelFeature?.getGeometry?.();
      const closestCoordinate = closestGeometry?.getCoordinates?.();
      if (Array.isArray(closestCoordinate)) {
        const closestPixel = map.getPixelFromCoordinate(closestCoordinate);
        const deltaX = Number(closestPixel?.[0]) - Number(evt.pixel?.[0]);
        const deltaY = Number(closestPixel?.[1]) - Number(evt.pixel?.[1]);
        const pixelDistance = Math.hypot(deltaX, deltaY);
        if (Number.isFinite(pixelDistance) && pixelDistance <= 18) {
          pegelFeature = closestPegelFeature;
        }
      }
    }
    if (pegelFeature) {
      pegelLayerClickEventIdRef.current += 1;
      setPegelLayerClickEvent({
        id: pegelLayerClickEventIdRef.current,
        type: 'pegel',
        properties: pegelFeature.getProperties ? pegelFeature.getProperties() : null,
      });
      return;
    }
  });

  return () => {
    if (clickKey) unByKey(clickKey);
  };
}, [shipLayersController, showShipMenu]);

useEffect(() => {
  if (!holidayModalState.open && !countryStatsModalState.open) return undefined;

  const onKeyDown = (event) => {
    if (event.key === 'Escape') {
      closeHolidayModal();
      closeCountryStatsModal();
    }
  };

  window.addEventListener('keydown', onKeyDown);
  return () => window.removeEventListener('keydown', onKeyDown);
}, [closeCountryStatsModal, closeHolidayModal, countryStatsModalState.open, holidayModalState.open]);

useEffect(() => {
  if (!showHolidayCountries && holidayModalState.open) {
    closeHolidayModal();
  }
}, [closeHolidayModal, holidayModalState.open, showHolidayCountries]);

useEffect(() => {
  if (!showCountryStats && countryStatsModalState.open) {
    closeCountryStatsModal();
  }
}, [closeCountryStatsModal, countryStatsModalState.open, showCountryStats]);

useEffect(() => {
  if (!showCountryStats) {
    setCountryStatsHoverHint((prev) => (prev.visible ? { ...prev, visible: false } : prev));
  }
}, [showCountryStats]);

useEffect(() => {
  if (!showCountryStats || countryStatsMetricOptions.length > 0) {
    return;
  }

  let cancelled = false;

  const loadCountryStatsMetrics = async () => {
    setCountryStatsMetricLoading(true);
    setCountryStatsMetricError('');
    try {
      const response = await axios.get(
        buildApiUrl('country-metrics/?has_observations=1&choropleth_only=1&numeric_only=1'),
        {
        headers: await getApiHeaders(),
        },
      );
      if (cancelled) return;
      const payload = Array.isArray(response.data) ? response.data : [];
      const options = payload
        .filter((item) => item?.data_name)
        .sort((a, b) => String(a.short_name || a.data_name).localeCompare(String(b.short_name || b.data_name)));
      setCountryStatsMetricOptions(options);
      if (!options.some((item) => item.data_name === countryStatsSelectedMetric)) {
        const preferred = options.find((item) => item.data_name === COUNTRY_STATS_DEFAULT_METRIC) || options[0];
        if (preferred?.data_name) {
          setCountryStatsSelectedMetric(preferred.data_name);
        }
      }
    } catch (error) {
      if (cancelled) return;
      console.error('Error loading country stats metrics:', error);
      setCountryStatsMetricError('Failed to load metric list.');
    } finally {
      if (!cancelled) {
        setCountryStatsMetricLoading(false);
      }
    }
  };

  loadCountryStatsMetrics();
  return () => {
    cancelled = true;
  };
}, [countryStatsMetricOptions.length, countryStatsSelectedMetric, getApiHeaders, showCountryStats]);

useEffect(() => {
  if (!showCountryStats || countryStatsCurrencyOptions.length > 1) {
    return;
  }

  let cancelled = false;

  const loadCountryStatsCurrencies = async () => {
    setCountryStatsCurrencyLoading(true);
    setCountryStatsCurrencyError('');
    try {
      const response = await axios.get(
        buildApiUrl('country-stats/currencies/'),
        {
          headers: await getApiHeaders(),
        },
      );
      if (cancelled) return;
      const payload = response?.data || {};
      const options = Array.isArray(payload.currencies)
        ? payload.currencies.filter((item) => typeof item === 'string' && item.trim())
        : COUNTRY_STATS_ALLOWED_CURRENCIES;
      const uniqueOptions = Array.from(new Set(options.map((item) => item.trim().toUpperCase())))
        .filter((currencyCode) => COUNTRY_STATS_ALLOWED_CURRENCIES.includes(currencyCode));
      setCountryStatsCurrencyOptions(
        uniqueOptions.length > 0 ? uniqueOptions : COUNTRY_STATS_ALLOWED_CURRENCIES,
      );
      if (!uniqueOptions.includes(String(countryStatsSelectedCurrency || '').trim().toUpperCase())) {
        setCountryStatsSelectedCurrency(payload.default_currency || COUNTRY_STATS_DEFAULT_CURRENCY);
      }
    } catch (error) {
      if (cancelled) return;
      console.error('Error loading country stats currencies:', error);
      setCountryStatsCurrencyError('Failed to load currency list.');
      setCountryStatsCurrencyOptions(COUNTRY_STATS_ALLOWED_CURRENCIES);
    } finally {
      if (!cancelled) {
        setCountryStatsCurrencyLoading(false);
      }
    }
  };

  loadCountryStatsCurrencies();
  return () => {
    cancelled = true;
  };
}, [countryStatsCurrencyOptions.length, countryStatsSelectedCurrency, getApiHeaders, showCountryStats]);

useEffect(() => {
  if (!countryStatsSelectedMetric) {
    setCountryStatsPalette(COUNTRY_STATS_DEFAULT_PALETTE);
    setCountryStatsNullColor('#E5E7EB');
    setCountryStatsLegendMeta(null);
    setCountryStatsLegendError('');
    setCountryStatsLegendLoading(false);
    return;
  }

  let cancelled = false;

  const loadCountryStatsMapMeta = async () => {
    setCountryStatsLegendLoading(true);
    setCountryStatsLegendError('');
    try {
      const response = await axios.get(
        buildApiUrl(
          `country-stats/map-meta/?metric=${encodeURIComponent(countryStatsSelectedMetric)}&base_currency=${encodeURIComponent(countryStatsSelectedCurrency)}`,
        ),
        {
          headers: await getApiHeaders(),
        },
      );
      if (cancelled) return;
      const payload = response.data || {};
      setCountryStatsPalette(
        Array.isArray(payload.palette) && payload.palette.length > 0
          ? payload.palette
          : COUNTRY_STATS_DEFAULT_PALETTE,
      );
      setCountryStatsNullColor(payload.null_color || '#E5E7EB');
      setCountryStatsLegendMeta(payload);
    } catch (error) {
      if (cancelled) return;
      console.error('Error loading country stats map metadata:', error);
      setCountryStatsPalette(COUNTRY_STATS_DEFAULT_PALETTE);
      setCountryStatsNullColor('#E5E7EB');
      setCountryStatsLegendMeta(null);
      setCountryStatsLegendError('Failed to load legend data.');
    } finally {
      if (!cancelled) {
        setCountryStatsLegendLoading(false);
      }
    }
  };

  loadCountryStatsMapMeta();
  return () => {
    cancelled = true;
  };
}, [countryStatsSelectedCurrency, countryStatsSelectedMetric, getApiHeaders]);

const getCountryCompareCenterLatitude = useCallback((feature, projection) => {
  if (!feature || !projection) return null;
  const geometry = feature.getGeometry?.();
  if (!geometry) return null;
  const center = getExtentCenter(geometry.getExtent());
  const lonLat = toLonLat(center, projection);
  const lat = Number(lonLat?.[1]);
  if (!Number.isFinite(lat)) return null;
  return Math.max(-85, Math.min(85, lat));
}, []);

const clampMercatorLat = useCallback((lat) => (
  Math.max(-85, Math.min(85, Number(lat)))
), []);

const normalizeLon = useCallback((lon) => {
  let normalized = Number(lon);
  if (!Number.isFinite(normalized)) return lon;
  while (normalized > 180) normalized -= 360;
  while (normalized < -180) normalized += 360;
  return normalized;
}, []);

const mapCoordinatesDeep = useCallback((coords, coordinateMapper) => {
  if (!Array.isArray(coords)) return coords;
  if (coords.length === 0) return coords;
  if (typeof coords[0] === 'number') {
    return coordinateMapper(coords);
  }
  return coords.map((entry) => mapCoordinatesDeep(entry, coordinateMapper));
}, []);

const rebuildCountryCompareGeometryForCenter = useCallback((targetCenterLonLat, projection) => {
  const baseGeometry4326 = countryCompareBaseGeometry4326Ref.current;
  const baseCenterLonLat = countryCompareBaseCenterLonLatRef.current;
  if (!baseGeometry4326 || !baseCenterLonLat || !projection || !Array.isArray(targetCenterLonLat)) {
    return null;
  }

  const [baseCenterLon, baseCenterLat] = baseCenterLonLat;
  const [targetCenterLonRaw, targetCenterLatRaw] = targetCenterLonLat;
  if (!Number.isFinite(baseCenterLon) || !Number.isFinite(baseCenterLat) || !Number.isFinite(targetCenterLonRaw) || !Number.isFinite(targetCenterLatRaw)) {
    return null;
  }

  const targetCenterLon = normalizeLon(targetCenterLonRaw);
  const targetCenterLat = clampMercatorLat(targetCenterLatRaw);
  const targetCenterProjected = fromLonLat([targetCenterLon, targetCenterLat], projection);
  const rotationRadians = Number(countryCompareRotationRadiansRef.current || 0);
  const isHalfTurn = Math.abs(rotationRadians - Math.PI) < 1e-6;
  const mercatorScaleRatio = Math.max(1e-4, Math.cos((baseCenterLat * Math.PI) / 180))
    / Math.max(1e-4, Math.cos((targetCenterLat * Math.PI) / 180));

  if (!countryCompareFlipDistortion) {
    const baseGeometryProjected = countryCompareBaseGeometryProjectedRef.current;
    const baseCenterProjected = countryCompareBaseCenterProjectedRef.current;
    if (!baseGeometryProjected || !Array.isArray(baseCenterProjected) || baseCenterProjected.length < 2) {
      return null;
    }

    const rebuiltProjected = baseGeometryProjected.clone();
    rebuiltProjected.translate(
      Number(targetCenterProjected[0]) - Number(baseCenterProjected[0]),
      Number(targetCenterProjected[1]) - Number(baseCenterProjected[1]),
    );
    if (countryCompareResizeWithLatitude) {
      rebuiltProjected.scale(mercatorScaleRatio, mercatorScaleRatio, targetCenterProjected);
    }
    if (isHalfTurn) {
      rebuiltProjected.rotate(Math.PI, targetCenterProjected);
    }
    return rebuiltProjected;
  }

  const rebuilt4326 = baseGeometry4326.clone();
  const rebuiltCoords = mapCoordinatesDeep(rebuilt4326.getCoordinates(), (coord) => {
    const lon = Number(coord[0]);
    const lat = Number(coord[1]);
    if (!Number.isFinite(lon) || !Number.isFinite(lat)) return coord;

    let latDelta = lat - baseCenterLat;
    const numerator = Math.max(1e-4, Math.cos((lat * Math.PI) / 180));
    let localLonDelta = (lon - baseCenterLon) * numerator;

    if (isHalfTurn && countryCompareFlipDistortion) {
      latDelta *= -1;
      localLonDelta *= -1;
    }

    const movedLat = clampMercatorLat(targetCenterLat + latDelta);
    const denominator = Math.max(1e-4, Math.cos((movedLat * Math.PI) / 180));
    const movedLon = normalizeLon(targetCenterLon + (localLonDelta / denominator));
    return [movedLon, movedLat];
  });

  rebuilt4326.setCoordinates(rebuiltCoords);
  rebuilt4326.transform('EPSG:4326', projection);
  if (!countryCompareResizeWithLatitude) {
    rebuilt4326.scale(1 / mercatorScaleRatio, 1 / mercatorScaleRatio, targetCenterProjected);
  }
  return rebuilt4326;
}, [clampMercatorLat, countryCompareFlipDistortion, countryCompareResizeWithLatitude, mapCoordinatesDeep, normalizeLon]);

const rotateCountryCompareBy180 = useCallback(() => {
  const map = mapInstanceRef.current;
  const feature = countryCompareFeatureRef.current;
  if (!map || !mapReady || !feature) return;

  const geometry = feature.getGeometry?.();
  if (!geometry) return;

  const projection = map.getView().getProjection();
  const currentCenterMap = getExtentCenter(geometry.getExtent());
  if (!Array.isArray(currentCenterMap) || currentCenterMap.length < 2) return;

  const currentCenterLonLat = toLonLat(currentCenterMap, projection);
  if (!Array.isArray(currentCenterLonLat) || currentCenterLonLat.length < 2) return;

  const currentRotation = Number(countryCompareRotationRadiansRef.current || 0);
  countryCompareRotationRadiansRef.current = Math.abs(currentRotation - Math.PI) < 1e-6
    ? 0
    : Math.PI;

  const rebuiltGeometry = rebuildCountryCompareGeometryForCenter(currentCenterLonLat, projection);
  if (!rebuiltGeometry) return;

  feature.setGeometry(rebuiltGeometry);
}, [mapReady, rebuildCountryCompareGeometryForCenter]);

useEffect(() => {
  const map = mapInstanceRef.current;
  const feature = countryCompareFeatureRef.current;
  if (!map || !mapReady || !feature) return;

  const geometry = feature.getGeometry?.();
  if (!geometry) return;

  const projection = map.getView().getProjection();
  const currentCenterMap = getExtentCenter(geometry.getExtent());
  if (!Array.isArray(currentCenterMap) || currentCenterMap.length < 2) return;

  const currentCenterLonLat = toLonLat(currentCenterMap, projection);
  if (!Array.isArray(currentCenterLonLat) || currentCenterLonLat.length < 2) return;

  const rebuiltGeometry = rebuildCountryCompareGeometryForCenter(currentCenterLonLat, projection);
  if (!rebuiltGeometry) return;

  feature.setGeometry(rebuiltGeometry);
}, [countryCompareFeatureVersion, countryCompareFlipDistortion, countryCompareResizeWithLatitude, mapReady, rebuildCountryCompareGeometryForCenter]);

useEffect(() => {
  if (!showCountryCompare) return;
  if (countryCompareOptions.length > 0) return;

  let cancelled = false;
  const loadCountries = async () => {
    setCountryCompareLoadingOptions(true);
    setCountryCompareError('');
    try {
      const headers = await getApiHeaders();
      const rawItems = [];
      let nextUrl = buildApiUrl('countries/?page_size=500');

      while (nextUrl) {
        const res = await axios.get(nextUrl, { headers });
        if (cancelled) return;
        const payload = res?.data;

        if (Array.isArray(payload)) {
          rawItems.push(...payload);
          break;
        }

        const pageResults = Array.isArray(payload?.results) ? payload.results : [];
        rawItems.push(...pageResults);
        nextUrl = payload?.next || null;
      }

      const options = rawItems
        .map((item) => ({
          iso3: String(item?.iso3_code || '').trim().toUpperCase(),
          name: String(item?.name || '').trim(),
        }))
        .filter((item) => item.iso3.length === 3 && item.name)
        .sort((a, b) => a.name.localeCompare(b.name));

      setCountryCompareOptions(options);
      if (!countryCompareSelectedIso3 && options.length > 0) {
        setCountryCompareSelectedIso3(options[0].iso3);
      }
    } catch (error) {
      if (cancelled) return;
      console.error('Error loading country options for comparison layer:', error);
      setCountryCompareError('Failed to load countries list.');
    } finally {
      if (!cancelled) {
        setCountryCompareLoadingOptions(false);
      }
    }
  };

  loadCountries();
  return () => {
    cancelled = true;
  };
}, [countryCompareOptions.length, countryCompareSelectedIso3, getApiHeaders, showCountryCompare]);

useEffect(() => {
  const map = mapInstanceRef.current;
  if (!showCountryCompare || !map || !mapReady) return;
  const iso3 = String(countryCompareSelectedIso3 || '').trim().toUpperCase();
  if (!iso3) return;

  let cancelled = false;
  const loadCountryBorder = async () => {
    setCountryCompareLoadingShape(true);
    setCountryCompareError('');
    try {
      const res = await axios.get(
        buildApiUrl(`world_borders/${encodeURIComponent(iso3)}/`),
        { headers: await getApiHeaders() }
      );
      if (cancelled) return;

      const payload = res?.data;
      const featureCollection = payload?.type === 'Feature'
        ? { type: 'FeatureCollection', features: [payload] }
        : payload;
      const features = new GeoJSON().readFeatures(featureCollection, {
        dataProjection: 'EPSG:4326',
        featureProjection: map.getView().getProjection(),
      });

      if (!features.length) {
        throw new Error('No geometry returned for selected country.');
      }

      const feature = features[0];
      const properties = feature.getProperties ? feature.getProperties() : {};
      feature.set('compareIso3', iso3);
      feature.set('compareName', properties?.country_name || properties?.name || iso3);

      countryCompareSourceRef.current?.clear();
      countryCompareSourceRef.current?.addFeature(feature);
      countryCompareFeatureRef.current = feature;
      countryCompareBaseGeometryProjectedRef.current = feature.getGeometry?.()?.clone?.() || null;
      countryCompareBaseCenterProjectedRef.current = countryCompareBaseGeometryProjectedRef.current
        ? getExtentCenter(countryCompareBaseGeometryProjectedRef.current.getExtent())
        : null;
      const baseGeometry4326 = feature.getGeometry?.()?.clone?.() || null;
      if (baseGeometry4326) {
        baseGeometry4326.transform(map.getView().getProjection(), 'EPSG:4326');
      }
      countryCompareRotationRadiansRef.current = 0;
      countryCompareBaseGeometry4326Ref.current = baseGeometry4326;
      countryCompareBaseCenterLonLatRef.current = baseGeometry4326
        ? getExtentCenter(baseGeometry4326.getExtent())
        : null;
      setCountryCompareFeatureVersion((prev) => prev + 1);
    } catch (error) {
      if (cancelled) return;
      console.error('Error loading selected country border:', error);
      countryCompareSourceRef.current?.clear();
      countryCompareFeatureRef.current = null;
      countryCompareBaseGeometryProjectedRef.current = null;
      countryCompareBaseCenterProjectedRef.current = null;
      countryCompareRotationRadiansRef.current = 0;
      countryCompareBaseGeometry4326Ref.current = null;
      countryCompareBaseCenterLonLatRef.current = null;
      setCountryCompareError('Failed to load country border geometry.');
    } finally {
      if (!cancelled) {
        setCountryCompareLoadingShape(false);
      }
    }
  };

  loadCountryBorder();
  return () => {
    cancelled = true;
  };
}, [countryCompareSelectedIso3, getApiHeaders, mapReady, showCountryCompare]);

useEffect(() => {
  const map = mapInstanceRef.current;
  if (!map || !mapReady) return undefined;

  if (countryCompareTranslateStartKeyRef.current) {
    unByKey(countryCompareTranslateStartKeyRef.current);
    countryCompareTranslateStartKeyRef.current = null;
  }
  if (countryCompareTranslatingKeyRef.current) {
    unByKey(countryCompareTranslatingKeyRef.current);
    countryCompareTranslatingKeyRef.current = null;
  }
  if (countryCompareTranslateEndKeyRef.current) {
    unByKey(countryCompareTranslateEndKeyRef.current);
    countryCompareTranslateEndKeyRef.current = null;
  }
  if (countryCompareTranslateRef.current) {
    map.removeInteraction(countryCompareTranslateRef.current);
    countryCompareTranslateRef.current = null;
  }

  if (!showCountryCompare || !countryCompareFeatureRef.current) {
    return undefined;
  }

  countryCompareTranslateRef.current = new Translate({
    features: new Collection([countryCompareFeatureRef.current]),
  });

  const applyCountryCompareAtEventCoordinate = (eventCoordinate) => {
    if (!Array.isArray(eventCoordinate) || eventCoordinate.length < 2) return;
    const feature = countryCompareFeatureRef.current;
    if (!feature) return;
    const projection = map.getView().getProjection();
    const offset = countryComparePointerCenterOffsetRef.current || [0, 0];
    const targetCenterMap = [
      Number(eventCoordinate[0]) + Number(offset[0] || 0),
      Number(eventCoordinate[1]) + Number(offset[1] || 0),
    ];
    const targetCenterLonLat = toLonLat(targetCenterMap, projection);
    const rebuiltGeometry = rebuildCountryCompareGeometryForCenter(
      targetCenterLonLat,
      projection
    );
    if (!rebuiltGeometry) return;
    feature.setGeometry(rebuiltGeometry);
  };

  countryCompareTranslateStartKeyRef.current = countryCompareTranslateRef.current.on('translatestart', (event) => {
    const feature = countryCompareFeatureRef.current;
    const geometry = feature?.getGeometry?.();
    if (!geometry || !Array.isArray(event?.coordinate)) return;
    const center = getExtentCenter(geometry.getExtent());
    countryComparePointerCenterOffsetRef.current = [
      center[0] - Number(event.coordinate[0]),
      center[1] - Number(event.coordinate[1]),
    ];
  });

  countryCompareTranslatingKeyRef.current = countryCompareTranslateRef.current.on('translating', (event) => {
    if (!Array.isArray(event?.coordinate)) return;
    const coord = [Number(event.coordinate[0]), Number(event.coordinate[1])];

    if (countryCompareDragRafRef.current && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(countryCompareDragRafRef.current);
      countryCompareDragRafRef.current = null;
    }

    if (typeof window !== 'undefined' && window.requestAnimationFrame) {
      countryCompareDragRafRef.current = window.requestAnimationFrame(() => {
        countryCompareDragRafRef.current = null;
        applyCountryCompareAtEventCoordinate(coord);
      });
    } else {
      applyCountryCompareAtEventCoordinate(coord);
    }
  });

  countryCompareTranslateEndKeyRef.current = countryCompareTranslateRef.current.on('translateend', (event) => {
    if (countryCompareDragRafRef.current && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(countryCompareDragRafRef.current);
      countryCompareDragRafRef.current = null;
    }
    if (Array.isArray(event?.coordinate)) {
      applyCountryCompareAtEventCoordinate([Number(event.coordinate[0]), Number(event.coordinate[1])]);
    }
  });

  map.addInteraction(countryCompareTranslateRef.current);

  return () => {
    if (countryCompareTranslateStartKeyRef.current) {
      unByKey(countryCompareTranslateStartKeyRef.current);
      countryCompareTranslateStartKeyRef.current = null;
    }
    if (countryCompareTranslatingKeyRef.current) {
      unByKey(countryCompareTranslatingKeyRef.current);
      countryCompareTranslatingKeyRef.current = null;
    }
    if (countryCompareTranslateEndKeyRef.current) {
      unByKey(countryCompareTranslateEndKeyRef.current);
      countryCompareTranslateEndKeyRef.current = null;
    }
    if (countryCompareDragRafRef.current && typeof window !== 'undefined' && window.cancelAnimationFrame) {
      window.cancelAnimationFrame(countryCompareDragRafRef.current);
      countryCompareDragRafRef.current = null;
    }
    if (countryCompareTranslateRef.current) {
      map.removeInteraction(countryCompareTranslateRef.current);
      countryCompareTranslateRef.current = null;
    }
  };
}, [countryCompareFeatureVersion, mapReady, rebuildCountryCompareGeometryForCenter, showCountryCompare]);

const shipLayerState = useMemo(
  () => ({
    locks: shipLayersController.isLocks,
    docks: shipLayersController.isDockingPoints,
    pegels: shipLayersController.isPegelsVisible,
    worldHeritageSites: shipLayersController.isWorldHeritageSitesVisible,
  }),
  [
    shipLayersController.isDockingPoints,
    shipLayersController.isLocks,
    shipLayersController.isPegelsVisible,
    shipLayersController.isWorldHeritageSitesVisible,
  ]
);
const hasActiveShipLayers =
  shipLayerState.locks ||
  shipLayerState.docks ||
  shipLayerState.pegels ||
  shipLayerState.worldHeritageSites;
const [mapViewInfo, setMapViewInfo] = useState({ zoom: null, center: null });
const [mapViewEditing, setMapViewEditing] = useState(false);
const [mapViewDraft, setMapViewDraft] = useState({ zoom: '', lat: '', lon: '' });
const [showMapViewInfo, setShowMapViewInfo] = useState(false);
const mapViewCoordMarkerRef = useRef(null);
const [hasMapViewCoordMarker, setHasMapViewCoordMarker] = useState(false);
const urlSyncIgnoreRef = useRef(false);
const urlSyncIgnoreReleaseTimerRef = useRef(null);
const urlSyncCameraWriteTimerRef = useRef(null);
const urlSyncInitialApplyDoneRef = useRef(false);
const urlSyncHydrationCompleteRef = useRef(false);
const urlSyncUiInitializedRef = useRef(false);
const buildUrlStateHrefRef = useRef(null);
const [selectedRiverStretch, setSelectedRiverStretch] = useState(null);
const selectedRiverFeatureRef = useRef(null);
const riverNameLookupRequestIdRef = useRef(0);
const [showLayersPanel, setShowLayersPanel] = useState(false);
const [showScaleCompassOverlay, setShowScaleCompassOverlay] = useState(false);
const [showTourRouteLegend, setShowTourRouteLegend] = useState(false);
const [showUserDeviceLocations, setShowUserDeviceLocations] = useState(false);
const [isLoadingUserDeviceLocations, setIsLoadingUserDeviceLocations] = useState(false);
const [userDeviceLocationsLastUpdatedAt, setUserDeviceLocationsLastUpdatedAt] = useState(null);
const [userDeviceLocationsLoadError, setUserDeviceLocationsLoadError] = useState('');
const [showTrackingDevicesPanel, setShowTrackingDevicesPanel] = useState(false);
const [mapLayoutPreview, setMapLayoutPreview] = useState({ active: false });
const [mapEditorWorkspace, setMapEditorWorkspace] = useState('map');
const [layoutPreviewSnapshot, setLayoutPreviewSnapshot] = useState(null);
const [isCapturingLayoutPreview, setIsCapturingLayoutPreview] = useState(false);
const [mapScaleOverlay, setMapScaleOverlay] = useState({ width: 0, text: "" });
const [mapRotationDegrees, setMapRotationDegrees] = useState(0);
const [closeFunctionsMenuSignal, setCloseFunctionsMenuSignal] = useState(0);
const [searchQuery, setSearchQuery] = useState('');
const [searchResults, setSearchResults] = useState([]);

const [searchOpen, setSearchOpen] = useState(false);
const [searchLoading, setSearchLoading] = useState(false);
const [showSearchBar, setShowSearchBar] = useState(false);
const searchAbortRef = useRef(null);
const searchDebounceRef = useRef(null);
const latestSearchRef = useRef('');
const searchInputRef = useRef(null);

const isAnySidebarActive = useMemo(() => (
  showMapListBar ||
  showSideEditBar ||
  showTourListBar ||
  showTourDetailBar ||
  showChatBar ||
  showShipMenu ||
  showAroundMeBar ||
  showPortDockEditBar ||
  showFlightTrackingBar ||
  showFlightPriceBar ||
  showFlightRouteCreateBar ||
  showFileShareBar ||
  showWorldHeritagePanel ||
  showTrackingDevicesPanel
), [
  showAroundMeBar,
  showChatBar,
  showFileShareBar,
  showFlightPriceBar,
  showFlightRouteCreateBar,
  showFlightTrackingBar,
  showMapListBar,
  showPortDockEditBar,
  showShipMenu,
  showSideEditBar,
  showTourDetailBar,
  showTourListBar,
  showTrackingDevicesPanel,
  showWorldHeritagePanel,
]);

// Distance measurement state
const [distanceMeasureActive, setDistanceMeasureActive] = useState(false);
const [distanceMeasurePoints, setDistanceMeasurePoints] = useState([]); // Array of [lon, lat] coordinates
const [distanceMeasureTotalKm, setDistanceMeasureTotalKm] = useState(0);
const [distanceMeasureUnit, setDistanceMeasureUnit] = useState('km');
const [distanceMeasureInsertAfterIndex, setDistanceMeasureInsertAfterIndex] = useState(null);
const distanceMeasureSourceRef = useRef(null);
const distanceMeasureLayerRef = useRef(null);
const distanceMeasureClickKeyRef = useRef(null);
const distanceMeasureTranslateRef = useRef(null);
const distanceMeasureTranslateKeyRef = useRef(null);

// Quick two-finger measurement state (only active when distanceMeasureActive is false)
const [quickMeasureResult, setQuickMeasureResult] = useState(null); // { distance: meters } or null
const [quickMeasureActive, setQuickMeasureActive] = useState(false);
const quickMeasureSourceRef = useRef(null);
const quickMeasureLayerRef = useRef(null);

// Sidebar cleanup registry - stores cleanup functions for each sidebar
const sidebarCleanupRef = useRef({
  mapList: null,
  tourList: null,
  tourDetail: null,
  chat: null,
  shipMenu: null,
  portDock: null,
  flightTracking: null,
  flightPrice: null,
  flightRouteCreate: null,
  fileShare: null,
  mapEdit: null,
  aroundMe: null,
});

// Execute cleanup for a specific sidebar
const executeCleanup = useCallback((sidebarName) => {
  const cleanup = sidebarCleanupRef.current[sidebarName];
  if (cleanup && typeof cleanup === 'function') {
    console.log(`Executing cleanup for: ${sidebarName}`);
    try {
      cleanup();
    } catch (error) {
      console.error(`Error executing cleanup for ${sidebarName}:`, error);
    }
    sidebarCleanupRef.current[sidebarName] = null;
  }
}, []);

// Execute ALL cleanups (for when closing all sidebars)
const executeAllCleanups = useCallback(() => {
  Object.keys(sidebarCleanupRef.current).forEach(key => {
    executeCleanup(key);
  });
}, [executeCleanup]);

// Register cleanup function for a sidebar
const registerSidebarCleanup = useCallback((sidebarName, cleanupFn) => {
  console.log(`Registering cleanup for: ${sidebarName}`);
  sidebarCleanupRef.current[sidebarName] = cleanupFn;
}, []);

// Layer management functions
const clearLayer = useCallback((sourceType = 'markers') => {
    const src = sourcesRef.current?.[sourceType];
    if (src) src.clear();
    if (sourceType === 'markers') {
      sourcesRef.current?.mapListMarkers?.clear();
      clusterSourceRef.current?.clear?.();
      mapViewCoordMarkerRef.current = null;
      setHasMapViewCoordMarker(false);
    }
}, []);

// Wrapper functions to manage sidebar state with automatic closing of other sidebars
const openMapListBar = useCallback(() => {
  // Execute cleanup for all OTHER sidebars
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(true);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setTourDetailWideSidebar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setFlightPriceHistoryState({
    open: false,
    loading: false,
    data: null,
    selectedRoute: null,
  });
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup, setShipMenuRouteMode]);

const openTourListBar = useCallback(() => {
  executeCleanup('mapList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(true);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setTourDetailWideSidebar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  setTourDetailMapOverlaysSuppressed(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const openTourDetailBar = useCallback(() => {
  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(true);
  setTourDetailWideSidebar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  setTourDetailMapOverlaysSuppressed(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const openFlightTrackingBarFromTourDetail = useCallback(() => {
  setTourDetailMapOverlaysSuppressed(true);
  setShowFlightTrackingBar(true);
}, []);

const selectTourAndOpenDetail = useCallback((tour) => {
  if (!tour?.id || isUserSwitching) return;

  const activeUserId = Number(activeUser?.object_id ?? activeUser?.id);
  const tourOwnerId = Number(tour?.user_id);
  const hasOwnerMismatch =
    Number.isFinite(activeUserId) &&
    activeUserId > 0 &&
    Number.isFinite(tourOwnerId) &&
    tourOwnerId > 0 &&
    activeUserId !== tourOwnerId;

  if (hasOwnerMismatch) {
    console.warn('Ignoring tour selection due to owner mismatch after user switch', {
      tourId: tour.id,
      tourOwnerId,
      activeUserId,
    });
    return;
  }

  setActiveTourToShow(tour);
  openTourDetailBar();
}, [activeUser?.id, activeUser?.object_id, isUserSwitching, openTourDetailBar]);

const activeTourInUserList = useMemo(() => {
  const activeTourId = activeTourToShow?.id;
  if (!activeTourId) return false;
  if (activeUser?.object_type === 'tour_user') return true;
  return (userTours || []).some((tour) => String(tour?.id) === String(activeTourId));
}, [activeTourToShow?.id, activeUser?.object_type, userTours]);

useEffect(() => {
  if (!activeTourToShow?.id || isUserSwitching) return;
  if (activeUser?.object_type === 'tour_user') return;
  if (!Array.isArray(userTours) || userTours.length === 0) return;
  if (activeTourInUserList) return;

  console.warn('Clearing stale active tour not present in current user tour list', {
    activeTourId: activeTourToShow.id,
    activeUserId: activeUser?.object_id || activeUser?.id,
  });
  setActiveTourToShow(null);
  setShowTourDetailBar(false);
  setTourDetailWideSidebar(false);
}, [
  activeTourInUserList,
  activeTourToShow?.id,
  activeUser?.id,
  activeUser?.object_id,
  activeUser?.object_type,
  isUserSwitching,
  userTours,
  setActiveTourToShow,
]);

const openChatBar = useCallback(() => {
  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setShowChatBar(true);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const openShipMenu = useCallback((modeOrVisible = null, options = {}) => {
  if (modeOrVisible === false) {
    executeCleanup('shipMenu');
    setShowShipMenu(false);
    setShipMenuRouteMode(null);
    setShipMenuActivateRiverMarker(false);
    setShipMenuRoutePrefill(null);
    setShipMenuReturnToAroundMe(null);
    setShipMenuReturnToTourDetail(false);
    return;
  }

  const routeMode = typeof modeOrVisible === "string" ? modeOrVisible : null;
  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShipMenuRouteMode(routeMode);
  setShipMenuActivateRiverMarker(Boolean(options.activateRiverMarker));
  setShipMenuRoutePrefill(options.prefillRoute || null);
  setShipMenuReturnToAroundMe(options.returnToAroundMe || null);
  setShipMenuReturnToTourDetail(Boolean(options.returnToTourDetail));
  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setTourDetailWideSidebar(false);
  setShowChatBar(false);
  setShowShipMenu(true);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup, setShipMenuRouteMode, setShipMenuActivateRiverMarker]);

const openAroundMeBar = useCallback((visible = true, options = {}) => {
  if (!visible) {
    executeCleanup('aroundMe');
    setShowAroundMeBar(false);
    setAroundMeRequestedSelectedLonLat(null);
    return;
  }

  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setTourDetailWideSidebar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowAroundMeBar(true);
  setAroundMeRequestedSelectedLonLat(
    Array.isArray(options?.selectedLonLat) && options.selectedLonLat.length >= 2
      ? [Number(options.selectedLonLat[0]), Number(options.selectedLonLat[1])]
      : null
  );
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const handleShipMenuVisibility = useCallback((visible) => {
  if (visible === false) {
    const returnConfig = shipMenuReturnToAroundMe;
    if (returnConfig?.selectedLonLat) {
      setShowShipMenu(false);
      setShipMenuRouteMode(null);
      setShipMenuActivateRiverMarker(false);
      setShipMenuRoutePrefill(null);
      setShipMenuReturnToAroundMe(null);
      setShipMenuReturnToTourDetail(false);
      openAroundMeBar(true, { selectedLonLat: returnConfig.selectedLonLat });
      return;
    }

    if (shipMenuReturnToTourDetail && activeTourToShow) {
      openShipMenu(false);
      setTourDetailMapOverlaysSuppressed(false);
      setShowMapListBar(false);
      setShowTourListBar(false);
      setTourListWideSidebar(false);
      setShowTourDetailBar(true);
      setTourDetailWideSidebar(false);
      return;
    }

    openShipMenu(false);
    return;
  }

  if (visible === true) {
    openShipMenu();
  }
}, [
  activeTourToShow,
  openAroundMeBar,
  openShipMenu,
  shipMenuReturnToAroundMe,
  shipMenuReturnToTourDetail,
]);

const openFlightTrackingBar = useCallback(() => {
  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(true);
  setShowFlightPriceBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const openFlightPriceBar = useCallback((tourId, mode = "tour", options = {}) => {
  const keepTourDetailBar = options?.keepTourDetailBar === true;
  executeCleanup('mapList');
  executeCleanup('tourList');
  if (!keepTourDetailBar) {
    executeCleanup('tourDetail');
  }
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  if (!keepTourDetailBar) {
    setShowTourDetailBar(false);
  }
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(true);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  setFlightPriceBarMode(mode);
  setFlightPriceBarTourId(tourId || null);
  setReturnToTourDetailOnClose(keepTourDetailBar);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const closeFlightPriceBar = useCallback(() => {
  setShowFlightPriceBar(false);
  setFlightPriceHistoryState({
    open: false,
    loading: false,
    data: null,
    selectedRoute: null,
  });
  if (returnToTourDetailOnClose && activeTourToShow) {
    setTourDetailMapOverlaysSuppressed(false);
    setShowTourDetailBar(true);
  }
  setReturnToTourDetailOnClose(false);
}, [returnToTourDetailOnClose, activeTourToShow]);

const closeFlightPriceHistory = useCallback(() => {
  setFlightPriceHistoryState({
    open: false,
    loading: false,
    data: null,
    selectedRoute: null,
  });
}, []);

useEffect(() => {
  if (!showFlightPriceBar && flightPriceHistoryState.open) {
    closeFlightPriceHistory();
  }
}, [closeFlightPriceHistory, flightPriceHistoryState.open, showFlightPriceBar]);

const openFlightPriceHistory = useCallback(async (route, nextFilters = null) => {
  if (!route) return;
  const emptyFilterSet = {
    outbound_start_from: "",
    outbound_end_until: "",
    return_start_from: "",
    return_start_until: "",
    return_end_until: "",
    max_leg_duration: "",
    min_leg_duration: "",
    max_total_layover: "",
    min_total_layover: "",
    max_layover_minutes: "",
    min_layover_minutes: "",
  };
  const filters = nextFilters || { ...emptyFilterSet, ...(route?.filters || flightPriceHistoryFilters) };
  setFlightPriceHistoryState({
    open: true,
    loading: true,
    data: null,
    selectedRoute: route,
  });
  if (nextFilters) {
    setFlightPriceHistoryFilters(nextFilters);
  } else if (route?.filters) {
    setFlightPriceHistoryFilters({ ...emptyFilterSet, ...route.filters });
  }

  try {
    const isMultiCity = route?.route_kind === "multi_city";
    const endpointBase = isMultiCity ? "flight-routes-multi-city" : "flight-routes";
    const params = {};
    if (filters.outbound_end_until) {
      params.outbound_end_until = filters.outbound_end_until;
    }
    if (filters.outbound_start_from) {
      params.outbound_start_from = filters.outbound_start_from;
    }
    if (filters.return_start_from) {
      params.return_start_from = filters.return_start_from;
    }
    if (filters.return_start_until) {
      params.return_start_until = filters.return_start_until;
    }
    if (filters.return_end_until) {
      params.return_end_until = filters.return_end_until;
    }
    if (filters.max_leg_duration) {
      params.max_leg_duration = filters.max_leg_duration;
    }
    if (filters.min_leg_duration) {
      params.min_leg_duration = filters.min_leg_duration;
    }
    if (filters.max_total_layover) {
      params.max_total_layover = filters.max_total_layover;
    }
    if (filters.min_total_layover) {
      params.min_total_layover = filters.min_total_layover;
    }
    if (filters.max_layover_minutes) {
      params.max_layover_minutes = filters.max_layover_minutes;
    }
    if (filters.min_layover_minutes) {
      params.min_layover_minutes = filters.min_layover_minutes;
    }
    const response = await axios({
      url: buildApiUrl(`${endpointBase}/${route.id}/price-history/`),
      method: "GET",
      params,
      headers: await getApiHeaders(),
    });

    setFlightPriceHistoryState((prev) => ({
      ...prev,
      data: response.data,
      loading: false,
    }));
  } catch (err) {
    console.error("Error fetching price history:", err);
    setFlightPriceHistoryState((prev) => ({
      ...prev,
      data: { error: err.response?.data?.error || "Failed to fetch price history" },
      loading: false,
    }));
  }
}, [getApiHeaders, flightPriceHistoryFilters]);

const saveFlightPriceHistoryFilters = useCallback(async (route, filters) => {
  if (!route) return;
  const cleanedFilters = Object.entries(filters || {}).reduce((acc, [key, value]) => {
    if (value !== "" && value != null) {
      acc[key] = value;
    }
    return acc;
  }, {});
  const endpointBase = route?.route_kind === "multi_city" ? "flight-routes-multi-city" : "flight-routes";
  await axios({
    url: buildApiUrl(`${endpointBase}/${route.id}/`),
    method: "PATCH",
    data: { filters: cleanedFilters },
    headers: await getApiHeaders(),
  });
  setFlightPriceHistoryFilters({ ...emptyFilterSet, ...cleanedFilters });
}, [getApiHeaders]);

const openFlightRouteCreateBar = useCallback((tourId, mode = "tour", options = {}) => {
  const keepTourDetailBar = options?.keepTourDetailBar === true;
  executeCleanup('mapList');
  executeCleanup('tourList');
  if (!keepTourDetailBar) {
    executeCleanup('tourDetail');
  }
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  if (!keepTourDetailBar) {
    setShowTourDetailBar(false);
  }
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(true);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  setFlightRouteCreateBarMode(mode);
  setFlightRouteCreateBarTourId(tourId || null);
  setReturnToTourDetailOnCloseCreate(keepTourDetailBar);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const closeFlightRouteCreateBar = useCallback(() => {
  setShowFlightRouteCreateBar(false);
  if (returnToTourDetailOnCloseCreate && activeTourToShow) {
    setTourDetailMapOverlaysSuppressed(false);
    setShowTourDetailBar(true);
  }
  setReturnToTourDetailOnCloseCreate(false);
}, [returnToTourDetailOnCloseCreate, activeTourToShow]);

const openFileShareBar = useCallback(() => {
  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('portDock');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(true);
  setShowAroundMeBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const toggleRiverKmLayer = useCallback(() => {
  setShowRiverKm(prev => !prev);
}, []);

const toggleHillshadeLayer = useCallback(() => {
  setShowHillshade(prev => !prev);
}, []);

const toggleBathymetryLayer = useCallback(() => {
  setShowBathymetry(prev => !prev);
}, []);

const toggleHydroBasinsLayer = useCallback(() => {
  setShowHydroBasins(prev => !prev);
}, []);

const toggleHolidayCountriesLayer = useCallback(() => {
  setShowHolidayCountries(prev => !prev);
}, []);

const toggleCountryStatsLayer = useCallback(() => {
  setShowCountryStats((prev) => !prev);
}, []);

const toggleCountryCompareLayer = useCallback(() => {
  setShowCountryCompare((prev) => !prev);
}, []);

const handleLayerMenuSelection = useCallback((action) => {
  if (typeof action === 'function') {
    action();
  }
  setShowLayersPanel(false);
}, []);

const getCurrentLocation = useCallback(async () => {
  const map = mapInstanceRef.current;
  if (!map) return;

  setIsLocating(true);

  if (!navigator?.geolocation) {
    alert('Geolocation is not supported by this browser.');
    setIsLocating(false);
    return;
  }

  if (typeof window !== 'undefined' && !window.isSecureContext) {
    alert('Geolocation requires HTTPS. Please use a secure connection.');
    setIsLocating(false);
    return;
  }

  if (navigator?.permissions?.query) {
    try {
      const status = await navigator.permissions.query({ name: 'geolocation' });
      if (status.state === 'denied') {
        alert('Location permission is blocked. Please enable it in your browser settings.');
        setIsLocating(false);
        return;
      }
    } catch (error) {
      console.warn('Geolocation permission check failed:', error);
    }
  }

  const geolocation = new Geolocation({
    trackingOptions: {
      enableHighAccuracy: true,
    },
    projection: map.getView().getProjection(),
  });

  geolocation.setTracking(true);

  geolocation.once('change:position', () => {
    const coordinates = geolocation.getPosition();
    if (coordinates) {
      // Drop/update a marker at the resolved current location.
      if (tempPointSourceRef.current) {
        if (tempPointFeatureRef.current) {
          tempPointFeatureRef.current.setGeometry(new Point(coordinates));
        } else {
          tempPointSourceRef.current.clear();
          const currentLocationFeature = new Feature(new Point(coordinates));
          tempPointSourceRef.current.addFeature(currentLocationFeature);
          tempPointFeatureRef.current = currentLocationFeature;
        }
      }

      map.getView().animate({
        center: coordinates,
        zoom: 16,
        duration: 1000,
      });
    }
    geolocation.setTracking(false);
    setIsLocating(false);
  });

  geolocation.once('error', (error) => {
    let message = 'Unable to get your location. Please try again.';
    if (error?.code === 1) {
      message = 'Location permission denied. Please allow location access in your browser.';
    } else if (error?.code === 2) {
      message = 'Location unavailable. Check your network or device settings.';
    } else if (error?.code === 3) {
      message = 'Location request timed out. Please try again.';
    }
    console.error('Geolocation error:', error);
    alert(message);
    geolocation.setTracking(false);
    setIsLocating(false);
  });
}, []);

const openPortDockEditBar = useCallback(() => {
  executeCleanup('mapList');
  executeCleanup('tourList');
  executeCleanup('tourDetail');
  executeCleanup('chat');
  executeCleanup('shipMenu');
  executeCleanup('flightTracking');
  executeCleanup('flightPrice');
  executeCleanup('flightRouteCreate');
  executeCleanup('fileShare');
  executeCleanup('mapEdit');
  executeCleanup('aroundMe');

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(true);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  clearLayer('markers');
}, [clearLayer, executeCleanup]);

const openDockEditFromLayerModal = useCallback((dockData) => {
  if (!dockData?.id) return;

  setShowShipLayerDockModal(false);
  setPendingDockEditRequest({
    ...dockData,
    requestId: `${dockData.id}-${Date.now()}`,
  });
  openPortDockEditBar();
}, [openPortDockEditBar]);

const closeAllSidebars = useCallback(() => {
  // Execute ALL cleanups when closing all sidebars
  executeAllCleanups();

  setShowMapListBar(false);
  setShowTourListBar(false);
  setTourListWideSidebar(false);
  setShowTourDetailBar(false);
  setShowChatBar(false);
  setShowShipMenu(false);
  setShowPortDockEditBar(false);
  setShowFlightTrackingBar(false);
  setShowFlightPriceBar(false);
  setShowFlightRouteCreateBar(false);
  setShowFileShareBar(false);
  setShowAroundMeBar(false);
  setShowTrackingDevicesPanel(false);
  setShowMapOnlyView(false);
  setMapOnlyOriginContext(null);
  setTourUserMapContext(null);
  setPendingDockEditRequest(null);
  setMapLayoutPreview({ active: false });
  setShowWorldHeritagePanel(false);
  setSelectedWorldHeritageSite(null);
  setWorldHeritageDetailsError("");
  setWorldHeritageParentSiteDetails(null);
  setWorldHeritageParentSiteDetailsError("");
  setIsLoadingWorldHeritageParentSiteDetails(false);
  shipLayersController.clearWorldHeritageActiveSelection?.();
  setReturnToTourDetailOnClose(false);
  setReturnToTourDetailOnCloseCreate(false);
  clearLayer('markers');
}, [clearLayer, executeAllCleanups, shipLayersController]);

const setTrackingDevicesPanelOpen = useCallback((nextValue) => {
  if (nextValue) {
    closeAllSidebars();
    setShowLayersPanel(false);
  }
  setShowTrackingDevicesPanel(Boolean(nextValue));
}, [closeAllSidebars]);

const handleMapLayoutPreviewChange = useCallback((nextPreview) => {
  setMapLayoutPreview(nextPreview?.active ? nextPreview : { active: false });
}, []);

useEffect(() => {
  if (!isUserSwitching) return;
  closeAllSidebars();
}, [closeAllSidebars, isUserSwitching]);

useEffect(() => {
  if (!isUserSwitching || !enableUrlStateSync) return;

  const params = new URLSearchParams(window.location.search || '');
  ['ui', 'tour', 'map', 'a_lat', 'a_lon', 'b_lat', 'b_lon'].forEach((key) => {
    params.delete(key);
  });

  const nextQuery = params.toString();
  const nextHref = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
  const currentHref = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;
  if (nextHref !== currentHref) {
    window.history.replaceState({}, '', nextHref);
  }
}, [enableUrlStateSync, isUserSwitching]);


// Function to check if current user is a tour user
const isTourUser = () => {
  return activeUser && activeUser.object_type === 'tour_user';
};
const isTourUserDetailActive = Boolean(activeTourToShow && activeUser?.object_type === 'tour_user');

useEffect(() => {
  if (isTourUserDetailActive && !isMobile) {
    setShowScaleCompassOverlay(true);
  }
}, [isMobile, isTourUserDetailActive]);

useEffect(() => {
  if (!isTourUserDetailActive) return;
  setShowTourRouteLegend(!isMobile);
}, [activeTourToShow?.id, isMobile, isTourUserDetailActive]);

useEffect(() => {
  if (!activeMapToShow || activeUser?.object_type !== 'map') return;

  const params = new URLSearchParams(window.location.search || '');
  const lon = Number(params.get('dock_lon'));
  const lat = Number(params.get('dock_lat'));
  if (!Number.isFinite(lon) || !Number.isFinite(lat) || lon < -180 || lon > 180 || lat < -90 || lat > 90) {
    setTourUserMapContext(null);
    return;
  }

  const label = params.get('dock_label') || 'Docking location';
  setTourUserMapContext({
    source: 'map_docking_url',
    dockingPoi: {
      id: `url-docking-${activeMapToShow?.id ?? 'map'}`,
      name: label,
      center: {
        type: 'Point',
        coordinates: [lon, lat],
      },
      markerLabel: '\u2693\uFE0E',
      markerLabelType: 'docking',
      isDockingMarker: true,
      isSyntheticDockingPoi: true,
    },
  });
}, [activeMapToShow, activeUser?.object_type]);

const openTourUserMapOnlyView = useCallback((mapItem, portVisit) => {
  if (!mapItem) return;

  const selectedDockingPoint = getSelectedDockingPoint(portVisit);
  const effectiveLocation = getEffectivePortLocation(portVisit);
  const effectiveCoordinates = Array.isArray(effectiveLocation?.coordinates) && effectiveLocation.coordinates.length === 2
    ? effectiveLocation.coordinates
    : null;

  const dockingPoi = effectiveCoordinates ? {
    id: `tour-docking-${portVisit?.id ?? 'unknown'}`,
    name: formatDockingPointLabel(selectedDockingPoint),
    center: {
      type: 'Point',
      coordinates: effectiveCoordinates,
    },
    markerLabel: '\u2693\uFE0E',
    markerLabelType: 'docking',
    isDockingMarker: true,
    isSyntheticDockingPoi: true,
  } : null;

  setActiveMapToShow(mapItem);
  setTourUserMapContext({
    source: 'tour_user_port_map',
    portVisitId: portVisit?.id ?? null,
    dockingPoi,
  });
  setMapOnlyOriginContext({
    source: 'tour_user_port_map',
    restoreTourUserSidebar: true,
  });
  setShowTourUserDetailView(false);
  setShowMapOnlyView(true);
}, [
  setActiveMapToShow,
]);

const handleMapOnlyViewClose = useCallback(() => {
  setShowMapOnlyView(false);
  setTourUserMapContext(null);

  if (mapOnlyOriginContext?.restoreTourUserSidebar && activeUser?.object_type === 'tour_user' && activeTourToShow) {
    setShowTourUserDetailView(true);
  }

  setMapOnlyOriginContext(null);
}, [activeTourToShow, activeUser?.object_type, mapOnlyOriginContext]);

const sidebarOccupiesMapPane = useMemo(() => {
  if (!sidebarConfig?.type) return false;
  return true;
}, [sidebarConfig?.type]);

const effectiveSidebarWidthPx = useMemo(() => {
  const mainWidth = !isMobile && sidebarOccupiesMapPane
    ? parseCssPixelValue(sidebarConfig.width, 0)
    : 0;
  const historyWidth = !isMobile && priceHistorySidebarState.open
    ? parseCssPixelValue(priceHistorySidebarState.width, 0)
    : 0;
  return Math.max(mainWidth, historyWidth);
}, [
  isMobile,
  priceHistorySidebarState.open,
  priceHistorySidebarState.width,
  sidebarConfig.width,
  sidebarOccupiesMapPane,
]);

const effectiveSidebarHeight = useMemo(() => {
  const mainHeight = isMobile && sidebarOccupiesMapPane
    ? normalizeCssLength(dynamicSidebarHeight || sidebarConfig.height, '0px')
    : '0px';
  const historyHeight = isMobile && priceHistorySidebarState.open
    ? normalizeCssLength(priceHistorySidebarState.height, '0px')
    : '0px';
  if (historyHeight === '0px') return mainHeight;
  if (mainHeight === '0px') return historyHeight;
  return `max(${mainHeight}, ${historyHeight})`;
}, [
  dynamicSidebarHeight,
  isMobile,
  priceHistorySidebarState.height,
  priceHistorySidebarState.open,
  sidebarConfig.height,
  sidebarOccupiesMapPane,
]);

const activeItinerarySidebarConfig = useMemo(
  () => (priceHistorySidebarState.open
    ? { width: priceHistorySidebarState.width, height: priceHistorySidebarState.height }
    : null),
  [priceHistorySidebarState.height, priceHistorySidebarState.open, priceHistorySidebarState.width]
);

const layoutSidebarConfig = useMemo(() => ({
  ...sidebarConfig,
  width: isMobile ? sidebarConfig.width : `${effectiveSidebarWidthPx}px`,
  height: isMobile ? effectiveSidebarHeight : sidebarConfig.height,
  activeItinerary: activeItinerarySidebarConfig,
}), [
  activeItinerarySidebarConfig,
  effectiveSidebarHeight,
  effectiveSidebarWidthPx,
  isMobile,
  sidebarConfig,
]);

const layoutSidebarWidthPx = useMemo(
  () => parseCssPixelValue(layoutSidebarConfig.width, 0),
  [layoutSidebarConfig.width]
);

const isMapLayoutPreviewActive = Boolean(
  !isMobile &&
  showMapListBar &&
  activeMapToEdit &&
  mapLayoutPreview?.active
);
const isMapExportSnapshotActive = Boolean(
  !isMobile &&
  showMapListBar &&
  activeMapToEdit &&
  mapEditorWorkspace === 'export' &&
  layoutPreviewSnapshot?.url
);
const mapLayoutPreviewChrome = useMemo(() => {
  if (!isMapLayoutPreviewActive) return null;
  const sidebarWidth = Math.max(0, Math.round(effectiveSidebarWidthPx));
  const stageTopPx = 48;
  const stagePaddingPx = 24;
  const stageWidthPx = Math.max(320, Math.round(viewportSize.width - sidebarWidth));
  const stageHeightPx = Math.max(320, Math.round(viewportSize.height - stageTopPx));
  const paper = getPreviewPaperSize(mapLayoutPreview?.visibilitySettings?.paper_size);
  const paperAspectRatio = paper.widthMm / paper.heightMm;
  const maxPageWidthPx = Math.max(320, stageWidthPx - (stagePaddingPx * 2));
  const maxPageHeightPx = Math.max(240, stageHeightPx - (stagePaddingPx * 2));
  let pageWidthPx = Math.min(maxPageWidthPx, maxPageHeightPx * paperAspectRatio);
  let pageHeightPx = pageWidthPx / paperAspectRatio;
  if (pageHeightPx > maxPageHeightPx) {
    pageHeightPx = maxPageHeightPx;
    pageWidthPx = pageHeightPx * paperAspectRatio;
  }

  pageWidthPx = Math.round(pageWidthPx);
  pageHeightPx = Math.round(pageHeightPx);

  const pageLeftPx = Math.round((stageWidthPx - pageWidthPx) / 2);
  const pageTopPx = stageTopPx + Math.round((stageHeightPx - pageHeightPx) / 2);
  const pageScale = pageWidthPx / paper.widthMm;
  const contentInsetPx = Math.round((mapLayoutPreview?.visibilitySettings?.borderless === false ? 3 : 0) * pageScale);
  const contentLeftPx = pageLeftPx + contentInsetPx;
  const contentTopPx = pageTopPx + contentInsetPx;
  const contentWidthPx = Math.max(1, pageWidthPx - (contentInsetPx * 2));
  const contentHeightPx = Math.max(1, pageHeightPx - (contentInsetPx * 2));
  const isBottomList = mapLayoutPreview?.layout === 'bottom_poi_list';
  const bottomListHeightPx = isBottomList
    ? Math.round(Math.min(contentHeightPx * 0.45, paper.bottomPanelHeightMm * pageScale))
    : 0;
  const mapHeightPx = isBottomList
    ? Math.max(1, contentHeightPx - bottomListHeightPx)
    : contentHeightPx;
  const mapWidthPx = isBottomList
    ? contentWidthPx
    : Math.max(1, Math.min(contentWidthPx, Math.round(mapHeightPx * PREVIEW_SIDE_MAP_ASPECT_RATIO)));
  const sideListWidthPx = isBottomList ? contentWidthPx : Math.max(1, contentWidthPx - mapWidthPx);
  const sideListHeightPx = isBottomList ? bottomListHeightPx : contentHeightPx;
  const sideListLeftPx = isBottomList ? contentLeftPx : contentLeftPx + mapWidthPx;
  const sideListTopPx = isBottomList ? contentTopPx + mapHeightPx : contentTopPx;

  return {
    stageTopPx,
    pageTopPx,
    pageActualLeftPx: pageLeftPx,
    pageWidthPx,
    pageHeightPx,
    mapLeftPx: contentLeftPx,
    mapTopPx: contentTopPx,
    mapWidthPx,
    mapHeightPx,
    sideListLeftPx,
    sideListTopPx,
    sideListWidthPx,
    sideListHeightPx,
    sidebarWidthPx: sidebarWidth,
  };
}, [
  effectiveSidebarWidthPx,
  isMapLayoutPreviewActive,
  mapLayoutPreview?.layout,
  mapLayoutPreview?.visibilitySettings?.borderless,
  mapLayoutPreview?.visibilitySettings?.paper_size,
  viewportSize.height,
  viewportSize.width,
]);

const mapExportSnapshotChrome = useMemo(() => {
  if (!isMapExportSnapshotActive || !layoutPreviewSnapshot?.url) return null;
  const sidebarWidth = Math.max(0, Math.round(effectiveSidebarWidthPx));
  const stageTopPx = 48;
  const stagePaddingPx = 24;
  const stageWidthPx = Math.max(320, Math.round(viewportSize.width - sidebarWidth));
  const stageHeightPx = Math.max(320, Math.round(viewportSize.height - stageTopPx));
  const snapshotWidth = Number(layoutPreviewSnapshot.width) || 297;
  const snapshotHeight = Number(layoutPreviewSnapshot.height) || 210;
  const snapshotAspectRatio = snapshotWidth / snapshotHeight;
  const maxImageWidthPx = Math.max(320, stageWidthPx - (stagePaddingPx * 2));
  const maxImageHeightPx = Math.max(240, stageHeightPx - (stagePaddingPx * 2));
  let imageWidthPx = Math.min(maxImageWidthPx, maxImageHeightPx * snapshotAspectRatio);
  let imageHeightPx = imageWidthPx / snapshotAspectRatio;
  if (imageHeightPx > maxImageHeightPx) {
    imageHeightPx = maxImageHeightPx;
    imageWidthPx = imageHeightPx * snapshotAspectRatio;
  }

  imageWidthPx = Math.round(imageWidthPx);
  imageHeightPx = Math.round(imageHeightPx);

  return {
    stageTopPx,
    stageRightPx: sidebarWidth,
    imageLeftPx: Math.round((stageWidthPx - imageWidthPx) / 2),
    imageTopPx: stageTopPx + Math.round((stageHeightPx - imageHeightPx) / 2),
    imageWidthPx,
    imageHeightPx,
  };
}, [
  effectiveSidebarWidthPx,
  isMapExportSnapshotActive,
  layoutPreviewSnapshot?.height,
  layoutPreviewSnapshot?.url,
  layoutPreviewSnapshot?.width,
  viewportSize.height,
  viewportSize.width,
]);

useEffect(() => () => {
  if (layoutPreviewSnapshotUrlRef.current) {
    URL.revokeObjectURL(layoutPreviewSnapshotUrlRef.current);
    layoutPreviewSnapshotUrlRef.current = null;
  }
}, []);

const captureLayoutPreviewSnapshot = useCallback(async () => {
  if (!isMapLayoutPreviewActive || !mapLayoutPreviewChrome || !interactionRootRef.current) {
    if (layoutPreviewSnapshotUrlRef.current) {
      URL.revokeObjectURL(layoutPreviewSnapshotUrlRef.current);
      layoutPreviewSnapshotUrlRef.current = null;
    }
    const nextSnapshot = {
      url: null,
      error: 'Open Layout before capturing the preview.',
      capturedAt: new Date().toISOString(),
    };
    setLayoutPreviewSnapshot(nextSnapshot);
    return nextSnapshot;
  }

  setIsCapturingLayoutPreview(true);

  try {
    const map = mapInstanceRef.current;
    const root = interactionRootRef.current;
    await new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
    if (map?.once && map?.renderSync) {
      await new Promise((resolve) => {
        const timer = window.setTimeout(resolve, 1200);
        map.once('rendercomplete', () => {
          window.clearTimeout(timer);
          resolve();
        });
        map.renderSync();
      });
    }

    const {
      pageActualLeftPx,
      pageTopPx,
      pageWidthPx,
      pageHeightPx,
    } = mapLayoutPreviewChrome;
    const previewClassNames = new Set([
      'MapLayoutPreview-page',
      'MapLayoutPreview-export-map-treatment',
      'MapLayoutPreview-side-list',
    ]);
    const shouldCaptureNode = (node) => {
      if (!(node instanceof Element)) return true;
      if (node === root) return true;
      if (mapRef.current?.contains(node)) return true;
      if ([...previewClassNames].some((className) => node.classList?.contains(className))) return true;
      if ([...previewClassNames].some((className) => node.closest?.(`.${className}`))) return true;
      return false;
    };

    const scale = Math.min(2, Math.max(1, window.devicePixelRatio || 1));
    const snapshotCanvas = await domToCanvas(root, {
      scale,
      backgroundColor: '#ffffff',
      width: pageWidthPx,
      height: pageHeightPx,
      style: {
        width: `${root.clientWidth}px`,
        height: `${root.clientHeight}px`,
        transform: `translate(${-pageActualLeftPx}px, ${-pageTopPx}px)`,
        transformOrigin: 'top left',
      },
      filter: shouldCaptureNode,
      fetch: {
        bypassingCache: false,
      },
    });

    const blob = await new Promise((resolve) => snapshotCanvas.toBlob(resolve, 'image/png'));
    if (!blob) throw new Error('Unable to encode preview snapshot.');

    const nextUrl = URL.createObjectURL(blob);
    if (layoutPreviewSnapshotUrlRef.current) {
      URL.revokeObjectURL(layoutPreviewSnapshotUrlRef.current);
    }
    layoutPreviewSnapshotUrlRef.current = nextUrl;

    const nextSnapshot = {
      url: nextUrl,
      error: '',
      capturedAt: new Date().toISOString(),
      width: snapshotCanvas.width,
      height: snapshotCanvas.height,
      blob,
    };
    setLayoutPreviewSnapshot(nextSnapshot);
    return nextSnapshot;
  } catch (error) {
    console.error('Failed to capture layout preview snapshot:', error);
    if (layoutPreviewSnapshotUrlRef.current) {
      URL.revokeObjectURL(layoutPreviewSnapshotUrlRef.current);
      layoutPreviewSnapshotUrlRef.current = null;
    }
    const nextSnapshot = {
      url: null,
      error: error?.message || 'Unable to capture the layout preview.',
      capturedAt: new Date().toISOString(),
    };
    setLayoutPreviewSnapshot(nextSnapshot);
    return nextSnapshot;
  } finally {
    setIsCapturingLayoutPreview(false);
  }
}, [isMapLayoutPreviewActive, mapLayoutPreviewChrome]);

const desktopTourUserBottomPaneHeight = isTourUserDetailActive && !isMobile
  ? normalizeCssLength(dynamicSidebarHeight || '128px', '128px')
  : '0px';
const isTourOverviewSettingsMode = isTourUserDetailActive;
const cruiseContextCode = useMemo(() => getCruiseContextFromLocation(), []);
const isCruiseContextMap = Boolean(
  cruiseContextCode && activeMapToShow && activeUser?.object_type === 'map'
);
const cruiseContextReturnUrl = useMemo(
  () => buildCruiseOverviewUrl(cruiseContextCode),
  [cruiseContextCode]
);
const cruiseContextMapLabel = useMemo(() => (
  activeMapToShow?.properties?.area_name ||
  activeMapToShow?.area_name ||
  activeMapToShow?.properties?.name ||
  activeMapToShow?.name ||
  'Online map'
), [activeMapToShow]);
const returnToCruiseOverview = useCallback(() => {
  if (!cruiseContextReturnUrl) return;

  const referrerCode = getGuestCruiseCodeFromLocation(document.referrer || null);
  const canUseHistory = (
    window.history.length > 1 &&
    referrerCode === cruiseContextCode
  );
  if (canUseHistory) {
    window.history.back();
    return;
  }

  window.location.assign(cruiseContextReturnUrl);
}, [cruiseContextCode, cruiseContextReturnUrl]);
const mapPaneStyle = useMemo(() => {
  if (mapLayoutPreviewChrome) {
    const { mapLeftPx, mapTopPx, mapWidthPx, mapHeightPx } = mapLayoutPreviewChrome;
    return {
      width: `${mapWidthPx}px`,
      height: `${mapHeightPx}px`,
      position: 'absolute',
      top: `${mapTopPx}px`,
      left: `${mapLeftPx}px`,
      zIndex: 3,
      background: '#9fc7d1',
      border: '1px solid rgba(11, 42, 68, 0.24)',
      boxShadow: '0 8px 24px rgba(15, 23, 42, 0.12)',
      overflow: 'hidden',
    };
  }

  return {
    width: isMobile ? '100%' : `calc(100% - ${effectiveSidebarWidthPx}px)`,
    height: isMobile
      ? `calc((var(--vh, 1vh) * 100) - ${effectiveSidebarHeight})`
      : isTourUserDetailActive
        ? `calc((var(--vh, 1vh) * 100) - ${desktopTourUserBottomPaneHeight})`
        : 'calc(var(--vh, 1vh) * 100)',
    position: 'absolute',
    top: 0,
    left: 0,
    zIndex: 1,
  };
}, [
  desktopTourUserBottomPaneHeight,
  effectiveSidebarHeight,
  effectiveSidebarWidthPx,
  isMobile,
  isTourUserDetailActive,
  mapLayoutPreviewChrome,
]);

const mapControlsRight = useMemo(
  () => (isMobile ? theme.spacing[2] : `calc(${layoutSidebarWidthPx}px + ${theme.spacing.lg})`),
  [isMobile, layoutSidebarWidthPx]
);

const mapControlsBottom = useMemo(
  () =>
    isMobile
      ? `calc(${effectiveSidebarHeight} + ${theme.spacing.lg})`
      : isTourUserDetailActive
        ? `calc(${desktopTourUserBottomPaneHeight} + ${theme.spacing.lg})`
        : theme.spacing[6],
  [desktopTourUserBottomPaneHeight, effectiveSidebarHeight, isMobile, isTourUserDetailActive]
);

const mapControlGap = theme.spacing[2];
const mapControlSize = theme.components.button.size.md;
const layersMenuZIndex = showLayersPanel
  ? Math.max(theme.zIndex.notification || 9998, theme.zIndex.mapControls + 20)
  : theme.zIndex.mapControls;
const searchPanelWidth = useMemo(
  () => (isMobile ? 'min(355px, calc(100% - 24px))' : '355px'),
  [isMobile]
);

const searchContainerStyle = useMemo(() => ({
  position: 'absolute',
  right: mapControlsRight,
  bottom: `calc(${mapControlsBottom} + ${mapControlSize} + ${mapControlGap})`,
  zIndex: theme.zIndex.mapControls,
  width: searchPanelWidth,
  maxWidth: 'calc(100% - 24px)',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-end',
}), [mapControlsRight, mapControlsBottom, mapControlGap, mapControlSize, searchPanelWidth]);

const layersControlStyle = useMemo(() => ({
  position: 'absolute',
  right: `calc(${mapControlsRight} + ${mapControlSize} + ${mapControlGap})`,
  bottom: mapControlsBottom,
  zIndex: layersMenuZIndex,
  width: isMobile ? theme.components.button.size.md : 'auto',
  height: theme.components.button.size.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}), [isMobile, layersMenuZIndex, mapControlsRight, mapControlsBottom, mapControlGap, mapControlSize]);

const searchButtonStyle = useMemo(() => ({
  top: isMobile ? '12px' : '16px',
  zIndex: theme.zIndex.mapControls,
  height: theme.components.button.size.md,
  borderRadius: '22px',
  border: '1px solid rgba(0,0,0,0.15)',
  background: 'rgba(255,255,255,0.95)',
  boxShadow: theme.shadows.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: showSearchBar ? 'flex-start' : 'center',
  gap: showSearchBar ? '8px' : '0',
  padding: showSearchBar ? '0 8px' : '0',
  cursor: 'pointer',
  transition: 'width 200ms ease',
  width: showSearchBar ? searchPanelWidth : theme.components.button.size.md,
  overflow: 'hidden',
  boxSizing: 'border-box',
}), [showSearchBar, searchPanelWidth]);

const layersButtonStyle = useMemo(() => ({
  zIndex: theme.zIndex.mapControls,
  width: theme.components.button.size.md,
  height: theme.components.button.size.md,
  borderRadius: theme.components.button.borderRadius,
  border: '1px solid rgba(0,0,0,0.15)',
  background: 'rgba(255,255,255,0.95)',
  boxShadow: theme.shadows.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
}), []);

const adminUserOptions = useMemo(() => {
  const byId = new globalThis.Map();

  const ownId = authenticatedUser?.object_id || authenticatedUser?.id;
  if (ownId) {
    byId.set(String(ownId), {
      id: ownId,
      username: authenticatedUser?.username,
      email: authenticatedUser?.email,
      full_name: authenticatedUser?.name || null,
      is_self: true,
    });
  }

  (adminSwitchableUsers || []).forEach((userOption) => {
    if (!userOption?.id) return;
    const key = String(userOption.id);
    const previous = byId.get(key) || {};
    byId.set(key, {
      ...previous,
      ...userOption,
      is_self: Boolean(userOption.is_self || previous.is_self || (ownId && Number(ownId) === Number(userOption.id))),
    });
  });

  return Array.from(byId.values()).sort((a, b) => {
    if (a.is_self && !b.is_self) return -1;
    if (!a.is_self && b.is_self) return 1;
    const labelA = `${a.full_name || ''} ${a.username || ''}`.trim().toLowerCase();
    const labelB = `${b.full_name || ''} ${b.username || ''}`.trim().toLowerCase();
    return labelA.localeCompare(labelB);
  });
}, [adminSwitchableUsers, authenticatedUser]);

const activeUserSwitchValue = useMemo(() => {
  const value = activeUser?.object_id || activeUser?.id || '';
  return value ? String(value) : '';
}, [activeUser]);

const adminSwitchControlStyle = useMemo(() => ({
  position: 'absolute',
  right: mapControlsRight,
  top: theme.spacing[2],
  zIndex: theme.zIndex.mapControls,
  display: 'flex',
  alignItems: 'center',
  gap: '8px',
  background: 'rgba(255,255,255,0.95)',
  border: '1px solid rgba(0,0,0,0.15)',
  borderRadius: '22px',
  boxShadow: theme.shadows.md,
  padding: '6px 10px',
  fontFamily: theme.typography.fontFamily.primary,
  maxWidth: isMobile ? 'calc(100% - 16px)' : '520px',
}), [isMobile, mapControlsRight]);

const handleAdminSwitchChange = useCallback((event) => {
  const nextId = Number.parseInt(event.target.value, 10);
  if (Number.isFinite(nextId)) {
    switchToUser(nextId);
  }
}, [switchToUser]);

const zoomControlsStyle = useMemo(() => ({
  position: 'absolute',
  right: mapControlsRight,
  bottom: !isTourUser()
    ? `calc(${mapControlsBottom} + ${mapControlSize} + ${mapControlGap} + ${mapControlSize} + ${mapControlGap})`
    : mapControlsBottom,
  zIndex: theme.zIndex.mapControls,
  display: 'flex',
  flexDirection: 'column',
  gap: mapControlGap,
}), [activeUser, mapControlsRight, mapControlsBottom, mapControlGap, mapControlSize]);

const compassControlStyle = useMemo(() => ({
  position: 'absolute',
  right: mapControlsRight,
  bottom: !isTourUser()
    ? `calc(${mapControlsBottom} + (${mapControlSize} * 4) + (${mapControlGap} * 4))`
    : `calc(${mapControlsBottom} + (${mapControlSize} * 2) + (${mapControlGap} * 2))`,
  zIndex: theme.zIndex.mapControls,
  pointerEvents: 'auto',
  width: mapControlSize,
  height: mapControlSize,
  borderRadius: '50%',
  border: '1px solid rgba(0,0,0,0.15)',
  background: 'rgba(255,255,255,0.95)',
  boxShadow: theme.shadows.md,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  cursor: 'pointer',
  padding: 0,
}), [activeUser, mapControlsRight, mapControlsBottom, mapControlGap, mapControlSize]);

const scaleControlStyle = useMemo(() => ({
  position: 'absolute',
  right: `calc(${mapControlsRight} + ${mapControlSize} + ${mapControlGap} + ${isMobile ? mapControlSize : '140px'} + ${mapControlGap})`,
  bottom: mapControlsBottom,
  zIndex: theme.zIndex.mapControls,
  minWidth: 112,
  height: isMobile ? 34 : mapControlSize,
  padding: isMobile ? '5px 14px' : '6px 12px',
  borderRadius: 999,
  border: '1px solid rgba(0,0,0,0.15)',
  background: 'rgba(255,255,255,0.95)',
  boxShadow: theme.shadows.md,
  pointerEvents: 'none',
  display: 'grid',
  justifyItems: 'center',
  alignContent: 'center',
  gap: 2,
  boxSizing: 'border-box',
}), [isMobile, mapControlsRight, mapControlsBottom, mapControlGap, mapControlSize]);

const handleZoom = useCallback((delta) => {
  const map = mapInstanceRef.current;
  if (!map) return;
  const view = map.getView();
  const currentZoom = view.getZoom() ?? 0;
  view.animate({ zoom: currentZoom + delta, duration: 200 });
}, []);

useEffect(() => {
  if (showSearchBar) {
    searchInputRef.current?.focus();
  }
}, [showSearchBar]);

const runGeoSearch = useCallback(async (query) => {
  if (!query) return;
  latestSearchRef.current = query;
  if (searchAbortRef.current) {
    searchAbortRef.current.abort();
  }
  const controller = new AbortController();
  searchAbortRef.current = controller;

  setSearchLoading(true);
  try {
    const res = await fetch(
      buildMapsUrl(`geoloc/search?q=${encodeURIComponent(query)}`),
      { signal: controller.signal }
    );
    if (!res.ok) {
      throw new Error(`Geolocation search failed: ${res.status}`);
    }
    const data = await res.json();
    if (latestSearchRef.current !== query) return;
    setSearchResults(Array.isArray(data) ? data : []);
    setSearchOpen(true);
  } catch (error) {
    if (error.name !== 'AbortError') {
      console.error('Geolocation search error:', error);
    }
    if (latestSearchRef.current === query) {
      setSearchResults([]);
      setSearchOpen(false);
    }
  } finally {
    if (latestSearchRef.current === query) {
      setSearchLoading(false);
    }
  }
}, []);

useEffect(() => {
  const query = searchQuery.trim();
  if (query.length < 2) {
    if (searchAbortRef.current) {
      searchAbortRef.current.abort();
    }
    setSearchResults([]);
    setSearchOpen(false);
    setSearchLoading(false);
    return;
  }
  if (searchDebounceRef.current) {
    clearTimeout(searchDebounceRef.current);
  }
  searchDebounceRef.current = setTimeout(() => {
    runGeoSearch(query);
  }, 350);
  return () => {
    if (searchDebounceRef.current) {
      clearTimeout(searchDebounceRef.current);
    }
  };
}, [searchQuery, runGeoSearch]);

useEffect(() => () => {
  if (searchAbortRef.current) {
    searchAbortRef.current.abort();
  }
}, []);

const handleSearchSelect = useCallback((result) => {
  const map = mapInstanceRef.current;
  if (!map || !result) return;
  const lat = Number(result.lat);
  const lon = Number(result.lon);
  if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;
  const view = map.getView();
  const targetZoom = Math.max(view.getZoom() || 0, 14);
  view.animate({
    center: fromLonLat([lon, lat]),
    zoom: targetZoom,
    duration: 600,
  });
  setSearchQuery(result.display_name || `${lat}, ${lon}`);
  setSearchOpen(false);
}, []);

const worldHole = ({w,s,e,n}) => ({
    outer: [[-180,-90],[-180,90],[180,90],[180,-90],[-180,-90]],
    inner: [[w,s],[w,n],[e,n],[e,s],[w,s]],
});
  
const haversineMeters = (lat1, lon1, lat2, lon2) => {
    const R = 6371000, toRad = (d) => d * Math.PI / 180;
    const dLat = toRad(lat2-lat1), dLon = toRad(lon2-lon1);
    const a = Math.sin(dLat/2)**2 + Math.cos(toRad(lat1))*Math.cos(toRad(lat2))*Math.sin(dLon/2)**2;
    return 2*R*Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
};

const isFiniteWSEN = (wsen) =>
    !!wsen && [wsen.w, wsen.s, wsen.e, wsen.n].every(Number.isFinite);

// Format distance for display
const formatDistance = (meters, unit = 'km') => {
    if (unit === 'nm') {
        return `${(meters / NAUTICAL_MILE_METERS).toFixed(2)} N.M.`;
    }
    if (meters < 1000) {
        return `${Math.round(meters)} m`;
    }
    return `${(meters / 1000).toFixed(2)} km`;
};

const getRiverSystemStrokeConfig = (cemt) => {
    switch (cemt) {
        case 'VII':
            return { color: '#173066', width: 7 };
        case 'VIc':
            return { color: '#173473', width: 6 };
        case 'VIb':
            return { color: '#173880', width: 5 };
        case 'VIa':
            return { color: '#173c8c', width: 4 };
        case 'Vb':
            return { color: '#174099', width: 3.5 };
        case 'IV':
            return { color: '#1744a6', width: 2.7 };
        default:
            return { color: '#1744a6', width: 3 };
    }
};

const CEMT_DIMENSIONS = {
    RA: "open boat: 5.5 x 2.0 m, draft 0.5 m, min bridge 2.0 m",
    RB: "cabin cruiser: 9.5 x 3.0 m, draft 1.0 m, min bridge 3.25 m",
    RC: "motor yacht: 15.0 x 4.0 m, draft 1.5 m, min bridge 4.0 m",
    RD: "sailing boat: 15.0 x 4.0 m, draft 2.1 m, min bridge 30.0 m",
    I: "east of Elbe: 41 x 4.7 m, draft 1.4 m, 180 t, min bridge 3.0 m; west of Elbe: 38.5 x 5.05 m, draft 2.2 m, 400 t, min bridge 4.0 m",
    II: "east of Elbe: up to 57 x 9.0 m, draft 1.6 m, up to 630 t; west of Elbe: up to 55 x 6.6 m, draft 2.5 m, up to 650 t, min bridge 4.0-5.0 m",
    III: "east of Elbe: up to 70 x 9.0 m, draft up to 2.0 m, up to 700 t; convoy up to 132 x 9.0 m, up to 1,200 t, min bridge 4.0 m; west of Elbe: up to 80 x 8.2 m, draft 2.5 m, up to 1,000 t",
    IV: "motor vessel up to 85 x 9.5 m, draft 2.5 m, up to 1,500 t; convoy up to 85 x 9.5 m, draft up to 2.8 m, up to 1,450 t; min bridge 5.25 or 7.0 m",
    VA: "large Rhine class: 95-110 x 11.4 m, draft 2.5-4.5 m, 1,600-3,000 t (up to 135m length with a special permit); min bridge 5.25 or 7.0 or 9.10 m",
    VB: "1x2 convoy: 172-185 m, 3,200-6,000 t",
    VIA: "2x1 convoy: 95-110 x 22.8 m (up to 135m length with a special permit), min bridge 7.0 or 9.10 m",
    VIB: "2x2 convoy: 185-195 m, 6,400-12,000 t; motor vessel baseline 140 x 15.0 m, draft 3.9 m",
    VIC: "2x3 convoy: 270-280 m, draft 2.5-4.0 m, 9,600-18,000 t, min bridge 9.10 m",
};

const getCemtDimensionLabel = (cemtRaw) => {
    if (!cemtRaw) return null;
    const normalized = String(cemtRaw).toUpperCase().replace(/\s+/g, "");
    const match = normalized.match(/(VII|VIC|VIB|VIA|VB|VA|IV|III|II|I)/);
    if (!match) return null;
    return CEMT_DIMENSIONS[match[1]] || null;
};

const setRiverFeatureStyle = useCallback((feature, { selected = false } = {}) => {
    if (!feature) return;
    const cemt = feature.get('riverSystemCemt') || feature.get('cemt');
    const { color, width } = getRiverSystemStrokeConfig(cemt);

    if (selected) {
        feature.setStyle([
            new Style({
                stroke: new Stroke({
                    color: 'rgba(255,255,255,0.95)',
                    width: width + 5,
                }),
            }),
            new Style({
                stroke: new Stroke({
                    color: '#ff8a00',
                    width: width + 2,
                }),
            }),
            new Style({
                stroke: new Stroke({
                    color,
                    width,
                }),
            }),
        ]);
        return;
    }

    feature.setStyle(new Style({
        stroke: new Stroke({ color, width }),
        fill: null,
    }));
}, []);

const clearSelectedRiverStretch = useCallback(() => {
    riverNameLookupRequestIdRef.current += 1;
    if (selectedRiverFeatureRef.current) {
        setRiverFeatureStyle(selectedRiverFeatureRef.current, { selected: false });
        selectedRiverFeatureRef.current = null;
    }
    setSelectedRiverStretch(null);
}, [setRiverFeatureStyle]);

const selectRiverStretch = useCallback(async (feature, clickedCoordinate) => {
    if (!feature) return;

    if (selectedRiverFeatureRef.current && selectedRiverFeatureRef.current !== feature) {
        setRiverFeatureStyle(selectedRiverFeatureRef.current, { selected: false });
    }

    selectedRiverFeatureRef.current = feature;
    setRiverFeatureStyle(feature, { selected: true });

    const map = mapInstanceRef.current;
    const proj = map?.getView?.().getProjection?.();
    const geometry = feature.getGeometry?.();
    let lengthMeters = null;
    let clickedLonLat = null;

    if (geometry && proj) {
        try {
            const geom4326 = geometry.clone();
            geom4326.transform(proj, 'EPSG:4326');
            lengthMeters = getGeodesicLength(geom4326, { projection: 'EPSG:4326' });
        } catch (error) {
            console.warn('Failed to compute river stretch length:', error);
        }
    }

    if (clickedCoordinate && proj) {
        try {
            clickedLonLat = toLonLat(clickedCoordinate, proj);
        } catch (error) {
            console.warn('Failed to compute clicked river coordinates:', error);
        }
    }

    const allProps = feature.getProperties?.() || {};
    const { geometry: _geometry, ...featureProps } = allProps;
    const name =
        featureProps.name ||
        featureProps.river_name ||
        featureProps.NAME ||
        null;
    const waterway = featureProps.waterway || featureProps.type || null;
    const cemt = feature.get('riverSystemCemt') || featureProps.cemt || null;
    const cemtDimensions = getCemtDimensionLabel(cemt);
    const sourceIndex = feature.get('riverSystemIndex');

    setSelectedRiverStretch({
        name,
        cemt,
        cemtDimensions,
        waterway,
        geometryType: geometry?.getType?.() || null,
        lengthLabel: Number.isFinite(lengthMeters) ? formatDistance(lengthMeters) : null,
        clickedLonLat: Array.isArray(clickedLonLat) ? clickedLonLat : null,
        sourceIndex: Number.isFinite(sourceIndex) ? sourceIndex + 1 : null,
    });

    if (!Array.isArray(clickedLonLat) || clickedLonLat.length < 2) return;

    const requestId = riverNameLookupRequestIdRef.current + 1;
    riverNameLookupRequestIdRef.current = requestId;

    try {
        const response = await axios.post(
            buildApiUrl("nearest_river_name/"),
            {
                longitude: Number(clickedLonLat[0]),
                latitude: Number(clickedLonLat[1]),
            },
            { headers: await getApiHeaders() }
        );

        const apiRiver = response?.data?.river || null;
        const apiRiverName = typeof apiRiver?.name === 'string' ? apiRiver.name.trim() : '';
        if (!apiRiverName) return;

        // Ignore stale responses if user clicked a different river (or cleared selection).
        if (riverNameLookupRequestIdRef.current !== requestId) return;
        if (selectedRiverFeatureRef.current !== feature) return;

        setSelectedRiverStretch((prev) => {
            if (!prev) return prev;
            return { ...prev, name: apiRiverName };
        });
    } catch (error) {
        console.warn("Failed to load nearest river name for selected river stretch:", error);
    }
}, [getApiHeaders, setRiverFeatureStyle]);

// Update distance measurement layer with points and lines
const updateDistanceMeasureLayer = useCallback((points) => {
    const source = distanceMeasureSourceRef.current;
    const map = mapInstanceRef.current;
    if (!source || !map) return;

    source.clear();
    const proj = map.getView().getProjection();

    let totalDistance = 0;

    // Add point features
    points.forEach((lonlat, index) => {
        const pointFeature = new Feature({
            geometry: new Point(fromLonLat(lonlat, proj)),
            pointIndex: index,
        });
        source.addFeature(pointFeature);
    });

    // Add line segments between consecutive points
    if (points.length >= 2) {
        for (let i = 0; i < points.length - 1; i++) {
            const start = points[i];
            const end = points[i + 1];

            // Calculate segment distance
            const segmentMeters = haversineMeters(start[1], start[0], end[1], end[0]);
            totalDistance += segmentMeters;

            const lineFeature = new Feature({
                geometry: new LineString([
                    fromLonLat(start, proj),
                    fromLonLat(end, proj),
                ]),
                segmentIndex: i,
                segmentDistance: formatDistance(segmentMeters, distanceMeasureUnit),
            });
            source.addFeature(lineFeature);
        }
    }

    setDistanceMeasureTotalKm(totalDistance);
}, [distanceMeasureUnit]);

const getDistanceMeasurePointsFromFeatures = useCallback(() => {
    const source = distanceMeasureSourceRef.current;
    const map = mapInstanceRef.current;
    if (!source || !map) return [];
    const proj = map.getView().getProjection();

    return source.getFeatures()
        .filter((feature) => feature.getGeometry()?.getType?.() === 'Point')
        .map((feature) => ({
            index: feature.get('pointIndex'),
            coord: feature.getGeometry().getCoordinates(),
        }))
        .filter((item) => Number.isFinite(item.index))
        .sort((a, b) => a.index - b.index)
        .map((item) => toLonLat(item.coord, proj));
}, []);

const deleteDistanceMeasurePoint = useCallback((pointIndex) => {
    setDistanceMeasurePoints((prev) => {
        const nextPoints = prev.filter((_, index) => index !== pointIndex);
        updateDistanceMeasureLayer(nextPoints);
        return nextPoints;
    });
    setDistanceMeasureInsertAfterIndex(null);
}, [updateDistanceMeasureLayer]);

const distanceMeasureWaypointRows = useMemo(() => {
    let runningTotalMeters = 0;

    return distanceMeasurePoints.map(([lon, lat], index) => {
        let distanceFromPreviousMeters = null;
        if (index > 0) {
            const previous = distanceMeasurePoints[index - 1];
            distanceFromPreviousMeters = haversineMeters(previous[1], previous[0], lat, lon);
            runningTotalMeters += distanceFromPreviousMeters;
        }

        return {
            lon,
            lat,
            distanceFromPreviousLabel: distanceFromPreviousMeters == null
                ? '-'
                : formatDistance(distanceFromPreviousMeters, distanceMeasureUnit),
            trailingTotalLabel: formatDistance(runningTotalMeters, distanceMeasureUnit),
        };
    });
}, [distanceMeasurePoints, distanceMeasureUnit]);
// ---- Style (cache by properties) ----
const markerStyleCacheRef = useRef({});
const markerStyle = useCallback((feature) => {
    const cache = markerStyleCacheRef.current;

    // Check if this is a cluster feature (only if clustering is enabled)
    const clusteredFeatures = feature.get('features');
    if (clusteredFeatures) {
        const currentZoom = mapInstanceRef.current?.getView?.()?.getZoom?.();
        const visibleClusteredFeatures = clusteredFeatures.filter((clusterFeature) => {
            const minZoom = Number(clusterFeature?.get?.('minZoom'));
            return !Number.isFinite(minZoom) || !Number.isFinite(currentZoom) || currentZoom >= minZoom;
        });
        if (!visibleClusteredFeatures.length) return null;
        if (visibleClusteredFeatures.length > 1) {
            // This is a cluster with multiple markers
            const size = visibleClusteredFeatures.length;
            const key = `cluster-${size}`;

            if (!cache[key]) {
                // Calculate radius based on cluster size (bigger for more markers)
                const baseRadius = 16;
                const maxRadius = 32;
                const radius = Math.min(baseRadius + Math.log(size) * 4, maxRadius);

                cache[key] = new Style({
                    image: new CircleStyle({
                        radius: radius,
                        fill: new Fill({ color: '#9c27b0' }), // Purple color for clusters
                        stroke: new Stroke({ color: '#ffffff', width: 3 })
                    }),
                    text: new Text({
                        text: size.toString(),
                        fill: new Fill({ color: '#ffffff' }),
                        font: size > 99 ? 'bold 10px Arial' : 'bold 14px Arial',
                        textAlign: 'center',
                        textBaseline: 'middle'
                    })
                });
            }
            return cache[key];
        }
    }

    // Single feature - get the actual feature from a cluster wrapper or use feature directly
    const currentZoom = mapInstanceRef.current?.getView?.()?.getZoom?.();
    const actualFeature = (clusteredFeatures || []).find((clusterFeature) => {
        const minZoom = Number(clusterFeature?.get?.('minZoom'));
        return !Number.isFinite(minZoom) || !Number.isFinite(currentZoom) || currentZoom >= minZoom;
    }) || clusteredFeatures?.[0] || feature;
    const num = actualFeature.get('number');
    const markerLabel = actualFeature.get('markerLabel');
    const markerLabelType = actualFeature.get('markerLabelType');
    const loungeCount = actualFeature.get('loungekeyLoungesCount');
    const hasLounges = Number(loungeCount) > 0;
    const isABMarker = actualFeature.get('isABMarker');
    const abLabel = actualFeature.get('abLabel');
    const isLockMarker = actualFeature.get('isLockMarker');
    const isDockingMarker = actualFeature.get('isDockingMarker');
    const isMapMarker = actualFeature.get('isMapMarker');
    const mapMarkerKind = actualFeature.get('mapMarkerKind');
    
    // Check if this is a map marker (ID starts with "map-")
    const featureId = actualFeature.getId();
    const isMapMarkerById = featureId && typeof featureId === 'string' && (featureId.startsWith('map-') || featureId.startsWith('mp-map-'));
    
    let keyBase, style;
    if (isMapMarker || isMapMarkerById) {
        const minZoom = Number(actualFeature.get('minZoom'));
        if (Number.isFinite(minZoom)) {
            const currentZoom = mapInstanceRef.current?.getView?.()?.getZoom?.();
            if (Number.isFinite(currentZoom) && currentZoom < minZoom) return null;
        }
        // Handle map markers with special styling
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        if (mapMarkerKind === 'tourFile') {
            const key = `tourFile|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}`;
            if (!cache[key]) {
                const fill = selected || highlighted ? '#075985' : '#0b6fcf';
                const iconSvg = `<svg xmlns="http://www.w3.org/2000/svg" width="42" height="42" viewBox="0 0 42 42"><circle cx="21" cy="21" r="19" fill="${fill}" stroke="#ffffff" stroke-width="3"/><path d="M14 12h10l5 5v13H14z" fill="#ffffff"/><path d="M24 12v6h5" fill="none" stroke="${fill}" stroke-width="2" stroke-linejoin="round"/><path d="M17 23h9M17 27h7" stroke="${fill}" stroke-width="2" stroke-linecap="round"/></svg>`;
                cache[key] = new Style({
                    zIndex: 3,
                    image: new IconStyle({
                        src: `data:image/svg+xml;charset=utf-8,${encodeURIComponent(iconSvg)}`,
                        imgSize: [42, 42],
                        scale: 1,
                    }),
                });
            }
            return cache[key];
        }
        const rawDisplacement = actualFeature.get('declutterDisplacementPx');
        const displacementDx = Array.isArray(rawDisplacement) && Number.isFinite(Number(rawDisplacement[0]))
            ? Number(rawDisplacement[0])
            : 0;
        const displacementDy = Array.isArray(rawDisplacement) && Number.isFinite(Number(rawDisplacement[1]))
            ? Number(rawDisplacement[1])
            : 0;
        const hasDeclutterDisplacement = Math.hypot(displacementDx, displacementDy) > 1;
        const manualLeaderCoordinates = actualFeature.get('manualLeaderCoordinates');
        const hasManualLeader = Array.isArray(manualLeaderCoordinates) && manualLeaderCoordinates.length >= 2;
        const imageDisplacement = hasDeclutterDisplacement ? [displacementDx, -displacementDy] : [0, 0];
        const hasLeader = hasManualLeader || hasDeclutterDisplacement;
        const key = `map|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}|d:${Math.round(displacementDx)},${Math.round(displacementDy)}|ml:${hasManualLeader?'1':'0'}`;
        const leaderKey = `leader|${key}`;
        
        if (!cache[key]) {
            cache[key] = new Style({
                zIndex: 2,
                image: new IconStyle({
                    src: buildMapListMarkerIconSrc({ selected, highlighted }),
                    imgSize: [42, 42],
                    scale: 1,
                    displacement: imageDisplacement,
                })
            });
        }
        if (hasLeader && !cache[leaderKey]) {
            cache[leaderKey] = new Style({
                zIndex: 1,
                geometry: (styleFeature) => {
                    const coordinates = styleFeature.get('manualLeaderCoordinates') || styleFeature.get('declutterLeaderCoordinates');
                    return Array.isArray(coordinates) && coordinates.length >= 2
                        ? new LineString(coordinates)
                        : null;
                },
                stroke: new Stroke({
                    color: selected || highlighted ? 'rgba(255,90,0,0.85)' : 'rgba(15,23,42,0.5)',
                    width: selected || highlighted ? 2.5 : 1.75,
                }),
            });
        }
        return hasLeader ? [cache[leaderKey], cache[key]] : cache[key];
    } else if (isLockMarker) {
        // Handle lock markers with special styling
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        const key = `lock|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}`;
        
        if (!cache[key]) {
            console.log('MainMap: Creating new lock marker style');
            cache[key] = new Style({
                image: new CircleStyle({
                    radius: 16,
                    fill: new Fill({ color: '#ff6b35' }), // Orange color for lock markers
                    stroke: new Stroke({ color: '#ffffff', width: 3 })
                }),
                text: new Text({
                    text: '🔒', // Lock emoji
                    fill: new Fill({ color: '#ffffff' }),
                    font: 'bold 12px Arial',
                    textAlign: 'center',
                    textBaseline: 'middle'
                })
            });
        }
        return cache[key];
    } else if (isABMarker && abLabel) {
        // Handle A/B markers with custom pin styling
        console.log('MainMap: Styling A/B marker:', abLabel, 'isABMarker:', isABMarker);
        const keyBase = `ab${abLabel}`;
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        const key = `${keyBase}|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}`;
        
        if (!cache[key]) {
            console.log('MainMap: Creating new A/B marker style for:', abLabel);
            const color = "#ff0000"; // Red color for both A and B markers
            // Use circle style similar to numbered markers, but 20% bigger than locks
            const radius = 14.4; // 12 * 1.2 = 20% bigger than lock markers
            cache[key] = new Style({
                image: new CircleStyle({
                    radius: radius,
                    fill: new Fill({ color: color }),
                    stroke: new Stroke({ color: '#ffffff', width: 3 })
                }),
                text: new Text({
                    text: abLabel,
                    fill: new Fill({ color: '#ffffff' }),
                    font: 'bold 12px Arial',
                    textAlign: 'center',
                    textBaseline: 'middle'
                })
            });
        }
        return cache[key];
    } else if (isDockingMarker || markerLabelType === 'docking') {
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        const dockingLabel = String(markerLabel || '⚓');
        const key = `docking:${dockingLabel}|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}`;

        if (!cache[key]) {
            cache[key] = new Style({
                image: new CircleStyle({
                    radius: selected ? 16 : 14,
                    fill: new Fill({ color: selected ? '#0f766e' : '#14b8a6' }),
                    stroke: new Stroke({ color: '#ffffff', width: selected ? 4 : 3 })
                }),
                text: new Text({
                    text: dockingLabel,
                    fill: new Fill({ color: '#ffffff' }),
                    font: 'bold 13px Arial',
                    textAlign: 'center',
                    textBaseline: 'middle'
                })
            });
        }
        return cache[key];
    } else if (markerLabelType === 'layover' && markerLabel) {
        const keyBase = `layover:${markerLabel}`;
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        const key = `${keyBase}|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}|l:${hasLounges?'1':'0'}`;

        if (!cache[key]) {
            cache[key] = new Style({
                text: new Text({
                    text: String(markerLabel),
                    fill: new Fill({ color: '#ffffff' }),
                    font: 'bold 11px Arial',
                    textAlign: 'center',
                    textBaseline: 'middle',
                    padding: [6, 10, 6, 10],
                    backgroundFill: new Fill({ color: '#00b5ad' }),
                    backgroundStroke: new Stroke({ color: '#ffffff', width: 3 })
                })
            });
        }
        if (hasLounges) {
            if (!cache.loungeDot) {
                cache.loungeDot = new Style({
                    text: new Text({
                        text: '●',
                        fill: new Fill({ color: '#f2711c' }),
                        stroke: new Stroke({ color: '#ffffff', width: 2 }),
                        font: 'bold 12px Arial',
                        offsetX: 0,
                        offsetY: -14,
                        textAlign: 'center',
                        textBaseline: 'middle',
                    })
                });
            }
            return [cache[key], cache.loungeDot];
        }
        return cache[key];
    } else if (markerLabelType === 'tourEvent' && markerLabel) {
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        const displacementIndex = Number(actualFeature.get('eventDisplacementIndex') || 0);
        const angle = ((displacementIndex % 6) / 6) * Math.PI * 2;
        const ring = Math.floor(displacementIndex / 6) + 1;
        const displacement = [
            Math.round(Math.cos(angle) * 18 * ring),
            Math.round(Math.sin(angle) * 18 * ring),
        ];
        const key = `tourEvent:${markerLabel}|d:${displacementIndex}|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}`;

        if (!cache[key]) {
            const radius = selected ? 12 : highlighted ? 11 : 9;
            cache[key] = new Style({
                image: new CircleStyle({
                    radius,
                    displacement,
                    fill: new Fill({ color: selected ? '#c2410c' : '#f97316' }),
                    stroke: new Stroke({ color: '#ffffff', width: selected ? 4 : 3 })
                }),
                text: new Text({
                    text: String(markerLabel),
                    fill: new Fill({ color: '#ffffff' }),
                    font: 'bold 9px Arial',
                    textAlign: 'center',
                    textBaseline: 'middle',
                    offsetX: displacement[0],
                    offsetY: displacement[1],
                })
            });
        }
        return cache[key];
    } else if (markerLabelType === 'currentShipLocation') {
        const highlighted = actualFeature.get('highlight');
        const selected = actualFeature.get('selected');
        const key = `currentShipLocation|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}`;

        if (!cache[key]) {
            const halo = new Style({
                image: new CircleStyle({
                    radius: highlighted ? 12 : 10,
                    fill: new Fill({ color: 'rgba(255,255,255,0.92)' }),
                    stroke: new Stroke({
                        color: selected ? '#ff5a00' : 'rgba(0,0,0,0.25)',
                        width: selected ? 3 : 2,
                    }),
                }),
                zIndex: 0,
            });
            const icon = new Style({
                image: new IconStyle({
                    src:
                        'data:image/svg+xml;charset=utf-8,' +
                        encodeURIComponent(`
                        <svg width="24" height="24" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path d="M20 21c-1.39 0-2.78-.47-4-1.32-2.44 1.71-5.56 1.71-8 0C6.78 20.53 5.39 21 4 21H2v2h2c1.38 0 2.74-.35 4-.99 2.52 1.29 5.48 1.29 8 0 1.26.65 2.62.99 4 .99h2v-2h-2zM3.95 19H4c1.6 0 3.02-.88 4-2 .98 1.12 2.4 2 4 2s3.02-.88 4-2c.98 1.12 2.4 2 4 2h.05l2.18-7.65-2.23-.73V4h-5V1H9v3H4v6.62l-2.23.73L3.95 19zM6 6h12v3.97L12 8 6 9.97V6z" fill="#f97316"/>
                        </svg>
                    `),
                    anchor: [0.5, 0.5],
                    anchorXUnits: 'fraction',
                    anchorYUnits: 'fraction',
                    scale: highlighted ? 1.15 : 1,
                }),
                zIndex: 1,
            });
            cache[key] = [halo, icon];
        }
        return cache[key];
    } else {
        // Handle regular numbered markers
        const keyBase = num ? `n${num}` : 'dot';
	        const highlighted = actualFeature.get('highlight');
	        const selected = actualFeature.get('selected');
	        const markerVisualStyle = actualFeature.get('marker_style') || actualFeature.get('markerStyle') || 'circle';
	        const markerText = markerVisualStyle === 'swim_stop' ? 'S' : (num ? String(num) : '');
	        const explicitMarkerRadius = Number(actualFeature.get('markerRadiusPx'));
	        const baseRadius = Number.isFinite(explicitMarkerRadius)
	            ? Math.max(4, Math.min(36, explicitMarkerRadius))
	            : (num ? 12 : 8);
        const markerScale = baseRadius / (num ? 12 : 8);
        const rawDisplacement = actualFeature.get('declutterDisplacementPx');
        const displacementDx = Array.isArray(rawDisplacement) && Number.isFinite(Number(rawDisplacement[0]))
            ? Number(rawDisplacement[0])
            : 0;
        const displacementDy = Array.isArray(rawDisplacement) && Number.isFinite(Number(rawDisplacement[1]))
            ? Number(rawDisplacement[1])
            : 0;
        const hasDeclutterDisplacement = Math.hypot(displacementDx, displacementDy) > 1;
        const manualLeaderCoordinates = actualFeature.get('manualLeaderCoordinates');
        const hasManualLeader = Array.isArray(manualLeaderCoordinates) && manualLeaderCoordinates.length >= 2;
        const hasLeader = hasManualLeader || hasDeclutterDisplacement;
	        const imageDisplacement = hasDeclutterDisplacement ? [displacementDx, -displacementDy] : [0, 0];
	        const key = `${keyBase}|style:${markerVisualStyle}|r:${baseRadius.toFixed(2)}|h:${highlighted?'1':'0'}|s:${selected?'1':'0'}|l:${hasLounges?'1':'0'}|d:${Math.round(displacementDx)},${Math.round(displacementDy)}|ml:${hasManualLeader?'1':'0'}`;
	        const leaderKey = `leader|${key}`;
        
        // Debug logging for selection changes
        if (selected) {
            console.log('Marker style called for selected feature:', actualFeature.getId(), 'selected:', selected, 'key:', key);
        }

	        if (!cache[key]) {
	            const selectedRadius = selected ? baseRadius + Math.max(3, baseRadius * 0.25) : baseRadius;
	            const finalRadius = highlighted ? selectedRadius + Math.max(2, baseRadius * 0.15) : selectedRadius;
	            const outline = selected ? Math.max(3, 4 * markerScale) : Math.max(1.5, 2 * markerScale);

	            if (markerVisualStyle === 'christmas_tree' || markerVisualStyle === 'anchor') {
	                const symbol = markerVisualStyle === 'christmas_tree' ? '🎄' : '⚓';
	                const symbolFill = markerVisualStyle === 'christmas_tree' ? '#166534' : '#0f766e';
	                const badgeOffset = Math.max(10, finalRadius * 0.78);
	                const showNumberBadge = markerVisualStyle !== 'anchor' && num;
	                cache[key] = [
	                    new Style({
	                        zIndex: 2,
	                        text: new Text({
	                            text: symbol,
	                            font: `${Math.max(20, finalRadius * 2.1).toFixed(1)}px Arial`,
	                            fill: new Fill({ color: symbolFill }),
	                            stroke: new Stroke({ color: '#ffffff', width: Math.max(3, outline) }),
	                            textAlign: 'center',
	                            textBaseline: 'middle',
	                            offsetX: displacementDx,
	                            offsetY: displacementDy,
	                        }),
	                    }),
	                    showNumberBadge ? new Style({
	                        zIndex: 3,
	                        image: new CircleStyle({
	                            radius: Math.max(7, finalRadius * 0.58),
	                            displacement: [displacementDx + badgeOffset, -(displacementDy - badgeOffset)],
	                            fill: new Fill({ color: selected ? '#ff5a00' : '#ef4444' }),
	                            stroke: new Stroke({ color: '#ffffff', width: Math.max(2, outline * 0.55) }),
	                        }),
	                        text: new Text({
	                            text: String(num),
	                            fill: new Fill({ color: '#ffffff' }),
	                            font: `bold ${Math.max(7, (num > 9 ? 8 : 10) * markerScale).toFixed(1)}px Arial`,
	                            textAlign: 'center',
	                            textBaseline: 'middle',
	                            offsetX: displacementDx + badgeOffset,
	                            offsetY: displacementDy - badgeOffset,
	                        }),
	                    }) : null,
	                ].filter(Boolean);
	            } else {
	                cache[key] = new Style({
	                    zIndex: 2,
	                    image: new CircleStyle({
	                        radius: finalRadius,
	                        displacement: imageDisplacement,
	                        fill: new Fill({ color: selected ? '#ff5a00' : '#007bff' }),
	                        stroke: new Stroke({ color: '#ffffff', width: outline })
	                    }),
	                    text: markerText ? new Text({
	                        text: markerText,
	                        fill: new Fill({ color: '#ffffff' }),
	                        font: `bold ${Math.max(8, (num > 9 ? 10 : 12) * markerScale).toFixed(1)}px Arial`,
	                        textAlign: 'center',
	                        textBaseline: 'middle',
	                        offsetX: displacementDx,
	                        offsetY: displacementDy,
	                    }) : undefined
	                });
	            }
	        }
        if (hasLeader && !cache[leaderKey]) {
            cache[leaderKey] = new Style({
                zIndex: 1,
                geometry: (styleFeature) => {
                    const coordinates = styleFeature.get('manualLeaderCoordinates') || styleFeature.get('declutterLeaderCoordinates');
                    return Array.isArray(coordinates) && coordinates.length >= 2
                        ? new LineString(coordinates)
                        : null;
                },
                stroke: new Stroke({
                    color: selected || highlighted ? 'rgba(255,90,0,0.85)' : 'rgba(15,23,42,0.5)',
                    width: selected || highlighted ? 2.5 : 1.75,
                }),
            });
        }
        if (hasLounges) {
            if (!cache.loungeDot) {
                cache.loungeDot = new Style({
                    text: new Text({
                        text: '●',
                        fill: new Fill({ color: '#f2711c' }),
                        stroke: new Stroke({ color: '#ffffff', width: 2 }),
                        font: 'bold 12px Arial',
                        offsetX: 0,
                        offsetY: -12,
                        textAlign: 'center',
                        textBaseline: 'middle',
                    })
                });
            }
            const markerStyles = Array.isArray(cache[key]) ? cache[key] : [cache[key]];
            return hasLeader
                ? [cache[leaderKey], ...markerStyles, cache.loungeDot]
                : [...markerStyles, cache.loungeDot];
        }
        if (hasLeader) {
            const markerStyles = Array.isArray(cache[key]) ? cache[key] : [cache[key]];
            return [cache[leaderKey], ...markerStyles];
        }
        return cache[key];
    }
}, []);

const poiNameLabelPreviewStyleCacheRef = useRef({});
const poiNameLabelPreviewStyle = useCallback((feature) => {
    const label = String(feature.get('label') || '').trim();
    if (!label) return null;
    const placement = feature.get('placement') || 'right';
    const isManual = placement === 'manual_center';
    const highlighted = Boolean(feature.get('highlighted'));
    const explicitFontPx = Number(feature.get('labelFontPx'));
    const fontPx = Number.isFinite(explicitFontPx)
        ? Math.max(8, Math.min(22, explicitFontPx))
        : (highlighted ? 14 : 13);
    const explicitGapPx = Number(feature.get('labelGapPx'));
    const labelGapPx = Number.isFinite(explicitGapPx) ? Math.max(2, Math.min(48, explicitGapPx)) : 10;
    const explicitMarkerRadiusPx = Number(feature.get('markerRadiusPx'));
    const markerRadiusPx = Number.isFinite(explicitMarkerRadiusPx) ? Math.max(4, Math.min(36, explicitMarkerRadiusPx)) : 16;
    const anchorGapPx = markerRadiusPx + labelGapPx;
    const key = `${label}|${placement}|${highlighted ? 'h' : 'n'}|f:${fontPx.toFixed(2)}|g:${anchorGapPx.toFixed(2)}`;
    const cache = poiNameLabelPreviewStyleCacheRef.current;
    if (!cache[key]) {
        cache[key] = new Style({
            text: new Text({
                text: label,
                font: `700 ${fontPx.toFixed(1)}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
                fill: new Fill({ color: '#1F3864' }),
                stroke: new Stroke({ color: 'rgba(255,255,255,0.96)', width: 4 }),
                backgroundFill: new Fill({ color: 'rgba(255,255,255,0.88)' }),
                backgroundStroke: new Stroke({ color: highlighted ? 'rgba(37,99,235,0.65)' : 'rgba(31,56,100,0.22)', width: 1 }),
                padding: [2, 5, 2, 5],
                textAlign: isManual ? 'center' : 'left',
                textBaseline: 'middle',
                offsetX: isManual ? 0 : anchorGapPx,
                offsetY: 0,
            }),
        });
    }
    return cache[key];
}, []);

const layoutRoutePreviewStyleCacheRef = useRef({});
const layoutRoutePreviewStyle = useCallback((feature) => {
    const mode = String(feature.get('mode') || 'route').toLowerCase();
    const key = `layout-route:${mode}`;
    const cache = layoutRoutePreviewStyleCacheRef.current;
    if (!cache[key]) {
        const color = mode === 'road'
            ? '#7c3aed'
            : mode === 'straight'
                ? '#475569'
                : '#1f6fb2';
        cache[key] = new Style({
            stroke: new Stroke({
                color,
                width: mode === 'road' ? 4 : 3.5,
                lineDash: mode === 'straight' ? [10, 8] : undefined,
                lineCap: 'round',
                lineJoin: 'round',
            }),
            zIndex: 66,
        });
    }
    return cache[key];
}, []);

// Ensure a polygon rectangle keeps TARGET_RATIO around its current center
function enforceRatioAroundCenter(polyGeom, targetRatio = TARGET_RATIO) {
    if (!polyGeom) return;
    const extent = polyGeom.getExtent(); // [minX, minY, maxX, maxY]
    const cx = (extent[0] + extent[2]) / 2;
    const cy = (extent[1] + extent[3]) / 2;
    let w = extent[2] - extent[0];
    let h = extent[3] - extent[1];
  
    const cur = w / h;
    if (Math.abs(cur - targetRatio) < 1e-6) return;
  
    if (cur > targetRatio) {
      // too wide → increase height
      h = w / targetRatio;
    } else {
      // too tall → increase width
      w = h * targetRatio;
    }
    const minX = cx - w / 2, maxX = cx + w / 2;
    const minY = cy - h / 2, maxY = cy + h / 2;
  
    polyGeom.setCoordinates([[
      [minX, minY],
      [maxX, minY],
      [maxX, maxY],
      [minX, maxY],
      [minX, minY],
    ]]);
  }

// --- map creation (create once; attach/detach target per mount) ---
useEffect(() => {
    // 1) Create Map + Layers ONCE
    if (!mapInstanceRef.current) {
    markersLayerRef.current = new VectorLayer({
        source: sourcesRef.current.markers,
        zIndex: 70, // Above route path (z-index 66)
        style: markerStyle,
    });
    mapListMarkersLayerRef.current = new VectorLayer({
        source: API_CONFIG.ENABLE_MARKER_CLUSTERING ? clusterSourceRef.current : sourcesRef.current.mapListMarkers,
        zIndex: 70,
        style: markerStyle,
    });
    poiNameLabelsLayerRef.current = new VectorLayer({
        source: sourcesRef.current.poiNameLabels,
        zIndex: 71,
        declutter: false,
        style: poiNameLabelPreviewStyle,
    });
    geoJsonLayerRef.current = new VectorLayer({
        source: sourcesRef.current.geoJson,
        zIndex: 5,
    });
    layoutRoutePreviewLayerRef.current = new VectorLayer({
        source: sourcesRef.current.layoutRoutePreview,
        zIndex: 66,
        style: layoutRoutePreviewStyle,
    });
    routeDistanceLabelsSourceRef.current = new VectorSource();
    routeDistanceLabelsLayerRef.current = new VectorLayer({
        source: routeDistanceLabelsSourceRef.current,
        zIndex: 67,
        declutter: true,
        style: (feature) => {
            const label = String(feature.get('label') || '');
            const compact = Boolean(feature.get('compactLabel'));
            const key = `${compact ? 'compact' : 'normal'}:${label}`;
            const cached = routeDistanceLabelStyleCacheRef.current.get(key);
            if (cached) return cached;

            const fontSize = compact ? 10 : 12;
            const padding = compact ? [2, 5, 2, 5] : [4, 7, 4, 7];
            const style = new Style({
                text: new Text({
                    text: label,
                    font: `700 ${fontSize}px system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif`,
                    fill: new Fill({ color: '#075985' }),
                    stroke: new Stroke({ color: 'rgba(255,255,255,0.95)', width: compact ? 2 : 3 }),
                    backgroundFill: new Fill({ color: 'rgba(255,255,255,0.94)' }),
                    backgroundStroke: new Stroke({ color: 'rgba(14,116,144,0.35)', width: 1 }),
                    padding,
                }),
            });
            routeDistanceLabelStyleCacheRef.current.set(key, style);
            return style;
        },
    });
    toursLayerRef.current = new VectorLayer({
        source: sourcesRef.current.tours,
        zIndex: 15,
    });
    userDeviceLocationsSourceRef.current = new VectorSource();
    userDeviceLocationsLayerRef.current = new VectorLayer({
        source: userDeviceLocationsSourceRef.current,
        zIndex: 69,
        style: markerStyle,
        visible: false,
    });

    // River kilometer markers vector tile layer
    // Style function defined outside to prevent recreation on every render
    const riverKmStyleFunction = function(feature, resolution) {
        try {
            const riverKm = feature.get('river_km');
            const riverKmValue = Number(riverKm);

            // Skip features without river_km data
            if (riverKm === undefined || riverKm === null || !Number.isFinite(riverKmValue)) {
                return null;
            }

            // Progressive density:
            // z6-z7 => every 100 km, z8 => every 50 km, z9+ => keep existing density.
            const zoom = Math.max(0, Math.floor(Math.log2(156543.03392804097 / resolution)));
            const intervalKm = zoom >= 9 ? 1 : zoom >= 8 ? 50 : zoom >= 6 ? 100 : null;
            if (!intervalKm) {
                return null;
            }

            const roundedRiverKm = Math.round(riverKmValue);
            if (intervalKm > 1) {
                const mod = ((roundedRiverKm % intervalKm) + intervalKm) % intervalKm;
                if (mod !== 0) {
                    return null;
                }
            }

            const kmText = `km ${Math.round(riverKmValue * 10) / 10}`;

            return new Style({
                image: new CircleStyle({
                    radius: 6,
                    fill: new Fill({ color: '#FF0000' }),
                    stroke: new Stroke({ color: '#FFFFFF', width: 2 }),
                }),
                text: new Text({
                    text: kmText,
                    font: 'bold 12px sans-serif',
                    fill: new Fill({ color: '#000000' }),
                    stroke: new Stroke({ color: '#FFFFFF', width: 3 }),
                    offsetY: -15,
                    textAlign: 'center',
                    textBaseline: 'bottom',
                }),
            });
        } catch (error) {
            console.error('Error styling river km feature:', error, feature);
            return null;
        }
    };

    const tileAuthQuery = getTileAuthQuery();
    const riverKmTilesUrl = buildApiUrl(`tiles/river_km/{z}/{x}/{y}.pbf${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
    const fullTileServerUrl = buildMapsUrl(`tiles/{z}/{x}/{y}.png${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
    const vpsLimitedCoverageUrl = buildTilesUrl('{z}/{x}/{y}.png');

	    const riverKmSource = new VectorTileSource({
	        format: new MVT(),
	        url: riverKmTilesUrl,
	        minZoom: 6, // Load tiles from zoom 6 and above; style function controls density
	        maxZoom: 18,
	        cacheSize: 256, // Reduced cache size to prevent memory issues
	        tileLoadFunction: undefined, // Use default tile loading
    });

    // Add comprehensive error and event handling for debugging
    riverKmSource.on('tileloadstart', (event) => {
        console.log('River km tile load started:', event.tile.getTileCoord());
    });

    riverKmSource.on('tileloadend', (event) => {
        console.log('River km tile loaded successfully:', event.tile.getTileCoord());
    });

    riverKmSource.on('tileloaderror', (event) => {
        console.error('River km tile load error:', event.tile.getTileCoord(), event);
    });

	    riverKmLayerRef.current = new VectorTileLayer({
	        source: riverKmSource,
        zIndex: 65, // Between tours (15) and markers (70)
        visible: false, // Start hidden
        declutter: true, // Prevent label overlap
        style: riverKmStyleFunction,
	        minZoom: 6, // Also set minZoom on layer to prevent rendering below zoom 6
	        maxZoom: 18,
        // renderMode defaults to 'hybrid' which is more stable than 'vector'
        preload: 0, // Don't preload tiles to prevent excessive loading
        useInterimTilesOnError: false, // Don't use interim tiles on error
    });

    const hydroBasinsStyleFunction = function(feature, resolution) {
        try {
            const featureKind = feature.get('feature_kind');
            const distMain = feature.get('dist_main');

            if (featureKind === 'km_marker') {
                if (distMain === undefined || distMain === null) {
                    return null;
                }

                const kmText = `${Math.round(Number(distMain))} km`;
                return new Style({
                    image: new CircleStyle({
                        radius: 5,
                        fill: new Fill({ color: '#7A1A24' }),
                        stroke: new Stroke({ color: '#FFFFFF', width: 2 }),
                    }),
                    text: new Text({
                        text: kmText,
                        font: 'bold 11px sans-serif',
                        fill: new Fill({ color: '#4A0D14' }),
                        stroke: new Stroke({ color: '#FFFFFF', width: 3 }),
                        offsetY: -14,
                        textAlign: 'center',
                        textBaseline: 'bottom',
                    }),
                });
            }

            const pfafLevel = Number(feature.get('pfaf_level') ?? 12);
            const level = Math.min(Math.max(pfafLevel, 1), 12);
            const fillAlpha = Math.max(0.008, 0.055 - level * 0.0035);
            const baseWidth = level <= 4 ? 4.4 : level <= 8 ? 3.6 : 2.8;
            const resolutionBoost = resolution < 120 ? 1.0 : resolution < 260 ? 0.6 : 0.2;
            const width = baseWidth + resolutionBoost;

            // Two-pass stroke (white casing + dark line) to stress watershed boundaries.
            return [
                new Style({
                    stroke: new Stroke({
                        color: 'rgba(255,245,245,0.92)',
                        width: width + 1.5,
                    }),
                    fill: new Fill({
                        color: `rgba(122, 26, 36, ${fillAlpha.toFixed(3)})`,
                    }),
                }),
                new Style({
                    stroke: new Stroke({
                        color: '#5C0D16',
                        width: width,
                    }),
                }),
            ];
        } catch (error) {
            console.error('Error styling HydroBASINS feature:', error, feature);
            return null;
        }
    };

    const holidayCountriesStyleByBucket = {
        '0-3': new Style({
            fill: new Fill({ color: 'rgba(183, 28, 28, 0.34)' }),
            stroke: new Stroke({ color: 'rgba(123, 12, 12, 0.95)', width: 1.5 }),
        }),
        '4-7': new Style({
            fill: new Fill({ color: 'rgba(230, 81, 0, 0.30)' }),
            stroke: new Stroke({ color: 'rgba(189, 66, 0, 0.95)', width: 1.4 }),
        }),
        '8-14': new Style({
            fill: new Fill({ color: 'rgba(251, 192, 45, 0.26)' }),
            stroke: new Stroke({ color: 'rgba(217, 119, 6, 0.95)', width: 1.3 }),
        }),
        '15-21': new Style({
            fill: new Fill({ color: 'rgba(2, 136, 209, 0.22)' }),
            stroke: new Stroke({ color: 'rgba(3, 105, 161, 0.90)', width: 1.2 }),
        }),
        default: new Style({
            fill: new Fill({ color: 'rgba(107, 114, 128, 0.18)' }),
            stroke: new Stroke({ color: 'rgba(75, 85, 99, 0.85)', width: 1.1 }),
        }),
    };

    const holidayCountriesStyleFunction = function(feature) {
        try {
            const urgencyBucket = feature.get('urgency_bucket');
            if (!urgencyBucket || !holidayCountriesStyleByBucket[urgencyBucket]) {
                return null;
            }
            return holidayCountriesStyleByBucket[urgencyBucket];
        } catch (error) {
            console.error('Error styling holiday countries feature:', error, feature);
            return null;
        }
    };

    const countryStatsStyleFunction = function(feature) {
        try {
            const rawBucketIndex = feature.get('bucket_index');
            const bucketIndex = Number(rawBucketIndex);
            const palette = Array.isArray(countryStatsPaletteRef.current) && countryStatsPaletteRef.current.length > 0
                ? countryStatsPaletteRef.current
                : COUNTRY_STATS_DEFAULT_PALETTE;
            const nullColor = countryStatsNullColorRef.current || '#E5E7EB';
            const cache = countryStatsStyleCacheRef.current;
            const paletteKey = palette.join('|');
            const styleKey = Number.isFinite(bucketIndex) && bucketIndex >= 1 && bucketIndex <= palette.length
                ? `${paletteKey}|${bucketIndex}`
                : `null|${nullColor}`;

            if (!cache.has(styleKey)) {
                const hexColor = Number.isFinite(bucketIndex) && bucketIndex >= 1 && bucketIndex <= palette.length
                    ? palette[bucketIndex - 1]
                    : nullColor;
                cache.set(styleKey, new Style({
                    fill: new Fill({
                        color: hexToRgba(hexColor, Number.isFinite(bucketIndex) ? 0.34 : 0.22),
                    }),
                    stroke: new Stroke({
                        color: Number.isFinite(bucketIndex) ? hexToRgba(hexColor, 0.92) : 'rgba(107, 114, 128, 0.85)',
                        width: 1.2,
                    }),
                }));
            }

            return cache.get(styleKey);
        } catch (error) {
            console.error('Error styling country stats feature:', error, feature);
            return null;
        }
    };

    const hydroBasinsParams = new URLSearchParams(tileAuthQuery || '');
    hydroBasinsParams.set('level', String(hydroBasinsLevel));
    const hydroBasinsTilesUrl = buildApiUrl(`tiles/hydrobasins/{z}/{x}/{y}.pbf?${hydroBasinsParams.toString()}`);
    const holidayCountriesTilesUrl = buildApiUrl(`tiles/holiday_countries/{z}/{x}/{y}.pbf${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
    const countryStatsParams = new URLSearchParams(tileAuthQuery || '');
    countryStatsParams.set('metric', countryStatsSelectedMetric || COUNTRY_STATS_DEFAULT_METRIC);
    countryStatsParams.set('base_currency', countryStatsSelectedCurrency || COUNTRY_STATS_DEFAULT_CURRENCY);
    const countryStatsTilesUrl = `${COUNTRY_STATS_TILE_BASE_URL}?${countryStatsParams.toString()}`;
    const hydroBasinsSource = new VectorTileSource({
        format: new MVT(),
        url: hydroBasinsTilesUrl,
        minZoom: 3,
        maxZoom: 18,
        cacheSize: 256,
    });

    const holidayCountriesSource = new VectorTileSource({
        format: new MVT(),
        url: holidayCountriesTilesUrl,
        minZoom: 1,
        maxZoom: 18,
        cacheSize: 256,
    });

    const countryStatsSource = new VectorTileSource({
        format: new MVT(),
        url: countryStatsTilesUrl,
        minZoom: 1,
        maxZoom: 18,
        cacheSize: 256,
    });

    if (!countryCompareSourceRef.current) {
        countryCompareSourceRef.current = new VectorSource();
    }

    const countryComparePolygonStyle = new Style({
        fill: new Fill({ color: 'rgba(13, 148, 136, 0.20)' }),
        stroke: new Stroke({ color: 'rgba(6, 78, 59, 0.98)', width: 4 }),
    });

    const countryCompareBorderCasingStyle = new Style({
        stroke: new Stroke({ color: 'rgba(240, 253, 250, 0.95)', width: 6 }),
    });

    const countryCompareLabelStyle = new Style({
        text: new Text({
            font: '600 13px sans-serif',
            fill: new Fill({ color: '#134e4a' }),
            stroke: new Stroke({ color: '#ffffff', width: 3 }),
            overflow: true,
            textAlign: 'center',
            textBaseline: 'middle',
        }),
        geometry: (feature) => {
            const geometry = feature?.getGeometry?.();
            if (!geometry) return null;
            return new Point(getExtentCenter(geometry.getExtent()));
        },
    });

    const countryCompareStyleFunction = (feature) => {
        const label = feature?.get?.('compareName') || feature?.get?.('name') || '';
        countryCompareLabelStyle.getText()?.setText(label || '');
        return [countryCompareBorderCasingStyle, countryComparePolygonStyle, countryCompareLabelStyle];
    };

    holidayCountriesLayerRef.current = new VectorTileLayer({
        source: holidayCountriesSource,
        zIndex: 63, // Below HydroBASINS (64), above base raster
        visible: false,
        declutter: false,
        style: holidayCountriesStyleFunction,
        minZoom: 1,
        maxZoom: 18,
        preload: 0,
        useInterimTilesOnError: false,
    });

    countryStatsLayerRef.current = new VectorTileLayer({
        source: countryStatsSource,
        zIndex: 62,
        visible: false,
        declutter: false,
        style: countryStatsStyleFunction,
        minZoom: 1,
        maxZoom: 18,
        preload: 0,
        useInterimTilesOnError: false,
    });

    countryCompareLayerRef.current = new VectorLayer({
        source: countryCompareSourceRef.current,
        zIndex: 69, // Keep comparison polygon above thematic layers and below markers.
        visible: false,
        style: countryCompareStyleFunction,
        declutter: true,
    });

    hydroBasinsLayerRef.current = new VectorTileLayer({
        source: hydroBasinsSource,
        zIndex: 64, // Below river km markers, above base map
        visible: false,
        declutter: true,
        style: hydroBasinsStyleFunction,
        minZoom: 3,
        maxZoom: 18,
        preload: 0,
        useInterimTilesOnError: false,
    });

    if (!riversSourceRef.current) {
        riversSourceRef.current = new VectorSource();
        riversLayerRef.current = new VectorLayer({
            source: riversSourceRef.current,
            zIndex: 30,
            visible: false,
        });
    }

    // Dual-layer tile system with smart failover:
    // Top layer: VPS limited coverage (tiles.qrmaps.me) - fast but limited, with transparent fallback
    // Bottom layer: Full tile server (maps.hosnedl.cloud) - complete coverage

    // Create VPS source with image-based error handling (CORS-friendly)
    const vpsSource = new XYZ({
      url: vpsLimitedCoverageUrl,
      crossOrigin: 'anonymous', // Enable CORS
      tileLoadFunction: (tile, src) => {
        const img = tile.getImage();

        // Set up error handler first (before setting src)
        img.onerror = function() {
          // VPS tile failed to load - use transparent tile
          img.src = TRANSPARENT_TILE_DATA_URL;
        };

        // Load the tile
        img.src = src;
      }
    });

	    const hillshadeSource = new XYZ({
	      url: HILLSHADE_TILE_URL,
	      maxZoom: 14,
      crossOrigin: 'anonymous',
	      tileLoadFunction: (tile, src) => {
	        const img = tile.getImage();
        img.onerror = function() {
          img.src = TRANSPARENT_TILE_DATA_URL;
        };
        img.src = src;
	      },
	    });

	    const bathymetrySource = new XYZ({
	      url: BATHYMETRY_TILE_URL,
	      maxZoom: 11,
      crossOrigin: 'anonymous',
	      tileLoadFunction: (tile, src) => {
	        const img = tile.getImage();
	        img.onerror = function() {
	          img.src = TRANSPARENT_TILE_DATA_URL;
	        };
	        img.src = src;
	      },
	    });

	    bathymetryLayerRef.current = new TileLayer({
	      source: bathymetrySource,
	      zIndex: 2,
	      opacity: 0.65,
	      visible: false,
	      minZoom: 3,
	      maxZoom: 19,
	    });

	    hillshadeLayerRef.current = new TileLayer({
	      source: hillshadeSource,
	      zIndex: 2.25,
	      opacity: 1,
	      visible: false,
	      minZoom: 3,
	      maxZoom: 19,
    });

    const initialUrlView = (() => {
      if (!enableUrlStateSync) return null;
      try {
        const params = new URLSearchParams(window.location.search || '');
        const lat = Number.parseFloat(params.get('lat') || '');
        const lon = Number.parseFloat(params.get('lon') || '');
        const zoom = Number.parseFloat(params.get('z') || '');
        const hasCenter = Number.isFinite(lat) && Number.isFinite(lon);
        const hasZoom = Number.isFinite(zoom);
        if (!hasCenter && !hasZoom) return null;
        return {
          centerLonLat: hasCenter ? [lon, lat] : [0, 0],
          zoom: hasZoom ? zoom : 2,
        };
      } catch {
        return null;
      }
    })();

    mapInstanceRef.current = new Map({
        // no target yet; attach below
        layers: [
        // Bottom layer: Full tile server (always has coverage)
        new TileLayer({
            source: new XYZ({
              url: fullTileServerUrl,
              crossOrigin: 'anonymous',
            }),
            zIndex: 0,
        }),
        // Top layer: VPS limited coverage (with automatic transparent fallback)
	        new TileLayer({
	            source: vpsSource,
	            zIndex: 1,
	        }),
	        bathymetryLayerRef.current,
	        hillshadeLayerRef.current,
	        countryStatsLayerRef.current,
        holidayCountriesLayerRef.current,
        countryCompareLayerRef.current,
        hydroBasinsLayerRef.current,
        riverKmLayerRef.current,
        riversLayerRef.current,
        layoutRoutePreviewLayerRef.current,
        markersLayerRef.current,
        mapListMarkersLayerRef.current,
        poiNameLabelsLayerRef.current,
        routeDistanceLabelsLayerRef.current,
        userDeviceLocationsLayerRef.current,
        geoJsonLayerRef.current,
        toursLayerRef.current,
        ],
        view: new View({
        center: fromLonLat(initialUrlView?.centerLonLat || [0, 0]),
        zoom: initialUrlView?.zoom ?? 2,
        minZoom: 1,
        maxZoom: 20,
        }),
        controls: defaultControls({ zoom: false, attribution: false, rotate: false }),
    });

    // ensure the container object exists (in case some code nulled it earlier)
    if (!clickKeyRef.current) {
        clickKeyRef.current = { markers: null, buildings: null, rivers: null, countryStatsHover: null };
    }
  
    // 2) Attach a normalized tap/click listener ONCE and keep the key
    const map = mapInstanceRef.current;
    const coarsePointer = typeof window !== 'undefined'
        && typeof window.matchMedia === 'function'
        && window.matchMedia('(pointer: coarse)').matches;
    const markerHitTolerance = coarsePointer ? 16 : 5;

    clickKeyRef.current.markers = map.on('singleclick', (evt) => {
        const clickedFeature = map.forEachFeatureAtPixel(
          evt.pixel,
          (feat) => feat,
          {
            layerFilter: (layer) => layer === markersLayerRef.current || layer === mapListMarkersLayerRef.current,
            hitTolerance: markerHitTolerance,
          }
        );

        if (!clickedFeature) {
            const countryStatsLayer = countryStatsLayerRef.current;
            const countryStatsLayerVisible = Boolean(countryStatsLayer?.getVisible?.());

            if (countryStatsLayerVisible) {
                const countryStatsFeature = map.forEachFeatureAtPixel(
                    evt.pixel,
                    (feature, layer) => {
                        if (layer === countryStatsLayer) return feature;
                        if (feature?.get && feature.get('metric_code') && feature.get('iso3')) return feature;
                        return null;
                    },
                    { hitTolerance: coarsePointer ? 16 : 6 }
                );

                if (countryStatsFeature) {
                    const countryStatsData = countryStatsFeature.getProperties ? countryStatsFeature.getProperties() : {};
                    if (!countryStatsData?.iso3) {
                        return;
                    }
                    loadCountryStatsModalData(countryStatsData);
                    return;
                }
            }

            const holidayLayer = holidayCountriesLayerRef.current;
            const holidayLayerVisible = Boolean(holidayLayer?.getVisible?.());

            if (holidayLayerVisible) {
                const holidayFeature = map.forEachFeatureAtPixel(
                    evt.pixel,
                    (feature, layer) => {
                        if (layer === holidayLayer) return feature;
                        if (feature?.get && feature.get('urgency_bucket') && feature.get('iso3')) return feature;
                        return null;
                    },
                    { hitTolerance: coarsePointer ? 16 : 6 }
                );

                if (holidayFeature) {
                    const holidayData = holidayFeature.getProperties ? holidayFeature.getProperties() : {};
                    if (!holidayData?.urgency_bucket || !holidayData?.iso3) {
                        return;
                    }
                    loadHolidayCountryModalData(holidayData);
                    return;
                }
            }
        }

        if (!clickedFeature) {
            subscribersRef.current.click.forEach((cb) => cb(null, null));
            return;
        }

        const features = clickedFeature.get('features');
        if (features && features.length > 1) {
            // Map-list marker cluster: zoom in to expand it.
            const extent = clickedFeature.getGeometry().getExtent();
            const view = map.getView();
            const currentZoom = view.getZoom();

            view.fit(extent, {
                duration: 500,
                padding: [100, 100, 100, 100],
                maxZoom: currentZoom + 2
            });
            return;
        }

        const actualFeature = features ? features[0] : clickedFeature;
        const id = actualFeature?.getId?.() ?? null;
        subscribersRef.current.click.forEach((cb) => cb(id, actualFeature || null));
    });

    if (!clickKeyRef.current.countryStatsHover && !coarsePointer) {
        clickKeyRef.current.countryStatsHover = map.on('pointermove', (evt) => {
          if (evt.dragging) {
            setCountryStatsHoverHint((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            const targetElement = map.getTargetElement?.();
            if (targetElement) targetElement.style.cursor = '';
            return;
          }

          const countryStatsLayer = countryStatsLayerRef.current;
          const countryStatsVisible = Boolean(countryStatsLayer?.getVisible?.());
          if (!countryStatsVisible) {
            setCountryStatsHoverHint((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            const targetElement = map.getTargetElement?.();
            if (targetElement) targetElement.style.cursor = '';
            return;
          }

          const hoverFeature = map.forEachFeatureAtPixel(
            evt.pixel,
            (feature, layer) => {
              if (layer === countryStatsLayer) return feature;
              if (feature?.get && feature.get('metric_code') && feature.get('iso3')) return feature;
              return null;
            },
            { hitTolerance: 4 }
          );

          const targetElement = map.getTargetElement?.();
          if (!hoverFeature) {
            setCountryStatsHoverHint((prev) => (prev.visible ? { ...prev, visible: false } : prev));
            if (targetElement) targetElement.style.cursor = '';
            return;
          }

          const featureProps = hoverFeature.getProperties ? hoverFeature.getProperties() : {};
          setCountryStatsHoverHint({
            visible: true,
            x: Number(evt.pixel?.[0] || 0) + 14,
            y: Number(evt.pixel?.[1] || 0) + 14,
            countryName: featureProps.country_name || featureProps.iso3 || 'Country',
            metricLabel: featureProps.metric_short_name || featureProps.metric_code || 'Value',
            metricValue: [
              featureProps.normalized_currency || '',
              formatCountryStatsHoverValue(featureProps.metric_value),
            ].filter(Boolean).join(' '),
          });
          if (targetElement) targetElement.style.cursor = 'pointer';
        });
    }
    }

    // Subscribers
    subscribersRef.current.buildingClick ??= new Set();
    subscribersRef.current.selectionChange ??= new Set();

    if (!editRectSourceRef.current) {
        editRectSourceRef.current = new VectorSource();
        editRectLayerRef.current = new VectorLayer({
        source: editRectSourceRef.current,
        zIndex: 50,
        style: new Style({
            stroke: new Stroke({ color: '#1976d2', width: 2 }),
            fill: new Fill({ color: 'rgba(25,118,210,0.10)' }),
          }),
        });
    }
    
    // Always ensure the edit rect layer is added to the current map instance
    if (editRectLayerRef.current && mapInstanceRef.current) {
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(editRectLayerRef.current)) {
            currentMap.addLayer(editRectLayerRef.current);
        }
    }
    
    // Ensure shade layer is properly connected to current map instance
    if (!shadeSourceRef.current) {
        shadeSourceRef.current = new VectorSource();
        shadeLayerRef.current = new VectorLayer({
        source: shadeSourceRef.current,
        zIndex: 40,
        style: new Style({
            fill: new Fill({ color: 'rgba(255,255,255,0.5)' }),
            stroke: new Stroke({ color: 'rgba(0,0,0,0)' }),
        }),
        });
    }
    
    // Always ensure the shade layer is added to the current map instance
    if (shadeLayerRef.current && mapInstanceRef.current) {
        // Remove from any previous map instance first
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(shadeLayerRef.current)) {
            currentMap.addLayer(shadeLayerRef.current);
        }
    }
    
    if (!buildingsSourceRef.current) {
        buildingsSourceRef.current = new VectorSource();
        buildingsLayerRef.current = new VectorLayer({
        source: buildingsSourceRef.current,
        zIndex: 60,
        style: (feature) => {
            const props = feature.get('properties') || {};
            const selected = !!feature.get('__selected');
            const important = +props.important_building === 1;
            const b = props.building;
    
            let color = '#0B40B1FF';
            let width = 1;
            if (important) { color = '#0D7805'; width = 3; }
            else if (['castle','church','cathedral'].includes(b)) { color = '#b31515'; width = 3; }
            else if (props.name != null) { color = '#CC7373FF'; width = 1; }
    
            if (selected) width = 6;
    
            return new Style({ stroke: new Stroke({ color, width }), fill: new Fill({ color: 'rgba(0,0,0,0)' }) });
        },
        });
    }
    
    // Always ensure the buildings layer is added to the current map instance
    if (buildingsLayerRef.current && mapInstanceRef.current) {
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(buildingsLayerRef.current)) {
            currentMap.addLayer(buildingsLayerRef.current);
        }
    }
    
    if (!tempPointSourceRef.current) {
        tempPointSourceRef.current = new VectorSource();
        tempPointLayerRef.current = new VectorLayer({
        source: tempPointSourceRef.current,
        zIndex: 70,
        style: new Style({
            image: new CircleStyle({
            radius: 8,
            fill: new Fill({ color: '#ff6b35' }),
            stroke: new Stroke({ color: '#fff', width: 2 }),
            }),
        }),
        });
    }

    // Always ensure the temp point layer is added to the current map instance
    if (tempPointLayerRef.current && mapInstanceRef.current) {
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(tempPointLayerRef.current)) {
            currentMap.addLayer(tempPointLayerRef.current);
        }
    }

    // Initialize A/B markers layer (for draggable route points)
    if (!abMarkersSourceRef.current) {
        abMarkersSourceRef.current = new VectorSource();
        abMarkersLayerRef.current = new VectorLayer({
            source: abMarkersSourceRef.current,
            zIndex: 71, // Above temp point
            style: (feature) => {
                const label = feature.get('label');
                const color = label === 'A' ? '#0B40B1' : '#B1500B';
                return new Style({
                    image: new CircleStyle({
                        radius: 12,
                        fill: new Fill({ color: color }),
                        stroke: new Stroke({ color: '#fff', width: 3 }),
                    }),
	                    text: new Text({
	                        text: label,
	                        fill: new Fill({ color: '#fff' }),
	                        font: 'bold 14px sans-serif',
	                        textAlign: 'center',
	                        textBaseline: 'middle',
	                    }),
                });
            },
        });
    }

    // Always ensure the A/B markers layer is added to the current map instance
    if (abMarkersLayerRef.current && mapInstanceRef.current) {
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(abMarkersLayerRef.current)) {
            currentMap.addLayer(abMarkersLayerRef.current);
        }
    }

    // Initialize distance measurement layer
    if (!distanceMeasureSourceRef.current) {
        distanceMeasureSourceRef.current = new VectorSource();
        distanceMeasureLayerRef.current = new VectorLayer({
            source: distanceMeasureSourceRef.current,
            zIndex: 72, // Above A/B markers
            style: (feature) => {
                const type = feature.getGeometry().getType();
                if (type === 'Point') {
                    const index = feature.get('pointIndex');
                    return new Style({
                        image: new CircleStyle({
                            radius: 8,
                            fill: new Fill({ color: '#e91e63' }), // Pink color for measurement points
                            stroke: new Stroke({ color: '#ffffff', width: 2 }),
                        }),
                        text: new Text({
                            text: String(index + 1),
                            fill: new Fill({ color: '#ffffff' }),
                            font: 'bold 10px Arial',
                            textAlign: 'center',
                            textBaseline: 'middle',
                        }),
                    });
                } else if (type === 'LineString') {
                    // Line style with distance labels
                    const segmentDistance = feature.get('segmentDistance');
                    const styles = [
                        new Style({
                            stroke: new Stroke({
                                color: 'rgba(233,30,99,0.18)',
                                width: 14,
                            }),
                        }),
                        new Style({
                            stroke: new Stroke({
                                color: '#e91e63',
                                width: 3,
                                lineDash: [8, 8],
                            }),
                        }),
                    ];
                    // Add distance label at midpoint if segment distance exists
                    if (segmentDistance != null) {
                        const coords = feature.getGeometry().getCoordinates();
                        if (coords.length >= 2) {
                            const midX = (coords[0][0] + coords[1][0]) / 2;
                            const midY = (coords[0][1] + coords[1][1]) / 2;
                            styles.push(new Style({
                                geometry: new Point([midX, midY]),
                                text: new Text({
                                    text: segmentDistance,
                                    fill: new Fill({ color: '#e91e63' }),
                                    stroke: new Stroke({ color: '#ffffff', width: 3 }),
                                    font: 'bold 12px Arial',
                                    textAlign: 'center',
                                    textBaseline: 'bottom',
                                    offsetY: -8,
                                }),
                            }));
                        }
                    }
                    return styles;
                }
                return null;
            },
        });
    }

    // Always ensure the distance measurement layer is added to the current map instance
    if (distanceMeasureLayerRef.current && mapInstanceRef.current) {
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(distanceMeasureLayerRef.current)) {
            currentMap.addLayer(distanceMeasureLayerRef.current);
        }
    }

    // Initialize quick two-finger measurement layer
    if (!quickMeasureSourceRef.current) {
        quickMeasureSourceRef.current = new VectorSource();
        quickMeasureLayerRef.current = new VectorLayer({
            source: quickMeasureSourceRef.current,
            zIndex: 73, // Above distance measurement layer
            style: (feature) => {
                const type = feature.getGeometry().getType();
                if (type === 'Point') {
                    const label = feature.get('label');
                    return new Style({
                        image: new CircleStyle({
                            radius: 10,
                            fill: new Fill({ color: '#ff9800' }), // Orange for quick measure
                            stroke: new Stroke({ color: '#ffffff', width: 2 }),
                        }),
                        text: new Text({
                            text: label || '',
                            fill: new Fill({ color: '#ffffff' }),
                            font: 'bold 12px Arial',
                            textAlign: 'center',
                            textBaseline: 'middle',
                        }),
                    });
                } else if (type === 'LineString') {
                    const distance = feature.get('distance');
                    const styles = [
                        new Style({
                            stroke: new Stroke({
                                color: '#ff9800',
                                width: 3,
                            }),
                        }),
                    ];
                    if (distance != null) {
                        const coords = feature.getGeometry().getCoordinates();
                        if (coords.length >= 2) {
                            const midX = (coords[0][0] + coords[1][0]) / 2;
                            const midY = (coords[0][1] + coords[1][1]) / 2;
                            styles.push(new Style({
                                geometry: new Point([midX, midY]),
                                text: new Text({
                                    text: distance,
                                    fill: new Fill({ color: '#ff9800' }),
                                    stroke: new Stroke({ color: '#ffffff', width: 3 }),
                                    font: 'bold 14px Arial',
                                    textAlign: 'center',
                                    textBaseline: 'bottom',
                                    offsetY: -10,
                                }),
                            }));
                        }
                    }
                    return styles;
                }
                return null;
            },
        });
    }

    // Always ensure the quick measurement layer is added to the current map instance
    if (quickMeasureLayerRef.current && mapInstanceRef.current) {
        const currentMap = mapInstanceRef.current;
        const layers = currentMap.getLayers();
        if (!layers.getArray().includes(quickMeasureLayerRef.current)) {
            currentMap.addLayer(quickMeasureLayerRef.current);
        }
    }

    // Add a click handler for buildings layer (once)
    if (!clickKeyRef.current.buildings) {
        const map = mapInstanceRef.current;
        clickKeyRef.current.buildings = map.on('click', (evt) => {
          const f = map.forEachFeatureAtPixel(
            evt.pixel,
            (feat) => feat,
            { layerFilter: (lyr) => lyr === buildingsLayerRef.current }
          );
          const proj = map.getView().getProjection();
          let payload;
      
          if (f) {
            const props = f.get('properties') || {};
            const geom = f.getGeometry()?.clone();
            let geometry = null;
            if (geom) {
              geom.transform(proj, 'EPSG:4326'); // send back in lon/lat
              geometry = new GeoJSON().writeGeometryObject(geom);
            }
            payload = { properties: props, geometry };
          }
      
          subscribersRef.current.buildingClick.forEach((cb) => cb(payload));
        });
      }

    if (!clickKeyRef.current.rivers) {
        const map = mapInstanceRef.current;
        clickKeyRef.current.rivers = map.on('click', (evt) => {
          const riverFeature = map.forEachFeatureAtPixel(
            evt.pixel,
            (feat) => feat,
            { layerFilter: (lyr) => lyr === riversLayerRef.current, hitTolerance: 6 }
          );

          if (!riverFeature) {
            clearSelectedRiverStretch();
            return;
          }

          selectRiverStretch(riverFeature, evt.coordinate);
        });
      }
      
    // 3) Attach DOM target for THIS mount
    const map = mapInstanceRef.current;
    map.setTarget(mapRef.current);

    // Initial fit once the map has rendered
    const fitInitial = () => {
    if (activeTourToShow?.ports_of_call?.[0]?.poc_info?.location) {
        fitToMarkerSource();
    }
    if (activeMapToShow?.properties?.boundaries) {
        fitMapToBoundaries(activeMapToShow.properties.boundaries);
    }
    };
    map.once('rendercomplete', fitInitial);

    // Keep size in sync with container
    const el = map.getTargetElement?.();
    if (el) {
      const updateMapSize = () => map.updateSize();
      if (typeof window !== 'undefined' && 'ResizeObserver' in window) {
        resizeObsRef.current = new ResizeObserver(updateMapSize);
        resizeObsRef.current.observe(el);
      } else if (typeof window !== 'undefined') {
        const rafIds = [];
        const timers = [0, 80, 220, 420, 800].map((delay) => (
          window.setTimeout(updateMapSize, delay)
        ));
        const scheduleUpdate = () => {
          rafIds.push(window.requestAnimationFrame(updateMapSize));
        };

        window.addEventListener('resize', scheduleUpdate);
        window.addEventListener('orientationchange', scheduleUpdate);
        resizeObsRef.current = {
          disconnect: () => {
            window.removeEventListener('resize', scheduleUpdate);
            window.removeEventListener('orientationchange', scheduleUpdate);
            rafIds.forEach((rafId) => window.cancelAnimationFrame(rafId));
            timers.forEach((timer) => window.clearTimeout(timer));
          },
        };
      }
    }

    setMapReady(true);

    return () => {
        if (resizeObsRef.current) { resizeObsRef.current.disconnect(); resizeObsRef.current = null; }
      
        if (clickKeyRef.current?.markers) { unByKey(clickKeyRef.current.markers); clickKeyRef.current.markers = null; }
        if (clickKeyRef.current?.buildings) { unByKey(clickKeyRef.current.buildings); clickKeyRef.current.buildings = null; }
        if (clickKeyRef.current?.rivers) { unByKey(clickKeyRef.current.rivers); clickKeyRef.current.rivers = null; }
        if (clickKeyRef.current?.countryStatsHover) { unByKey(clickKeyRef.current.countryStatsHover); clickKeyRef.current.countryStatsHover = null; }
        if (markerDeclutterMoveKeyRef.current) { unByKey(markerDeclutterMoveKeyRef.current); markerDeclutterMoveKeyRef.current = null; }
        if (poiNameLabelTranslateEndKeyRef.current) { unByKey(poiNameLabelTranslateEndKeyRef.current); poiNameLabelTranslateEndKeyRef.current = null; }
        setCountryStatsHoverHint((prev) => (prev.visible ? { ...prev, visible: false } : prev));
      
        if (mapInstanceRef.current) {
          if (shadeLayerRef.current)     mapInstanceRef.current.removeLayer(shadeLayerRef.current);
          if (editRectLayerRef.current)  mapInstanceRef.current.removeLayer(editRectLayerRef.current);
          if (buildingsLayerRef.current) mapInstanceRef.current.removeLayer(buildingsLayerRef.current);
          if (tempPointLayerRef.current) mapInstanceRef.current.removeLayer(tempPointLayerRef.current);
      
          if (modifyRef.current)        { mapInstanceRef.current.removeInteraction(modifyRef.current);        modifyRef.current = null; }
          if (translateRef.current)     { mapInstanceRef.current.removeInteraction(translateRef.current);     translateRef.current = null; }
          if (resizeRef.current)        { mapInstanceRef.current.removeInteraction(resizeRef.current);        resizeRef.current = null; }
          if (translatePointRef.current){ mapInstanceRef.current.removeInteraction(translatePointRef.current); translatePointRef.current = null; }
          if (poiNameLabelTranslateRef.current) { mapInstanceRef.current.removeInteraction(poiNameLabelTranslateRef.current); poiNameLabelTranslateRef.current = null; }
        }
      
        mapInstanceRef.current?.setTarget(undefined);
        mapInstanceRef.current = null;
      };
// eslint-disable-next-line react-hooks/exhaustive-deps
}, []); // ← Strict Mode safe

// Function to determine sidebar configuration based on active sidebars
const getSidebarConfig = useCallback(() => {
  // Priority order: map user > tour user > map list > tour list > chat > cruise planning > flight tracking
  if (activeMapToShow && showMapOnlyView) {
    return {
      type: 'map_only',
      width: '400px',
      height: isMobile ? '35vh' : '100vh',
      position: 'right'
    };
  }

  if (activeTourToShow && activeUser?.object_type === 'tour_user') {
    if (!showTourUserDetailView) {
      return {
        type: null,
        width: '0px',
        height: '0px',
        position: 'right'
      };
    }
    return {
      type: 'tour_user',
      width: '400px',
      height: '45vh',
      position: 'right'
    };
  }

  if (showMapListBar && activeMapToEdit && activeUser?.object_type !== 'tour_user') {
    return {
      type: 'map_edit',
      width: isMobile ? '400px' : '520px',
      height: isMobile ? '76px' : '100vh',
      position: 'right'
    };
  }

  if (showMapListBar) {
    return {
      type: 'map_list',
      width: '400px',
      height: isMobile ? '40vh' : '100vh',
      position: 'right'
    };
  }
  
  if (showPortDockEditBar) {
    return {
      type: 'port_dock_edit',
      width: '400px',
      height: isMobile ? '40vh' : '100vh',
      position: 'right'
    };
  }
  if (showTourListBar) {
    return {
      type: 'tour_list',
      width: tourListWideSidebar && !isMobile ? '560px' : '400px',
      height: isMobile ? '50vh' : '100vh',
      position: 'right'
    };
  }
  
  if (showTourDetailBar && activeTourInUserList) {
    return {
      type: 'tour_detail',
      width: tourDetailWideSidebar && !isMobile ? '560px' : '400px',
      height: isMobile ? '50vh' : '100vh',
      position: 'right'
    };
  }

  if (showChatBar) {
    return {
      type: 'chat',
      width: '400px',
      height: isMobile ? '60vh' : '100vh',
      position: 'right'
    };
  }
  
  if (showShipMenu) {
    return {
      type: 'cruise_planning',
      width: isMobile ? '400px' : '560px',
      height: isMobile ? '68vh' : '100vh',
      position: 'right'
    };
  }

  if (showTrackingDevicesPanel) {
    return {
      type: 'tracking_devices',
      width: isMobile ? '400px' : '560px',
      height: isMobile ? '62vh' : '100vh',
      position: 'right'
    };
  }

  if (showAroundMeBar) {
    return {
      type: 'around_me',
      width: '400px',
      height: isMobile ? '55vh' : '100vh',
      position: 'right'
    };
  }

  if (showWorldHeritagePanel) {
    return {
      type: 'world_heritage',
      width: '420px',
      height: isMobile ? '60vh' : '100vh',
      position: 'right'
    };
  }
  
  if (showFlightTrackingBar) {
    return {
      type: 'flight_tracking',
      width: '400px',
      height: isMobile ? '60vh' : '100vh',
      position: 'right'
    };
  }

  if (showFlightRouteCreateBar) {
    return {
      type: 'flight_route_create',
      width: '400px',
      height: isMobile ? '65vh' : '100vh',
      position: 'right'
    };
  }

  if (showFlightPriceBar) {
    return {
      type: 'flight_price',
      width: '400px',
      height: isMobile ? '60vh' : '100vh',
      position: 'right'
    };
  }

  if (showFileShareBar) {
    return {
      type: 'file_share',
      width: '400px',
      height: isMobile ? '70vh' : '100vh',
      position: 'right'
    };
  }

  // Default: no sidebar
  return {
    type: null,
    width: '0px',
    height: '0px',
    position: 'right'
  };
}, [
  activeMapToShow,
  activeMapToEdit,
  activeTourToShow,
  activeUser?.object_type,
  showMapListBar,
  showPortDockEditBar,
  showTourListBar,
  tourListWideSidebar,
  activeTourInUserList,
  showChatBar,
  showShipMenu,
  showTrackingDevicesPanel,
  showAroundMeBar,
  showWorldHeritagePanel,
  showFlightTrackingBar,
  showFlightRouteCreateBar,
  showFlightPriceBar,
  showFileShareBar,
  showMapOnlyView,
  showTourUserDetailView,
  tourDetailWideSidebar,
  isMobile
]);

useEffect(() => {
  if (activeTourToShow && activeUser?.object_type === 'tour_user') {
    setShowTourUserDetailView(true);
  }
}, [activeTourToShow, activeUser?.object_type]);

// Update sidebar config when any sidebar state changes
useEffect(() => {
  const newConfig = getSidebarConfig();
  setSidebarConfig(newConfig);
  if (previousSidebarTypeRef.current !== newConfig.type) {
    setDynamicSidebarHeight(null);
  }
  previousSidebarTypeRef.current = newConfig.type;
}, [getSidebarConfig]);

const buildUrlStateHref = useCallback((overrides = null) => {
  if (!enableUrlStateSync) return null;
  if (window.location.pathname.startsWith('/f/')) return null;

  const params = new URLSearchParams(window.location.search || '');
  const uiType = showTrackingDevicesPanel ? 'tracking_devices' : (sidebarConfig?.type || null);
  const center = Array.isArray(overrides?.centerLatLon)
    ? overrides.centerLatLon
    : Array.isArray(mapViewInfo.center)
    ? mapViewInfo.center
    : null;
  const zoom = typeof overrides?.zoom === 'number' ? overrides.zoom : mapViewInfo.zoom;
  const activeMapIdForUrl =
    uiType === 'map_edit'
      ? activeMapToEdit?.id
      : uiType === 'map_only'
      ? activeMapToShow?.id
      : null;
  const activeTourIdForUrl = uiType === 'tour_detail' ? activeTourToShow?.id : null;
  const pointAForUrl = uiType === 'cruise_planning' ? shipMenuPlannerPoints?.pointA : null;
  const pointBForUrl = uiType === 'cruise_planning' ? shipMenuPlannerPoints?.pointB : null;

  if (uiType) {
    params.set('ui', uiType);
  } else {
    params.delete('ui');
  }

  if (center && Number.isFinite(center[0]) && Number.isFinite(center[1])) {
    params.set('lat', center[0].toFixed(5));
    params.set('lon', center[1].toFixed(5));
  } else {
    params.delete('lat');
    params.delete('lon');
  }

  if (typeof zoom === 'number' && Number.isFinite(zoom)) {
    params.set('z', zoom.toFixed(2));
  } else {
    params.delete('z');
  }

  if (activeMapIdForUrl != null && activeMapIdForUrl !== '') {
    params.set('map', String(activeMapIdForUrl));
  } else {
    params.delete('map');
  }

  if (activeTourIdForUrl != null && activeTourIdForUrl !== '') {
    params.set('tour', String(activeTourIdForUrl));
  } else {
    params.delete('tour');
  }

  if (Array.isArray(pointAForUrl?.coordinate) && pointAForUrl.coordinate.length >= 2) {
    const [aLat, aLon] = pointAForUrl.coordinate;
    if (Number.isFinite(Number(aLat)) && Number.isFinite(Number(aLon))) {
      params.set('a_lat', Number(aLat).toFixed(5));
      params.set('a_lon', Number(aLon).toFixed(5));
    } else {
      params.delete('a_lat');
      params.delete('a_lon');
    }
  } else {
    params.delete('a_lat');
    params.delete('a_lon');
  }

  if (Array.isArray(pointBForUrl?.coordinate) && pointBForUrl.coordinate.length >= 2) {
    const [bLat, bLon] = pointBForUrl.coordinate;
    if (Number.isFinite(Number(bLat)) && Number.isFinite(Number(bLon))) {
      params.set('b_lat', Number(bLat).toFixed(5));
      params.set('b_lon', Number(bLon).toFixed(5));
    } else {
      params.delete('b_lat');
      params.delete('b_lon');
    }
  } else {
    params.delete('b_lat');
    params.delete('b_lon');
  }

  const nextQuery = params.toString();
  const nextHref = `${window.location.pathname}${nextQuery ? `?${nextQuery}` : ''}${window.location.hash || ''}`;
  const currentHref = `${window.location.pathname}${window.location.search || ''}${window.location.hash || ''}`;

  if (nextHref === currentHref) return null;
  return nextHref;
}, [
  activeMapToEdit?.id,
  activeMapToShow?.id,
  activeTourToShow?.id,
  enableUrlStateSync,
  mapViewInfo.center,
  mapViewInfo.zoom,
  showTrackingDevicesPanel,
  shipMenuPlannerPoints?.pointA?.coordinate?.[0],
  shipMenuPlannerPoints?.pointA?.coordinate?.[1],
  shipMenuPlannerPoints?.pointB?.coordinate?.[0],
  shipMenuPlannerPoints?.pointB?.coordinate?.[1],
  sidebarConfig?.type,
]);

useEffect(() => {
  buildUrlStateHrefRef.current = buildUrlStateHref;
}, [buildUrlStateHref]);

const urlSyncDiscreteStateKey = useMemo(() => JSON.stringify({
  ui: sidebarConfig?.type || null,
  trackingDevices: showTrackingDevicesPanel,
  mapEditId: activeMapToEdit?.id ?? null,
  mapShowId: activeMapToShow?.id ?? null,
  tourId: activeTourToShow?.id ?? null,
}), [activeMapToEdit?.id, activeMapToShow?.id, activeTourToShow?.id, showTrackingDevicesPanel, sidebarConfig?.type]);

const applyUrlStateFromLocation = useCallback(async () => {
  if (!enableUrlStateSync || !mapReady) return;

  const params = new URLSearchParams(window.location.search || '');
  const ui = (params.get('ui') || '').trim();
  const mapIdParam = (params.get('map') || '').trim();
  const tourIdParam = (params.get('tour') || '').trim();
  const aLatParam = Number.parseFloat(params.get('a_lat') || '');
  const aLonParam = Number.parseFloat(params.get('a_lon') || '');
  const bLatParam = Number.parseFloat(params.get('b_lat') || '');
  const bLonParam = Number.parseFloat(params.get('b_lon') || '');
  const lat = Number.parseFloat(params.get('lat') || '');
  const lon = Number.parseFloat(params.get('lon') || '');
  const zoom = Number.parseFloat(params.get('z') || '');

  const hasCenter = Number.isFinite(lat) && Number.isFinite(lon);
  const hasZoom = Number.isFinite(zoom);
  const hasCamera = hasCenter || hasZoom;
  const hasA = Number.isFinite(aLatParam) && Number.isFinite(aLonParam);
  const hasB = Number.isFinite(bLatParam) && Number.isFinite(bLonParam);

  if (!ui && !hasCamera) return;

  urlSyncIgnoreRef.current = true;
  if (urlSyncIgnoreReleaseTimerRef.current) {
    window.clearTimeout(urlSyncIgnoreReleaseTimerRef.current);
    urlSyncIgnoreReleaseTimerRef.current = null;
  }

  if (hasCamera) {
    const map = mapInstanceRef.current;
    const view = map?.getView?.();
    if (view) {
      if (hasCenter) view.setCenter(fromLonLat([lon, lat]));
      if (hasZoom) view.setZoom(zoom);
    }
  }

  const sameId = (value, other) => String(value ?? '') === String(other ?? '');
  const findMapById = (mapId) => {
    if (!mapId) return null;
    return (
      userMaps.find((item) => sameId(item?.id, mapId)) ||
      corporateMaps.find((item) => sameId(item?.id, mapId)) ||
      null
    );
  };
  const findTourById = (tourId) => {
    if (!tourId) return null;
    return userTours.find((item) => sameId(item?.id, tourId)) || null;
  };

  try {
    if (ui) {
      if (ui === 'map_list') {
        openMapListBar();
      } else if (ui === 'map_edit') {
        openMapListBar();
        if (mapIdParam) {
          let nextMap = activeMapToEdit && sameId(activeMapToEdit?.id, mapIdParam) ? activeMapToEdit : null;
          if (!nextMap) {
            nextMap = await fetchFullMapData(mapIdParam);
          }
          if (!nextMap) nextMap = findMapById(mapIdParam);
          if (nextMap) setActiveMapToEdit(nextMap);
        }
      } else if (ui === 'tour_list') {
        openTourListBar();
      } else if (ui === 'tour_detail') {
        if (tourIdParam) {
          let nextTour = activeTourToShow && sameId(activeTourToShow?.id, tourIdParam) ? activeTourToShow : null;
          if (!nextTour) nextTour = findTourById(tourIdParam);
          if (!nextTour) nextTour = await fetchFullTourData(tourIdParam);
          if (nextTour) {
            setActiveTourToShow(nextTour);
            openTourDetailBar();
          }
        } else if (activeTourToShow) {
          openTourDetailBar();
        }
      } else if (ui === 'chat') {
        openChatBar();
      } else if (ui === 'cruise_planning' || ui === 'ship') {
        const prefillRoute = (hasA || hasB)
          ? {
              ...(hasA ? { pointA: { label: 'Point A', coordinate: [aLatParam, aLonParam] } } : {}),
              ...(hasB ? { pointB: { label: 'Point B', coordinate: [bLatParam, bLonParam] } } : {}),
            }
          : null;
        openShipMenu(true, prefillRoute ? { prefillRoute } : {});
      } else if (ui === 'around_me') {
        openAroundMeBar(true);
      } else if (ui === 'port_dock_edit') {
        openPortDockEditBar();
      } else if (ui === 'flight_tracking') {
        openFlightTrackingBar();
      } else if (ui === 'flight_route_create') {
        openFlightRouteCreateBar();
      } else if (ui === 'flight_price') {
        openFlightPriceBar();
      } else if (ui === 'file_share') {
        openFileShareBar();
      } else if (ui === 'tracking_devices' || ui === 'tracking-devices') {
        setTrackingDevicesPanelOpen(true);
      } else if (ui === 'world_heritage' && selectedWorldHeritageSite) {
        setShowWorldHeritagePanel(true);
      } else if (ui === 'map_only') {
        closeAllSidebars();
        setShowMapOnlyView(true);
        if (mapIdParam) {
          let nextMap = activeMapToShow && sameId(activeMapToShow?.id, mapIdParam) ? activeMapToShow : null;
          if (!nextMap) nextMap = findMapById(mapIdParam);
          if (!nextMap) nextMap = await fetchFullMapData(mapIdParam);
          if (nextMap) setActiveMapToShow(nextMap);
        }
      } else if (ui === 'tour_user') {
        closeAllSidebars();
      }
    } else {
      closeAllSidebars();
    }
  } finally {
    urlSyncIgnoreReleaseTimerRef.current = window.setTimeout(() => {
      urlSyncIgnoreRef.current = false;
      urlSyncIgnoreReleaseTimerRef.current = null;
    }, 200);
  }

}, [
  activeMapToEdit,
  activeMapToShow,
  activeTourToShow,
  closeAllSidebars,
  corporateMaps,
  enableUrlStateSync,
  fetchFullMapData,
  fetchFullTourData,
  mapReady,
  openAroundMeBar,
  openChatBar,
  openFileShareBar,
  openFlightPriceBar,
  openFlightRouteCreateBar,
  openFlightTrackingBar,
  openMapListBar,
  openPortDockEditBar,
  openShipMenu,
  openTourDetailBar,
  openTourListBar,
  selectedWorldHeritageSite,
  setActiveMapToEdit,
  setActiveMapToShow,
  setActiveTourToShow,
  setTrackingDevicesPanelOpen,
  userMaps,
  userTours,
]);

useEffect(() => {
  if (!enableUrlStateSync || !mapReady) return;
  if (urlSyncInitialApplyDoneRef.current) return;

  urlSyncInitialApplyDoneRef.current = true;
  Promise.resolve(applyUrlStateFromLocation())
    .catch((error) => {
      console.warn('URL state restore failed:', error);
    })
    .finally(() => {
      urlSyncHydrationCompleteRef.current = true;
    });
}, [applyUrlStateFromLocation, enableUrlStateSync, mapReady]);

useEffect(() => {
  if (!enableUrlStateSync) return;

  const handlePopState = () => {
    applyUrlStateFromLocation();
  };

  window.addEventListener('popstate', handlePopState);
  return () => {
    window.removeEventListener('popstate', handlePopState);
  };
}, [applyUrlStateFromLocation, enableUrlStateSync]);

useEffect(() => {
  if (!enableUrlStateSync || !mapReady) return;

  const map = mapInstanceRef.current;
  if (!map) return;

  const moveEndKey = map.on('moveend', () => {
    if (!urlSyncHydrationCompleteRef.current) return;
    if (urlSyncIgnoreRef.current) return;

    const view = map.getView?.();
    const center = view?.getCenter?.();
    if (!view || !center) return;

    const [lon, lat] = toLonLat(center, view.getProjection());
    const zoom = view.getZoom?.();
    const nextHref = buildUrlStateHrefRef.current?.({
      centerLatLon: [lat, lon],
      zoom: typeof zoom === 'number' ? zoom : undefined,
    });
    if (!nextHref) return;
    window.history.replaceState({}, '', nextHref);
  });

  return () => {
    if (moveEndKey) unByKey(moveEndKey);
  };
}, [enableUrlStateSync, mapReady]);

useEffect(() => {
  if (!enableUrlStateSync) return;
  if (!urlSyncHydrationCompleteRef.current) return;
  if (urlSyncIgnoreRef.current) return;

  if (!urlSyncUiInitializedRef.current) {
    urlSyncUiInitializedRef.current = true;
    return;
  }

  const nextHref = buildUrlStateHrefRef.current?.();
  if (!nextHref) return;
  window.history.pushState({}, '', nextHref);
}, [enableUrlStateSync, urlSyncDiscreteStateKey]);

useEffect(() => {
  if (!enableUrlStateSync) return;
  if (!urlSyncHydrationCompleteRef.current) return;
  if (urlSyncIgnoreRef.current) return;

  if (urlSyncCameraWriteTimerRef.current) {
    window.clearTimeout(urlSyncCameraWriteTimerRef.current);
    urlSyncCameraWriteTimerRef.current = null;
  }

  urlSyncCameraWriteTimerRef.current = window.setTimeout(() => {
    if (urlSyncIgnoreRef.current) return;
    const nextHref = buildUrlStateHrefRef.current?.();
    if (!nextHref) return;
    window.history.replaceState({}, '', nextHref);
  }, 350);

  return () => {
    if (urlSyncCameraWriteTimerRef.current) {
      window.clearTimeout(urlSyncCameraWriteTimerRef.current);
      urlSyncCameraWriteTimerRef.current = null;
    }
  };
}, [
  enableUrlStateSync,
  sidebarConfig?.type,
  shipMenuPlannerPoints?.pointA?.coordinate?.[0],
  shipMenuPlannerPoints?.pointA?.coordinate?.[1],
  shipMenuPlannerPoints?.pointB?.coordinate?.[0],
  shipMenuPlannerPoints?.pointB?.coordinate?.[1],
]);

useEffect(() => () => {
  if (urlSyncIgnoreReleaseTimerRef.current) {
    window.clearTimeout(urlSyncIgnoreReleaseTimerRef.current);
  }
  if (urlSyncCameraWriteTimerRef.current) {
    window.clearTimeout(urlSyncCameraWriteTimerRef.current);
  }
}, []);

// Update map size when sidebar config changes (especially important on mobile)
useEffect(() => {
  const map = mapInstanceRef.current;
  if (!map) return;

  const rafIds = [];
  const update = () => map.updateSize();
  rafIds.push(requestAnimationFrame(update));
  rafIds.push(requestAnimationFrame(() => {
    rafIds.push(requestAnimationFrame(update));
  }));
  const timers = [80, 220, 420].map((delay) => window.setTimeout(update, delay));

  return () => {
    rafIds.forEach((rafId) => cancelAnimationFrame(rafId));
    timers.forEach((timer) => window.clearTimeout(timer));
  };
}, [
  effectiveSidebarHeight,
  effectiveSidebarWidthPx,
  mapPaneStyle,
  sidebarConfig?.type,
]);

const getLayoutPreviewRotationRadians = (preview) => {
  const rawDegrees = Number(preview?.visibilitySettings?.map_rotation_degrees);
  if (!Number.isFinite(rawDegrees)) return 0;
  const normalizedDegrees = ((rawDegrees + 180) % 360 + 360) % 360 - 180;
  return (Math.abs(normalizedDegrees) < 0.001 ? 0 : normalizedDegrees) * (Math.PI / 180);
};

const getUnrotatedViewportWsen = (view, size) => {
  if (!view || !size) return null;
  const center = view.getCenter?.();
  const resolution = view.getResolution?.();
  if (!center || !Number.isFinite(Number(resolution))) return null;

  const halfWidth = (Number(size[0]) * Number(resolution)) / 2;
  const halfHeight = (Number(size[1]) * Number(resolution)) / 2;
  const extent = [
    Number(center[0]) - halfWidth,
    Number(center[1]) - halfHeight,
    Number(center[0]) + halfWidth,
    Number(center[1]) + halfHeight,
  ];
  const [w, s, e, n] = transformExtent(extent, view.getProjection(), 'EPSG:4326');
  if (![w, s, e, n].every((value) => Number.isFinite(Number(value)))) return null;
  return { w, s, e, n };
};

useEffect(() => {
  const map = mapInstanceRef.current;
  const view = map?.getView?.();
  if (!view) return;

  if (!isMapLayoutPreviewActive) {
    if (layoutPreviewPreviousRotationRef.current != null) {
      view.setRotation?.(layoutPreviewPreviousRotationRef.current);
      layoutPreviewPreviousRotationRef.current = null;
    }
    return;
  }

  if (layoutPreviewPreviousRotationRef.current == null) {
    layoutPreviewPreviousRotationRef.current = view.getRotation?.() || 0;
  }

  const nextRotation = getLayoutPreviewRotationRadians(mapLayoutPreview);
  if (Math.abs((view.getRotation?.() || 0) - nextRotation) > 0.000001) {
    view.setRotation?.(nextRotation);
  }
}, [
  isMapLayoutPreviewActive,
  mapLayoutPreview?.visibilitySettings?.map_rotation_degrees,
]);

useEffect(() => {
  const map = mapInstanceRef.current;
  const wsen = mapLayoutPreview?.wsen;
  if (!isMapLayoutPreviewActive) {
    layoutPreviewFitKeyRef.current = null;
    return undefined;
  }
  if (!map || !wsen) return undefined;

  const previewFitKey = mapLayoutPreview?.fitKey || 'layout-preview';
  const { w, s, e, n } = wsen;
  if (![w, s, e, n].every((value) => Number.isFinite(Number(value)))) return undefined;
  if (mapLayoutPreview?.trackViewportBounds && layoutPreviewFitKeyRef.current === previewFitKey) {
    return undefined;
  }
  layoutPreviewFitKeyRef.current = previewFitKey;

  const fitPreview = () => {
    const size = map.getSize?.();
    const view = map.getView?.();
    if (!size || !view) return;
    layoutPreviewProgrammaticFitUntilRef.current = Date.now() + 700;
    map.updateSize();
    const targetRotation = getLayoutPreviewRotationRadians(mapLayoutPreview);
    if (Math.abs(view.getRotation?.() || 0) > 0.000001) {
      view.setRotation?.(0);
    }
    const projection = view.getProjection();
    const extent = transformExtent([Number(w), Number(s), Number(e), Number(n)], 'EPSG:4326', projection);
    view.fit(extent, {
      size: map.getSize?.() || size,
      padding: [0, 0, 0, 0],
      duration: 180,
      nearest: false,
    });
    if (Math.abs(targetRotation) > 0.000001) {
      view.setRotation?.(targetRotation);
    }
  };

  const rafIds = [];
  rafIds.push(requestAnimationFrame(fitPreview));
  rafIds.push(requestAnimationFrame(() => {
    rafIds.push(requestAnimationFrame(fitPreview));
  }));
  const timers = [120, 280, 520].map((delay) => window.setTimeout(fitPreview, delay));

  return () => {
    rafIds.forEach((rafId) => cancelAnimationFrame(rafId));
    timers.forEach((timer) => window.clearTimeout(timer));
  };
}, [
  isMapLayoutPreviewActive,
  mapLayoutPreview?.fitKey,
  mapLayoutPreview?.trackViewportBounds,
  mapLayoutPreview?.wsen,
  mapPaneStyle,
]);

useEffect(() => {
  const map = mapInstanceRef.current;
  const source = sourcesRef.current?.layoutRoutePreview;
  if (!source) return;

  source.clear();
  if (!isMapLayoutPreviewActive || !mapLayoutPreview?.routeGeojson || !map) return;

  const features = new GeoJSON().readFeatures(mapLayoutPreview.routeGeojson, {
    dataProjection: 'EPSG:4326',
    featureProjection: map.getView().getProjection(),
  });
  source.addFeatures(features);
}, [
  isMapLayoutPreviewActive,
  mapLayoutPreview?.routeGeojson,
]);

useEffect(() => {
  const map = mapInstanceRef.current;
  const onViewportBoundsChange = mapLayoutPreview?.onViewportBoundsChange;
  if (
    !map ||
    !isMapLayoutPreviewActive ||
    !mapLayoutPreview?.trackViewportBounds ||
    typeof onViewportBoundsChange !== 'function'
  ) {
    return undefined;
  }

  const updateBounds = () => {
    const size = map.getSize?.();
    const view = map.getView?.();
    if (!size || !view) return;
    const wsen = getUnrotatedViewportWsen(view, size);
    if (!wsen) return;
    onViewportBoundsChange(wsen, {
      source: Date.now() <= layoutPreviewProgrammaticFitUntilRef.current ? 'fit' : 'user',
    });
  };

  const key = map.on('moveend', updateBounds);
  return () => unByKey(key);
}, [
  isMapLayoutPreviewActive,
  mapLayoutPreview?.onViewportBoundsChange,
  mapLayoutPreview?.trackViewportBounds,
  mapPaneStyle,
]);

// Update tile sources when tile auth query changes (no map rebuild)
useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    const tileAuthQuery = getTileAuthQuery();
    const fullTileServerUrl = buildMapsUrl(`tiles/{z}/{x}/{y}.png${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
    const vpsLimitedCoverageUrl = buildTilesUrl('{z}/{x}/{y}.png');
    const riverKmTilesUrl = buildApiUrl(`tiles/river_km/{z}/{x}/{y}.pbf${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
    const hydroBasinsParams = new URLSearchParams(tileAuthQuery || '');
    hydroBasinsParams.set('level', String(hydroBasinsLevel));
    const hydroBasinsTilesUrl = buildApiUrl(`tiles/hydrobasins/{z}/{x}/{y}.pbf?${hydroBasinsParams.toString()}`);
    const holidayCountriesTilesUrl = buildApiUrl(`tiles/holiday_countries/{z}/{x}/{y}.pbf${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
    const countryStatsParams = new URLSearchParams(tileAuthQuery || '');
    countryStatsParams.set('metric', countryStatsSelectedMetric || COUNTRY_STATS_DEFAULT_METRIC);
    countryStatsParams.set('base_currency', countryStatsSelectedCurrency || COUNTRY_STATS_DEFAULT_CURRENCY);
    const countryStatsTilesUrl = `${COUNTRY_STATS_TILE_BASE_URL}?${countryStatsParams.toString()}`;

    const bottomLayer = map.getLayers().item(0); // Full tile server
    const topLayer = map.getLayers().item(1); // VPS limited coverage
    const satelliteActive = canUseSatelliteTiles && showMaptilerSatellite;

    if (satelliteActive) {
      // In satellite mode, keep base raster layers hidden.
      bottomLayer?.setVisible(false);
      topLayer?.setVisible(false);
    } else {
      bottomLayer?.setVisible(true);
      topLayer?.setVisible(true);

      // Bottom layer (standard loading)
      bottomLayer.setSource(new XYZ({ url: fullTileServerUrl, crossOrigin: 'anonymous' }));

      // Top layer (VPS with image-based error handling - CORS-friendly)
      const vpsSource = new XYZ({
        url: vpsLimitedCoverageUrl,
        crossOrigin: 'anonymous', // Enable CORS
        tileLoadFunction: (tile, src) => {
          const img = tile.getImage();

          // Set up error handler first (before setting src)
          img.onerror = function() {
            // VPS tile failed to load - use transparent tile
            img.src = 'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mNk+M9QDwADhgGAWjR9awAAAABJRU5ErkJggg==';
          };

          // Load the tile
          img.src = src;
        }
      });

      topLayer.setSource(vpsSource);
    }

	    if (riverKmLayerRef.current) {
	      riverKmLayerRef.current.setSource(new VectorTileSource({
	        format: new MVT(),
	        url: riverKmTilesUrl,
	        minZoom: 6,
	        maxZoom: 18,
	        cacheSize: 256,
	      }));
	    }

    if (hydroBasinsLayerRef.current) {
      hydroBasinsLayerRef.current.setSource(new VectorTileSource({
        format: new MVT(),
        url: hydroBasinsTilesUrl,
        minZoom: 3,
        maxZoom: 18,
        cacheSize: 256,
      }));
    }

    if (holidayCountriesLayerRef.current) {
      holidayCountriesLayerRef.current.setSource(new VectorTileSource({
        format: new MVT(),
        url: holidayCountriesTilesUrl,
        minZoom: 1,
        maxZoom: 18,
        cacheSize: 256,
      }));
    }

    if (countryStatsLayerRef.current) {
      countryStatsLayerRef.current.setSource(new VectorTileSource({
        format: new MVT(),
        url: countryStatsTilesUrl,
        minZoom: 1,
        maxZoom: 18,
        cacheSize: 256,
      }));
    }
}, [canUseSatelliteTiles, countryStatsSelectedCurrency, countryStatsSelectedMetric, getTileAuthQuery, hydroBasinsLevel, showMaptilerSatellite]);

// If markerStyle identity changes, reapply it to the layer (don't recreate layer)
useEffect(() => {
    markersLayerRef.current?.setStyle(markerStyle);
    mapListMarkersLayerRef.current?.setStyle(markerStyle);
}, [markerStyle]);

// Sync river km layer visibility with state
useEffect(() => {
    if (riverKmLayerRef.current) {
        console.log('River km layer visibility changed to:', showRiverKm);
        riverKmLayerRef.current.setVisible(showRiverKm);
    }
}, [showRiverKm]);

const effectiveHillshadeVisible = isMapLayoutPreviewActive
    ? Boolean(mapLayoutPreview?.visibilitySettings?.render_hillshade)
    : showHillshade;
const effectiveBathymetryVisible = isMapLayoutPreviewActive
    ? Boolean(mapLayoutPreview?.visibilitySettings?.render_bathymetry)
    : showBathymetry;

useEffect(() => {
	    if (hillshadeLayerRef.current) {
	        hillshadeLayerRef.current.setVisible(effectiveHillshadeVisible);
	    }
}, [effectiveHillshadeVisible]);

useEffect(() => {
	    if (bathymetryLayerRef.current) {
	        bathymetryLayerRef.current.setVisible(effectiveBathymetryVisible);
	    }
}, [effectiveBathymetryVisible]);

useEffect(() => {
	    if (hydroBasinsLayerRef.current) {
	        console.log('HydroBASINS layer visibility changed to:', showHydroBasins);
        hydroBasinsLayerRef.current.setVisible(showHydroBasins);
    }
}, [showHydroBasins]);

useEffect(() => {
    if (holidayCountriesLayerRef.current) {
        console.log('Holiday countries layer visibility changed to:', showHolidayCountries);
        holidayCountriesLayerRef.current.setVisible(showHolidayCountries);
    }
}, [showHolidayCountries]);

useEffect(() => {
    if (countryStatsLayerRef.current) {
        countryStatsLayerRef.current.setVisible(showCountryStats && Boolean(countryStatsSelectedMetric));
    }
}, [countryStatsSelectedMetric, showCountryStats]);

useEffect(() => {
    if (countryCompareLayerRef.current) {
        countryCompareLayerRef.current.setVisible(showCountryCompare);
    }
}, [showCountryCompare]);

// Manage satellite layer via backend proxy (premium feature)
useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const bottomLayer = map.getLayers().item(0); // Full tile server
    const topLayer = map.getLayers().item(1); // VPS limited coverage

    // If user doesn't have satellite access, ensure layer is removed and toggle is off
    if (!canUseSatelliteTiles) {
        if (maptilerLayerRef.current) {
            map.removeLayer(maptilerLayerRef.current);
            maptilerLayerRef.current = null;
        }
        bottomLayer?.setVisible(true);
        topLayer?.setVisible(true);
        setShowMaptilerSatellite(false);
        return;
    }

    // If toggled on, create/update layer source with the current short-lived tile token
    if (showMaptilerSatellite) {
        bottomLayer?.setVisible(false);
        topLayer?.setVisible(false);
        const tileAuthQuery = getTileAuthQuery();
        const satelliteTilesUrl = buildApiUrl(`satellite/{z}/{x}/{y}/${tileAuthQuery ? `?${tileAuthQuery}` : ''}`);
        const satelliteSource = new XYZ({
            url: satelliteTilesUrl,
            crossOrigin: 'anonymous'
        });

        if (!maptilerLayerRef.current) {
            maptilerLayerRef.current = new TileLayer({
                source: satelliteSource,
                zIndex: 3, // Above the base and hillshade layers.
            });

            map.addLayer(maptilerLayerRef.current);
        } else {
            maptilerLayerRef.current.setSource(satelliteSource);
        }
        maptilerLayerRef.current.setVisible(true);
    } else {
        // Toggled off - hide the layer but keep it around
        if (maptilerLayerRef.current) {
            maptilerLayerRef.current.setVisible(false);
        }
        bottomLayer?.setVisible(true);
        topLayer?.setVisible(true);
    }
}, [canUseSatelliteTiles, getTileAuthQuery, showMaptilerSatellite]);

const loadRiversSystem = useCallback(async () => {
    const map = mapInstanceRef.current;
    if (!map || !riversSourceRef.current) return false;
    try {
        const res = await axios({
            url: buildApiUrl('list_rivers/'),
            method: 'post',
            data: { cruise: 'ams_bud' },
            headers: await getApiHeaders(),
        });
        clearSelectedRiverStretch();
        riversSourceRef.current.clear();
        const rivers = res.data?.rivers || [];
        const fmt = new GeoJSON();
        rivers.forEach((river, riverIndex) => {
            const feats = fmt.readFeatures(river.rivers.segments, {
                dataProjection: 'EPSG:4326',
                featureProjection: map.getView().getProjection(),
            });
            feats.forEach((f) => {
                f.set('riverSystemCemt', river.rivers.cemt || null);
                f.set('riverSystemIndex', riverIndex);
                setRiverFeatureStyle(f, { selected: false });
                riversSourceRef.current.addFeature(f);
            });
        });
        riversLoadedRef.current = true;
        return true;
    } catch (e) {
        console.error(e);
        return false;
    }
}, [clearSelectedRiverStretch, getApiHeaders, setRiverFeatureStyle]);

// Sync river system layer visibility and load data on demand
useEffect(() => {
    if (!mapReady || !riversLayerRef.current) return;
    riversLayerRef.current.setVisible(showRiversSystem);
    if (!showRiversSystem || riversLoadedRef.current) return;
    loadRiversSystem();
}, [loadRiversSystem, mapReady, showRiversSystem]);

useEffect(() => {
    if (!showRiversSystem) {
        clearSelectedRiverStretch();
    }
}, [clearSelectedRiverStretch, showRiversSystem]);

// Handle distance measurement click events
useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map) return;

    // Remove existing click handler if any
    if (distanceMeasureClickKeyRef.current) {
        unByKey(distanceMeasureClickKeyRef.current);
        distanceMeasureClickKeyRef.current = null;
    }

    if (distanceMeasureActive) {
        // Add click handler for measurement mode
        distanceMeasureClickKeyRef.current = map.on('click', (evt) => {
            const proj = map.getView().getProjection();
            const [lon, lat] = toLonLat(evt.coordinate, proj);

            setDistanceMeasurePoints((prev) => {
                const newPoint = [lon, lat];
                const shouldInsert = Number.isInteger(distanceMeasureInsertAfterIndex)
                    && distanceMeasureInsertAfterIndex >= 0
                    && distanceMeasureInsertAfterIndex < prev.length;
                const newPoints = shouldInsert
                    ? [
                        ...prev.slice(0, distanceMeasureInsertAfterIndex + 1),
                        newPoint,
                        ...prev.slice(distanceMeasureInsertAfterIndex + 1),
                    ]
                    : [...prev, newPoint];
                updateDistanceMeasureLayer(newPoints);
                return newPoints;
            });
            setDistanceMeasureInsertAfterIndex(null);
        });
    }

    return () => {
        if (distanceMeasureClickKeyRef.current) {
            unByKey(distanceMeasureClickKeyRef.current);
            distanceMeasureClickKeyRef.current = null;
        }
    };
}, [distanceMeasureActive, distanceMeasureInsertAfterIndex, updateDistanceMeasureLayer]);

// Handle distance measurement marker and segment dragging
useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return;

    if (distanceMeasureTranslateKeyRef.current) {
        unByKey(distanceMeasureTranslateKeyRef.current);
        distanceMeasureTranslateKeyRef.current = null;
    }
    if (distanceMeasureTranslateRef.current) {
        map.removeInteraction(distanceMeasureTranslateRef.current);
        distanceMeasureTranslateRef.current = null;
    }

    if (!distanceMeasureActive || !distanceMeasureLayerRef.current) return;

    distanceMeasureTranslateRef.current = new Translate({
        layers: [distanceMeasureLayerRef.current],
        filter: (feature) => {
            const type = feature.getGeometry()?.getType?.();
            return type === 'Point' || type === 'LineString';
        },
    });

    distanceMeasureTranslateKeyRef.current = distanceMeasureTranslateRef.current.on('translateend', (event) => {
        const translatedFeatures = event.features?.getArray?.() || [];
        const lineFeature = translatedFeatures.find((feature) => feature.getGeometry()?.getType?.() === 'LineString');

        if (lineFeature) {
            const segmentIndex = lineFeature.get('segmentIndex');
            const lineCoords = lineFeature.getGeometry()?.getCoordinates?.() || [];
            const fallbackCoordinate = lineCoords.length >= 2
                ? [
                    (lineCoords[0][0] + lineCoords[1][0]) / 2,
                    (lineCoords[0][1] + lineCoords[1][1]) / 2,
                ]
                : null;
            const insertCoordinate = event.coordinate || fallbackCoordinate;

            if (Number.isInteger(segmentIndex) && insertCoordinate) {
                const proj = map.getView().getProjection();
                const insertLonLat = toLonLat(insertCoordinate, proj);
                setDistanceMeasurePoints((prev) => {
                    if (segmentIndex < 0 || segmentIndex >= prev.length - 1) {
                        updateDistanceMeasureLayer(prev);
                        return prev;
                    }
                    const nextPoints = [
                        ...prev.slice(0, segmentIndex + 1),
                        insertLonLat,
                        ...prev.slice(segmentIndex + 1),
                    ];
                    updateDistanceMeasureLayer(nextPoints);
                    return nextPoints;
                });
                setDistanceMeasureInsertAfterIndex(null);
                return;
            }
        }

        const nextPoints = getDistanceMeasurePointsFromFeatures();
        if (!nextPoints.length) return;
        setDistanceMeasurePoints(nextPoints);
        updateDistanceMeasureLayer(nextPoints);
        setDistanceMeasureInsertAfterIndex(null);
    });

    map.addInteraction(distanceMeasureTranslateRef.current);

    return () => {
        if (distanceMeasureTranslateKeyRef.current) {
            unByKey(distanceMeasureTranslateKeyRef.current);
            distanceMeasureTranslateKeyRef.current = null;
        }
        if (distanceMeasureTranslateRef.current) {
            map.removeInteraction(distanceMeasureTranslateRef.current);
            distanceMeasureTranslateRef.current = null;
        }
    };
}, [distanceMeasureActive, getDistanceMeasurePointsFromFeatures, mapReady, updateDistanceMeasureLayer]);

// Clear measurement when deactivated
useEffect(() => {
    if (!distanceMeasureActive) {
        setDistanceMeasurePoints([]);
        setDistanceMeasureTotalKm(0);
        setDistanceMeasureInsertAfterIndex(null);
        if (distanceMeasureSourceRef.current) {
            distanceMeasureSourceRef.current.clear();
        }
    }
}, [distanceMeasureActive, quickMeasureActive]);

useEffect(() => {
    if (!distanceMeasureActive || distanceMeasurePoints.length === 0) return;
    updateDistanceMeasureLayer(distanceMeasurePoints);
}, [distanceMeasureActive, distanceMeasurePoints, distanceMeasureUnit, updateDistanceMeasureLayer]);

// Two-finger touch quick measurement (only when distanceMeasureActive is false)
useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || distanceMeasureActive || !quickMeasureActive) return;

    const mapElement = map.getTargetElement();
    if (!mapElement) return;

    const handleTouchStart = (evt) => {
        // Only handle exactly 2 fingers
        if (evt.touches.length !== 2) return;

        // Prevent default pinch-zoom behavior
        evt.preventDefault();

        const touch1 = evt.touches[0];
        const touch2 = evt.touches[1];

        // Get the map element's bounding rect for offset calculation
        const rect = mapElement.getBoundingClientRect();

        // Convert touch positions to pixels relative to map element
        const pixel1 = [
            touch1.clientX - rect.left,
            touch1.clientY - rect.top
        ];
        const pixel2 = [
            touch2.clientX - rect.left,
            touch2.clientY - rect.top
        ];

        // Convert pixels to map coordinates
        const coord1 = map.getCoordinateFromPixel(pixel1);
        const coord2 = map.getCoordinateFromPixel(pixel2);

        if (!coord1 || !coord2) return;

        const proj = map.getView().getProjection();
        const [lon1, lat1] = toLonLat(coord1, proj);
        const [lon2, lat2] = toLonLat(coord2, proj);

        // Calculate distance
        const distanceMeters = haversineMeters(lat1, lon1, lat2, lon2);

        // Update quick measure layer
        const source = quickMeasureSourceRef.current;
        if (source) {
            source.clear();

            // Add point A
            const pointA = new Feature({
                geometry: new Point(coord1),
                label: 'A',
            });
            source.addFeature(pointA);

            // Add point B
            const pointB = new Feature({
                geometry: new Point(coord2),
                label: 'B',
            });
            source.addFeature(pointB);

            // Add line with distance
            const line = new Feature({
                geometry: new LineString([coord1, coord2]),
                distance: formatDistance(distanceMeters),
            });
            source.addFeature(line);
        }

        setQuickMeasureResult({ distance: distanceMeters });
    };

    const handleTouchEnd = () => {
        // Keep the measurement visible - user can dismiss it manually
    };

    mapElement.addEventListener('touchstart', handleTouchStart, { passive: false });
    mapElement.addEventListener('touchmove', handleTouchStart, { passive: false });
    mapElement.addEventListener('touchend', handleTouchEnd);

    return () => {
        mapElement.removeEventListener('touchstart', handleTouchStart);
        mapElement.removeEventListener('touchmove', handleTouchStart);
        mapElement.removeEventListener('touchend', handleTouchEnd);
    };
}, [distanceMeasureActive, quickMeasureActive]);

// Clear quick measurement visuals when disabled
useEffect(() => {
    if (quickMeasureActive) return;
    setQuickMeasureResult(null);
    if (quickMeasureSourceRef.current) {
        quickMeasureSourceRef.current.clear();
    }
}, [quickMeasureActive]);

// When the whole component truly unmounts (route change), clean up the map listener
useEffect(() => {
    return () => {
    if (clickKeyRef.current?.markers) {
        unByKey(clickKeyRef.current.markers);
        clickKeyRef.current.markers = null;
    }
    if (clickKeyRef.current?.buildings) {
        unByKey(clickKeyRef.current.buildings);
        clickKeyRef.current.buildings = null;
    }
    if (clickKeyRef.current?.countryStatsHover) {
        unByKey(clickKeyRef.current.countryStatsHover);
        clickKeyRef.current.countryStatsHover = null;
    }
    if (countryCompareTranslateStartKeyRef.current) {
        unByKey(countryCompareTranslateStartKeyRef.current);
        countryCompareTranslateStartKeyRef.current = null;
    }
    if (countryCompareTranslatingKeyRef.current) {
        unByKey(countryCompareTranslatingKeyRef.current);
        countryCompareTranslatingKeyRef.current = null;
    }
    if (countryCompareTranslateEndKeyRef.current) {
        unByKey(countryCompareTranslateEndKeyRef.current);
        countryCompareTranslateEndKeyRef.current = null;
    }
    if (countryCompareTranslateRef.current) {
        mapInstanceRef.current.removeInteraction(countryCompareTranslateRef.current);
        countryCompareTranslateRef.current = null;
    }
    if (modifyRef.current) { mapInstanceRef.current.removeInteraction(modifyRef.current); modifyRef.current = null; }
    if (translateRef.current) { mapInstanceRef.current.removeInteraction(translateRef.current); translateRef.current = null; }
    if (translatePointRef.current) { mapInstanceRef.current.removeInteraction(translatePointRef.current); translatePointRef.current = null; }
    mapInstanceRef.current?.setTarget(undefined);
    mapInstanceRef.current = null;
    };
}, []);

// --- markers selection helpers ---
const setSelected = useCallback((id, on, options = {}) => {
    console.log('setSelected called:', id, on);
    // Access the underlying feature sources, not cluster wrappers.
    const f = sourcesRef.current?.markers.getFeatureById(id)
        || sourcesRef.current?.mapListMarkers?.getFeatureById(id);
    if (!f) {
        console.log('Feature not found for id:', id);
        return;
    }
    console.log('Setting selected property on feature:', f.getId(), 'to:', !!on);
    f.set('selected', !!on);

    // Clear the style cache to force style recalculation
    markerStyleCacheRef.current = {};

    f.changed();

    // Force map-list cluster source to refresh when selecting a clustered map-list marker.
    if (API_CONFIG.ENABLE_MARKER_CLUSTERING && f.get('isMapListMarker')) {
        clusterSourceRef.current?.changed();
    }

    console.log('Feature changed event triggered, cache cleared');

    // If selecting a marker (on === true), check if it's in a cluster and zoom to ungroup
    if (on && options.fit !== false && mapInstanceRef.current) {
        const map = mapInstanceRef.current;
        const view = map.getView();
        const currentZoom = view.getZoom();

        if (API_CONFIG.ENABLE_MARKER_CLUSTERING && f.get('isMapListMarker')) {
            // Find cluster feature containing this marker
            const clusterFeatures = clusterSourceRef.current?.getFeatures?.() || [];
            let foundInCluster = false;

            for (const clusterFeature of clusterFeatures) {
                const features = clusterFeature.get('features');
                if (features && features.length > 1) {
                    // This is a cluster with multiple features
                    const containsSelected = features.some(feat => feat.getId() === id);
                    if (containsSelected) {
                        foundInCluster = true;
                        // Zoom in to break up the cluster
                        const extent = clusterFeature.getGeometry().getExtent();
                        view.fit(extent, {
                            duration: 500,
                            padding: [100, 100, 100, 100],
                            maxZoom: currentZoom + 3
                        });
                        break;
                    }
                }
            }

            // If not in a cluster, just center on the marker
            if (!foundInCluster) {
                const geometry = f.getGeometry();
                if (geometry) {
                    view.animate({
                        center: geometry.getCoordinates(),
                        duration: 500,
                        zoom: Math.max(currentZoom, 15) // Ensure decent zoom level
                    });
                }
            }
        } else {
            // No clustering - just center on the marker
            const geometry = f.getGeometry();
            if (geometry) {
                view.animate({
                    center: geometry.getCoordinates(),
                    duration: 500,
                    zoom: Math.max(currentZoom, 15) // Ensure decent zoom level
                });
            }
        }
    }
}, []);

const subscribersRef = useRef({ click: new Set() });
const onMarkerClick = useCallback(cb => {
    subscribersRef.current.click.add(cb);
    return () => subscribersRef.current.click.delete(cb);
}, []);

// ❌ REMOVE the second click effect you had before — it duplicated handlers
// (Your once-attached handler above is enough.)

// --- methods ---
const addMarker = useCallback((coordinates, properties = {}) => {
    if (!mapInstanceRef.current) return;
    const marker = new Feature({
    geometry: new Point(fromLonLat(coordinates)),
    ...properties
    });
    sourcesRef.current?.markers.addFeature(marker);
    return marker;
}, []);

const addMappoints = useCallback((mappoints = []) => {
    const markerSrc = sourcesRef.current?.markers;
    const mapListSrc = sourcesRef.current?.mapListMarkers;
    if (!markerSrc || !mapListSrc || !mappoints.length) return [];
    const map = mapInstanceRef.current;
    const featureProjection = map ? map.getView().getProjection() : 'EPSG:3857';

    const fc = {
    type: 'FeatureCollection',
    features: mappoints
        .filter(p => p?.center?.type === 'Point' && Array.isArray(p.center.coordinates))
        .map((p) => {
        const { center, ...properties } = p;
        return ({
        type: 'Feature',
        geometry: center, // lon/lat in EPSG:4326
        properties
        });
        })
    };

    const features = new GeoJSON().readFeatures(fc, {
    dataProjection: 'EPSG:4326',
    featureProjection
    });

    features.forEach(f => {
    const id = f.get('id');
    if (id != null) f.setId(`mp-${id}`);
    const manualLeaderCoordinates = f.get('manualLeaderCoordinates');
    if (Array.isArray(manualLeaderCoordinates) && manualLeaderCoordinates.length >= 2) {
        const projectedLeaderCoordinates = manualLeaderCoordinates
            .map((coordinates) => (
                Array.isArray(coordinates) && coordinates.length >= 2
                    ? fromLonLat([Number(coordinates[0]), Number(coordinates[1])], featureProjection)
                    : null
            ))
            .filter((coordinates) => (
                Array.isArray(coordinates) &&
                coordinates.length >= 2 &&
                Number.isFinite(coordinates[0]) &&
                Number.isFinite(coordinates[1])
            ));
        if (projectedLeaderCoordinates.length >= 2) {
            f.set('manualLeaderCoordinates', projectedLeaderCoordinates, true);
        } else {
            f.unset?.('manualLeaderCoordinates', true);
        }
    }
    });

    const mapListFeatures = [];
    const markerFeatures = [];
    features.forEach((feature) => {
        const id = String(feature.get('id') ?? feature.getId?.() ?? '');
        if (feature.get('isMapListMarker') || (feature.get('isMapMarker') && id.startsWith('map-'))) {
            mapListFeatures.push(feature);
        } else {
            markerFeatures.push(feature);
        }
    });

    if (markerFeatures.length) markerSrc.addFeatures(markerFeatures);
    if (mapListFeatures.length) {
        mapListSrc.addFeatures(mapListFeatures);
        clusterSourceRef.current?.changed?.();
    }
    if (map) {
        declutterMapMarkerFeatures(markerSrc.getFeatures(), map);
        if (!API_CONFIG.ENABLE_MARKER_CLUSTERING) {
            declutterMapMarkerFeatures(mapListSrc.getFeatures(), map, { includeMapListMarkers: true });
        }
        if (!markerDeclutterMoveKeyRef.current) {
            markerDeclutterMoveKeyRef.current = map.on('moveend', () => {
                const markerFeatures = sourcesRef.current?.markers?.getFeatures?.() || [];
                const mapListMarkerFeatures = sourcesRef.current?.mapListMarkers?.getFeatures?.() || [];
                declutterMapMarkerFeatures(markerFeatures, map);
                if (!API_CONFIG.ENABLE_MARKER_CLUSTERING) {
                    declutterMapMarkerFeatures(mapListMarkerFeatures, map, { includeMapListMarkers: true });
                }
            });
        }
    }
    return features;
}, []);

const clearPoiNameLabelTranslateInteraction = useCallback(() => {
    const map = mapInstanceRef.current;
    if (poiNameLabelTranslateEndKeyRef.current) {
        unByKey(poiNameLabelTranslateEndKeyRef.current);
        poiNameLabelTranslateEndKeyRef.current = null;
    }
    if (map && poiNameLabelTranslateRef.current) {
        map.removeInteraction(poiNameLabelTranslateRef.current);
    }
    poiNameLabelTranslateRef.current = null;
}, []);

const setPoiNameLabelPreviews = useCallback((labelPreviews = [], options = {}) => {
    const src = sourcesRef.current?.poiNameLabels;
    if (!src) return [];
    clearPoiNameLabelTranslateInteraction();
    src.clear();
    const map = mapInstanceRef.current;
    const featureProjection = map ? map.getView().getProjection() : 'EPSG:3857';
    const fc = {
        type: 'FeatureCollection',
        features: (labelPreviews || [])
            .filter((item) => item?.center?.type === 'Point' && Array.isArray(item.center.coordinates))
            .map((item) => {
                const { center, ...properties } = item;
                return ({
                    type: 'Feature',
                    geometry: center,
                    properties,
                });
            }),
    };
    const features = new GeoJSON().readFeatures(fc, {
        dataProjection: 'EPSG:4326',
        featureProjection,
    });
    features.forEach((feature) => {
        const id = feature.get('id');
        if (id != null) feature.setId(`poi-name-label-${id}`);
        if (map && feature.get('placement') !== 'manual_center') {
            const coordinate = feature.getGeometry?.()?.getCoordinates?.();
            const pixel = coordinate ? map.getPixelFromCoordinate(coordinate) : null;
            const label = String(feature.get('label') || '');
            const labelFontPx = Number(feature.get('labelFontPx'));
            const fontPx = Number.isFinite(labelFontPx) ? Math.max(8, Math.min(22, labelFontPx)) : 13;
            const labelGapPx = Number(feature.get('labelGapPx'));
            const markerRadiusPx = Number(feature.get('markerRadiusPx'));
            const anchorGapPx = (
                (Number.isFinite(markerRadiusPx) ? Math.max(4, Math.min(36, markerRadiusPx)) : 16)
                + (Number.isFinite(labelGapPx) ? Math.max(2, Math.min(48, labelGapPx)) : 10)
            );
            const estimatedWidthPx = Math.min(260, Math.max(28, label.length * fontPx * 0.56 + 10));
            if (pixel && Number.isFinite(pixel[0]) && Number.isFinite(pixel[1])) {
                feature.getGeometry?.()?.setCoordinates?.(map.getCoordinateFromPixel([
                    pixel[0] + anchorGapPx + (estimatedWidthPx / 2),
                    pixel[1],
                ]));
                feature.set('placement', 'manual_center', true);
                feature.set('computedFromMarker', true, true);
            }
        }
    });
    src.addFeatures(features);
    if (map && features.length && typeof options.onMoveEnd === 'function') {
        poiNameLabelTranslateRef.current = new Translate({
            layers: [poiNameLabelsLayerRef.current],
            filter: (feature) => feature?.get?.('id') != null,
        });
        poiNameLabelTranslateEndKeyRef.current = poiNameLabelTranslateRef.current.on('translateend', (event) => {
            const movedFeatures = event?.features;
            if (!movedFeatures || typeof movedFeatures.forEach !== 'function') return;
            const projection = map.getView().getProjection();
            movedFeatures.forEach((feature) => {
                const coordinate = feature.getGeometry?.()?.getCoordinates?.();
                const id = feature.get('id');
                if (id == null || !coordinate) return;
                options.onMoveEnd({
                    id,
                    label_center: {
                        type: 'Point',
                        coordinates: toLonLat(coordinate, projection),
                    },
                });
            });
        });
        map.addInteraction(poiNameLabelTranslateRef.current);
    }
    return features;
}, [clearPoiNameLabelTranslateInteraction]);

const clearPoiNameLabelPreviews = useCallback(() => {
    clearPoiNameLabelTranslateInteraction();
    sourcesRef.current?.poiNameLabels?.clear();
}, [clearPoiNameLabelTranslateInteraction]);

const getMarkerDeclutterPlacements = useCallback(() => {
    const map = mapInstanceRef.current;
    const markerFeatures = sourcesRef.current?.markers?.getFeatures?.() || [];
    if (!map) return [];
    return markerFeatures
        .map((feature) => {
            const targetCoordinate = feature.getGeometry?.()?.getCoordinates?.();
            if (!targetCoordinate) return null;
            const leaderCoordinates = feature.get('declutterLeaderCoordinates');
            const markerCoordinate = Array.isArray(leaderCoordinates) && leaderCoordinates.length >= 2
                ? leaderCoordinates[1]
                : targetCoordinate;
            const rawId = feature.get('id') ?? String(feature.getId?.() || '').replace(/^mp-/, '');
            const targetLonLat = toLonLat(targetCoordinate, map.getView().getProjection());
            const markerLonLat = toLonLat(markerCoordinate, map.getView().getProjection());
            return {
                id: rawId,
                feature_id: feature.getId?.() || null,
                target_center: { type: 'Point', coordinates: targetLonLat },
                marker_center: { type: 'Point', coordinates: markerLonLat },
                decluttered: Math.hypot(markerLonLat[0] - targetLonLat[0], markerLonLat[1] - targetLonLat[1]) > 1e-9,
            };
        })
        .filter(Boolean);
}, []);

const getPoiNameLabelPlacements = useCallback(() => {
    const map = mapInstanceRef.current;
    const labelFeatures = sourcesRef.current?.poiNameLabels?.getFeatures?.() || [];
    if (!map) return [];
    return labelFeatures
        .map((feature) => {
            const coordinate = feature.getGeometry?.()?.getCoordinates?.();
            if (!coordinate) return null;
            const rawId = feature.get('id') ?? String(feature.getId?.() || '').replace(/^poi-name-label-/, '');
            const placement = feature.get('placement') || 'right';
            let centerCoordinate = coordinate;
            if (placement !== 'manual_center') {
                const pixel = map.getPixelFromCoordinate(coordinate);
                const label = String(feature.get('label') || '');
                const labelFontPx = Number(feature.get('labelFontPx'));
                const fontPx = Number.isFinite(labelFontPx) ? Math.max(8, Math.min(22, labelFontPx)) : 13;
                const labelGapPx = Number(feature.get('labelGapPx'));
                const markerRadiusPx = Number(feature.get('markerRadiusPx'));
                const anchorGapPx = (
                    (Number.isFinite(markerRadiusPx) ? Math.max(4, Math.min(36, markerRadiusPx)) : 16)
                    + (Number.isFinite(labelGapPx) ? Math.max(2, Math.min(48, labelGapPx)) : 10)
                );
                const estimatedWidthPx = Math.min(260, Math.max(28, label.length * fontPx * 0.56 + 10));
                if (pixel && Number.isFinite(pixel[0]) && Number.isFinite(pixel[1])) {
                    centerCoordinate = map.getCoordinateFromPixel([
                        pixel[0] + anchorGapPx + (estimatedWidthPx / 2),
                        pixel[1],
                    ]);
                }
            }
            return {
                id: rawId,
                label_center: {
                    type: 'Point',
                    coordinates: toLonLat(centerCoordinate, map.getView().getProjection()),
                },
            };
        })
        .filter(Boolean);
}, []);

const fitToMarkerSource = useCallback(({ padding = [50, 50, 50, 50] } = {}) => {
    const map = mapInstanceRef.current;
    const src = sourcesRef.current?.markers;
    const mapListSrc = sourcesRef.current?.mapListMarkers;
    if (!map || !src) return;

    const extents = [src.getExtent(), mapListSrc?.getExtent?.()]
        .filter((extent) => extent && isFinite(extent[0]));
    if (!extents.length) return;
    const extent = extents.reduce((acc, item) => ([
        Math.min(acc[0], item[0]),
        Math.min(acc[1], item[1]),
        Math.max(acc[2], item[2]),
        Math.max(acc[3], item[3]),
    ]));

    const singlePoint = extent[0] === extent[2] && extent[1] === extent[3];
    const toFit = singlePoint ? bufferExtent(extent, 10) : extent;

    const size = map.getSize();
    if (!size || size[0] === 0 || size[1] === 0) {
    map.once('rendercomplete', () => {
        map.getView().fit(toFit, { size: map.getSize(), padding, duration: 500 });
    });
    return;
    }
    map.getView().fit(toFit, { size, padding, duration: 500 });
}, []);

// Function to refresh tour markers when tour is updated
const refreshTourMarkers = useCallback(() => {
    if (!activeTourToShow?.ports_of_call) return;
    
    // Clear existing markers
    clearLayer('markers');
    
    // Convert tour ports to mappoints format
    const mappoints = activeTourToShow.ports_of_call
        .map((p) => {
            const center = getEffectivePortCoordinates(p);
            return center ? {
                id: p.id,
                number: p.visit_order,
                center,
                popup: p.poc_info.name,
            } : null;
        })
        .filter(Boolean);
    
    // Add markers to map
    if (mappoints.length > 0) {
        addMappoints(mappoints);
        // Fit to the new markers
        setTimeout(() => fitToMarkerSource(), 100);
    }
}, [activeTourToShow, clearLayer, addMappoints, fitToMarkerSource]);

const addGeoJson = useCallback((geoJsonData, properties = {}) => {
    const map = mapInstanceRef.current;
    const src = sourcesRef.current?.geoJson;
    if (!map || !src) return [];

    const features = new GeoJSON().readFeatures(geoJsonData, {
    dataProjection: 'EPSG:4326',
    featureProjection: map.getView().getProjection()
    });

    const flightPathColors = ['#e74c3c', '#f1c40f', '#27ae60', '#2980b9', '#8e44ad', '#e67e22'];
    features.forEach(feature => {
    feature.setProperties(properties);
    const kind = feature.get('kind');
    if (kind === 'flightPath') {
        const legIndex = feature.get('legIndex') ?? 0;
        const color = feature.get('color') || flightPathColors[legIndex % flightPathColors.length];
        const dashed = feature.get('dashed');
        feature.setStyle(new Style({
        stroke: new Stroke({
            color,
            width: 4,
            lineDash: dashed ? [4, 6] : undefined
        }),
        zIndex: 2
        }));
    } else if (kind === 'sailedTrack') {
        feature.setStyle(new Style({
            stroke: new Stroke({
            color: '#d946ef',
            width: 5,
            lineCap: 'round',
            lineJoin: 'round'
            }),
            zIndex: 2
        }));
    }
    });
    src.addFeatures(features);
    return features;
}, []);

const getRouteLabelCandidateCoordinates = useCallback((feature) => {
    const geometry = feature?.getGeometry?.();
    if (!geometry) return [];

    const collectLineCandidates = (line) => {
        if (!line?.getCoordinateAt || !line?.getCoordinates) return [];
        const coords = line.getCoordinates();
        if (!Array.isArray(coords) || coords.length < 2) return [];
        return [0.5, 0.36, 0.64].map((fraction) => {
            const coordinate = line.getCoordinateAt(fraction);
            const before = line.getCoordinateAt(Math.max(0, fraction - 0.02));
            const after = line.getCoordinateAt(Math.min(1, fraction + 0.02));
            return { coordinate, before, after, length: line.getLength?.() || 0 };
        });
    };

    if (geometry.getType?.() === 'LineString') {
        return collectLineCandidates(geometry);
    }

    if (geometry.getType?.() === 'MultiLineString' && typeof geometry.getLineStrings === 'function') {
        return geometry
            .getLineStrings()
            .flatMap(collectLineCandidates)
            .sort((a, b) => b.length - a.length);
    }

    return [];
}, []);

const boxesIntersect = (a, b) => (
    a.left < b.right &&
    a.right > b.left &&
    a.top < b.bottom &&
    a.bottom > b.top
);

const renderRouteDistanceLabels = useCallback(() => {
    const map = mapInstanceRef.current;
    const source = routeDistanceLabelsSourceRef.current;
    if (!map || !source) return;

    source.clear();
    const groups = routeDistanceLabelGroupsRef.current || [];
    if (!groups.length) return;

    const zoom = map.getView?.()?.getZoom?.() ?? 10;
    const compactLabels = zoom <= 9;
    const acceptedBoxes = [];
    groups.forEach((group) => {
        const label = String(group?.label || '').trim();
        const features = Array.isArray(group?.features) ? group.features : [];
        if (!label || !features.length) return;

        const candidates = features
            .flatMap(getRouteLabelCandidateCoordinates)
            .filter((candidate) => Array.isArray(candidate.coordinate))
            .sort((a, b) => b.length - a.length);
        if (!candidates.length) return;

        const labelWidth = compactLabels
            ? Math.max(34, label.length * 5.8 + 12)
            : Math.max(44, label.length * 7 + 18);
        const labelHeight = compactLabels ? 18 : 24;
        const offsets = compactLabels ? [0, 12, -12, 24, -24, 38, -38] : [0, 16, -16, 30, -30, 46, -46];

        for (const candidate of candidates) {
            const pixel = map.getPixelFromCoordinate(candidate.coordinate);
            const beforePixel = map.getPixelFromCoordinate(candidate.before || candidate.coordinate);
            const afterPixel = map.getPixelFromCoordinate(candidate.after || candidate.coordinate);
            if (!pixel || !beforePixel || !afterPixel) continue;

            const dx = afterPixel[0] - beforePixel[0];
            const dy = afterPixel[1] - beforePixel[1];
            const length = Math.hypot(dx, dy) || 1;
            const normal = [-dy / length, dx / length];

            for (const offset of offsets) {
                const x = pixel[0] + normal[0] * offset;
                const y = pixel[1] + normal[1] * offset;
                const box = {
                    left: x - labelWidth / 2,
                    right: x + labelWidth / 2,
                    top: y - labelHeight / 2,
                    bottom: y + labelHeight / 2,
                };
                if (acceptedBoxes.some((accepted) => boxesIntersect(box, accepted))) continue;

                const feature = new Feature({
                    geometry: new Point(map.getCoordinateFromPixel([x, y])),
                    label,
                    compactLabel: compactLabels,
                    routeLabelId: group.id,
                });
                source.addFeature(feature);
                acceptedBoxes.push(box);
                return;
            }
        }
    });
}, [getRouteLabelCandidateCoordinates]);

const addRouteDistanceLabels = useCallback((features = []) => {
    const grouped = new globalThis.Map();
    (Array.isArray(features) ? features : []).forEach((feature) => {
        const label = String(feature?.get?.('routeDistanceLabel') || '').trim();
        if (!label) return;
        const id = String(feature.get('routeDistanceLabelId') || label);
        if (!grouped.has(id)) {
            grouped.set(id, { id, label, features: [] });
        }
        grouped.get(id).features.push(feature);
    });

    routeDistanceLabelGroupsRef.current = Array.from(grouped.values());
    renderRouteDistanceLabels();
}, [renderRouteDistanceLabels]);

const clearRouteDistanceLabels = useCallback(() => {
    routeDistanceLabelGroupsRef.current = [];
    routeDistanceLabelsSourceRef.current?.clear();
}, []);

useEffect(() => {
    const map = mapInstanceRef.current;
    if (!map || !mapReady) return undefined;
    const view = map.getView?.();
    const refresh = () => renderRouteDistanceLabels();
    const moveKey = map.on('moveend', refresh);
    view?.on?.('change:resolution', refresh);
    view?.on?.('change:rotation', refresh);
    return () => {
        unByKey(moveKey);
        view?.un?.('change:resolution', refresh);
        view?.un?.('change:rotation', refresh);
    };
}, [mapReady, renderRouteDistanceLabels]);

const removeFeature = useCallback((feature, sourceType = 'markers') => {
  const src = sourcesRef.current?.[sourceType];
  if (src) src.removeFeature(feature);
  if (sourceType === 'markers' && feature && feature === mapViewCoordMarkerRef.current) {
    mapViewCoordMarkerRef.current = null;
    setHasMapViewCoordMarker(false);
  }
}, []);


const fitMapToBoundaries = useCallback(
    (boundaries, { padding = [50, 50, 50, 50], duration = 800 } = {}) => {
    const map = mapInstanceRef.current;
    if (!map || !boundaries || !Array.isArray(boundaries.coordinates)) return;

    try {
        const poly = new Polygon(boundaries.coordinates);
        const view = map.getView();
        const viewProj = view.getProjection();
        poly.transform('EPSG:4326', viewProj);

        const doFit = () =>
        view.fit(poly, {
            size: map.getSize(),
            padding,
            duration,
        });

        const size = map.getSize();
        if (!size || size[0] === 0 || size[1] === 0) {
        map.once('rendercomplete', doFit);
        } else {
        doFit();
        }
    } catch (err) {
        console.error('Error fitting map to boundaries:', err);
    }
    },[]
);

const isEventInsideMapViewport = useCallback((target) => {
  if (!(target instanceof Node)) return false;
  return mapRef.current?.contains(target) || false;
}, []);

const shouldProxyWheelToMap = useCallback((target, event) => {
  if (!(target instanceof Element)) return false;
  if (isEventInsideMapViewport(target)) return false;

  if (event.ctrlKey || event.metaKey) {
    return true;
  }

  const root = interactionRootRef.current;
  if (isInsideScrollableOverlay(target, root)) return false;

  return true;
}, [isEventInsideMapViewport]);

const proxyOverlayZoomToMap = useCallback((zoomDelta, clientPoint = null) => {
  if (!Number.isFinite(zoomDelta) || zoomDelta === 0) return;

  const map = mapInstanceRef.current;
  const view = map?.getView?.();
  const mapElement = map?.getTargetElement?.();
  if (!map || !view || !mapElement) return;

  const rect = mapElement.getBoundingClientRect();
  const hasClientPoint =
    clientPoint &&
    Number.isFinite(clientPoint.x) &&
    Number.isFinite(clientPoint.y);

  const pixel = hasClientPoint &&
    clientPoint.x >= rect.left &&
    clientPoint.x <= rect.right &&
    clientPoint.y >= rect.top &&
    clientPoint.y <= rect.bottom
      ? [clientPoint.x - rect.left, clientPoint.y - rect.top]
      : [rect.width / 2, rect.height / 2];

  const anchor = map.getCoordinateFromPixel(pixel);
  view.adjustZoom(zoomDelta, anchor);
}, []);

useEffect(() => {
  const root = interactionRootRef.current;
  if (!root) return undefined;

  const resetOverlayZoomGesture = () => {
    overlayZoomGestureStateRef.current.touchDistance = null;
    overlayZoomGestureStateRef.current.gestureScale = null;
  };

  const handleWheel = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (!shouldProxyWheelToMap(target, event)) return;

    const normalizedDelta = clamp(-event.deltaY / 240, -1, 1);
    if (!normalizedDelta) return;

    event.preventDefault();
    proxyOverlayZoomToMap(normalizedDelta, { x: event.clientX, y: event.clientY });
  };

const handleTouchStart = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || isEventInsideMapViewport(target)) return;

    if (event.touches.length !== 2) {
      overlayZoomGestureStateRef.current.touchDistance = null;
      return;
    }

    const distance = getTouchDistance(event.touches);
    if (!distance) return;

    event.preventDefault();
    if (isInsideScrollableOverlay(target, root)) {
      overlayZoomGestureStateRef.current.touchDistance = null;
      return;
    }
    overlayZoomGestureStateRef.current.touchDistance = distance;
  };

  const handleTouchMove = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || isEventInsideMapViewport(target)) return;
    if (event.touches.length !== 2) return;

    event.preventDefault();
    if (isInsideScrollableOverlay(target, root)) return;

    const previousDistance = overlayZoomGestureStateRef.current.touchDistance;
    const nextDistance = getTouchDistance(event.touches);
    if (!previousDistance || !nextDistance) return;

    const zoomDelta = Math.log2(nextDistance / previousDistance);
    if (!Number.isFinite(zoomDelta) || Math.abs(zoomDelta) < 0.001) return;

    overlayZoomGestureStateRef.current.touchDistance = nextDistance;
    proxyOverlayZoomToMap(zoomDelta, getTouchMidpoint(event.touches));
  };

  const handleTouchEnd = (event) => {
    if (event.touches.length < 2) {
      overlayZoomGestureStateRef.current.touchDistance = null;
    }
  };

  const handleGestureStart = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    event.preventDefault();
    if (isEventInsideMapViewport(target) || isInsideScrollableOverlay(target, root)) {
      overlayZoomGestureStateRef.current.gestureScale = null;
      return;
    }
    overlayZoomGestureStateRef.current.gestureScale = Number(event.scale) || 1;
  };

  const handleGestureChange = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    event.preventDefault();
    if (isEventInsideMapViewport(target) || isInsideScrollableOverlay(target, root)) return;

    const previousScale = overlayZoomGestureStateRef.current.gestureScale || 1;
    const nextScale = Number(event.scale) || previousScale;
    const zoomDelta = Math.log2(nextScale / previousScale);
    if (!Number.isFinite(zoomDelta) || Math.abs(zoomDelta) < 0.001) return;

    overlayZoomGestureStateRef.current.gestureScale = nextScale;
    proxyOverlayZoomToMap(zoomDelta, { x: event.clientX, y: event.clientY });
  };

  root.addEventListener('wheel', handleWheel, { capture: true, passive: false });
  root.addEventListener('touchstart', handleTouchStart, { capture: true, passive: false });
  root.addEventListener('touchmove', handleTouchMove, { capture: true, passive: false });
  root.addEventListener('touchend', handleTouchEnd, { capture: true });
  root.addEventListener('touchcancel', resetOverlayZoomGesture, { capture: true });
  root.addEventListener('gesturestart', handleGestureStart, { capture: true, passive: false });
  root.addEventListener('gesturechange', handleGestureChange, { capture: true, passive: false });
  root.addEventListener('gestureend', resetOverlayZoomGesture, { capture: true });

  return () => {
    root.removeEventListener('wheel', handleWheel, { capture: true });
    root.removeEventListener('touchstart', handleTouchStart, { capture: true });
    root.removeEventListener('touchmove', handleTouchMove, { capture: true });
    root.removeEventListener('touchend', handleTouchEnd, { capture: true });
    root.removeEventListener('touchcancel', resetOverlayZoomGesture, { capture: true });
    root.removeEventListener('gesturestart', handleGestureStart, { capture: true });
    root.removeEventListener('gesturechange', handleGestureChange, { capture: true });
    root.removeEventListener('gestureend', resetOverlayZoomGesture, { capture: true });
  };
}, [isEventInsideMapViewport, proxyOverlayZoomToMap, shouldProxyWheelToMap]);

useEffect(() => {
  const isExternalOverlayTarget = (target) => {
    if (!(target instanceof Element)) return false;
    const root = interactionRootRef.current;
    if (root?.contains(target)) return false;
    return Boolean(target.closest('[data-scroll-overlay="true"], .ui.modal, .modals'));
  };

  const handlePortalWheel = (event) => {
    if (!event.ctrlKey && !event.metaKey) return;
    if (!isExternalOverlayTarget(event.target)) return;
    event.preventDefault();
  };

  const handlePortalTouch = (event) => {
    if (event.touches?.length !== 2) return;
    if (!isExternalOverlayTarget(event.target)) return;
    event.preventDefault();
  };

  const handlePortalGesture = (event) => {
    if (!isExternalOverlayTarget(event.target)) return;
    event.preventDefault();
  };

  document.addEventListener('wheel', handlePortalWheel, { capture: true, passive: false });
  document.addEventListener('touchstart', handlePortalTouch, { capture: true, passive: false });
  document.addEventListener('touchmove', handlePortalTouch, { capture: true, passive: false });
  document.addEventListener('gesturestart', handlePortalGesture, { capture: true, passive: false });
  document.addEventListener('gesturechange', handlePortalGesture, { capture: true, passive: false });

  return () => {
    document.removeEventListener('wheel', handlePortalWheel, { capture: true });
    document.removeEventListener('touchstart', handlePortalTouch, { capture: true });
    document.removeEventListener('touchmove', handlePortalTouch, { capture: true });
    document.removeEventListener('gesturestart', handlePortalGesture, { capture: true });
    document.removeEventListener('gesturechange', handlePortalGesture, { capture: true });
  };
}, []);

useEffect(() => {
  const map = mapInstanceRef.current;
  if (!map || !mapReady || !showScaleCompassOverlay) return undefined;

  const view = map.getView();
  const updateOverlay = () => {
    const rotation = view.getRotation?.() || 0;
    setMapRotationDegrees(rotation * (180 / Math.PI));

    const resolution = view.getResolution?.();
    const center = view.getCenter?.();
    if (!resolution || !center) return;

    const pointResolution = getPointResolution(
      view.getProjection(),
      resolution,
      center,
      'm'
    );
    if (!pointResolution) return;

    const meters = getScaleStepMeters(pointResolution * 150);
    setMapScaleOverlay({
      width: meters / pointResolution,
      text: formatScaleMeters(meters),
    });
  };

  view.on('change:rotation', updateOverlay);
  view.on('change:resolution', updateOverlay);
  view.on('change:center', updateOverlay);
  updateOverlay();

  return () => {
    view.un('change:rotation', updateOverlay);
    view.un('change:resolution', updateOverlay);
    view.un('change:center', updateOverlay);
  };
}, [mapReady, showScaleCompassOverlay]);

useEffect(() => {
  const layer = userDeviceLocationsLayerRef.current;
  const source = userDeviceLocationsSourceRef.current;
  if (!layer || !source) return undefined;

  layer.setVisible(Boolean(showUserDeviceLocations && canShowUserDeviceLocations));
  if (!showUserDeviceLocations || !canShowUserDeviceLocations) {
    source.clear();
    return undefined;
  }

  let cancelled = false;
  const loadDeviceLocations = async () => {
    setIsLoadingUserDeviceLocations(true);
    if (typeof navigator !== 'undefined' && navigator.onLine === false) {
      if (!cancelled) {
        setUserDeviceLocationsLoadError('Device locations unavailable offline');
        setIsLoadingUserDeviceLocations(false);
      }
      return;
    }
    try {
      const keys = await fetchTrackingKeys(getApiHeaders);
      if (cancelled) return;

      source.clear();
      const map = mapInstanceRef.current;
      const projection = map?.getView?.()?.getProjection?.();
      keys
        .filter((key) => key?.is_active && key?.latest_position?.longitude != null && key?.latest_position?.latitude != null)
        .forEach((key) => {
          const lon = Number(key.latest_position.longitude);
          const lat = Number(key.latest_position.latitude);
          if (!Number.isFinite(lon) || !Number.isFinite(lat)) return;

          const feature = new Feature({
            geometry: new Point(fromLonLat([lon, lat], projection)),
            markerLabel: 'ship',
            markerLabelType: 'currentShipLocation',
            name: key.name || 'Tracking device',
            trackingKeyData: key,
          });
          feature.setId(`user-device-${key.id}`);
          source.addFeature(feature);
        });
      setUserDeviceLocationsLastUpdatedAt(new Date());
      setUserDeviceLocationsLoadError('');
    } catch (error) {
      if (!cancelled) {
        const isNetworkError = error?.code === 'ERR_NETWORK' || !error?.response;
        setUserDeviceLocationsLoadError(
          isNetworkError
            ? 'Device locations could not refresh'
            : 'Device locations failed to load'
        );
        if (!isNetworkError) {
          console.error('Error loading user device locations:', error);
          source.clear();
        } else {
          console.warn('Device locations skipped due to network availability:', error?.message || error);
        }
      }
    } finally {
      if (!cancelled) setIsLoadingUserDeviceLocations(false);
    }
  };

  loadDeviceLocations();
  const intervalId = window.setInterval(loadDeviceLocations, 60000);
  return () => {
    cancelled = true;
    window.clearInterval(intervalId);
  };
}, [canShowUserDeviceLocations, getApiHeaders, showUserDeviceLocations]);

const mapMethods = useMemo(() => {
    const view = {
        getViewState: () => {
        const m = mapInstanceRef.current; if (!m) return null;
        const c = m.getView().getCenter(); const z = m.getView().getZoom();
        const [lon, lat] = c ? toLonLat(c) : [0,0];
        return { center: [lon, lat], zoom: z ?? 0 };
        },
        setViewState: ({ center, zoom }) => {
        const m = mapInstanceRef.current; if (!m) return;
        if (center) m.getView().setCenter(fromLonLat(center));
        if (zoom != null) m.getView().setZoom(zoom);
        },
        getBounds4326: () => {
        const m = mapInstanceRef.current; if (!m) return null;
        const size = m.getSize(); if (!size) return null;
        const viewProj = m.getView().getProjection();
        const ext = m.getView().calculateExtent(size);
        const [w,s,e,n] = transformExtent(ext, viewProj, 'EPSG:4326');
        return { w,s,e,n };
        },
    };
    
    // Selection rectangle
   // helpers/refs required above this block:
// const editRectSourceRef = useRef(new VectorSource());
// const shadeSourceRef    = useRef(new VectorSource());
// const editRectFeatureRef = useRef(null);
// const shadeFeatureRef    = useRef(null);
// const modifyRef          = useRef(null);
// const translateRef       = useRef(null);
// const geomChangeKeyRef   = useRef(null); // NEW: store OL event key
// const subscribersRef = useRef({ selectionChange: new Set() });

// Custom ResizeInteraction class that preserves aspect ratio
class ResizeInteraction extends Modify {
  constructor(options) {
    super(options);
    this.preserveAspectRatio = options.preserveAspectRatio || false;
    this.originalAspectRatio = null;
    this.isResizing = false;
  }

  handleModifyStart_(event) {
    super.handleModifyStart_(event);
    if (this.preserveAspectRatio && event.features.getArray().length > 0) {
      const feature = event.features.getArray()[0];
      const geometry = feature.getGeometry();
      if (geometry instanceof Polygon) {
        const extent = geometry.getExtent();
        const width = extent[2] - extent[0];
        const height = extent[3] - extent[1];
        this.originalAspectRatio = width / height;
        this.isResizing = true;
      }
    }
  }

  handleModifyEnd_(event) {
    super.handleModifyEnd_(event);
    this.isResizing = false;
    this.originalAspectRatio = null;
  }

  handleModify_(event) {
    if (this.preserveAspectRatio && this.isResizing && this.originalAspectRatio) {
      // Apply aspect ratio constraint during resize
      const feature = event.features.getArray()[0];
      const geometry = feature.getGeometry();
      if (geometry instanceof Polygon) {
        const extent = geometry.getExtent();
        const centerX = (extent[0] + extent[2]) / 2;
        const centerY = (extent[1] + extent[3]) / 2;
        
        // Calculate new dimensions maintaining aspect ratio
        let width = extent[2] - extent[0];
        let height = extent[3] - extent[1];
        
        // Determine which dimension changed more and adjust the other
        const currentAspectRatio = width / height;
        if (Math.abs(currentAspectRatio - this.originalAspectRatio) > 0.1) {
          if (width > height) {
            height = width / this.originalAspectRatio;
          } else {
            width = height * this.originalAspectRatio;
          }
          
          // Recreate polygon with new dimensions
          const halfWidth = width / 2;
          const halfHeight = height / 2;
          const newCoords = [
            [centerX - halfWidth, centerY - halfHeight],
            [centerX + halfWidth, centerY - halfHeight],
            [centerX + halfWidth, centerY + halfHeight],
            [centerX - halfWidth, centerY + halfHeight],
            [centerX - halfWidth, centerY - halfHeight]
          ];
          
          geometry.setCoordinates([newCoords]);
        }
      }
    }
    
    super.handleModify_(event);
  }
}

// worldHole(wsen:{w,s,e,n}) must exist and return {outer:[[lon,lat]...], inner:[[lon,lat]...]}
// layers/styles for editRectSourceRef & shadeSourceRef should already be added to the map.

const selectionRect = {
    isReady: () => {
      const m = mapInstanceRef.current;
      if (!m || !editRectSourceRef.current || !editRectLayerRef.current) return false;
      return m.getLayers().getArray().includes(editRectLayerRef.current);
    },
  
    ensure: (wsen) => {
        const m = mapInstanceRef.current; if (!m) return;
        const proj = m.getView().getProjection();
      
        if (geomChangeKeyRef.current) {
          unByKey(geomChangeKeyRef.current);
          geomChangeKeyRef.current = null;
        }
      
        let f;
        if (!wsen) {
          // create default rect in pixel space (with target ratio)
          const sz = m.getSize(); if (!sz) return;
          let w = Math.max(220, Math.floor(sz[0]*0.3));
          let h = Math.max(160, Math.floor(sz[1]*0.25));
          const targetRatio = selectionAspectRatioRef.current || TARGET_RATIO;
          const wantH = Math.round(w / targetRatio);
          h = wantH; // force our aspect ratio
      
          const x = Math.floor(sz[0]*0.2), y = Math.floor(sz[1]*0.2);
          const tl = m.getCoordinateFromPixel([x, y]);
          const tr = m.getCoordinateFromPixel([x + w, y]);
          const br = m.getCoordinateFromPixel([x + w, y + h]);
          const bl = m.getCoordinateFromPixel([x, y + h]);
      
          f = new Feature(new Polygon([[tl, tr, br, bl, tl]]));
        } else {
          // build from WSEN in EPSG:4326 → map proj
          const ring = [
            [wsen.w, wsen.s],
            [wsen.w, wsen.n],
            [wsen.e, wsen.n],
            [wsen.e, wsen.s],
            [wsen.w, wsen.s],
          ].map((c) => fromLonLat(c, proj));
          f = new Feature(new Polygon([ring]));
        }
      
        editRectSourceRef.current.clear();
        editRectSourceRef.current.addFeature(f);
        editRectFeatureRef.current = f;

        f.setId('edit-rect');

      
        // ensure exact ratio in map units as well
        enforceRatioAroundCenter(f.getGeometry(), selectionAspectRatioRef.current || TARGET_RATIO);
        
        selectionRect._emitChange();
      },
  
    setWSEN: (w, s, e, n) => selectionRect.ensure({ w, s, e, n }),

    setVisible: (visible = true) => {
      const nextVisible = Boolean(visible);
      editRectLayerRef.current?.setVisible?.(nextVisible);
      shadeLayerRef.current?.setVisible?.(nextVisible);
      if (!nextVisible) {
        selectionRect.disableEdit();
      }
    },

    setAspectRatio: (ratio, options = {}) => {
      const nextRatio = Number(ratio);
      if (!Number.isFinite(nextRatio) || nextRatio <= 0) return;
      selectionAspectRatioRef.current = nextRatio;
      if (options.adjustExisting === false || !editRectFeatureRef.current) return;
      enforceRatioAroundCenter(editRectFeatureRef.current.getGeometry(), selectionAspectRatioRef.current || TARGET_RATIO);
      selectionRect._emitChange();
      if (!resizeRef.current) {
        selectionRect.showOverlayOutside(true);
      }
    },
  
    getWSEN: () => {
      const m = mapInstanceRef.current; if (!m || !editRectFeatureRef.current) return null;
      const ext = editRectFeatureRef.current.getGeometry().getExtent();
      const [w, s, e, n] = transformExtent(ext, m.getView().getProjection(), 'EPSG:4326');
      return { w, s, e, n };
    },
  
    enableEdit: () => {
      const m = mapInstanceRef.current; 
      if (!m || !editRectFeatureRef.current) return;
  
      // remove old interactions
      if (modifyRef.current)    { m.removeInteraction(modifyRef.current);    modifyRef.current = null; }
      if (translateRef.current) { m.removeInteraction(translateRef.current); translateRef.current = null; }
      if (resizeRef.current)    { m.removeInteraction(resizeRef.current);    resizeRef.current = null; }
  
        // Use ol-ext Transform; let it manage handles
    resizeRef.current = new Transform({
        translateFeature: true,
        translate: true,
        scale: true,
        stretch: false,           // scaling from corners/edges; no “non-uniform” stretch
        rotate: false,
        keepAspectRatio: () => true,    // keep aspect ratio while scaling
        layers: (layer) => layer === editRectLayerRef.current,
        filter: (f, layer) => f === editRectFeatureRef.current, // only our rect Feature
        hitTolerance: 6,
    });
  
      // keep WSEN in sync & clamp to exact ratio after moves
    const onChange = () => {
        enforceRatioAroundCenter(editRectFeatureRef.current.getGeometry(), selectionAspectRatioRef.current || TARGET_RATIO);
        selectionRect._emitChange();
    };
    resizeRef.current.on(['scaling', 'translating', 'scaleend', 'translateend'], onChange);
    m.addInteraction(resizeRef.current);

    // If you had a geometry 'change' listener before, unbind it:
    if (geomChangeKeyRef.current) { unByKey(geomChangeKeyRef.current); geomChangeKeyRef.current = null; }
    
    // hide overlay while editing
    shadeSourceRef.current.clear();

    console.log('Transform added. Feature present?', !!editRectFeatureRef.current);

    },
  
    disableEdit: () => {
      const m = mapInstanceRef.current; if (!m) return;
      if (resizeRef.current)    { m.removeInteraction(resizeRef.current);    resizeRef.current = null; }
      if (modifyRef.current)    { m.removeInteraction(modifyRef.current);    modifyRef.current = null; }
      if (translateRef.current) { m.removeInteraction(translateRef.current); translateRef.current = null; }
    },
  
    showOverlayOutside: (show) => {
      shadeSourceRef.current.clear();
      if (!show) return;
      shadeLayerRef.current?.setVisible?.(true);
  
      const m = mapInstanceRef.current; if (!m) return;
      const wsen = selectionRect.getWSEN(); if (!wsen) return;
  
      const proj = m.getView().getProjection();
      const { outer, inner } = worldHole(wsen);
      const outerP = outer.map((c) => fromLonLat(c, proj));
      const innerP = inner.map((c) => fromLonLat(c, proj));
      const feat = new Feature(new Polygon([outerP, innerP]));
      shadeSourceRef.current.addFeature(feat);
      shadeFeatureRef.current = feat;
    },
  
    clear: () => {
      selectionRect.disableEdit();
      editRectSourceRef.current.clear();
      shadeSourceRef.current.clear();
      editRectFeatureRef.current = null;
      shadeFeatureRef.current = null;
    },
  
    onChange: (cb) => {
      subscribersRef.current.selectionChange.add(cb);
      return () => subscribersRef.current.selectionChange.delete(cb);
    },
    offChange: (cb) => subscribersRef.current.selectionChange.delete(cb),
    _emitChange: () => {
      const val = selectionRect.getWSEN();
      subscribersRef.current.selectionChange.forEach((cb) => cb(val));
    },
  
    createOrShowFromWSEN: (wsen, showOverlay = true) => {
      const m = mapInstanceRef.current;
      if (!m) return;
  
      if (!selectionRect.isReady()) {
        // defer until the first frame renders
        m.once('rendercomplete', () => {
          selectionRect.ensure(wsen);
          if (showOverlay) selectionRect.showOverlayFromWSEN(wsen);
        });
        return;
      }
      selectionRect.ensure(wsen);
      if (showOverlay) selectionRect.showOverlayFromWSEN(wsen);
    },
  
    showOverlayFromWSEN: (wsen) => {
      if (!isFiniteWSEN(wsen)) return;
      editRectLayerRef.current?.setVisible?.(true);
      shadeLayerRef.current?.setVisible?.(true);
      const m = mapInstanceRef.current; if (!m) return;
      const proj = m.getView().getProjection();
      const { outer, inner } = worldHole(wsen);
      const outerP = outer.map((c) => fromLonLat(c, proj));
      const innerP = inner.map((c) => fromLonLat(c, proj));
      const feat = new Feature(new Polygon([outerP, innerP]));
      shadeSourceRef.current.clear();
      shadeSourceRef.current.addFeature(feat);
      shadeFeatureRef.current = feat;
    },
  
    enterSelectionMode: () => {
      selectionRect.setVisible(true);
      if (!editRectFeatureRef.current) selectionRect.ensure(); // default rect
      selectionRect.enableEdit();         // state 1: editable
    },
  
    exitSelectionMode: () => {
      selectionRect.disableEdit();
      selectionRect.showOverlayOutside(true); // state 2: outside shaded
    },
  };
  
  
    
    // Temp point API
    const tempPoint = {
        createAtCenter: () => {
        const m = mapInstanceRef.current; if (!m) return;
        const c = m.getView().getCenter(); if (!c) return;
        tempPointSourceRef.current.clear();
        const f = new Feature(new Point(c));
        tempPointSourceRef.current.addFeature(f);
        tempPointFeatureRef.current = f;
        },
        createAtLonLat: ([lon, lat]) => {
        const m = mapInstanceRef.current; if (!m) return;
        tempPointSourceRef.current.clear();
        const f = new Feature(new Point(fromLonLat([lon, lat], m.getView().getProjection())));
        tempPointSourceRef.current.addFeature(f);
        tempPointFeatureRef.current = f;
        },
        enableDrag: (onDragEnd) => {
        const m = mapInstanceRef.current; if (!m || !tempPointFeatureRef.current) return;
        if (translatePointRef.current) m.removeInteraction(translatePointRef.current);
        translatePointRef.current = new Translate({ features: new Collection([tempPointFeatureRef.current]) });

        // Add drag end callback if provided
        if (onDragEnd) {
            translatePointRef.current.on('translateend', () => {
                const lonlat = tempPoint.getLonLat();
                if (lonlat) {
                    onDragEnd(lonlat);
                }
            });
        }

        m.addInteraction(translatePointRef.current);
        },
        getLonLat: () => {
        if (!tempPointFeatureRef.current) return null;
        const m = mapInstanceRef.current; if (!m) return null;
        const c = tempPointFeatureRef.current.getGeometry().getCoordinates();
        const [lon, lat] = toLonLat(c, m.getView().getProjection());
        return [lon, lat];
        },
        getMarker: () => {
        return tempPointFeatureRef.current;
        },
        clear: () => {
        const m = mapInstanceRef.current; if (m && translatePointRef.current) m.removeInteraction(translatePointRef.current);
        translatePointRef.current = null;
        tempPointSourceRef.current.clear();
        tempPointFeatureRef.current = null;
        },
        createAt: (lonlat) => tempPoint.createAtLonLat(lonlat),
        remove: () => tempPoint.clear(),
    };

    // A/B Markers API - draggable route markers
    const abMarkers = {
        createA: (lonlat) => {
            const m = mapInstanceRef.current; if (!m) return;
            // Remove existing A marker
            if (abMarkerAFeatureRef.current) {
                abMarkersSourceRef.current.removeFeature(abMarkerAFeatureRef.current);
            }
            // Remove drag interaction
            if (translateARef.current) {
                m.removeInteraction(translateARef.current);
                translateARef.current = null;
            }

            // Create new marker
            let coords;
            if (lonlat) {
                coords = fromLonLat(lonlat, m.getView().getProjection());
            } else {
                coords = m.getView().getCenter();
            }

            const f = new Feature({
                geometry: new Point(coords),
                label: 'A'
            });
            abMarkersSourceRef.current.addFeature(f);
            abMarkerAFeatureRef.current = f;
            return f;
        },

        createB: (lonlat) => {
            const m = mapInstanceRef.current; if (!m) return;
            // Remove existing B marker
            if (abMarkerBFeatureRef.current) {
                abMarkersSourceRef.current.removeFeature(abMarkerBFeatureRef.current);
            }
            // Remove drag interaction
            if (translateBRef.current) {
                m.removeInteraction(translateBRef.current);
                translateBRef.current = null;
            }

            // Create new marker
            let coords;
            if (lonlat) {
                coords = fromLonLat(lonlat, m.getView().getProjection());
            } else {
                coords = m.getView().getCenter();
            }

            const f = new Feature({
                geometry: new Point(coords),
                label: 'B'
            });
            abMarkersSourceRef.current.addFeature(f);
            abMarkerBFeatureRef.current = f;
            return f;
        },

        enableDragA: (onDragEnd) => {
            const m = mapInstanceRef.current;
            if (!m || !abMarkerAFeatureRef.current) return;

            // Remove existing interaction
            if (translateARef.current) m.removeInteraction(translateARef.current);

            // Create new translate interaction
            translateARef.current = new Translate({
                features: new Collection([abMarkerAFeatureRef.current])
            });

            // Add drag end callback if provided
            if (onDragEnd) {
                translateARef.current.on('translateend', () => {
                    const lonlat = abMarkers.getLonLatA();
                    onDragEnd(lonlat);
                });
            }

            m.addInteraction(translateARef.current);
        },

        enableDragB: (onDragEnd) => {
            const m = mapInstanceRef.current;
            if (!m || !abMarkerBFeatureRef.current) return;

            // Remove existing interaction
            if (translateBRef.current) m.removeInteraction(translateBRef.current);

            // Create new translate interaction
            translateBRef.current = new Translate({
                features: new Collection([abMarkerBFeatureRef.current])
            });

            // Add drag end callback if provided
            if (onDragEnd) {
                translateBRef.current.on('translateend', () => {
                    const lonlat = abMarkers.getLonLatB();
                    onDragEnd(lonlat);
                });
            }

            m.addInteraction(translateBRef.current);
        },

        getLonLatA: () => {
            if (!abMarkerAFeatureRef.current) return null;
            const m = mapInstanceRef.current; if (!m) return null;
            const c = abMarkerAFeatureRef.current.getGeometry().getCoordinates();
            const [lon, lat] = toLonLat(c, m.getView().getProjection());
            return [lon, lat];
        },

        getLonLatB: () => {
            if (!abMarkerBFeatureRef.current) return null;
            const m = mapInstanceRef.current; if (!m) return null;
            const c = abMarkerBFeatureRef.current.getGeometry().getCoordinates();
            const [lon, lat] = toLonLat(c, m.getView().getProjection());
            return [lon, lat];
        },

        removeA: () => {
            const m = mapInstanceRef.current;
            if (m && translateARef.current) {
                m.removeInteraction(translateARef.current);
                translateARef.current = null;
            }
            if (abMarkerAFeatureRef.current) {
                abMarkersSourceRef.current.removeFeature(abMarkerAFeatureRef.current);
                abMarkerAFeatureRef.current = null;
            }
        },

        removeB: () => {
            const m = mapInstanceRef.current;
            if (m && translateBRef.current) {
                m.removeInteraction(translateBRef.current);
                translateBRef.current = null;
            }
            if (abMarkerBFeatureRef.current) {
                abMarkersSourceRef.current.removeFeature(abMarkerBFeatureRef.current);
                abMarkerBFeatureRef.current = null;
            }
        },

        clear: () => {
            abMarkers.removeA();
            abMarkers.removeB();
        },

        hasA: () => !!abMarkerAFeatureRef.current,
        hasB: () => !!abMarkerBFeatureRef.current,
    };

    // Buildings layer
    const buildings = {
        setGeoJSON: (geojson) => {
        const m = mapInstanceRef.current; if (!m) return 0;
        const proj = m.getView().getProjection();
        const feats = new GeoJSON().readFeatures(geojson, {
            dataProjection: 'EPSG:4326',
            featureProjection: proj,
        });
        // normalize id
        feats.forEach((f) => {
            const p = f.get('properties') || {};
            if (!f.getId()) f.setId(p.osm_id || p.id || Math.random().toString(36).slice(2));
        });
        buildingsSourceRef.current.clear();
        buildingsSourceRef.current.addFeatures(feats);
        return feats.length;
        },
        clear: () => buildingsSourceRef.current.clear(),
        onClick: (cb) => subscribersRef.current.buildingClick.add(cb),
        offClick: (cb) => subscribersRef.current.buildingClick.delete(cb),
        selectById: (id) => {
        buildingsSourceRef.current.getFeatures().forEach(f => {
            f.set('__selected', f.getId() === id);
            f.changed();
        });
        },
        clearSelection: () => {
        buildingsSourceRef.current.getFeatures().forEach(f => { if (f.get('__selected')) { f.set('__selected', false); f.changed(); } });
        },
    };
    
    const measure = {
        haversineMeters,
    };

    const mapLayerControls = {
        getHillshadeVisible: () => showHillshade,
        getBathymetryVisible: () => showBathymetry,
        setHillshadeVisible: (visible) => setShowHillshade(Boolean(visible)),
        setBathymetryVisible: (visible) => setShowBathymetry(Boolean(visible)),
    };

    // Create or update mapMethodsRef with stable identity
    // The ref object itself never changes, only its properties get updated
    if (!mapMethodsRef.current) {
        mapMethodsRef.current = {
            // These will be updated below
            addMarker: null,
            addMappoints: null,
            setPoiNameLabelPreviews: null,
            clearPoiNameLabelPreviews: null,
            getMarkerDeclutterPlacements: null,
            getPoiNameLabelPlacements: null,
            addGeoJson: null,
            addRouteDistanceLabels: null,
            clearRouteDistanceLabels: null,
            clearLayer: null,
            removeFeature: null,
            fitMapToBoundaries: null,
            fitToMarkerSource: null,
            refreshTourMarkers: null,
            onMarkerClick: null,
            selectMarker: null,
            deselectMarker: null,
            registerSidebarCleanup: null,
            // Static methods that don't depend on changing callbacks
            featureIdFor: (numericId) => `mp-${numericId}`,
            getMap: () => mapInstanceRef.current,
            isMapReady: () => !!mapInstanceRef.current,
            // Internal API objects (stable, only use refs internally)
            view,
            selectionRect,
            tempPoint,
            abMarkers,
            buildings,
            measure,
            mapLayerControls,
        };
    }

    // Update function references that depend on changing callbacks
    mapMethodsRef.current.addMarker = addMarker;
    mapMethodsRef.current.addMappoints = addMappoints;
    mapMethodsRef.current.setPoiNameLabelPreviews = setPoiNameLabelPreviews;
    mapMethodsRef.current.clearPoiNameLabelPreviews = clearPoiNameLabelPreviews;
    mapMethodsRef.current.getMarkerDeclutterPlacements = getMarkerDeclutterPlacements;
    mapMethodsRef.current.getPoiNameLabelPlacements = getPoiNameLabelPlacements;
    mapMethodsRef.current.addGeoJson = addGeoJson;
    mapMethodsRef.current.addRouteDistanceLabels = addRouteDistanceLabels;
    mapMethodsRef.current.clearRouteDistanceLabels = clearRouteDistanceLabels;
    mapMethodsRef.current.clearLayer = clearLayer;
    mapMethodsRef.current.removeFeature = removeFeature;
    mapMethodsRef.current.fitMapToBoundaries = fitMapToBoundaries;
    mapMethodsRef.current.fitToMarkerSource = fitToMarkerSource;
    mapMethodsRef.current.refreshTourMarkers = refreshTourMarkers;
    mapMethodsRef.current.onMarkerClick = onMarkerClick;
    mapMethodsRef.current.selectMarker = (id, options) => setSelected(id, true, options);
    mapMethodsRef.current.deselectMarker = (id, options) => setSelected(id, false, options);
    mapMethodsRef.current.registerSidebarCleanup = registerSidebarCleanup;
    mapMethodsRef.current.mapLayerControls = mapLayerControls;

    return mapMethodsRef.current;
}, [addGeoJson, addMarker, addMappoints, addRouteDistanceLabels, clearLayer, clearPoiNameLabelPreviews, clearRouteDistanceLabels, fitMapToBoundaries, fitToMarkerSource, getMarkerDeclutterPlacements, getPoiNameLabelPlacements, onMarkerClick, refreshTourMarkers, registerSidebarCleanup, removeFeature, setPoiNameLabelPreviews, setSelected, showBathymetry, showHillshade]);

// --- map update when activeMapToShow changes ---
useEffect(() => {
    const map = mapInstanceRef.current;
    if (!activeMapToShow || !mapReady || !map) return;

    const { mappoints, boundaries } = activeMapToShow.properties || {};

    clearLayer('markers');
    if (Array.isArray(mappoints) && mappoints.length) {
    addMappoints(mappoints);
    }

    if (!boundaries) return;

    const doFit = () => fitMapToBoundaries(boundaries);
    const size = map.getSize();
    if (!size || size[0] === 0 || size[1] === 0) {
    map.once('rendercomplete', doFit);
    } else {
    doFit();
    }
}, [mapReady, activeMapToShow, clearLayer, addMappoints, fitMapToBoundaries]);

useEffect(() => {
    const map = mapInstanceRef.current;
    if (!mapReady || !map) return;

    const view = map.getView();
    const updateViewInfo = () => {
        const zoom = view.getZoom();
        const center = view.getCenter();
        if (!center) {
            setMapViewInfo({ zoom: zoom ?? null, center: null });
            return;
        }
        const [lon, lat] = toLonLat(center, view.getProjection());
        setMapViewInfo({
            zoom: typeof zoom === 'number' ? zoom : null,
            center: [lat, lon],
        });
    };

    updateViewInfo();
    const keys = [
        view.on('change:center', updateViewInfo),
        view.on('change:resolution', updateViewInfo),
    ];
    return () => {
        keys.forEach((key) => unByKey(key));
    };
}, [mapReady]);

const openMapViewEditor = useCallback(() => {
    setMapViewDraft({
        zoom: mapViewInfo.zoom !== null ? mapViewInfo.zoom.toFixed(2) : '',
        lat: mapViewInfo.center ? mapViewInfo.center[0].toFixed(5) : '',
        lon: mapViewInfo.center ? mapViewInfo.center[1].toFixed(5) : '',
    });
    setMapViewEditing(true);
}, [mapViewInfo]);

const applyMapViewDraft = useCallback(() => {
    const map = mapInstanceRef.current;
    if (!map) return;
    const view = map.getView();

    const nextZoom = Number.parseFloat(mapViewDraft.zoom);
    const nextLat = Number.parseFloat(mapViewDraft.lat);
    const nextLon = Number.parseFloat(mapViewDraft.lon);

    const hasZoom = Number.isFinite(nextZoom);
    const hasCenter = Number.isFinite(nextLat) && Number.isFinite(nextLon);

    if (hasCenter) {
        view.animate({
            center: fromLonLat([nextLon, nextLat]),
            duration: 300,
        });
    }
    if (hasZoom) {
        view.animate({
            zoom: nextZoom,
            duration: 300,
        });
    }
}, [mapViewDraft]);

const formatMapViewCoordValue = (value) => (
    Number.isFinite(value)
        ? value.toFixed(8).replace(/\.?0+$/, '')
        : ''
);

const parseMapViewCoordPart = (part) => {
    const text = String(part ?? '').trim();
    if (!text) return null;

    const hemisphereMatch = text.match(/[NSEW]/i);
    const hemisphere = hemisphereMatch ? hemisphereMatch[0].toUpperCase() : null;
    const axis = hemisphere ? (hemisphere === 'N' || hemisphere === 'S' ? 'lat' : 'lon') : null;

    const numberMatches = text.match(/[-+]?\d*\.?\d+(?:[eE][-+]?\d+)?/g);
    if (!numberMatches || numberMatches.length === 0) return null;

    const nums = numberMatches.map((value) => Number.parseFloat(value));
    if (nums.some((value) => !Number.isFinite(value))) return null;

    let decimalDegrees;
    if (nums.length === 1) {
        decimalDegrees = nums[0];
    } else if (nums.length === 2) {
        const [degRaw, min] = nums;
        const sign = degRaw < 0 ? -1 : 1;
        const deg = Math.abs(degRaw);
        decimalDegrees = sign * (deg + (Math.abs(min) / 60));
    } else {
        const [degRaw, min, sec] = nums;
        const sign = degRaw < 0 ? -1 : 1;
        const deg = Math.abs(degRaw);
        decimalDegrees = sign * (deg + (Math.abs(min) / 60) + (Math.abs(sec) / 3600));
    }

    if (hemisphere) {
        const hemiSign = hemisphere === 'S' || hemisphere === 'W' ? -1 : 1;
        decimalDegrees = hemiSign * Math.abs(decimalDegrees);
    }

    return { axis, value: decimalDegrees };
};

const parseMapViewCoordClipboard = (rawText) => {
    const raw = String(rawText ?? '').trim();
    const parts = raw
        .split(/[,\n;]+/)
        .map((part) => part.trim())
        .filter(Boolean);

    if (parts.length !== 2) {
        // Support plain space-separated decimal degrees pairs like: "48.83602 12.94361"
        const simplePairMatch = raw.match(/^\s*([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)\s+([-+]?\d*\.?\d+(?:[eE][-+]?\d+)?)\s*$/);
        if (!simplePairMatch) return null;
        return {
            first: formatMapViewCoordValue(Number.parseFloat(simplePairMatch[1])),
            second: formatMapViewCoordValue(Number.parseFloat(simplePairMatch[2])),
            explicitAxes: false,
        };
    }

    const parsed = parts.map(parseMapViewCoordPart);
    if (parsed.some((item) => !item)) return null;

    const [first, second] = parsed;
    const hasAxes = Boolean(first.axis || second.axis);

    if (hasAxes) {
        const latPart = parsed.find((item) => item.axis === 'lat');
        const lonPart = parsed.find((item) => item.axis === 'lon');
        if (!latPart || !lonPart) return null;

        return {
            lat: formatMapViewCoordValue(latPart.value),
            lon: formatMapViewCoordValue(lonPart.value),
            explicitAxes: true,
        };
    }

    return {
        first: formatMapViewCoordValue(first.value),
        second: formatMapViewCoordValue(second.value),
        explicitAxes: false,
    };
};

const handleMapViewCoordPaste = (targetField, e) => {
    const rawText = e.clipboardData?.getData('text') ?? '';
    if (!rawText) return;

    const parsed = parseMapViewCoordClipboard(rawText);
    if (!parsed) return;

    e.preventDefault();
    setMapViewDraft((prev) => {
        if (parsed.explicitAxes) {
            return { ...prev, lat: parsed.lat, lon: parsed.lon };
        }

        return targetField === 'lat'
            ? { ...prev, lat: parsed.first, lon: parsed.second }
            : { ...prev, lon: parsed.first, lat: parsed.second };
    });
};

const removeMapViewCoordMarker = useCallback(() => {
    if (!mapViewCoordMarkerRef.current) return;
    removeFeature(mapViewCoordMarkerRef.current, 'markers');
}, [removeFeature]);

const addMarkerAtMapViewCoords = useCallback(() => {
    const sourceLat = mapViewEditing && mapViewDraft.lat !== '' ? mapViewDraft.lat : (mapViewInfo.center ? String(mapViewInfo.center[0]) : '');
    const sourceLon = mapViewEditing && mapViewDraft.lon !== '' ? mapViewDraft.lon : (mapViewInfo.center ? String(mapViewInfo.center[1]) : '');

    const lat = Number.parseFloat(sourceLat);
    const lon = Number.parseFloat(sourceLon);
    if (!Number.isFinite(lat) || !Number.isFinite(lon)) return;

    if (mapViewCoordMarkerRef.current) {
        removeFeature(mapViewCoordMarkerRef.current, 'markers');
    }

    const marker = addMarker([lon, lat], {
        name: `Marker ${lat.toFixed(5)}, ${lon.toFixed(5)}`,
        isMapViewCoordMarker: true,
    });
    mapViewCoordMarkerRef.current = marker ?? null;
    setHasMapViewCoordMarker(Boolean(marker));
}, [addMarker, mapViewDraft.lat, mapViewDraft.lon, mapViewEditing, mapViewInfo.center, removeFeature]);

const formatHolidayDate = useCallback((value) => {
  if (!value) return 'Unknown date';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return 'Unknown date';
  return date.toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}, []);

const userDeviceLocationsLastUpdatedLabel = useMemo(() => {
  if (!userDeviceLocationsLastUpdatedAt) return 'Not updated yet';
  return userDeviceLocationsLastUpdatedAt.toLocaleTimeString(undefined, {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
  });
}, [userDeviceLocationsLastUpdatedAt]);

const userDeviceLocationsStatusLabel = useMemo(() => {
  if (userDeviceLocationsLoadError) {
    return userDeviceLocationsLastUpdatedAt
      ? `${userDeviceLocationsLoadError}; last updated ${userDeviceLocationsLastUpdatedLabel}`
      : userDeviceLocationsLoadError;
  }
  if (isLoadingUserDeviceLocations && !userDeviceLocationsLastUpdatedAt) {
    return 'Devices updating...';
  }
  return `Devices updated ${userDeviceLocationsLastUpdatedLabel}`;
}, [
  isLoadingUserDeviceLocations,
  userDeviceLocationsLastUpdatedAt,
  userDeviceLocationsLastUpdatedLabel,
  userDeviceLocationsLoadError,
]);

const renderLayerToggle = (on, color) => (
  <span style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
    <span
      style={{
        fontSize: theme.typography.fontSize.sm,
        color: on ? theme.colors.neutral[900] : theme.colors.neutral[600],
      }}
    >
      {on ? 'On' : 'Off'}
    </span>
    <span
      style={{
        position: 'relative',
        width: '34px',
        height: '18px',
        borderRadius: '999px',
        background: on ? color : 'rgba(0,0,0,0.2)',
        transition: 'background 160ms ease',
      }}
      aria-hidden="true"
    >
      <span
        style={{
          position: 'absolute',
          top: '2px',
          left: on ? '18px' : '2px',
          width: '14px',
          height: '14px',
          borderRadius: '50%',
          background: '#fff',
          boxShadow: '0 1px 2px rgba(0,0,0,0.25)',
          transition: 'left 160ms ease',
        }}
      />
    </span>
  </span>
);

const layersPanelStyle = {
  position: isMobile ? 'fixed' : 'absolute',
  right: isMobile ? theme.spacing[2] : 0,
  bottom: isMobile ? `calc(${mapControlsBottom} + ${mapControlSize} + ${mapControlGap})` : 'calc(100% + 8px)',
  top: isMobile ? '12px' : 'auto',
  minWidth: isMobile ? 'min(320px, calc(100vw - 16px))' : '220px',
  width: isMobile ? 'min(320px, calc(100vw - 16px))' : '280px',
  maxWidth: 'calc(100vw - 16px)',
  maxHeight: isMobile ? 'none' : 'min(560px, calc(100dvh - 140px))',
  overflowY: 'auto',
  overscrollBehavior: 'contain',
  WebkitOverflowScrolling: 'touch',
  touchAction: 'pan-y',
  zIndex: layersMenuZIndex,
  background: 'rgba(255,255,255,0.98)',
  border: '1px solid rgba(0,0,0,0.12)',
  borderRadius: '8px',
  boxShadow: theme.shadows.md,
  padding: theme.spacing[2],
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing[2],
  textAlign: 'left',
  fontSize: theme.typography.fontSize.base,
};

const layersGridStyle = {
  display: 'grid',
  gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '1fr',
  gap: theme.spacing[2],
  alignItems: 'stretch',
};

const layersFullWidthStyle = isMobile ? { gridColumn: '1 / -1' } : {};

const getLayerButtonStyle = (active, color, disabled = false) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: isMobile ? 'center' : 'space-between',
  gap: '8px',
  width: '100%',
  minHeight: isMobile ? '52px' : 'auto',
  padding: isMobile ? `${theme.spacing[2]}` : `${theme.spacing[2]} ${theme.spacing[3]}`,
  borderRadius: '8px',
  border: active ? `1px solid ${color}` : '1px solid rgba(0,0,0,0.08)',
  background: active ? color : '#fff',
  color: active ? '#fff' : theme.colors.neutral[800],
  cursor: disabled ? 'not-allowed' : 'pointer',
  fontSize: theme.typography.fontSize.base,
  fontWeight: active && isMobile ? 600 : 400,
  lineHeight: 1.2,
  textAlign: isMobile ? 'center' : 'left',
  opacity: disabled ? 0.6 : 1,
});

const renderMapLayoutPreviewChrome = () => {
  if (!mapLayoutPreviewChrome) return null;

  const {
    stageTopPx,
    pageTopPx,
    pageActualLeftPx,
    pageWidthPx,
    pageHeightPx,
    mapLeftPx,
    mapTopPx,
    mapWidthPx,
    mapHeightPx,
    sideListLeftPx,
    sideListTopPx,
    sideListWidthPx,
    sideListHeightPx,
    sidebarWidthPx,
  } = mapLayoutPreviewChrome;
  const pois = Array.isArray(mapLayoutPreview?.pois) ? mapLayoutPreview.pois : [];
  const layout = mapLayoutPreview?.layout || 'default';
  const isBottomList = layout === 'bottom_poi_list';
  const sideListTitle = mapLayoutPreview?.areaName || activeMapToEdit?.properties?.area_name || 'Map preview';
  const sideListArea = mapLayoutPreview?.countryName || activeMapToEdit?.properties?.country_name || '';
  const renderQr = Boolean(mapLayoutPreview?.visibilitySettings?.render_qr);
  const showAmenitiesLegend = mapLayoutPreview?.visibilitySettings?.show_amenities !== false;
  const exportPanelBg = '#d8bf7a';
  const exportPanelBgSoft = '#e4d09a';
  const exportNavy = '#0a2742';
  const exportMuted = '#36526b';
  const exportSeparator = 'rgba(10, 39, 66, 0.26)';
  const markerBlue = '#1465ad';
  const titleFontPx = isBottomList
    ? Math.max(13, Math.min(19, Math.round(sideListHeightPx * 0.14)))
    : Math.max(15, Math.min(26, Math.round(sideListWidthPx * 0.11)));
  const subtitleFontPx = Math.max(9, Math.round(titleFontPx * 0.5));
  const poiFontPx = isBottomList
    ? Math.max(9, Math.min(12, Math.round(sideListHeightPx * 0.085)))
    : Math.max(10, Math.min(14, Math.round(sideListWidthPx * 0.055)));

  const sideListStyle = isBottomList
    ? {
        position: 'absolute',
        left: `${sideListLeftPx}px`,
        top: `${sideListTopPx}px`,
        width: `${sideListWidthPx}px`,
        height: `${sideListHeightPx}px`,
        zIndex: 5,
        background: exportPanelBg,
        border: `1px solid ${exportSeparator}`,
        borderTop: '0',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
        display: 'grid',
        gridTemplateColumns: 'minmax(150px, 0.72fr) minmax(0, 2fr) auto',
        gap: '12px',
        padding: `${Math.max(10, Math.min(16, sideListHeightPx * 0.12))}px ${Math.max(12, Math.min(18, sideListWidthPx * 0.025))}px`,
        boxSizing: 'border-box',
        pointerEvents: 'auto',
      }
    : {
        position: 'absolute',
        left: `${sideListLeftPx}px`,
        top: `${sideListTopPx}px`,
        width: `${sideListWidthPx}px`,
        height: `${sideListHeightPx}px`,
        zIndex: 5,
        background: exportPanelBg,
        border: `1px solid ${exportSeparator}`,
        borderLeft: '0',
        boxShadow: '0 8px 24px rgba(15, 23, 42, 0.14)',
        display: 'flex',
        flexDirection: 'column',
        padding: `${Math.max(14, Math.min(24, sideListHeightPx * 0.04))}px ${Math.max(10, Math.min(20, sideListWidthPx * 0.075))}px ${Math.max(12, Math.min(18, sideListHeightPx * 0.03))}px`,
        boxSizing: 'border-box',
        pointerEvents: 'auto',
      };

  return (
    <>
      <div
        data-scroll-overlay="true"
        className="MapLayoutPreview-stage"
        style={{
          position: 'absolute',
          top: `${stageTopPx}px`,
          left: 0,
          right: `${sidebarWidthPx}px`,
          bottom: 0,
          zIndex: 2,
          background: '#ffffff',
          pointerEvents: 'auto',
        }}
      />
      <div
        data-scroll-overlay="true"
        aria-hidden="true"
        className="MapLayoutPreview-page"
        style={{
          position: 'absolute',
          top: `${pageTopPx}px`,
          left: `${pageActualLeftPx}px`,
          width: `${pageWidthPx}px`,
          height: `${pageHeightPx}px`,
          zIndex: 2,
          background: '#ffffff',
          boxShadow: '0 0 0 1px rgba(15, 23, 42, 0.08)',
          pointerEvents: 'none',
        }}
      />
      <div
        data-scroll-overlay="true"
        aria-hidden="true"
        className="MapLayoutPreview-export-map-treatment"
        style={{
          position: 'absolute',
          top: `${mapTopPx}px`,
          left: `${mapLeftPx}px`,
          width: `${mapWidthPx}px`,
          height: `${mapHeightPx}px`,
          zIndex: 4,
          pointerEvents: 'none',
          overflow: 'hidden',
          border: '1px solid rgba(10, 39, 66, 0.16)',
          boxSizing: 'border-box',
        }}
      >
        <div
          style={{
            position: 'absolute',
            inset: 0,
            background: [
              'radial-gradient(circle at 18% 24%, rgba(255,255,255,0.36) 0, rgba(255,255,255,0) 18%)',
              'radial-gradient(circle at 75% 64%, rgba(15, 76, 129, 0.16) 0, rgba(15, 76, 129, 0) 34%)',
              'linear-gradient(135deg, rgba(21, 74, 103, 0.18), rgba(255,255,255,0.02) 42%, rgba(6, 50, 86, 0.14))',
            ].join(', '),
            mixBlendMode: 'multiply',
            opacity: 0.72,
          }}
        />
      </div>
      <div
        data-scroll-overlay="true"
        className="MapLayoutPreview-side-list"
        style={sideListStyle}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: `${titleFontPx}px`, lineHeight: 1.02, fontWeight: 900, color: exportNavy, letterSpacing: '0.02em', textTransform: 'uppercase', overflowWrap: 'anywhere' }}>
            {sideListTitle}
          </div>
          {sideListArea ? (
            <div style={{ marginTop: '5px', fontSize: `${subtitleFontPx}px`, fontWeight: 800, color: exportMuted, letterSpacing: '0.04em', textTransform: 'uppercase', overflowWrap: 'anywhere' }}>
              {sideListArea}
            </div>
          ) : null}
          {!isBottomList ? (
            <div style={{ marginTop: '18px', fontSize: '11px', fontWeight: 900, color: exportNavy, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
              Sights & Highlights
            </div>
          ) : null}
        </div>

        <div
          style={{
            flex: 1,
            minHeight: 0,
            overflow: 'hidden',
            display: 'grid',
            gridTemplateColumns: isBottomList ? 'repeat(3, minmax(0, 1fr))' : '1fr',
            alignContent: 'start',
            gap: isBottomList ? '5px 12px' : 0,
            marginTop: isBottomList ? 0 : '10px',
          }}
        >
          {pois.length ? pois.slice(0, isBottomList ? 15 : 18).map((poi) => (
            <div
              key={poi.id}
              style={{
                display: 'grid',
                gridTemplateColumns: isBottomList ? '20px minmax(0, 1fr)' : '26px minmax(0, 1fr)',
                gap: isBottomList ? '6px' : '9px',
                alignItems: 'center',
                minWidth: 0,
                padding: isBottomList ? '0' : '9px 0',
                borderBottom: isBottomList ? 0 : `1px solid ${exportSeparator}`,
              }}
            >
              <span
                style={{
                  width: isBottomList ? '18px' : '23px',
                  height: isBottomList ? '18px' : '23px',
                  borderRadius: '999px',
                  background: markerBlue,
                  color: '#ffffff',
                  display: 'inline-flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: isBottomList ? '10px' : '12px',
                  fontWeight: 900,
                  lineHeight: 1,
                  boxShadow: '0 0 0 2px rgba(255,255,255,0.78)',
                }}
              >
                {poi.number}
              </span>
              <span style={{ fontSize: `${poiFontPx}px`, lineHeight: 1.18, fontWeight: 800, color: exportNavy, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {poi.name || `Point ${poi.number}`}
              </span>
            </div>
          )) : (
            <div style={{ fontSize: '12px', color: exportMuted }}>No POIs yet.</div>
          )}
        </div>

        <div
          style={{
            display: 'grid',
            gap: isBottomList ? '8px' : '12px',
            alignSelf: isBottomList ? 'stretch' : 'auto',
            minWidth: isBottomList ? '150px' : 0,
          }}
        >
          {showAmenitiesLegend ? (
            <div style={{ borderTop: `1px solid ${exportSeparator}`, paddingTop: isBottomList ? '8px' : '12px' }}>
              <div style={{ fontSize: '10px', fontWeight: 900, color: exportNavy, letterSpacing: '0.08em', textTransform: 'uppercase', marginBottom: '7px' }}>
                Amenities
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: isBottomList ? '8px' : '11px', color: exportNavy, flexWrap: 'wrap' }}>
                {[
                  ['credit card outline', 'ATM'],
                  ['bath', 'Toilet'],
                  ['medkit', 'Pharmacy'],
                  ['info circle', 'Info'],
                ].map(([iconName, label]) => (
                  <span key={label} title={label} style={{ display: 'inline-flex', alignItems: 'center', gap: '4px', fontSize: '10px', fontWeight: 800 }}>
                    <Icon name={iconName} style={{ margin: 0, fontSize: '12px' }} />
                    {!isBottomList ? label : null}
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          <div style={{ color: exportNavy }}>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: '7px' }}>
              <div>
                <div style={{ height: '10px', width: isBottomList ? '58px' : '76px', borderLeft: `2px solid ${exportNavy}`, borderRight: `2px solid ${exportNavy}`, borderBottom: `2px solid ${exportNavy}` }} />
                <div style={{ marginTop: '3px', fontSize: '9px', lineHeight: 1, fontWeight: 900, letterSpacing: '0.04em' }}>
                  SCALE
                </div>
              </div>
              <span style={{ fontSize: '10px', lineHeight: 1.1, fontWeight: 800, color: exportMuted }}>
                approx.
              </span>
            </div>
          </div>
        </div>

        {renderQr ? (
          <div style={{ marginTop: '12px', display: 'flex', justifyContent: isBottomList ? 'flex-start' : 'center' }}>
            <div style={{ width: '46px', height: '46px', border: `1px solid ${exportSeparator}`, background: exportPanelBgSoft, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Icon name="qrcode" style={{ margin: 0, color: exportNavy }} />
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
};

const renderMapExportSnapshotPreview = () => {
  if (!mapExportSnapshotChrome || !layoutPreviewSnapshot?.url) return null;

  const {
    stageTopPx,
    stageRightPx,
    imageLeftPx,
    imageTopPx,
    imageWidthPx,
    imageHeightPx,
  } = mapExportSnapshotChrome;

  return (
    <>
      <div
        data-scroll-overlay="true"
        className="MapExportSnapshotPreview-stage"
        style={{
          position: 'absolute',
          top: `${stageTopPx}px`,
          left: 0,
          right: `${stageRightPx}px`,
          bottom: 0,
          zIndex: 680,
          background: '#ffffff',
          pointerEvents: 'auto',
        }}
      />
      <div
        data-scroll-overlay="true"
        className="MapExportSnapshotPreview-page"
        aria-hidden="true"
        style={{
          position: 'absolute',
          top: `${imageTopPx}px`,
          left: `${imageLeftPx}px`,
          width: `${imageWidthPx}px`,
          height: `${imageHeightPx}px`,
          zIndex: 681,
          display: 'block',
          background: '#ffffff',
          border: '1px solid rgba(15, 23, 42, 0.10)',
          boxShadow: '0 10px 34px rgba(15, 23, 42, 0.16)',
          overflow: 'hidden',
          pointerEvents: 'none',
        }}
      >
        <img
          src={layoutPreviewSnapshot.url}
          alt="Layout preview snapshot for export"
          style={{
            width: '100%',
            height: '100%',
            display: 'block',
            objectFit: 'contain',
            background: '#ffffff',
          }}
        />
      </div>
    </>
  );
};

return (
    <MapProvider mapMethods={mapMethods}>
    <div
      ref={interactionRootRef}
      style={{ position: 'relative', width: '100%', height: 'calc(var(--vh, 1vh) * 100)', overflow: 'hidden' }}
    >
        <div
        ref={mapRef}
        style={mapPaneStyle}
        />

        {isCruiseContextMap && (
          <div
            data-scroll-overlay="true"
            style={{
              position: 'absolute',
              top: 12,
              left: 12,
              right: 12,
              zIndex: 1200,
              minHeight: 48,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '8px 12px',
              border: '1px solid rgba(203, 213, 225, 0.95)',
              borderRadius: 10,
              background: 'rgba(255, 255, 255, 0.97)',
              boxShadow: '0 8px 22px rgba(15, 23, 42, 0.16)',
              color: '#0f172a',
            }}
          >
            <Button
              type="button"
              size="small"
              icon="arrow left"
              content="Back to cruise"
              onClick={returnToCruiseOverview}
              style={{
                margin: 0,
                flex: '0 0 auto',
                borderRadius: 7,
                background: '#0b6fcf',
                color: '#fff',
                boxShadow: 'none',
                fontWeight: 800,
              }}
            />
            <div style={{ minWidth: 0, display: 'grid', gap: 1 }}>
              <div style={{ fontSize: 12, color: '#64748b', fontWeight: 700 }}>
                Cruise map
              </div>
              <div
                style={{
                  fontSize: isMobile ? 14 : 16,
                  fontWeight: 800,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                }}
              >
                {cruiseContextMapLabel}
              </div>
            </div>
          </div>
        )}

        {renderMapLayoutPreviewChrome()}
        {renderMapExportSnapshotPreview()}

        {showScaleCompassOverlay && !isMapLayoutPreviewActive && !isMapExportSnapshotActive && (
          <>
            <button
              className="TourUser-map-compass-control"
              type="button"
              title="Reset map north"
              aria-label="Reset map north"
              onClick={() => {
                  const view = mapInstanceRef.current?.getView?.();
                  view?.animate?.({ rotation: 0, duration: 200 });
                }}
              style={compassControlStyle}
            >
              <div
                style={{
                  transform: `rotate(${mapRotationDegrees}deg)`,
                  transition: 'transform 120ms ease',
                  color: '#f97316',
                  fontWeight: 800,
                  lineHeight: 1,
                  display: 'grid',
                  justifyItems: 'center',
                  gap: 1,
                }}
              >
                <span style={{ fontSize: 10, letterSpacing: 0 }}>N</span>
                <span style={{ fontSize: 18 }}>▲</span>
              </div>
            </button>
            <div
              style={scaleControlStyle}
            >
              <div style={{ fontSize: 12, fontWeight: 700, color: '#111827', textAlign: 'center', width: '100%', lineHeight: 1 }}>
                {mapScaleOverlay.text || 'Scale'}
              </div>
              <div
                style={{
                  height: 7,
                  width: Math.max(32, Math.min(150, mapScaleOverlay.width || 0)),
                  borderLeft: '2px solid #111827',
                  borderRight: '2px solid #111827',
                  borderBottom: '2px solid #111827',
                }}
              />
            </div>
          </>
        )}

        {showUserDeviceLocations && canShowUserDeviceLocations && (
          <div
            style={{
              position: 'absolute',
              top: isMobile ? '12px' : '16px',
              left: isMobile ? '12px' : '16px',
              zIndex: theme.zIndex.mapControls + 2,
              pointerEvents: 'none',
              padding: '7px 10px',
              borderRadius: 8,
              border: '1px solid rgba(15,23,42,0.16)',
              background: 'rgba(255,255,255,0.94)',
              boxShadow: theme.shadows.sm,
              color: userDeviceLocationsLoadError ? '#9f1239' : '#111827',
              fontSize: 12,
              fontWeight: 700,
              lineHeight: 1.2,
            }}
          >
            {userDeviceLocationsStatusLabel}
          </div>
        )}

        {countryStatsHoverHint.visible && (
          <div
            style={{
              position: 'absolute',
              left: `${countryStatsHoverHint.x}px`,
              top: `${countryStatsHoverHint.y}px`,
              zIndex: theme.zIndex.mapControls + 2,
              pointerEvents: 'none',
              transform: 'translate(0, -100%)',
              maxWidth: isMobile ? '220px' : '280px',
              padding: '8px 10px',
              borderRadius: '10px',
              background: 'rgba(15, 23, 42, 0.92)',
              color: '#fff',
              boxShadow: theme.shadows.md,
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.sm,
              lineHeight: 1.35,
            }}
          >
            <div style={{ fontWeight: 700 }}>
              {countryStatsHoverHint.countryName}
            </div>
            <div style={{ color: 'rgba(255,255,255,0.86)' }}>
              {countryStatsHoverHint.metricLabel}: {countryStatsHoverHint.metricValue}
            </div>
          </div>
        )}

        {showCountryStats && (
          <div
            data-scroll-overlay="true"
            style={{
              position: 'absolute',
              top: isMobile ? '12px' : '16px',
              left: isMobile ? '12px' : '16px',
              zIndex: theme.zIndex.mapControls + 1,
              width: isMobile ? 'min(276px, calc(100vw - 24px))' : '320px',
              maxHeight: isMobile ? 'min(44vh, 360px)' : 'min(70vh, 620px)',
              overflowY: 'auto',
              padding: isMobile ? '10px' : '14px',
              borderRadius: isMobile ? '10px' : '12px',
              border: '1px solid rgba(15, 23, 42, 0.10)',
              background: 'rgba(255,255,255,0.97)',
              boxShadow: theme.shadows.md,
              display: 'grid',
              gap: isMobile ? '8px' : '12px',
              fontFamily: theme.typography.fontFamily.primary,
            }}
          >
            <button
              type="button"
              onClick={() => setCountryStatsPanelCollapsed((previous) => !previous)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ display: 'grid', gap: isMobile ? '2px' : '4px' }}>
                <span style={{ fontSize: isMobile ? theme.typography.fontSize.sm : theme.typography.fontSize.base, fontWeight: 800, color: '#0f172a' }}>
                  Country stats
                </span>
                {!countryStatsPanelCollapsed && (
                  <span style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#475569' }}>
                    Choropleth controls and legend
                  </span>
                )}
              </span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#475569',
                  transform: countryStatsPanelCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 160ms ease',
                }}
              >
                ˅
              </span>
            </button>

            {!countryStatsPanelCollapsed && (
              <>
            <div style={{ display: 'grid', gap: isMobile ? '4px' : '6px' }}>
              <label
                htmlFor="country-stats-overlay-select"
                style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#1f2937', fontWeight: 600 }}
              >
                Stat
              </label>
              <select
                id="country-stats-overlay-select"
                value={countryStatsSelectedMetric}
                onChange={(event) => setCountryStatsSelectedMetric(event.target.value)}
                disabled={countryStatsMetricLoading}
                style={{
                  fontSize: isMobile ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
                  border: '1px solid rgba(0,0,0,0.18)',
                  borderRadius: '8px',
                  padding: isMobile ? '5px 8px' : '6px 10px',
                  background: '#fff',
                }}
              >
                <option value="">
                  {countryStatsMetricLoading ? 'Loading stats...' : 'Select statistic'}
                </option>
                {countryStatsMetricOptions.map((option) => (
                  <option key={option.data_name} value={option.data_name}>
                    {option.short_name || option.long_name || option.data_name}
                  </option>
                ))}
              </select>
              {currentCountryStatsMetricOption && !isMobile && (
                <div style={{ fontSize: theme.typography.fontSize.sm, color: '#64748b' }}>
                  {currentCountryStatsMetricOption.long_name || currentCountryStatsMetricOption.short_name}
                </div>
              )}
              {countryStatsMetricError && (
                <div style={{ fontSize: theme.typography.fontSize.sm, color: '#b91c1c' }}>
                  {countryStatsMetricError}
                </div>
              )}
            </div>

            <div style={{ display: 'grid', gap: isMobile ? '4px' : '6px' }}>
              <label
                htmlFor="country-stats-overlay-currency-select"
                style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#1f2937', fontWeight: 600 }}
              >
                Currency
              </label>
              <select
                id="country-stats-overlay-currency-select"
                value={countryStatsSelectedCurrency}
                onChange={(event) => setCountryStatsSelectedCurrency(event.target.value)}
                disabled={countryStatsCurrencyLoading}
                style={{
                  fontSize: isMobile ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
                  border: '1px solid rgba(0,0,0,0.18)',
                  borderRadius: '8px',
                  padding: isMobile ? '5px 8px' : '6px 10px',
                  background: '#fff',
                  opacity: countryStatsMetricSupportsCurrency ? 1 : 0.72,
                }}
              >
                <option value="">
                  {countryStatsCurrencyLoading ? 'Loading currencies...' : 'Select currency'}
                </option>
                {countryStatsCurrencyOptions.map((currencyCode) => (
                  <option key={currencyCode} value={currencyCode}>
                    {currencyCode}
                  </option>
                ))}
              </select>
              {!countryStatsMetricSupportsCurrency && !isMobile && (
                <div style={{ fontSize: theme.typography.fontSize.sm, color: '#64748b' }}>
                  Applies to GDP, salary, rent, shopping, and other currency-based stats.
                </div>
              )}
              {countryStatsCurrencyError && (
                <div style={{ fontSize: theme.typography.fontSize.sm, color: '#b91c1c' }}>
                  {countryStatsCurrencyError}
                </div>
              )}
            </div>

            <div
              style={{
                display: 'grid',
                gap: isMobile ? '6px' : '8px',
                paddingTop: '2px',
                borderTop: '1px solid rgba(15, 23, 42, 0.08)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', gap: '10px', alignItems: 'baseline' }}>
                <div style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#1f2937', fontWeight: 700 }}>
                  Legend
                </div>
                {currentCountryStatsMetricOption && (
                  <div style={{ fontSize: theme.typography.fontSize.xs, color: '#64748b', textAlign: 'right', maxWidth: isMobile ? '110px' : 'none' }}>
                    {formatCountryStatsOptionUnitLabel(currentCountryStatsMetricOption) || (countryStatsMetricSupportsCurrency ? countryStatsSelectedCurrency : '')}
                  </div>
                )}
              </div>

              {countryStatsLegendLoading && (
                <div style={{ fontSize: theme.typography.fontSize.sm, color: '#64748b' }}>
                  Loading legend...
                </div>
              )}

              {!countryStatsLegendLoading && countryStatsLegendError && (
                <div style={{ fontSize: theme.typography.fontSize.sm, color: '#b91c1c' }}>
                  {countryStatsLegendError}
                </div>
              )}

              {!countryStatsLegendLoading && !countryStatsLegendError && countryStatsLegendItems.length > 0 && (
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                    gap: isMobile ? '4px 8px' : '6px 10px',
                  }}
                >
                  {[...countryStatsLegendItems, { color: countryStatsNullColor, label: 'No current data' }].map((item) => (
                    <div
                      key={`${item.color}-${item.label}`}
                      style={{
                        display: 'grid',
                        gridTemplateColumns: isMobile ? '12px 1fr' : '16px 1fr',
                        gap: isMobile ? '6px' : '8px',
                        alignItems: 'center',
                        minWidth: 0,
                      }}
                    >
                      <span
                        aria-hidden="true"
                        style={{
                          width: isMobile ? '12px' : '16px',
                          height: isMobile ? '12px' : '16px',
                          borderRadius: isMobile ? '3px' : '4px',
                          background: item.color,
                          border: '1px solid rgba(15, 23, 42, 0.10)',
                        }}
                      />
                      <span style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#334155', lineHeight: 1.2 }}>
                        {item.label}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        )}

        {showCountryCompare && (
          <div
            data-scroll-overlay="true"
            style={{
              position: 'absolute',
              top: showCountryStats
                ? (isMobile ? 'calc(min(44vh, 360px) + 22px)' : '16px')
                : (isMobile ? '12px' : '16px'),
              left: showCountryStats && !isMobile ? '352px' : (isMobile ? '12px' : '16px'),
              zIndex: theme.zIndex.mapControls + 1,
              width: isMobile ? 'calc(100vw - 24px)' : '320px',
              maxWidth: isMobile ? 'calc(100vw - 24px)' : '320px',
              maxHeight: isMobile ? 'min(34vh, 280px)' : 'min(52vh, 360px)',
              overflowY: 'auto',
              overflowX: 'hidden',
              padding: isMobile ? '10px' : '12px',
              borderRadius: isMobile ? '10px' : '12px',
              border: '1px solid rgba(15, 23, 42, 0.10)',
              background: 'rgba(255,255,255,0.97)',
              boxShadow: theme.shadows.md,
              display: 'grid',
              gap: isMobile ? '8px' : '10px',
              fontFamily: theme.typography.fontFamily.primary,
              boxSizing: 'border-box',
            }}
          >
            <button
              type="button"
              onClick={() => setCountryComparePanelCollapsed((previous) => !previous)}
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: '10px',
                border: 'none',
                background: 'transparent',
                padding: 0,
                cursor: 'pointer',
                textAlign: 'left',
              }}
            >
              <span style={{ display: 'grid', gap: isMobile ? '2px' : '4px' }}>
                <span style={{ fontSize: isMobile ? theme.typography.fontSize.sm : theme.typography.fontSize.base, fontWeight: 800, color: '#0f172a' }}>
                  Country Compare
                </span>
                {!countryComparePanelCollapsed && (
                  <span style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#475569' }}>
                    Country shape controls
                  </span>
                )}
              </span>
              <span
                aria-hidden="true"
                style={{
                  fontSize: isMobile ? '12px' : '14px',
                  color: '#475569',
                  transform: countryComparePanelCollapsed ? 'rotate(0deg)' : 'rotate(180deg)',
                  transition: 'transform 160ms ease',
                }}
              >
                ˅
              </span>
            </button>

            {!countryComparePanelCollapsed && (
              <>
                <div style={{ display: 'grid', gap: isMobile ? '4px' : '6px' }}>
                  <label
                    htmlFor="country-compare-select"
                    style={{ fontSize: isMobile ? theme.typography.fontSize.xs : theme.typography.fontSize.sm, color: '#1f2937', fontWeight: 600 }}
                  >
                    Country shape
                  </label>
                  <select
                    id="country-compare-select"
                    value={countryCompareSelectedIso3}
                    onChange={(event) => setCountryCompareSelectedIso3(event.target.value)}
                    disabled={countryCompareLoadingOptions}
                    style={{
                      fontSize: isMobile ? theme.typography.fontSize.sm : theme.typography.fontSize.base,
                      border: '1px solid rgba(0,0,0,0.18)',
                      borderRadius: '8px',
                      padding: isMobile ? '5px 8px' : '6px 10px',
                      background: '#fff',
                      width: '100%',
                      minWidth: 0,
                      boxSizing: 'border-box',
                    }}
                  >
                    <option value="">{countryCompareLoadingOptions ? 'Loading countries...' : 'Select country'}</option>
                    {countryCompareOptions.map((option) => (
                      <option key={option.iso3} value={option.iso3}>
                        {`${option.name} (${option.iso3})`}
                      </option>
                    ))}
                  </select>
                </div>

                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  <button
                    type="button"
                    onClick={rotateCountryCompareBy180}
                    disabled={countryCompareLoadingShape || !countryCompareFeatureRef.current}
                    style={{
                      alignSelf: 'flex-start',
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: 600,
                      color: '#134e4a',
                      background: countryCompareLoadingShape || !countryCompareFeatureRef.current ? '#f1f5f9' : '#ccfbf1',
                      border: '1px solid rgba(15, 118, 110, 0.28)',
                      borderRadius: '8px',
                      padding: isMobile ? '5px 9px' : '6px 10px',
                      cursor: countryCompareLoadingShape || !countryCompareFeatureRef.current ? 'not-allowed' : 'pointer',
                      opacity: countryCompareLoadingShape || !countryCompareFeatureRef.current ? 0.65 : 1,
                    }}
                  >
                    Rotate 180°
                  </button>
                  <button
                    type="button"
                    onClick={() => setCountryCompareResizeWithLatitude((prev) => !prev)}
                    disabled={countryCompareLoadingShape || !countryCompareFeatureRef.current}
                    style={{
                      alignSelf: 'flex-start',
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: 600,
                      color: countryCompareResizeWithLatitude ? '#7c2d12' : '#334155',
                      background: countryCompareLoadingShape || !countryCompareFeatureRef.current
                        ? '#f1f5f9'
                        : (countryCompareResizeWithLatitude ? '#ffedd5' : '#e2e8f0'),
                      border: `1px solid ${countryCompareResizeWithLatitude ? 'rgba(249, 115, 22, 0.35)' : 'rgba(100, 116, 139, 0.35)'}`,
                      borderRadius: '8px',
                      padding: isMobile ? '5px 9px' : '6px 10px',
                      cursor: countryCompareLoadingShape || !countryCompareFeatureRef.current ? 'not-allowed' : 'pointer',
                      opacity: countryCompareLoadingShape || !countryCompareFeatureRef.current ? 0.65 : 1,
                    }}
                  >
                    {countryCompareResizeWithLatitude ? 'Resize: On' : 'Resize: Off'}
                  </button>
                  <button
                    type="button"
                    onClick={() => setCountryCompareFlipDistortion((prev) => !prev)}
                    disabled={countryCompareLoadingShape || !countryCompareFeatureRef.current}
                    style={{
                      alignSelf: 'flex-start',
                      fontSize: theme.typography.fontSize.sm,
                      fontWeight: 600,
                      color: countryCompareFlipDistortion ? '#14532d' : '#334155',
                      background: countryCompareLoadingShape || !countryCompareFeatureRef.current
                        ? '#f1f5f9'
                        : (countryCompareFlipDistortion ? '#dcfce7' : '#e2e8f0'),
                      border: `1px solid ${countryCompareFlipDistortion ? 'rgba(34, 197, 94, 0.35)' : 'rgba(100, 116, 139, 0.35)'}`,
                      borderRadius: '8px',
                      padding: isMobile ? '5px 9px' : '6px 10px',
                      cursor: countryCompareLoadingShape || !countryCompareFeatureRef.current ? 'not-allowed' : 'pointer',
                      opacity: countryCompareLoadingShape || !countryCompareFeatureRef.current ? 0.65 : 1,
                    }}
                  >
                    {countryCompareFlipDistortion ? 'Projection shape: On' : 'Projection shape: Off'}
                  </button>
                </div>

                {countryCompareLoadingShape && (
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: '#4b5563' }}>
                    Loading geometry...
                  </div>
                )}

                {countryCompareError && (
                  <div style={{ fontSize: theme.typography.fontSize.sm, color: '#b91c1c' }}>
                    {countryCompareError}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {showBootLoading && (
          <div
            style={{
              position: 'absolute',
              inset: 0,
              zIndex: 5,
              background: 'rgba(255,255,255,0.85)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontFamily: theme.typography.fontFamily.primary,
              fontSize: theme.typography.fontSize.md,
              color: '#333',
            }}
          >
            Loading your data...
          </div>
        )}

        {!isTourUser() && !isMapLayoutPreviewActive && (
          <div style={searchContainerStyle}>
            <div style={searchButtonStyle}>
              <button
                type="button"
                aria-label="Open search"
                onClick={() => setShowSearchBar(true)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '18px',
                  height: '18px',
                  border: 'none',
                  background: 'transparent',
                  padding: 0,
                  cursor: 'pointer',
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <circle cx="11" cy="11" r="7" fill="none" stroke="currentColor" strokeWidth="2" />
                  <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" />
                </svg>
              </button>
              {showSearchBar && (
                <div style={{ position: 'relative', flex: 1 }}>
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    onFocus={() => setSearchOpen((searchResults || []).length > 0)}
                    onBlur={() => setTimeout(() => setSearchOpen(false), 150)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && searchResults.length > 0) {
                        handleSearchSelect(searchResults[0]);
                      }
                      if (e.key === 'Escape') {
                        setShowSearchBar(false);
                        setSearchOpen(false);
                      }
                    }}
                    placeholder="Search place or address"
                    style={{
                      width: '100%',
                      padding: '8px 48px 8px 4px',
                      borderRadius: '16px',
                      border: 'none',
                      background: 'transparent',
                      outline: 'none',
                      fontSize: '14px',
                    }}
                  />
                  <button
                    type="button"
                    aria-label="Close search"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => {
                      if (searchAbortRef.current) {
                        searchAbortRef.current.abort();
                      }
                      setSearchQuery('');
                      setSearchResults([]);
                      setSearchOpen(false);
                      setShowSearchBar(false);
                    }}
                    style={{
                      position: 'absolute',
                      right: '6px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      width: '18px',
                      height: '18px',
                      border: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                      padding: 0,
                    }}
                  >
                    <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                      <line x1="6" y1="6" x2="18" y2="18" stroke="currentColor" strokeWidth="2" />
                      <line x1="18" y1="6" x2="6" y2="18" stroke="currentColor" strokeWidth="2" />
                    </svg>
                  </button>
                  {searchLoading && (
                    <div
                      style={{
                        position: 'absolute',
                        right: '32px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        fontSize: '12px',
                        color: '#777',
                      }}
                    >
                      ...
                    </div>
                  )}
                </div>
              )}
            </div>
                {showSearchBar && searchOpen && (
                  <div
                    style={{
                      position: 'absolute',
                      bottom: 'calc(100% + 6px)',
                      right: `calc(${mapControlSize} + ${mapControlGap})`,
                      left: 'auto',
                      width: searchPanelWidth,
                      maxHeight: '240px',
                      overflowY: 'auto',
                      background: 'rgba(255,255,255,0.98)',
                      border: '1px solid rgba(0,0,0,0.12)',
                  borderRadius: '8px',
                  boxShadow: theme.shadows.md,
                }}
              >
                {searchResults.length === 0 && !searchLoading ? (
                  <div style={{ padding: '10px 12px', fontSize: '13px', color: '#666' }}>
                    No results
                  </div>
                ) : (
                  searchResults.slice(0, 8).map((result) => (
                    <div
                      key={result.place_id || result.osm_id || result.display_name}
                      onMouseDown={() => handleSearchSelect(result)}
                      style={{
                        padding: '10px 12px',
                        cursor: 'pointer',
                        borderBottom: '1px solid rgba(0,0,0,0.06)',
                        fontSize: '13px',
                      }}
                    >
                      {result.display_name}
                    </div>
                  ))
                )}
              </div>
            )}
          </div>
        )}

        {canSwitchUsers && !isAnySidebarActive && (
          <div style={adminSwitchControlStyle}>
            <span
              style={{
                fontSize: theme.typography.fontSize.sm,
                color: theme.colors.neutral[700],
                fontWeight: 600,
                whiteSpace: 'nowrap',
              }}
            >
              Viewing as
            </span>
            <select
              value={activeUserSwitchValue}
              onChange={handleAdminSwitchChange}
              disabled={isUserSwitching || adminUserOptions.length === 0}
              style={{
                border: '1px solid rgba(0,0,0,0.18)',
                borderRadius: '16px',
                padding: '4px 8px',
                fontSize: theme.typography.fontSize.sm,
                minWidth: isMobile ? '170px' : '260px',
                maxWidth: isMobile ? '200px' : '320px',
                background: '#fff',
              }}
            >
              {adminUserOptions.map((userOption) => {
                const displayName = userOption.full_name || userOption.username || `User ${userOption.id}`;
                const emailSuffix = userOption.email ? ` (${userOption.email})` : '';
                const selfSuffix = userOption.is_self ? ' [Me]' : '';
                return (
                  <option key={userOption.id} value={String(userOption.id)}>
                    {`${displayName}${emailSuffix}${selfSuffix}`}
                  </option>
                );
              })}
            </select>
            {isImpersonating && (
              <button
                type="button"
                onClick={() => switchToUser(authenticatedUser?.object_id || authenticatedUser?.id)}
                disabled={isUserSwitching}
                style={{
                  border: 'none',
                  borderRadius: '16px',
                  padding: '4px 10px',
                  background: 'rgba(25,118,210,0.12)',
                  color: '#0f4a8a',
                  cursor: isUserSwitching ? 'not-allowed' : 'pointer',
                  fontSize: theme.typography.fontSize.sm,
                  whiteSpace: 'nowrap',
                }}
              >
                Back to me
              </button>
            )}
            {isUserSwitching && (
              <span
                style={{
                  fontSize: theme.typography.fontSize.xs,
                  color: theme.colors.neutral[600],
                  whiteSpace: 'nowrap',
                }}
              >
                Switching...
              </span>
            )}
          </div>
        )}

        {/* Geolocation Button */}
        {!isMapLayoutPreviewActive && !isTourUserDetailActive && (
          <GeolocationButton
            className="TourUser-map-current-device-button"
            onClick={getCurrentLocation}
            isLocating={isLocating}
            isMobile={isMobile}
            sidebarHeight={effectiveSidebarHeight}
            sidebarWidth={`${layoutSidebarWidthPx}px`}
            offsetBottom={isTourUserDetailActive && !isMobile ? desktopTourUserBottomPaneHeight : '0px'}
          />
        )}

        {!isMapLayoutPreviewActive && (!isTourUser() || isTourUserDetailActive) && (
          <div style={layersControlStyle}>
            <div style={{ position: 'relative' }}>
              <button
                className="TourUser-map-layers-button"
                type="button"
                aria-label="Settings"
                style={{
                  ...layersButtonStyle,
                  width: isMobile ? theme.components.button.size.md : '140px',
                  borderRadius: isMobile ? theme.components.button.borderRadius : '22px',
                  gap: isMobile ? 0 : '10px',
                  padding: isMobile ? 0 : '0 16px',
                  fontFamily: theme.typography.fontFamily.primary,
                  fontSize: theme.typography.fontSize.base,
                  color: theme.colors.neutral[800],
                }}
                onClick={() => {
                  setShowLayersPanel((prev) => {
                    if (!prev) {
                      setCloseFunctionsMenuSignal((signal) => signal + 1);
                    }
                    return !prev;
                  });
                }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                  <path
                    d="M12 8.5a3.5 3.5 0 110 7 3.5 3.5 0 010-7zm8.3 3.5a7.8 7.8 0 00-.1-1.1l2-1.5-2-3.4-2.4 1a8.2 8.2 0 00-1.9-1.1L15.6 3h-7.2l-.3 2.4a8.2 8.2 0 00-1.9 1.1l-2.4-1-2 3.4 2 1.5a7.8 7.8 0 000 2.2l-2 1.5 2 3.4 2.4-1a8.2 8.2 0 001.9 1.1l.3 2.4h7.2l.3-2.4a8.2 8.2 0 001.9-1.1l2.4 1 2-3.4-2-1.5c.1-.4.1-.7.1-1.1z"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                {!isMobile && <span>Settings</span>}
              </button>
              {showLayersPanel && (
                <>
                <button
                  type="button"
                  aria-label="Close settings"
                  onClick={() => setShowLayersPanel(false)}
                  style={{
                    position: 'fixed',
                    inset: 0,
                    zIndex: layersMenuZIndex - 1,
                    border: 0,
                    background: 'transparent',
                    padding: 0,
                    cursor: 'default',
                  }}
                />
                <div data-scroll-overlay="true" style={layersPanelStyle}>
                  <div style={layersGridStyle}>
                  {canUseSatelliteTiles && (
                    <button
                      type="button"
                      onClick={() => handleLayerMenuSelection(() => setShowMaptilerSatellite((prev) => !prev))}
                      style={getLayerButtonStyle(showMaptilerSatellite, '#4CAF50')}
                    >
                      <span>Satellite</span>
                      {!isMobile && renderLayerToggle(showMaptilerSatellite, '#4CAF50')}
                    </button>
                  )}
                  {!isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(toggleRiverKmLayer)}
                    style={getLayerButtonStyle(showRiverKm, '#1976d2')}
                  >
                    <span>River Km</span>
                    {!isMobile && renderLayerToggle(showRiverKm, '#1976d2')}
                  </button>
                  )}
	                  {(!isTourOverviewSettingsMode || !isMobile) && (
	                  <button
	                    type="button"
	                    onClick={() => handleLayerMenuSelection(toggleHillshadeLayer)}
	                    style={getLayerButtonStyle(showHillshade, '#6B7280')}
	                  >
	                    <span style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>Hillshade</span>
	                    {!isMobile && renderLayerToggle(showHillshade, '#6B7280')}
	                  </button>
	                  )}
	                  {(!isTourOverviewSettingsMode || !isMobile) && (
	                  <button
	                    type="button"
	                    onClick={() => handleLayerMenuSelection(toggleBathymetryLayer)}
	                    style={getLayerButtonStyle(showBathymetry, '#0E7490')}
	                  >
	                    <span style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>Bathymetry</span>
	                    {!isMobile && renderLayerToggle(showBathymetry, '#0E7490')}
	                  </button>
	                  )}
	                  <button
	                    type="button"
	                    onClick={() => handleLayerMenuSelection(toggleHolidayCountriesLayer)}
                    style={getLayerButtonStyle(showHolidayCountries, '#D32F2F')}
                  >
                    <span style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>Holidays</span>
                    {!isMobile && renderLayerToggle(showHolidayCountries, '#D32F2F')}
                  </button>
                  {!isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(toggleCountryStatsLayer)}
                    style={getLayerButtonStyle(showCountryStats, '#C2410C')}
                  >
                    <span style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>Country stats</span>
                    {!isMobile && renderLayerToggle(showCountryStats, '#C2410C')}
                  </button>
                  )}
                  {!isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(toggleCountryCompareLayer)}
                    style={getLayerButtonStyle(showCountryCompare, '#0f766e')}
                  >
                    <span style={{ flex: 1, textAlign: isMobile ? 'center' : 'left' }}>Country Compare</span>
                    {!isMobile && renderLayerToggle(showCountryCompare, '#0f766e')}
                  </button>
                  )}
                  {!isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(toggleHydroBasinsLayer)}
                    style={getLayerButtonStyle(showHydroBasins, '#0B7285')}
                  >
                    <span>HydroBASINS</span>
                    {!isMobile && renderLayerToggle(showHydroBasins, '#0B7285')}
                  </button>
                  )}
                  {!isTourOverviewSettingsMode && (
                  <div
                    style={{
                      ...layersFullWidthStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '8px',
                      padding: `${theme.spacing[2]} ${theme.spacing[3]}`,
                      borderRadius: '6px',
                      border: '1px solid rgba(0,0,0,0.08)',
                      background: '#fff',
                      fontSize: theme.typography.fontSize.base,
                    }}
                  >
                    <label
                      htmlFor="hydrobasins-level-select"
                      style={{
                        fontSize: theme.typography.fontSize.base,
                        color: '#1f2937',
                        fontWeight: 600,
                      }}
                    >
                      Basin level
                    </label>
                    <select
                      id="hydrobasins-level-select"
                      value={hydroBasinsLevel}
                      onChange={(event) => handleLayerMenuSelection(() => setHydroBasinsLevel(parseInt(event.target.value, 10)))}
                      style={{
                        fontSize: theme.typography.fontSize.base,
                        border: '1px solid rgba(0,0,0,0.2)',
                        borderRadius: '6px',
                        padding: '4px 8px',
                        background: '#fff',
                      }}
                    >
                      {Array.from({ length: 12 }, (_, idx) => idx + 1).map((level) => (
                        <option key={level} value={level}>
                          {`L${level}`}
                        </option>
                      ))}
                    </select>
                  </div>
                  )}
                  {!isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(() => setShowMapViewInfo((prev) => !prev))}
                    style={getLayerButtonStyle(showMapViewInfo, '#1976d2')}
                  >
                    <span>Coordinates</span>
                    {!isMobile && renderLayerToggle(showMapViewInfo, '#1976d2')}
                  </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(() => setShowScaleCompassOverlay((prev) => !prev))}
                    style={getLayerButtonStyle(showScaleCompassOverlay, '#f97316')}
                  >
                    <span>Scale & Compass</span>
                    {!isMobile && renderLayerToggle(showScaleCompassOverlay, '#f97316')}
                  </button>
                  {isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(() => setShowTourRouteLegend((prev) => !prev))}
                    style={getLayerButtonStyle(showTourRouteLegend, '#334155')}
                  >
                    <span>Route legend</span>
                    {!isMobile && renderLayerToggle(showTourRouteLegend, '#334155')}
                  </button>
                  )}
                  {!isTourOverviewSettingsMode && canShowUserDeviceLocations && (
                    <button
                      type="button"
                      onClick={() => handleLayerMenuSelection(() => setShowUserDeviceLocations((prev) => !prev))}
                      style={getLayerButtonStyle(showUserDeviceLocations, '#f97316')}
                    >
                      <span>{isLoadingUserDeviceLocations ? 'Loading Devices...' : 'My Devices'}</span>
                      {!isMobile && renderLayerToggle(showUserDeviceLocations, '#f97316')}
                    </button>
                  )}
                  {!isTourOverviewSettingsMode && canShowRiverSystemLayer && (
                    <button
                      type="button"
                      onClick={() => handleLayerMenuSelection(() => setShowRiversSystem((prev) => !prev))}
                      style={getLayerButtonStyle(showRiversSystem, '#1744a6')}
                    >
                      <span>River System</span>
                      {!isMobile && renderLayerToggle(showRiversSystem, '#1744a6')}
                    </button>
                  )}
                  {!isTourOverviewSettingsMode && canShowLocksLayer && (
                    <button
                      type="button"
                      onClick={() => handleLayerMenuSelection(() => shipLayersController.toggleLocks?.())}
                      disabled={!mapReady}
                      style={getLayerButtonStyle(shipLayerState.locks, '#1976d2', !mapReady)}
                    >
                      <span>Locks</span>
                      {!isMobile && renderLayerToggle(shipLayerState.locks, '#1976d2')}
                    </button>
                  )}
                  {!isTourOverviewSettingsMode && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(() => shipLayersController.toggleWorldHeritageSites?.())}
                    disabled={!mapReady}
                    style={getLayerButtonStyle(shipLayerState.worldHeritageSites, '#c69214', !mapReady)}
                  >
                    <span style={{ flex: 1, minWidth: 0, textAlign: isMobile ? 'center' : 'left' }}>World Heritage Sites</span>
                    {!isMobile && renderLayerToggle(shipLayerState.worldHeritageSites, '#c69214')}
                  </button>
                  )}
                  {!isTourOverviewSettingsMode && canShowDocksLayer && (
                    <button
                      type="button"
                      onClick={() => handleLayerMenuSelection(() => shipLayersController.toggleDocks?.())}
                      disabled={!mapReady}
                      style={getLayerButtonStyle(shipLayerState.docks, '#1976d2', !mapReady)}
                    >
                      <span>Docks</span>
                      {!isMobile && renderLayerToggle(shipLayerState.docks, '#1976d2')}
                    </button>
                  )}
                  {!isTourOverviewSettingsMode && canShowPegelsLayer && (
                    <button
                      type="button"
                      onClick={() => handleLayerMenuSelection(() => shipLayersController.togglePegels?.())}
                      disabled={!mapReady}
                      style={getLayerButtonStyle(shipLayerState.pegels, '#1976d2', !mapReady)}
                    >
                      <span>Pegels</span>
                      {!isMobile && renderLayerToggle(shipLayerState.pegels, '#1976d2')}
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(() => setDistanceMeasureActive(prev => !prev))}
                    style={getLayerButtonStyle(distanceMeasureActive, '#e91e63')}
                  >
                    <span>Measure</span>
                    {!isMobile && renderLayerToggle(distanceMeasureActive, '#e91e63')}
                  </button>
                  {isMobile && (
                  <button
                    type="button"
                    onClick={() => handleLayerMenuSelection(() => setQuickMeasureActive(prev => !prev))}
                    style={getLayerButtonStyle(quickMeasureActive, '#ff9800')}
                  >
                    <span>Quick Measure</span>
                    {!isMobile && renderLayerToggle(quickMeasureActive, '#ff9800')}
                  </button>
                  )}
                  </div>
                </div>
                </>
              )}
            </div>
          </div>
        )}

        {!isMapLayoutPreviewActive && (
          <div style={zoomControlsStyle}>
            <button
              type="button"
              aria-label="Zoom in"
              style={layersButtonStyle}
              onClick={() => handleZoom(1)}
            >
              +
            </button>
            <button
              type="button"
              aria-label="Zoom out"
              style={layersButtonStyle}
              onClick={() => handleZoom(-1)}
            >
              −
            </button>
          </div>
        )}

        {showMapViewInfo && (
          <div
            onClick={() => {
              if (!mapViewEditing) openMapViewEditor();
            }}
            style={{
              position: 'absolute',
              left: theme.spacing[2],
              top: theme.spacing[2],
              zIndex: theme.zIndex.mapControls,
              background: 'rgba(255,255,255,0.95)',
              border: '1px solid rgba(0,0,0,0.15)',
              borderRadius: '22px',
              padding: '6px 10px',
              fontSize: theme.typography.fontSize.sm,
              fontFamily: theme.typography.fontFamily.primary,
              color: theme.colors.neutral[800],
              boxShadow: theme.shadows.md,
              cursor: mapViewEditing ? 'default' : 'pointer',
            }}
          >
            {mapViewEditing ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '6px' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>Zoom</span>
                  <input
                    type="text"
                    value={mapViewDraft.zoom}
                    onChange={(e) => setMapViewDraft((prev) => ({ ...prev, zoom: e.target.value }))}
                    onBlur={applyMapViewDraft}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyMapViewDraft();
                      if (e.key === 'Escape') setMapViewEditing(false);
                    }}
                    style={{
                      width: '48px',
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily.primary,
                      border: '1px solid rgba(0,0,0,0.15)',
                      borderRadius: '16px',
                      padding: '0 6px',
                      height: '28px',
                      background: '#fff',
                    }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>Lat</span>
                  <input
                    type="text"
                    value={mapViewDraft.lat}
                    onChange={(e) => setMapViewDraft((prev) => ({ ...prev, lat: e.target.value }))}
                    onPaste={(e) => handleMapViewCoordPaste('lat', e)}
                    onBlur={applyMapViewDraft}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyMapViewDraft();
                      if (e.key === 'Escape') setMapViewEditing(false);
                    }}
                    style={{
                      width: '78px',
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily.primary,
                      border: '1px solid rgba(0,0,0,0.15)',
                      borderRadius: '16px',
                      padding: '0 6px',
                      height: '28px',
                      background: '#fff',
                    }}
                  />
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <span>Lon</span>
                  <input
                    type="text"
                    value={mapViewDraft.lon}
                    onChange={(e) => setMapViewDraft((prev) => ({ ...prev, lon: e.target.value }))}
                    onPaste={(e) => handleMapViewCoordPaste('lon', e)}
                    onBlur={applyMapViewDraft}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') applyMapViewDraft();
                      if (e.key === 'Escape') setMapViewEditing(false);
                    }}
                    style={{
                      width: '78px',
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily.primary,
                      border: '1px solid rgba(0,0,0,0.15)',
                      borderRadius: '16px',
                      padding: '0 6px',
                      height: '28px',
                      background: '#fff',
                    }}
                  />
                </label>
                <button
                  type="button"
                  onClick={addMarkerAtMapViewCoords}
                  title="Place marker at displayed coordinates"
                  aria-label="Place marker at displayed coordinates"
                  style={{
                    border: '1px solid rgba(0,0,0,0.15)',
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '0 8px',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily.primary,
                    height: '28px',
                    cursor: 'pointer',
                  }}
                >
                  Mark
                </button>
                {hasMapViewCoordMarker && (
                  <button
                    type="button"
                    onClick={removeMapViewCoordMarker}
                    title="Delete coordinate-bar marker"
                    aria-label="Delete coordinate-bar marker"
                    style={{
                      border: '1px solid rgba(220,0,0,0.2)',
                      background: '#fff',
                      color: '#c62828',
                      borderRadius: '16px',
                      padding: '0 8px',
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily.primary,
                      height: '28px',
                      cursor: 'pointer',
                    }}
                  >
                    Del
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => setMapViewEditing(false)}
                  style={{
                    border: '1px solid rgba(0,0,0,0.15)',
                    background: '#fff',
                    borderRadius: '16px',
                    padding: '0 10px',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily.primary,
                    height: '28px',
                    cursor: 'pointer',
                  }}
                >
                  Close
                </button>
              </div>
            ) : (
              <div style={{ display: 'inline-flex', alignItems: 'center', gap: '8px' }}>
                <span>
                Zoom {mapViewInfo.zoom !== null ? mapViewInfo.zoom.toFixed(2) : '--'} •{' '}
                {mapViewInfo.center
                  ? `${mapViewInfo.center[0].toFixed(5)}, ${mapViewInfo.center[1].toFixed(5)}`
                  : '--, --'}
                </span>
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    addMarkerAtMapViewCoords();
                  }}
                  title="Place marker at displayed coordinates"
                  aria-label="Place marker at displayed coordinates"
                  style={{
                    border: '1px solid rgba(0,0,0,0.15)',
                    background: '#fff',
                    borderRadius: '14px',
                    padding: '0 8px',
                    fontSize: theme.typography.fontSize.sm,
                    fontFamily: theme.typography.fontFamily.primary,
                    height: '24px',
                    lineHeight: 1,
                    cursor: 'pointer',
                  }}
                >
                  Mark
                </button>
                {hasMapViewCoordMarker && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      removeMapViewCoordMarker();
                    }}
                    title="Delete coordinate-bar marker"
                    aria-label="Delete coordinate-bar marker"
                    style={{
                      border: '1px solid rgba(220,0,0,0.2)',
                      background: '#fff',
                      color: '#c62828',
                      borderRadius: '14px',
                      padding: '0 8px',
                      fontSize: theme.typography.fontSize.sm,
                      fontFamily: theme.typography.fontFamily.primary,
                      height: '24px',
                      lineHeight: 1,
                      cursor: 'pointer',
                    }}
                  >
                    Del
                  </button>
                )}
              </div>
            )}
          </div>
        )}

        {showRiversSystem && selectedRiverStretch && (
          <div
            style={{
              position: 'absolute',
              left: theme.spacing[2],
              top: showMapViewInfo ? '58px' : theme.spacing[2],
              zIndex: theme.zIndex.mapControls,
              background: 'rgba(255,255,255,0.97)',
              border: '1px solid rgba(23,68,166,0.22)',
              borderRadius: '14px',
              padding: '10px 12px',
              minWidth: isMobile ? '220px' : '280px',
              maxWidth: isMobile ? 'calc(100vw - 24px)' : '360px',
              boxShadow: theme.shadows.md,
              fontFamily: theme.typography.fontFamily.primary,
              color: theme.colors.neutral[800],
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '8px', marginBottom: '6px' }}>
              <strong style={{ fontSize: theme.typography.fontSize.sm }}>River stretch</strong>
              <button
                type="button"
                onClick={clearSelectedRiverStretch}
                style={{
                  border: '1px solid rgba(0,0,0,0.12)',
                  background: '#fff',
                  borderRadius: '10px',
                  padding: '2px 8px',
                  fontSize: theme.typography.fontSize.xs,
                  cursor: 'pointer',
                }}
              >
                Close
              </button>
            </div>
            <div style={{ fontSize: theme.typography.fontSize.xs, lineHeight: 1.45 }}>
              {selectedRiverStretch.name && <div><strong>Name:</strong> {selectedRiverStretch.name}</div>}
              {selectedRiverStretch.cemt && <div><strong>CEMT:</strong> {selectedRiverStretch.cemt}</div>}
              {selectedRiverStretch.cemtDimensions && (
                <div style={{ color: theme.colors.neutral[700] }}>
                  <strong>Max vessel:</strong> {selectedRiverStretch.cemtDimensions}
                </div>
              )}
              {selectedRiverStretch.waterway && <div><strong>Type:</strong> {selectedRiverStretch.waterway}</div>}
              {selectedRiverStretch.lengthLabel && <div><strong>Length (approx):</strong> {selectedRiverStretch.lengthLabel}</div>}
            </div>
          </div>
        )}

        {/* Distance Measurement Bar */}
        {distanceMeasureActive && (
          <div
            onClick={(event) => event.stopPropagation()}
            style={{
              position: 'absolute',
              left: theme.spacing[2],
              bottom: `calc(${mapControlsBottom} + 40px)`,
              zIndex: theme.zIndex.mapControls,
              background: 'rgba(233,30,99,0.95)',
              border: '1px solid rgba(233,30,99,0.3)',
              borderRadius: isMobile ? '22px' : '14px',
              padding: isMobile ? '6px 12px' : '10px 12px',
              fontSize: theme.typography.fontSize.sm,
              fontFamily: theme.typography.fontFamily.primary,
              color: '#fff',
              boxShadow: theme.shadows.md,
              display: 'grid',
              gap: isMobile ? '8px' : '10px',
              minWidth: isMobile ? 'auto' : '420px',
              maxWidth: isMobile ? 'calc(100vw - 24px)' : '540px',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ flex: '0 0 auto' }}>
                <path d="M2 12h4m12 0h4M12 2v4m0 12v4" />
                <circle cx="12" cy="12" r="3" />
              </svg>
              <span style={{ flex: 1, minWidth: 0 }}>
                {Number.isInteger(distanceMeasureInsertAfterIndex)
                  ? `Click map to insert after point ${distanceMeasureInsertAfterIndex + 1}`
                  : distanceMeasurePoints.length === 0
                  ? 'Click to add points'
                  : distanceMeasurePoints.length === 1
                  ? '1 point • Click to add more'
                  : `${distanceMeasurePoints.length} points • Total: ${formatDistance(distanceMeasureTotalKm, distanceMeasureUnit)}`}
              </span>
              <button
                type="button"
                title={distanceMeasureUnit === 'km' ? 'Show nautical miles' : 'Show kilometers'}
                onClick={(event) => {
                  event.stopPropagation();
                  setDistanceMeasureUnit((unit) => (unit === 'km' ? 'nm' : 'km'));
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: theme.typography.fontSize.xs,
                  color: '#fff',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                {distanceMeasureUnit === 'km' ? 'km' : 'N.M.'}
              </button>
              {distanceMeasurePoints.length > 0 && (
                <button
                  type="button"
                  onClick={(event) => {
                    event.stopPropagation();
                    setDistanceMeasurePoints([]);
                    setDistanceMeasureTotalKm(0);
                    setDistanceMeasureInsertAfterIndex(null);
                    distanceMeasureSourceRef.current?.clear();
                  }}
                  style={{
                    background: 'rgba(255,255,255,0.2)',
                    border: 'none',
                    borderRadius: '12px',
                    padding: '2px 8px',
                    fontSize: theme.typography.fontSize.xs,
                    color: '#fff',
                    cursor: 'pointer',
                    flex: '0 0 auto',
                  }}
                >
                  Clear
                </button>
              )}
              <button
                type="button"
                onClick={(event) => {
                  event.stopPropagation();
                  setDistanceMeasureActive(false);
                }}
                style={{
                  background: 'rgba(255,255,255,0.2)',
                  border: 'none',
                  borderRadius: '12px',
                  padding: '2px 8px',
                  fontSize: theme.typography.fontSize.xs,
                  color: '#fff',
                  cursor: 'pointer',
                  flex: '0 0 auto',
                }}
              >
                Close
              </button>
            </div>

            {!isMobile && distanceMeasurePoints.length > 0 && (
              <div
                style={{
                  display: 'grid',
                  gap: '6px',
                  maxHeight: '220px',
                  overflowY: 'auto',
                  paddingTop: '2px',
                }}
              >
                {distanceMeasureWaypointRows.map((waypoint, index) => (
                  <div
                    key={`${index}-${waypoint.lon}-${waypoint.lat}`}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '24px minmax(0, 1fr) auto auto',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '6px',
                      borderRadius: '8px',
                      background: distanceMeasureInsertAfterIndex === index
                        ? 'rgba(255,255,255,0.24)'
                        : 'rgba(255,255,255,0.12)',
                    }}
                  >
                    <span style={{ fontWeight: 700, textAlign: 'center' }}>{index + 1}</span>
                    <span style={{ display: 'grid', gap: '2px', minWidth: 0 }}>
                      <span style={{ fontSize: theme.typography.fontSize.xs, lineHeight: 1.2 }}>
                        {waypoint.lat.toFixed(5)}, {waypoint.lon.toFixed(5)}
                      </span>
                      <span style={{ fontSize: '11px', lineHeight: 1.2, color: 'rgba(255,255,255,0.82)' }}>
                        Prev: {waypoint.distanceFromPreviousLabel} | Total: {waypoint.trailingTotalLabel}
                      </span>
                    </span>
                    <button
                      type="button"
                      title={`Insert waypoint after ${index + 1}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        setDistanceMeasureInsertAfterIndex(index);
                      }}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      +
                    </button>
                    <button
                      type="button"
                      title={`Delete waypoint ${index + 1}`}
                      onClick={(event) => {
                        event.stopPropagation();
                        deleteDistanceMeasurePoint(index);
                      }}
                      style={{
                        width: '24px',
                        height: '24px',
                        borderRadius: '12px',
                        border: 'none',
                        background: 'rgba(255,255,255,0.2)',
                        color: '#fff',
                        cursor: 'pointer',
                        fontWeight: 700,
                        lineHeight: 1,
                      }}
                    >
                      x
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Quick Two-Finger Measurement Bar */}
        {quickMeasureResult && !distanceMeasureActive && (
          <div
            style={{
              position: 'absolute',
              left: theme.spacing[2],
              bottom: `calc(${mapControlsBottom} + 40px)`,
              zIndex: theme.zIndex.mapControls,
              background: 'rgba(255,152,0,0.95)',
              border: '1px solid rgba(255,152,0,0.3)',
              borderRadius: '22px',
              padding: '6px 12px',
              fontSize: theme.typography.fontSize.sm,
              fontFamily: theme.typography.fontFamily.primary,
              color: '#fff',
              boxShadow: theme.shadows.md,
              display: 'flex',
              alignItems: 'center',
              gap: '8px',
            }}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
            <span>
              A → B: {formatDistance(quickMeasureResult.distance)}
            </span>
            <button
              type="button"
              onClick={() => {
                setQuickMeasureResult(null);
                quickMeasureSourceRef.current?.clear();
              }}
              style={{
                background: 'rgba(255,255,255,0.2)',
                border: 'none',
                borderRadius: '12px',
                padding: '2px 8px',
                fontSize: theme.typography.fontSize.xs,
                color: '#fff',
                cursor: 'pointer',
              }}
            >
              Dismiss
            </button>
          </div>
        )}

        {/* LoadMenu - Main menu button and sidebar controls */}
        {!isMapLayoutPreviewActive && !(
          (activeTourToShow && activeUser?.object_type === 'tour_user') ||
          (activeMapToShow && activeUser?.object_type === 'map')
        ) && (
          <LoadMenu
            map={mapInstanceRef.current}
            userID={activeUser?.id}
            activeUser={activeUser}
            mapBuildings={[]}
            getApiHeaders={getApiHeaders}
            canEditMap={activeUser?.subscription?.can_edit_maps || false}
            canReadMap={activeUser?.subscription?.can_read_maps || false}
            showMapListBar={showMapListBar}
            setShowMapListBar={openMapListBar}
            showTourListBar={showTourListBar}
            setShowTourListBar={openTourListBar}
            setShowTourDetailBar={openTourDetailBar}
            setShowSideEditBar={setShowSideEditBar}
            setShowChatBar={openChatBar}
            showChatBar={showChatBar}
            showShipMenu={showShipMenu}
            setShowShipMenu={openShipMenu}
            openShipMenuWithMode={openShipMenu}
            showAroundMeBar={showAroundMeBar}
            setShowAroundMeBar={openAroundMeBar}
            openPortDockEditBar={openPortDockEditBar}
            showFlightTrackingBar={showFlightTrackingBar}
            setShowFlightTrackingBar={openFlightTrackingBar}
            showFlightPriceBar={showFlightPriceBar}
            openFlightPriceBar={openFlightPriceBar}
            setShowFlightPriceBar={setShowFlightPriceBar}
            showFlightRouteCreateBar={showFlightRouteCreateBar}
            openFlightRouteCreateBar={openFlightRouteCreateBar}
            setShowFlightRouteCreateBar={setShowFlightRouteCreateBar}
            showFileShareBar={showFileShareBar}
            setShowFileShareBar={openFileShareBar}
            isMobile={isMobile}
            mapControlsRight={mapControlsRight}
            mapControlsBottom={mapControlsBottom}
            mapControlGap={mapControlGap}
            mapControlSize={mapControlSize}
            onMenuOpen={() => setShowLayersPanel(false)}
            closeFunctionsMenuSignal={closeFunctionsMenuSignal}
            setActiveMapToEdit={setActiveMapToEdit}
            fetchFullMapData={fetchFullMapData}
            userTours={userTours}
            selectTourAndOpenDetail={selectTourAndOpenDetail}
            setShowTrackingDevices={setTrackingDevicesPanelOpen}
          />
        )}
        
        {/* MapListBar - User and Corporate Maps - Always visible when showMapListBar is true */}
        {showMapListBar && !isTourUser() && !(isMobile && activeMapToEdit) && (
          <MapListBar
            activeUser={activeUser}
            userMaps={userMaps}
            setUserMaps={setUserMaps}
            corporateMaps={corporateMaps}
            setCorporateMaps={setCorporateMaps}
            setActiveMapToEdit={setActiveMapToEdit}
            getApiHeaders={getApiHeaders}
            setShowSideListBar={closeAllSidebars}
            isMobile={isMobile}
            activeMapToEdit={activeMapToEdit}
            sidebarConfig={sidebarConfig}
            fetchFullMapData={fetchFullMapData}
            onHeightChange={setDynamicSidebarHeight}
          />
        )}
        
        {/* MapEditBar - Map Editing Interface - Only visible when editing a specific map */}
        {showMapListBar && activeMapToEdit && !isTourUser() && (
          <Suspense fallback={lazyFallback}>
            <MapEditBar
              mapBuildings={[]}
              activeMapToEdit={activeMapToEdit}
              setActiveMapToEdit={setActiveMapToEdit}
              addBuildingToList={() => {}} // kept for compatibility (not used)
              setMapBuildings={() => {}} // MapEditBar now manages its own mapBuildings
              sBuildingMarkers={null}
              activeUser={activeUser}
              userMaps={userMaps}
              setUserMaps={setUserMaps}
              setCorporateMaps={setCorporateMaps}
              setShowSideListBar={setShowMapListBar}
              setShowSideEditBar={setShowSideEditBar}
              getApiHeaders={getApiHeaders}
              isMobile={isMobile}
              sidebarConfig={sidebarConfig}
              // New props for returning to TourDetailBar
              returnToTourDetailBar={Boolean(showTourDetailBar || activeMapToEdit?._returnToTourId)}
              setShowTourDetailBar={setShowTourDetailBar}
              // Props for tour memory functionality
              setActiveTourToShow={setActiveTourToShow}
              userTours={userTours}
              onHeightChange={setDynamicSidebarHeight}
              onLayoutPreviewChange={handleMapLayoutPreviewChange}
              onWorkspaceChange={setMapEditorWorkspace}
              isCapturingLayoutPreview={isCapturingLayoutPreview}
              onCaptureLayoutPreview={captureLayoutPreviewSnapshot}
            />
          </Suspense>
        )}
        
        {(showShipMenu || showLayersPanel || hasActiveShipLayers) && (
          <Suspense fallback={lazyFallback}>
            <ShipMenu
              map={mapInstanceRef.current}   // passes OL map (also available via MapProvider)
              showShipMenu={showShipMenu}
              getApiHeaders={getApiHeaders}
              getTileAuthQuery={getTileAuthQuery}
              setShowShipMenu={handleShipMenuVisibility} // lets child close itself
              requestedRouteMode={shipMenuRouteMode}
              requestedRiverMarkerActive={shipMenuActivateRiverMarker}
              requestedRoutePrefill={shipMenuRoutePrefill}
              shipLayersController={shipLayersController}
              isMobile={isMobile}
              userID={activeUser?.id}
              activeUser={activeUser}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
              onCruisePlanningPointsChange={setShipMenuPlannerPoints}
              mapLayerClickEvent={shipLayerClickEvent}
              onInteractiveLayersChange={handleShipMenuInteractiveLayersChange}
              onDockEditRequestHandlerChange={handleShipMenuDockEditHandlerChange}
            />
          </Suspense>
        )}

        {showTrackingDevicesPanel && (
          <Suspense fallback={lazyFallback}>
            <TrackingDevicesBar
              map={mapInstanceRef.current}
              showTrackingDevices={showTrackingDevicesPanel}
              setShowTrackingDevices={setTrackingDevicesPanelOpen}
              getApiHeaders={getApiHeaders}
              getTileAuthQuery={getTileAuthQuery}
              isMobile={isMobile}
              activeUser={activeUser}
              userTours={userTours}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
              onInteractiveLayersChange={handleShipMenuInteractiveLayersChange}
            />
          </Suspense>
        )}

        <Suspense fallback={null}>
          <PegelModal
            getApiHeaders={getApiHeaders}
            mapLayerClickEvent={pegelLayerClickEvent}
            isPegelsVisible={shipLayerState.pegels}
            availablePegels={shipLayersController.availablePegels}
            onFeedback={() => {}}
            isMobile={isMobile}
          />
        </Suspense>

        <Modal
          open={showShipLayerLockModal}
          onClose={() => setShowShipLayerLockModal(false)}
          size="small"
        >
          <Header icon="lock" content={selectedShipLayerLock?.lockData?.name || "Lock Details"} />
          <Modal.Content>
            {selectedShipLayerLock?.lockData?.tags && (
              <List>
                {selectedShipLayerLock.lockData.tags.CEMT && (
                  <List.Item>
                    <Icon name="ship" />
                    <List.Content>
                      <List.Header>CEMT Class</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.tags.CEMT}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerLock.lockData.tags.lockage && (
                  <List.Item>
                    <Icon name="arrow up" />
                    <List.Content>
                      <List.Header>Lockage</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.tags.lockage}m</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerLock.lockData.tags.phone && (
                  <List.Item>
                    <Icon name="phone" />
                    <List.Content>
                      <List.Header>Phone</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.tags.phone}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerLock.lockData.tags.vhf && (
                  <List.Item>
                    <Icon name="radio" />
                    <List.Content>
                      <List.Header>VHF Channel</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.tags.vhf}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerLock.lockData.tags.lock_name && (
                  <List.Item>
                    <Icon name="tag" />
                    <List.Content>
                      <List.Header>Lock Name</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.tags.lock_name}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerLock.lockData.altitude && (
                  <List.Item>
                    <Icon name="arrow up" />
                    <List.Content>
                      <List.Header>Altitude</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.altitude}m</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerLock.lockData.waterway_distance && (
                  <List.Item>
                    <Icon name="road" />
                    <List.Content>
                      <List.Header>Waterway Distance</List.Header>
                      <List.Description>{selectedShipLayerLock.lockData.waterway_distance}km</List.Description>
                    </List.Content>
                  </List.Item>
                )}
              </List>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button color="green" onClick={() => setShowShipLayerLockModal(false)}>
              <Icon name="checkmark" /> Close
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal
          open={showShipLayerDockModal}
          onClose={() => setShowShipLayerDockModal(false)}
          size="small"
        >
          <Header icon="anchor" content={selectedShipLayerDock?.dockData?.full_name || "Docking Location Details"} />
          <Modal.Content>
            {selectedShipLayerDock && (
              <List>
                {selectedShipLayerDock.dockData?.name && (
                  <List.Item>
                    <Icon name="tag" />
                    <List.Content>
                      <List.Header>Name</List.Header>
                      <List.Description>{selectedShipLayerDock.dockData.name}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerDock.dockData?.docking_type?.name && (
                  <List.Item>
                    <Icon name="ship" />
                    <List.Content>
                      <List.Header>Docking Type</List.Header>
                      <List.Description>{selectedShipLayerDock.dockData.docking_type.name}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerDock.dockData?.port_of_call?.name && (
                  <List.Item>
                    <Icon name="map marker" />
                    <List.Content>
                      <List.Header>Port of Call</List.Header>
                      <List.Description>{selectedShipLayerDock.dockData.port_of_call.name}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerDock.dockData?.port_of_call?.country?.name && (
                  <List.Item>
                    <Icon name="flag" />
                    <List.Content>
                      <List.Header>Country</List.Header>
                      <List.Description>{selectedShipLayerDock.dockData.port_of_call.country.name}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerDock.dockData?.notes && (
                  <List.Item>
                    <Icon name="sticky note" />
                    <List.Content>
                      <List.Header>Notes</List.Header>
                      <List.Description>{selectedShipLayerDock.dockData.notes}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerDock.dockData?.river_km && (
                  <List.Item>
                    <Icon name="road" />
                    <List.Content>
                      <List.Header>River KM</List.Header>
                      <List.Description>{selectedShipLayerDock.dockData.river_km}km</List.Description>
                    </List.Content>
                  </List.Item>
                )}
              </List>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button
              color="blue"
              onClick={() => {
                const dockData = selectedShipLayerDock?.dockData;
                openDockEditFromLayerModal(dockData);
              }}
            >
              <Icon name="edit" /> Edit
            </Button>
            <Button color="green" onClick={() => setShowShipLayerDockModal(false)}>
              <Icon name="checkmark" /> Close
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal
          open={showShipLayerShipModal}
          onClose={() => setShowShipLayerShipModal(false)}
          size="small"
        >
          <Header icon="ship" content={selectedShipLayerShip?.shipData?.name || "Ship Details"} />
          <Modal.Content>
            {selectedShipLayerShip && (
              <List>
                {selectedShipLayerShip.shipData?.name && (
                  <List.Item>
                    <Icon name="ship" />
                    <List.Content>
                      <List.Header>Ship Name</List.Header>
                      <List.Description>{selectedShipLayerShip.shipData.name}</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerShip.shipData?.speed && (
                  <List.Item>
                    <Icon name="tachometer alternate" />
                    <List.Content>
                      <List.Header>Speed</List.Header>
                      <List.Description>{selectedShipLayerShip.shipData.speed} knots</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerShip.speedKmh && (
                  <List.Item>
                    <Icon name="tachometer alternate" />
                    <List.Content>
                      <List.Header>Speed (km/h)</List.Header>
                      <List.Description>{selectedShipLayerShip.speedKmh.toFixed(1)} km/h</List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerShip.shipData?.latitude && selectedShipLayerShip.shipData?.longitude && (
                  <List.Item>
                    <Icon name="map marker" />
                    <List.Content>
                      <List.Header>Position</List.Header>
                      <List.Description>
                        {selectedShipLayerShip.shipData.latitude.toFixed(6)}, {selectedShipLayerShip.shipData.longitude.toFixed(6)}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerShip.shipData?.last_pos_update && (
                  <List.Item>
                    <Icon name="clock" />
                    <List.Content>
                      <List.Header>Last Position Update</List.Header>
                      <List.Description>
                        {new Date(selectedShipLayerShip.shipData.last_pos_update).toLocaleString()}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                )}
                {selectedShipLayerShip.shipData?.update_age !== undefined && (
                  <List.Item>
                    <Icon name="hourglass half" />
                    <List.Content>
                      <List.Header>Update Age</List.Header>
                      <List.Description>{selectedShipLayerShip.shipData.update_age} minutes ago</List.Description>
                    </List.Content>
                  </List.Item>
                )}
              </List>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button color="green" onClick={() => setShowShipLayerShipModal(false)}>
              <Icon name="checkmark" /> Close
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal
          open={showShipHistoryMarkerModal}
          onClose={() => setShowShipHistoryMarkerModal(false)}
          size="small"
        >
          <Header icon="map marker alternate" content={selectedShipHistoryMarker?.name || "Position Passage"} />
          <Modal.Content>
            {selectedShipHistoryMarker && (() => {
              const passageDate = selectedShipHistoryMarker.lastPosUpdate
                ? new Date(selectedShipHistoryMarker.lastPosUpdate)
                : null;
              const hasValidPassageDate = passageDate && !Number.isNaN(passageDate.getTime());
              return (
                <List>
                  <List.Item>
                    <Icon name="calendar alternate outline" />
                    <List.Content>
                      <List.Header>Date of passage</List.Header>
                      <List.Description>
                        {hasValidPassageDate ? passageDate.toLocaleDateString() : "Unknown"}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                  <List.Item>
                    <Icon name="clock outline" />
                    <List.Content>
                      <List.Header>Time of passage</List.Header>
                      <List.Description>
                        {hasValidPassageDate ? passageDate.toLocaleTimeString() : "Unknown"}
                      </List.Description>
                    </List.Content>
                  </List.Item>
                  {selectedShipHistoryMarker.speedKmh !== null && (
                    <List.Item>
                      <Icon name="tachometer alternate" />
                      <List.Content>
                        <List.Header>Recorded speed</List.Header>
                        <List.Description>
                          {selectedShipHistoryMarker.speedKmh.toFixed(1)} km/h
                        </List.Description>
                      </List.Content>
                    </List.Item>
                  )}
                  {selectedShipHistoryMarker.latitude !== null && selectedShipHistoryMarker.longitude !== null && (
                    <List.Item>
                      <Icon name="map marker alternate" />
                      <List.Content>
                        <List.Header>Position</List.Header>
                        <List.Description>
                          {selectedShipHistoryMarker.latitude.toFixed(6)}, {selectedShipHistoryMarker.longitude.toFixed(6)}
                        </List.Description>
                      </List.Content>
                    </List.Item>
                  )}
                </List>
              );
            })()}
          </Modal.Content>
          <Modal.Actions>
            <Button color="green" onClick={() => setShowShipHistoryMarkerModal(false)}>
              <Icon name="checkmark" /> Close
            </Button>
          </Modal.Actions>
        </Modal>

        <Modal
          open={showShipLayerPathModal}
          onClose={() => setShowShipLayerPathModal(false)}
          size="small"
        >
          <Header icon="ship" content="Cruise Path Details" />
          <Modal.Content>
            {selectedShipLayerPath?.routeInfo && (
              <div>
                {selectedShipLayerPath.routeInfo.route_type === "sea" ? (
                  <List>
                    <List.Item>
                      <Icon name="road" />
                      <List.Content>
                        <List.Header>Distance</List.Header>
                        <List.Description>{Math.round(selectedShipLayerPath.routeInfo.distance_km)} km</List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="ship" />
                      <List.Content>
                        <List.Header>Route Mode</List.Header>
                        <List.Description>{selectedShipLayerPath.routeInfo.route_mode}</List.Description>
                      </List.Content>
                    </List.Item>
                  </List>
                ) : selectedShipLayerPath.routeInfo.route_type === "road" ? (
                  <List>
                    <List.Item>
                      <Icon name="road" />
                      <List.Content>
                        <List.Header>Distance</List.Header>
                        <List.Description>{Math.round(selectedShipLayerPath.routeInfo.distance_km)} km</List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="clock" />
                      <List.Content>
                        <List.Header>Estimated Travel Time</List.Header>
                        <List.Description>{Number(selectedShipLayerPath.routeInfo.duration_hours || 0).toFixed(1)} hours</List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="car" />
                      <List.Content>
                        <List.Header>Route Mode</List.Header>
                        <List.Description>Fastest car route</List.Description>
                      </List.Content>
                    </List.Item>
                  </List>
                ) : (
                  <List>
                    <List.Item>
                      <Icon name="road" />
                      <List.Content>
                        <List.Header>Distance</List.Header>
                        <List.Description>{Math.round(selectedShipLayerPath.routeInfo.distance_to_cruise)} km</List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="clock" />
                      <List.Content>
                        <List.Header>Estimated Travel Time</List.Header>
                        <List.Description>{selectedShipLayerPath.routeInfo.time_to_cruise.toFixed(1)} hours</List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="tachometer alternate" />
                      <List.Content>
                        <List.Header>Time Range</List.Header>
                        <List.Description>
                          Min: {selectedShipLayerPath.routeInfo.time_to_cruise_min.toFixed(1)}h |
                          Max: {selectedShipLayerPath.routeInfo.time_to_cruise_90.toFixed(1)}h
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="arrow up" />
                      <List.Content>
                        <List.Header>Altitude Change</List.Header>
                        <List.Description>
                          From {selectedShipLayerPath.routeInfo.Point_A_alt}m to {selectedShipLayerPath.routeInfo.Point_B_alt}m
                          ({selectedShipLayerPath.routeInfo.Point_B_alt - selectedShipLayerPath.routeInfo.Point_A_alt > 0 ? '+' : ''}
                          {selectedShipLayerPath.routeInfo.Point_B_alt - selectedShipLayerPath.routeInfo.Point_A_alt}m)
                        </List.Description>
                      </List.Content>
                    </List.Item>
                    <List.Item>
                      <Icon name="lock" />
                      <List.Content>
                        <List.Header>Locks</List.Header>
                        <List.Description>{selectedShipLayerPath.routeInfo.lock_count} locks along the route</List.Description>
                      </List.Content>
                    </List.Item>
                  </List>
                )}
              </div>
            )}
          </Modal.Content>
          <Modal.Actions>
            <Button color="green" onClick={() => setShowShipLayerPathModal(false)}>
              <Icon name="checkmark" /> Close
            </Button>
          </Modal.Actions>
        </Modal>

        <WorldHeritageSidebarPanel
          isVisible={showWorldHeritagePanel}
          isMobile={isMobile}
          sidebarConfig={sidebarConfig}
          onClose={closeWorldHeritagePanel}
          onHeightChange={setDynamicSidebarHeight}
          selectedWorldHeritageSite={selectedWorldHeritageSite}
          isLoadingWorldHeritageDetails={isLoadingWorldHeritageDetails}
          worldHeritageDetailsError={worldHeritageDetailsError}
          parentWorldHeritageSite={worldHeritageParentSiteDetails}
          isLoadingParentWorldHeritageDetails={isLoadingWorldHeritageParentSiteDetails}
          parentWorldHeritageDetailsError={worldHeritageParentSiteDetailsError}
          isWorldHeritageComponent={isWorldHeritageComponentPanel}
          onOpenParentSite={openWorldHeritageParentSite}
        />

        {countryStatsModalState.open && (
          <div
            onClick={closeCountryStatsModal}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.36)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2100,
              padding: isMobile ? '16px' : '24px',
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: 'min(760px, 100%)',
                maxHeight: '84vh',
                overflowY: 'auto',
                borderRadius: '12px',
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
                padding: isMobile ? '16px' : '20px',
                fontFamily: theme.typography.fontFamily.primary,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'flex-end',
                  gap: '8px',
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <button
                    type="button"
                    onClick={closeCountryStatsModal}
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#64748b',
                      cursor: 'pointer',
                      fontSize: '20px',
                      lineHeight: 1,
                      padding: '2px 4px',
                    }}
                    aria-label="Close country stats modal"
                  >
                    ×
                  </button>
                </div>
              </div>

              <div style={{ marginTop: '14px' }}>
                {countryStatsModalState.loading && (
                  <div style={{ color: '#334155', fontSize: theme.typography.fontSize.base }}>
                    Loading country statistics...
                  </div>
                )}

                {!countryStatsModalState.loading && countryStatsModalState.error && (
                  <div style={{ color: '#b91c1c', fontSize: theme.typography.fontSize.base }}>
                    {countryStatsModalState.error}
                  </div>
                )}

                {!countryStatsModalState.loading && !countryStatsModalState.error && countryStatsModalState.metrics.length === 0 && (
                  <div style={{ color: '#334155', fontSize: theme.typography.fontSize.base }}>
                    No statistics available for this country.
                  </div>
                )}

                {!countryStatsModalState.loading && !countryStatsModalState.error && countryStatsModalState.metrics.length > 0 && (
                  (() => {
                    const groupedMetrics = countryStatsModalState.metrics.reduce((acc, metric) => {
                      const sectionTitle = getCountryStatsSectionTitle(metric.category);
                      if (!acc[sectionTitle]) acc[sectionTitle] = [];
                      acc[sectionTitle].push(metric);
                      return acc;
                    }, {});
                    const sectionOrder = ['Geography', 'People and society', 'Economy and prices', 'Other'];
                    const orderedSections = sectionOrder.filter((section) => groupedMetrics[section]?.length > 0);
                    const flagUrl = countryStatsModalState.displayMedia?.flag_url || '';
                    const capitalPhotoUrl = countryStatsModalState.displayMedia?.capital_photo_url || '';
                    const capitalCaption = countryStatsModalState.displayMedia?.capital_caption || countryStatsModalState.countryCapital || 'Capital';

                    return (
                      <div
                        style={{
                          display: 'grid',
                          gap: '14px',
                        }}
                      >
                        {(countryStatsModalState.countryName || flagUrl || capitalPhotoUrl) && (
                          <div
                            style={{
                              display: 'grid',
                              gridTemplateColumns: isMobile ? '1fr' : '240px minmax(0, 1fr)',
                              gap: '14px',
                              alignItems: 'stretch',
                            }}
                          >
                            <div
                              style={{
                                borderRadius: '12px',
                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                background: '#f8fafc',
                                padding: '14px',
                                display: 'flex',
                                flexDirection: 'column',
                                justifyContent: 'space-between',
                                gap: '14px',
                                minHeight: isMobile ? 'unset' : '250px',
                              }}
                            >
                              <div style={{ display: 'grid', gap: '12px' }}>
                                <div
                                  style={{
                                    fontSize: theme.typography.fontSize.xl,
                                    lineHeight: 1.15,
                                    color: '#0f172a',
                                    fontWeight: 800,
                                  }}
                                >
                                  {countryStatsModalState.countryName}
                                </div>
                                {flagUrl ? (
                                  <img
                                    src={flagUrl}
                                    alt={`${countryStatsModalState.countryName} flag`}
                                    style={{ width: '100%', maxHeight: '116px', objectFit: 'contain', alignSelf: 'center' }}
                                  />
                                ) : (
                                  <div style={{ width: '100%', height: '116px', background: '#e2e8f0', borderRadius: '8px' }} />
                                )}
                              </div>
                              <button
                                type="button"
                                onClick={() => {
                                  const iso3 = String(countryStatsModalState.countryIso3 || '').trim().toUpperCase();
                                  if (!iso3) return;
                                  window.open(
                                    buildApiUrl(
                                      `country-stats/${encodeURIComponent(iso3)}/pdf/?base_currency=${encodeURIComponent(countryStatsSelectedCurrency)}`,
                                    ),
                                    '_blank',
                                    'noopener,noreferrer',
                                  );
                                }}
                                disabled={!countryStatsModalState.countryIso3}
                                style={{
                                  border: '1px solid rgba(0,0,0,0.12)',
                                  background: '#fff',
                                  color: '#0f172a',
                                  borderRadius: '10px',
                                  padding: '8px 12px',
                                  fontSize: theme.typography.fontSize.sm,
                                  fontWeight: 600,
                                  cursor: countryStatsModalState.countryIso3 ? 'pointer' : 'not-allowed',
                                  opacity: countryStatsModalState.countryIso3 ? 1 : 0.6,
                                  alignSelf: isMobile ? 'stretch' : 'center',
                                }}
                              >
                                Generate PDF
                              </button>
                            </div>
                            <div
                              style={{
                                borderRadius: '12px',
                                border: '1px solid rgba(15, 23, 42, 0.08)',
                                background: '#f8fafc',
                                padding: '12px',
                                display: 'grid',
                                gap: '8px',
                              }}
                            >
                              {capitalPhotoUrl ? (
                                <img
                                  src={capitalPhotoUrl}
                                  alt={capitalCaption}
                                  style={{ width: '100%', height: isMobile ? '200px' : '210px', objectFit: 'cover', borderRadius: '8px' }}
                                />
                              ) : (
                                <div style={{ width: '100%', height: isMobile ? '200px' : '210px', background: '#e2e8f0', borderRadius: '8px' }} />
                              )}
                              <div style={{ fontSize: theme.typography.fontSize.sm, color: '#334155', fontWeight: 600 }}>
                                {capitalCaption}
                              </div>
                            </div>
                          </div>
                        )}

                        {orderedSections.map((sectionTitle) => (
                          <div
                            key={sectionTitle}
                            style={{
                              borderRadius: '12px',
                              border: '1px solid rgba(15, 23, 42, 0.08)',
                              background: '#fff',
                              overflow: 'hidden',
                            }}
                          >
                            <div
                              style={{
                                background: '#a8b9e0',
                                color: '#0f172a',
                                fontSize: theme.typography.fontSize.sm,
                                fontWeight: 700,
                                padding: '8px 12px',
                              }}
                            >
                              {sectionTitle}
                            </div>
                            <div style={{ padding: '10px 12px', display: 'grid', gap: '10px' }}>
                              {groupedMetrics[sectionTitle].map((metric) => {
                                const isActiveMetric = metric.code === countryStatsSelectedMetric;
                                const sourceYear = metric.source_year ?? metric.year ?? null;
                                const sourceName = metric.source_name
                                  ? prettifyCountryStatsSourceName(metric.source_name)
                                  : '';
                                const sourceLink = getCountryStatsSourceLink(metric);
                                const unitLabel = formatCountryStatsUnitLabel(metric);
                                return (
                                  <div
                                    key={`${sectionTitle}-${metric.code}-${metric.year ?? 'latest'}`}
                                    style={{
                                      display: 'grid',
                                      gridTemplateColumns: isMobile ? '1fr' : 'minmax(170px, 220px) 1fr',
                                      gap: '8px 14px',
                                      alignItems: 'start',
                                      paddingBottom: '8px',
                                      borderBottom: '1px solid rgba(148, 163, 184, 0.18)',
                                    }}
                                  >
                                    <div style={{ color: isActiveMetric ? '#9a3412' : '#0f172a', fontWeight: isActiveMetric ? 800 : 700 }}>
                                      {metric.short_name || metric.long_name || metric.code}
                                      {unitLabel && (
                                        <div style={{ marginTop: '2px', fontSize: theme.typography.fontSize.xs, color: '#64748b', fontWeight: 500 }}>
                                          {unitLabel}
                                        </div>
                                      )}
                                    </div>
                                    <div style={{ display: 'grid', gap: '4px', justifyItems: isMobile ? 'start' : 'end' }}>
                                      <div
                                        style={{
                                          fontWeight: isActiveMetric ? 800 : 500,
                                          color: '#1f2937',
                                          lineHeight: 1.4,
                                          textAlign: isMobile ? 'left' : 'right',
                                          wordBreak: 'break-word',
                                        }}
                                      >
                                        {formatCountryStatsMetricValue(metric)}
                                      </div>
                                      {(sourceYear || sourceName) && (
                                        <div
                                          style={{
                                            fontSize: theme.typography.fontSize.xs,
                                            color: '#64748b',
                                            background: 'rgba(148, 163, 184, 0.10)',
                                            padding: '4px 6px',
                                            borderRadius: '8px',
                                            textAlign: isMobile ? 'left' : 'right',
                                          }}
                                        >
                                          {sourceYear ? `Year: ${sourceYear}` : ''}
                                          {sourceYear && sourceName ? ' • ' : ''}
                                          {sourceName && (
                                            <>
                                              Source:{' '}
                                              {sourceLink ? (
                                                <a
                                                  href={sourceLink}
                                                  target="_blank"
                                                  rel="noopener noreferrer"
                                                  style={{ color: '#0f766e', textDecoration: 'none' }}
                                                >
                                                  {sourceName}
                                                </a>
                                              ) : (
                                                <span>{sourceName}</span>
                                              )}
                                            </>
                                          )}
                                        </div>
                                      )}
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>
                        ))}
                      </div>
                    );
                  })()
                )}
              </div>
            </div>
          </div>
        )}

        {holidayModalState.open && (
          <div
            onClick={closeHolidayModal}
            style={{
              position: 'fixed',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.36)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 2100,
              padding: isMobile ? '16px' : '24px',
            }}
          >
            <div
              onClick={(event) => event.stopPropagation()}
              style={{
                width: 'min(620px, 100%)',
                maxHeight: '80vh',
                overflowY: 'auto',
                borderRadius: '12px',
                background: '#fff',
                border: '1px solid rgba(0,0,0,0.1)',
                boxShadow: '0 20px 50px rgba(15, 23, 42, 0.25)',
                padding: isMobile ? '16px' : '20px',
                fontFamily: theme.typography.fontFamily.primary,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  justifyContent: 'space-between',
                  gap: '12px',
                }}
              >
                <div>
                  <h3
                    style={{
                      margin: 0,
                      fontSize: theme.typography.fontSize.lg,
                      color: '#0f172a',
                    }}
                  >
                    Upcoming Holidays: {holidayModalState.countryName}
                  </h3>
                  <div
                    style={{
                      marginTop: '4px',
                      fontSize: theme.typography.fontSize.sm,
                      color: '#475569',
                    }}
                  >
                    {holidayModalState.countryIso3 || 'Unknown ISO3'}
                    {Number.isFinite(holidayModalState.daysUntil) && (
                      <span>{` • Next in ${holidayModalState.daysUntil} day${holidayModalState.daysUntil === 1 ? '' : 's'}`}</span>
                    )}
                    {holidayModalState.urgencyBucket ? <span>{` • Bucket ${holidayModalState.urgencyBucket}`}</span> : null}
                  </div>
                </div>
                <button
                  type="button"
                  onClick={closeHolidayModal}
                  style={{
                    border: 'none',
                    background: 'transparent',
                    color: '#64748b',
                    cursor: 'pointer',
                    fontSize: '20px',
                    lineHeight: 1,
                    padding: '2px 4px',
                  }}
                  aria-label="Close holiday modal"
                >
                  ×
                </button>
              </div>

              <div style={{ marginTop: '14px' }}>
                {holidayModalState.loading && (
                  <div style={{ color: '#334155', fontSize: theme.typography.fontSize.base }}>
                    Loading upcoming holidays...
                  </div>
                )}

                {!holidayModalState.loading && holidayModalState.error && (
                  <div style={{ color: '#b91c1c', fontSize: theme.typography.fontSize.base }}>
                    {holidayModalState.error}
                  </div>
                )}

                {!holidayModalState.loading && !holidayModalState.error && holidayModalState.holidays.length === 0 && (
                  <div style={{ color: '#334155', fontSize: theme.typography.fontSize.base }}>
                    No holidays in the next 21 days.
                  </div>
                )}

                {!holidayModalState.loading && !holidayModalState.error && holidayModalState.holidays.length > 0 && (
                  <div style={{ display: 'grid', gap: '10px' }}>
                    {holidayModalState.holidays.map((holiday) => {
                      const holidayName = holiday?.name || holiday?.local_name || 'Holiday';
                      const localName = holiday?.local_name && holiday.local_name !== holidayName ? holiday.local_name : '';
                      const typeLabel = holiday?.holiday_type || '';
                      const dayLabel = Number.isFinite(holiday?.days_until)
                        ? `in ${holiday.days_until} day${holiday.days_until === 1 ? '' : 's'}`
                        : '';

                      return (
                        <div
                          key={`${holiday?.id ?? 'no-id'}-${holiday?.date ?? 'no-date'}-${holidayName}`}
                          style={{
                            border: '1px solid rgba(15, 23, 42, 0.12)',
                            borderRadius: '8px',
                            padding: '10px 12px',
                            background: 'rgba(248, 250, 252, 0.8)',
                          }}
                        >
                          <div style={{ fontWeight: 600, color: '#0f172a' }}>{holidayName}</div>
                          {localName ? (
                            <div style={{ marginTop: '2px', color: '#334155', fontSize: theme.typography.fontSize.sm }}>
                              {localName}
                            </div>
                          ) : null}
                          <div style={{ marginTop: '6px', color: '#475569', fontSize: theme.typography.fontSize.sm }}>
                            {formatHolidayDate(holiday?.date)}
                            {dayLabel ? ` • ${dayLabel}` : ''}
                            {typeLabel ? ` • ${typeLabel}` : ''}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {showAroundMeBar && (
          <Suspense fallback={lazyFallback}>
            <AroundMeBar
              showAroundMeBar={showAroundMeBar}
              setShowAroundMeBar={openAroundMeBar}
              openShipMenuWithMode={openShipMenu}
              requestedSelectedLonLat={aroundMeRequestedSelectedLonLat}
              getApiHeaders={getApiHeaders}
              isMobile={isMobile}
              activeUser={activeUser}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
            />
          </Suspense>
        )}

        {/* PortDockEditBar - Port and Docking Location Creation/Editing */}
        {showPortDockEditBar && (
          <PortDockEditBar
            showPortDockEditBar={showPortDockEditBar}
            setShowPortDockEditBar={setShowPortDockEditBar}
            getApiHeaders={getApiHeaders}
            activeUser={activeUser}
            isMobile={isMobile}
            sidebarConfig={sidebarConfig}
            onHeightChange={setDynamicSidebarHeight}
            externalDockEditRequest={pendingDockEditRequest}
            onExternalDockEditRequestHandled={() => setPendingDockEditRequest(null)}
            onPortCreated={() => {
              // Callback when port is created - could refresh ShipMenu data if needed
              console.log("Port created successfully");
            }}
            onDockCreated={async () => {
              try {
                await shipLayersController.refreshDocks?.();
              } catch (error) {
                console.error("Error refreshing docking locations:", error);
              }
            }}
          />
        )}

        {/* TourListBar - Tour List - Always visible when showTourListBar is true */}
        {showTourListBar && (
          <TourListBar
            map={mapInstanceRef.current}   // passes OL map (also available via MapProvider)
            activeUser={activeUser}
            mapBuildings={null} // kept for compatibility; not used
            userTours={userTours}
            setUserTours={setUserTours}
            setActiveTourToShow={setActiveTourToShow}
            setShowTourListBar={setShowTourListBar}
            selectTourAndOpenDetail={selectTourAndOpenDetail}
            setActiveMapToEdit={setActiveMapToEdit}
            fetchFullMapData={fetchFullMapData}
            getApiHeaders={getApiHeaders}
            isMobile={isMobile}
            isUserSwitching={isUserSwitching}
            sidebarConfig={sidebarConfig}
            onHeightChange={setDynamicSidebarHeight}
            onWideSidebarChange={setTourListWideSidebar}
          />
        )}

        {/* FlightTrackingBar - Flight Tracking - Always visible when showFlightTrackingBar is true */}
        {showFlightTrackingBar && (
          <Suspense fallback={lazyFallback}>
            <FlightTrackingBar
              map={mapInstanceRef.current}   // passes OL map (also available via MapProvider)
              showFlightTrackingBar={showFlightTrackingBar}
              getApiHeaders={getApiHeaders}
              setShowFlightTrackingBar={setShowFlightTrackingBar}
              isMobile={isMobile}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
              tourId={activeTourToShow ? activeTourToShow.id : null}
            />
          </Suspense>
        )}

        {showFlightRouteCreateBar && (
          <Suspense fallback={lazyFallback}>
            <FlightRouteCreateBar
              getApiHeaders={getApiHeaders}
              showFlightPriceBar={showFlightRouteCreateBar}
              setShowFlightPriceBar={setShowFlightRouteCreateBar}
              onClose={closeFlightRouteCreateBar}
              isMobile={isMobile}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
              tourId={flightRouteCreateBarMode === "tour" ? flightRouteCreateBarTourId : null}
              activeTour={flightRouteCreateBarMode === "tour" ? activeTourToShow : null}
              mode={flightRouteCreateBarMode}
            />
          </Suspense>
        )}

        {showFlightPriceBar && (
          <Suspense fallback={lazyFallback}>
            <FlightPriceBar
              getApiHeaders={getApiHeaders}
              showFlightPriceBar={showFlightPriceBar}
              setShowFlightPriceBar={setShowFlightPriceBar}
              onClose={closeFlightPriceBar}
              isMobile={isMobile}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
              onOpenPriceHistory={openFlightPriceHistory}
              onClosePriceHistory={closeFlightPriceHistory}
              activePriceHistoryRoute={flightPriceHistoryState.selectedRoute}
              tourId={flightPriceBarMode === "tour" ? flightPriceBarTourId : null}
              activeTour={flightPriceBarMode === "tour" ? activeTourToShow : null}
              mode={flightPriceBarMode}
              openFlightRouteCreateBar={openFlightRouteCreateBar}
            />
          </Suspense>
        )}

        {flightPriceHistoryState.open && (
          <Suspense fallback={lazyFallback}>
            <FlightPriceItineraryBar
              getApiHeaders={getApiHeaders}
              open={flightPriceHistoryState.open}
              onClose={closeFlightPriceHistory}
              isMobile={isMobile}
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
              onSidebarStateChange={setPriceHistorySidebarState}
              priceHistoryData={flightPriceHistoryState.data}
              loadingPriceHistory={flightPriceHistoryState.loading}
              selectedRouteForHistory={flightPriceHistoryState.selectedRoute}
              filters={flightPriceHistoryFilters}
              onApplyFilters={async (filters) => {
                await saveFlightPriceHistoryFilters(flightPriceHistoryState.selectedRoute, filters);
                openFlightPriceHistory(flightPriceHistoryState.selectedRoute, filters);
              }}
            />
          </Suspense>
        )}

        {/* ChatBar - Chat Bot - Always visible when showChatBar is true */}
        {showChatBar && (
          <Suspense fallback={lazyFallback}>
            <ChatBar
              map={mapInstanceRef.current}   // passes OL map (also available via MapProvider)
              activeUser={activeUser}
              mapBuildings={[]} // kept for compatibility; not used
              userTours={userTours}
              setUserTours={setUserTours}
              setActiveTourToShow={setActiveTourToShow}
              setShowTourListBar={setShowTourListBar}
              setShowChatBar={setShowChatBar}
              getApiHeaders={getApiHeaders}
              isMobile={isMobile}
              sessionCookies={null} // Add session cookies if available
              sidebarConfig={sidebarConfig}
              onHeightChange={setDynamicSidebarHeight}
            />
          </Suspense>
        )}

        {/* FileShareBar - File Sharing - Always visible when showFileShareBar is true */}
        {showFileShareBar && (
          <FileShareBar
            getApiHeaders={getApiHeaders}
            onClose={() => setShowFileShareBar(false)}
            isMobile={isMobile}
            sidebarConfig={sidebarConfig}
            onHeightChange={setDynamicSidebarHeight}
          />
        )}

        {/* TourDetailBar - Tour Detail - Always visible when showTourDetailBar is true */}
        {showTourDetailBar && activeTourToShow && !isUserSwitching && activeTourInUserList && (
          <Suspense fallback={lazyFallback}>
            <TourDetailBar
              map={mapInstanceRef.current}   // passes OL map (also available via MapProvider)
              activeTourToShow={activeTourToShow}
              setActiveTourToShow={setActiveTourToShow}
              setShowTourDetailBar={setShowTourDetailBar}
              setShowTourListBar={setShowTourListBar}
              setNumberedMarkers={null} // optional: still called if provided
              getApiHeaders={getApiHeaders}
              openShipMenuWithMode={openShipMenu}
              isMobile={isMobile}
              setUserTours={setUserTours}
              activeUser={activeUser}
              sidebarConfig={sidebarConfig}
              // New props for MapEditBar integration
              activeMapToEdit={activeMapToEdit}
              setActiveMapToEdit={setActiveMapToEdit}
              setShowMapListBar={setShowMapListBar}
              onHeightChange={setDynamicSidebarHeight}
              setShowFlightTrackingBar={setShowFlightTrackingBar}
              onOpenFlightTrackingSidebar={openFlightTrackingBarFromTourDetail}
              suppressMapOverlays={tourDetailMapOverlaysSuppressed}
              setSuppressMapOverlays={setTourDetailMapOverlaysSuppressed}
              onWideSidebarChange={setTourDetailWideSidebar}
              openFlightPriceBar={openFlightPriceBar}
              openFlightRouteCreateBar={openFlightRouteCreateBar}
            />
          </Suspense>
        )}


        {activeTourToShow && activeUser?.object_type === 'tour_user' && (
        <TourDetailBarTourUser
            map={mapInstanceRef.current}
            activeTourToShow={activeTourToShow}
            setActiveTourToShow={setActiveTourToShow}
            setShowTourDetailBar={closeAllSidebars}
            setShowTourListBar={closeAllSidebars}
            getApiHeaders={getApiHeaders}
            isMobile={isMobile}
            setUserTours={setUserTours}
            activeUser={activeUser}
            onOpenMapInApp={openTourUserMapOnlyView}
            sidebarConfig={sidebarConfig}
            isVisible={showTourUserDetailView}
            setIsVisible={setShowTourUserDetailView}
            onHeightChange={setDynamicSidebarHeight}
            showRouteLegend={showTourRouteLegend}
            isFullTourPending={isFullTourPending}
        />
        )}

        {activeMapToShow && (activeUser?.object_type === 'map' || activeUser?.object_type === 'tour_user') && (
        <MapOnlyView
            mapData={activeMapToShow}
            isMobile={isMobile}
            tourMapContext={tourUserMapContext}
            onClose={handleMapOnlyViewClose}
            sidebarConfig={sidebarConfig}
            isVisible={showMapOnlyView}
            setIsVisible={setShowMapOnlyView}
            onHeightChange={setDynamicSidebarHeight}
        />
        )}
    </div>
    </MapProvider>
);
}
